
import type { Keyword } from './keywords.g';

import type { Character } from './characters.g';

import type { Plot } from './plots.g';

import type { Role } from './roles.g';

import type { Tragedy } from './tragedys.g';

import type { Incident } from './incidents.g';

import type { Script } from './scripts.g';

import type { Tag } from './tags.g';


type ReadonlyRecursive<T> = {
  readonly [P in keyof T]: T[P] extends (infer U)[]
  ? ReadonlyArray<ReadonlyRecursive<U>>
  : T[P] extends string
  ? T[P]
  : T[P] extends number
  ? T[P]
  : T[P] extends null
  ? null
  : T[P] extends undefined
  ? undefined
  : ReadonlyRecursive<T[P]>
  ;
};

;


export const keywords = [

 ...[
  {
    "id": "boy",
    "name": "Boy"
  },
  {
    "id": "girl",
    "name": "Girl"
  },
  {
    "id": "student",
    "name": "Student"
  },
  {
    "id": "man",
    "name": "Man"
  },
  {
    "id": "woman",
    "name": "Woman"
  },
  {
    "id": "adult",
    "name": "Adult"
  },
  {
    "id": "construct",
    "name": "Construct"
  },
  {
    "id": "animal",
    "name": "Animal"
  },
  {
    "id": "tree",
    "name": "Tree"
  },
  {
    "id": "littleSisterKeyword",
    "name": "Little Sister"
  }
]
] as const;
    export const keywordsLookup = keywords.reduce((p, c) => {
        if (typeof c === 'object' && c !== null && 'id' in c) {
            p[c.id] = c;
        }
        return p;
    }, {} as Record<typeof keywords[number]['id'], ReadonlyRecursive<Keyword>>);
    
    export function isKeywordId(id: unknown): id is keyof typeof keywordsLookup {
        return typeof id === 'string' && id in keywordsLookup;
    }
    
    ;


export const characters = [

 ...[
  {
    "id": "youngGirl",
    "name": "Young Girl",
    "edition": "Promo",
    "paranoiaLimit": 1,
    "tags": [
      "student",
      "girl"
    ],
    "startLocation": [
      "School"
    ],
    "forbiddenLocation": [
      "City",
      "Hospital",
      "Shrine"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 1,
        "description": "This Character‘s location restriction is removed"
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "Move this character t an adjacent location",
        "timesPerLoop": 1
      }
    ]
  },
  {
    "id": "copycat",
    "name": "Copycat",
    "edition": "Promo",
    "paranoiaLimit": 2,
    "plotLessRole": "plot duplicate",
    "tags": [
      "student",
      "boy"
    ],
    "startLocation": [
      "City"
    ],
    "forbiddenLocation": [],
    "abilities": [
      {
        "type": "passive",
        "description": "Script Creation: This Character must copy the role of another Character in the script (max amount ignored)."
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "Loop 2 or later: Reveal the name of all characters with the same Role as :copycat:. This cannot be refused by :goodwill: Refusel."
      }
    ]
  },
  {
    "id": "servant",
    "name": "Servant",
    "edition": "Promo",
    "paranoiaLimit": 3,
    "tags": [
      "adult",
      "woman"
    ],
    "startLocation": [
      "City",
      "School"
    ],
    "forbiddenLocation": [],
    "abilities": [
      {
        "type": "passive",
        "description": "The :servant: serves the :richStudent: and the :boss:. If one of them moves and she shares the location, the Leader moves her with one of them ignoring her own movement. If any onf them dies at her location, she dies instead."
      },
      {
        "type": "active",
        "goodwillRank": 4,
        "description": "Choose any other character. For the remainder of the Loop, she also serves that character.",
        "timesPerLoop": 1
      }
    ]
  }
],
 ...[
  {
    "id": "sennin",
    "name": "Sennin",
    "edition": "Promo",
    "paranoiaLimit": 0,
    "tags": [
      "adult",
      "man"
    ],
    "startLocation": [
      "Hospital",
      "Shrine"
    ],
    "scriptSpecified": [
      {
        "type": "number",
        "name": "Paranoia Limit",
        "description": "Specify a number for X to be the :paranoia: Limit. When checking the :paranoia: Limit for anything besides an Incident, treat this character's :paranoia: Limit as 0.",
        "optional": false
      }
    ],
    "forbiddenLocation": [],
    "abilities": [
      {
        "type": "passive",
        "description": "Script Creation: Specify a number “X” to be the :paranoia: Limit. Treat this character’s :paranoia: Limit as 0 for anything besides an Incident."
      },
      {
        "type": "passive",
        "description": "If this Character is an Incident Culprit, you may treat him as if he is in the next clockwise Location."
      },
      {
        "type": "active",
        "goodwillRank": 5,
        "description": "Move this Character to any Location, and revive one corpse in that area. Put “X” :goodwill: on the revived Character.",
        "timesPerLoop": 1
      }
    ]
  },
  {
    "id": "uploader",
    "name": "Uploader",
    "edition": "Promo",
    "paranoiaLimit": 2,
    "tags": [
      "student",
      "man"
    ],
    "startLocation": [
      "The Far Side"
    ],
    "forbiddenLocation": [],
    "abilities": [
      {
        "type": "passive",
        "description": "When the first Incident of the Loop occurs, during that Day's End Phase, the Mastermind sets an EX Card on a boy or girl. If the :uploader: uses a mandatory Ability, it is treated as being in the location of the EX Card."
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "+1 :goodwill: and -1 :paranoia: on a character in this Location. You can then transfer the EX Card to another character in the same area (who is not the :uploader:)."
      }
    ]
  }
],
 ...[],
 ...[
  {
    "id": "boyStudent",
    "name": "Boy Student",
    "edition": "Base",
    "paranoiaLimit": 2,
    "tags": [
      "student",
      "boy"
    ],
    "startLocation": [
      "School"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 2,
        "description": "-1 :paranoia: on student in same location."
      }
    ]
  },
  {
    "id": "girlStudent",
    "name": "Girl Student",
    "edition": "Base",
    "paranoiaLimit": 3,
    "tags": [
      "student",
      "girl"
    ],
    "startLocation": [
      "School"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 2,
        "description": "-1 :paranoia: on student in same location."
      }
    ]
  },
  {
    "id": "richStudent",
    "name": "Rich Man’s Daughter",
    "edition": "Base",
    "paranoiaLimit": 1,
    "tags": [
      "student",
      "girl"
    ],
    "startLocation": [
      "School"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 3,
        "restrictedToLocation": [
          "School",
          "City"
        ],
        "description": "+1 :goodwill: on character in same location."
      }
    ]
  },
  {
    "id": "classRep",
    "name": "Class Rep",
    "edition": "Base",
    "paranoiaLimit": 2,
    "tags": [
      "student",
      "girl"
    ],
    "startLocation": [
      "School"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 2,
        "timesPerLoop": 1,
        "description": "Leader gets one (1×∞) card back."
      }
    ]
  },
  {
    "id": "mysteryBoy",
    "name": "Mystery Boy",
    "edition": "Base",
    "paranoiaLimit": 3,
    "tags": [
      "student",
      "boy"
    ],
    "startLocation": [
      "School"
    ],
    "plotLessRole": "not in plots",
    "abilities": [
      {
        "type": "passive",
        "description": "Always has a role not associated with current plot."
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "immuneToGoodwillRefusel": true,
        "description": "Reveal own role."
      }
    ]
  },
  {
    "id": "shrineMaiden",
    "name": "Shrine Maiden",
    "edition": "Base",
    "paranoiaLimit": 2,
    "tags": [
      "student",
      "girl"
    ],
    "startLocation": [
      "Shrine"
    ],
    "forbiddenLocation": [
      "City"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 3,
        "restrictedToLocation": [
          "Shrine"
        ],
        "description": "-1 :intrigue: on Shrine."
      },
      {
        "type": "active",
        "goodwillRank": 5,
        "timesPerLoop": 1,
        "description": "Reveal role of character in same location"
      }
    ]
  },
  {
    "id": "alien",
    "name": "Alien",
    "edition": "Base",
    "paranoiaLimit": 2,
    "tags": [
      "girl"
    ],
    "startLocation": [
      "Shrine"
    ],
    "forbiddenLocation": [
      "Hospital"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 4,
        "timesPerLoop": 1,
        "description": "Kill one character in same location."
      },
      {
        "type": "active",
        "goodwillRank": 5,
        "timesPerLoop": 1,
        "description": "Revive one corpse in same location."
      }
    ]
  },
  {
    "id": "godlyBeing",
    "name": "Godly Being",
    "edition": "Base",
    "paranoiaLimit": 3,
    "tags": [
      "man",
      "woman"
    ],
    "startLocation": [
      "Shrine"
    ],
    "comesInLater": true,
    "scriptSpecified": [
      {
        "name": "enters on loop",
        "type": "number"
      }
    ],
    "abilities": [
      {
        "type": "passive",
        "description": "Enters game on predefined loop"
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "timesPerLoop": 1,
        "description": "Reveal culprit for 1 incident."
      },
      {
        "type": "active",
        "goodwillRank": 5,
        "description": "-1 :intrigue: on same location or Character in same location."
      }
    ]
  },
  {
    "id": "policeOfficer",
    "name": "Police Officer",
    "edition": "Base",
    "paranoiaLimit": 3,
    "tags": [
      "man",
      "adult"
    ],
    "startLocation": [
      "City"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 4,
        "timesPerLoop": 1,
        "description": "Reveal culprit for former incident."
      },
      {
        "type": "active",
        "goodwillRank": 5,
        "timesPerLoop": 1,
        "description": "Put an Extra marker on another character in same location. Remove that marker to prevent that character from dying."
      }
    ]
  },
  {
    "id": "officeWorker",
    "name": "Office Worker",
    "edition": "Base",
    "paranoiaLimit": 2,
    "tags": [
      "man",
      "adult"
    ],
    "startLocation": [
      "City"
    ],
    "forbiddenLocation": [
      "School"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "Reveal own role."
      }
    ]
  },
  {
    "id": "informer",
    "name": "Informer",
    "edition": "Base",
    "paranoiaLimit": 3,
    "tags": [
      "woman",
      "adult"
    ],
    "startLocation": [
      "City"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 5,
        "timesPerLoop": 1,
        "description": "Leader names the title of any Subplot. Then, the Mastermind must name the title of any other active subplot."
      }
    ]
  },
  {
    "id": "popIdol",
    "name": "Pop Idol",
    "edition": "Base",
    "paranoiaLimit": 2,
    "tags": [
      "girl",
      "student"
    ],
    "startLocation": [
      "City"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "-1 :paranoia: on character in same location."
      },
      {
        "type": "active",
        "goodwillRank": 4,
        "description": "+1 :goodwill: on character in same location."
      }
    ]
  },
  {
    "id": "journalist",
    "name": "Journalist",
    "edition": "Base",
    "paranoiaLimit": 2,
    "tags": [
      "adult",
      "man"
    ],
    "startLocation": [
      "City"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 2,
        "description": "+1 :paranoia: on any other character."
      },
      {
        "type": "active",
        "goodwillRank": 2,
        "description": "+1 :intrigue: on this Location or any character in this Location."
      }
    ]
  },
  {
    "id": "boss",
    "name": "Boss",
    "edition": "Base",
    "paranoiaLimit": 4,
    "tags": [
      "adult",
      "man"
    ],
    "startLocation": [
      "City"
    ],
    "scriptSpecified": [
      {
        "name": "Turf",
        "type": "location"
      }
    ],
    "abilities": [
      {
        "type": "passive",
        "description": "May be regarded as in his turf."
      },
      {
        "type": "active",
        "goodwillRank": 5,
        "timesPerLoop": 1,
        "description": "Reveal role of character in his turf."
      }
    ]
  },
  {
    "id": "doctor",
    "name": "Doctor",
    "edition": "Base",
    "paranoiaLimit": 2,
    "tags": [
      "adult",
      "man"
    ],
    "startLocation": [
      "Hospital"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 2,
        "description": "+/-1 :paranoia: on character in same location."
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "revoke location restirction for :patient:."
      }
    ]
  },
  {
    "id": "patient",
    "name": "Patient",
    "edition": "Base",
    "paranoiaLimit": 2,
    "tags": [
      "boy"
    ],
    "startLocation": [
      "Hospital"
    ],
    "forbiddenLocation": [
      "City",
      "School",
      "Shrine"
    ],
    "abilities": []
  },
  {
    "id": "nurse",
    "name": "Nurse",
    "edition": "Base",
    "paranoiaLimit": 3,
    "tags": [
      "adult",
      "woman"
    ],
    "startLocation": [
      "Hospital"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 2,
        "immuneToGoodwillRefusel": true,
        "description": "-1 :paranoia: on panicked character in same location. This ability cannot be refused by :goodwill: Refusel."
      }
    ]
  },
  {
    "id": "henchman",
    "name": "Henchman",
    "edition": "Base",
    "paranoiaLimit": 1,
    "tags": [
      "adult",
      "man"
    ],
    "startLocation": [
      "City",
      "School",
      "Shrine",
      "Hospital"
    ],
    "abilities": [
      {
        "type": "passive",
        "description": "Mastermind chooses start location each loop"
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "Dose not trigger incidents."
      }
    ]
  }
],
 ...[
  {
    "id": "teacher",
    "name": "Teacher",
    "edition": "Cosmic Evil",
    "paranoiaLimit": 2,
    "tags": [
      "adult",
      "woman"
    ],
    "startLocation": [
      "School"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "Pick a :student: in this location, and add or remove a :paranoia: from that student."
      },
      {
        "type": "active",
        "goodwillRank": 4,
        "timesPerLoop": 1,
        "description": "Reveal the role of one :student: in this location."
      }
    ]
  },
  {
    "id": "transferStudent",
    "name": "Transfer Student",
    "edition": "Cosmic Evil",
    "paranoiaLimit": 2,
    "tags": [
      "student",
      "girl"
    ],
    "comesInLater": true,
    "scriptSpecified": [
      {
        "name": "enters on day",
        "type": "number"
      }
    ],
    "startLocation": [
      "School"
    ],
    "abilities": [
      {
        "type": "passive",
        "description": "This character does not appear on the board until the start of the day specified by the script."
      },
      {
        "type": "active",
        "goodwillRank": 2,
        "description": "Change an :intrigue: conuter on any other character in this location to a :goodwill: counter."
      }
    ]
  },
  {
    "id": "soldier",
    "name": "Soldier",
    "edition": "Cosmic Evil",
    "paranoiaLimit": 3,
    "tags": [
      "adult",
      "man"
    ],
    "startLocation": [
      "Hospital"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 2,
        "timesPerLoop": 1,
        "description": "Place 2 :paranoia: on another character in this location."
      },
      {
        "type": "active",
        "goodwillRank": 5,
        "timesPerLoop": 1,
        "description": "The Protagonists cannot die for the reminder of the loop."
      }
    ]
  },
  {
    "id": "blackCat",
    "name": "Black Cat",
    "edition": "Cosmic Evil",
    "paranoiaLimit": 0,
    "tags": [
      "animal"
    ],
    "doseNotTriggerIncidentEffect": true,
    "startLocation": [
      "Shrine"
    ],
    "abilities": [
      {
        "type": "passive",
        "description": "At the start of each loop, place an :intrigue: on the Shrine."
      },
      {
        "type": "passive",
        "description": "Incidents of which this character is the culprit, change their effect into \"no effect\". (rule-wise they occur)"
      }
    ]
  }
],
 ...[
  {
    "id": "scientist",
    "name": "Scientist",
    "edition": "Midnight Circle",
    "paranoiaLimit": 2,
    "tags": [
      "adult",
      "man"
    ],
    "startLocation": [
      "Hospital"
    ],
    "abilities": [
      {
        "type": "passive",
        "description": "At the start of a loop, place either a :paranoia: counter, a :goodwill: counter or an :intrigue: counter on this character."
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "Remove all counters from this character. Then, if you use the Extra gauge, increase or decrease this gauge."
      }
    ]
  },
  {
    "id": "forensicSpecialist",
    "name": "Forensic Specialist",
    "edition": "Midnight Circle",
    "paranoiaLimit": 3,
    "tags": [
      "adult",
      "man"
    ],
    "startLocation": [
      "City"
    ],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 2,
        "timesPerLoop": 1,
        "description": "Move any one counter between any two other characters in this location."
      },
      {
        "type": "active",
        "goodwillRank": 5,
        "timesPerLoop": 1,
        "description": "Reveal the role of any one corpse."
      }
    ]
  },
  {
    "id": "ai",
    "name": "A.I.",
    "edition": "Midnight Circle",
    "paranoiaLimit": 4,
    "tags": [
      "construct"
    ],
    "startLocation": [
      "City"
    ],
    "forbiddenLocation": [
      "Shrine",
      "Hospital",
      "School"
    ],
    "abilities": [
      {
        "type": "passive",
        "description": "This character cannot have the role :person:."
      },
      {
        "type": "passive",
        "description": "When determining wether an Incident triggers, to which this character is the culprit, all conters on this character conut as :paranoia: conuters."
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "timesPerLoop": 1,
        "description": "The Leader may resolve one incident in the Script, wit the :ai: as the culprit. (This is not considered triggering the event.)"
      }
    ]
  },
  {
    "id": "illusion",
    "name": "Illusion",
    "edition": "Midnight Circle",
    "paranoiaLimit": 3,
    "tags": [
      "construct",
      "woman"
    ],
    "startLocation": [
      "Shrine"
    ],
    "abilities": [
      {
        "type": "passive",
        "description": "No action cards can be placed on this characte. All cards palced on this location are also applied to this character."
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "timesPerLoop": 1,
        "description": "Move any character from this location to any other location."
      },
      {
        "type": "active",
        "goodwillRank": 4,
        "timesPerLoop": 1,
        "description": "Remove thsi character from the board for the rest of the loop."
      }
    ]
  }
],
 ...[
  {
    "id": "partTimer",
    "name": "Part-Timer",
    "edition": "Rei",
    "paranoiaLimit": 1,
    "tags": [
      "adult",
      "man"
    ],
    "startLocation": [
      "City"
    ],
    "forbiddenLocation": [],
    "abilities": [
      {
        "type": "passive",
        "description": "This character ignores its Role and is a :person: instead."
      },
      {
        "type": "passive",
        "description": "At the end of the day, if there are 3 or more total counters on this character, he dies."
      },
      {
        "type": "passive",
        "description": "At the start of the day, if this character is dead, place ':partTimerAlternate:' in the City."
      }
    ]
  },
  {
    "id": "partTimerAlternate",
    "name": "Part-Timer(?)",
    "edition": "Rei",
    "paranoiaLimit": 3,
    "tags": [
      "girl"
    ],
    "startLocation": [
      "City"
    ],
    "forbiddenLocation": [],
    "nonSelectableCharacter": true,
    "abilities": [
      {
        "type": "passive",
        "description": "This character has the same Role, and is the Culprit of the same Incident(s), as :partTimer:."
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "Reveal this character's Role. Place 2 :goodwill: counters on any character at this location.",
        "timesPerLoop": 1
      }
    ]
  },
  {
    "id": "sectFounder",
    "name": "Sect Founder",
    "edition": "Rei",
    "paranoiaLimit": 3,
    "tags": [
      "adult",
      "woman"
    ],
    "startLocation": [
      "Shrine"
    ],
    "forbiddenLocation": [],
    "abilities": [
      {
        "type": "passive",
        "description": "If this character is the culprit of an Incident that resolves, its effects resolve twice."
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "Place a :goodwill: on an other Character who is at their :paranoia: Limit."
      },
      {
        "type": "active",
        "goodwillRank": 4,
        "description": "Reveal the role of another character who is at their :paranoia: Limit in this location.",
        "timesPerLoop": 1
      }
    ]
  },
  {
    "id": "metaworldDenizen",
    "name": "Metaworld Denizen",
    "edition": "Rei",
    "paranoiaLimit": 2,
    "tags": [
      "girl"
    ],
    "startLocation": [
      "Shrine"
    ],
    "forbiddenLocation": [],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "Place 1 :hope: or :despair: counter on any character at this location. If this character has :goodwill: Refusel and at least 1 :goodwill: counter, the Mastermind may use this ability as their own",
        "timesPerLoop": 1
      }
    ]
  },
  {
    "id": "littleSister",
    "name": "Little Sister",
    "edition": "Rei",
    "paranoiaLimit": 3,
    "tags": [
      "littleSisterKeyword",
      "girl"
    ],
    "startLocation": [
      "Shrine"
    ],
    "forbiddenLocation": [],
    "abilities": [
      {
        "type": "passive",
        "description": "During script creation, this character cannot be given a Role with :goodwill: Refusel."
      },
      {
        "type": "active",
        "goodwillRank": 5,
        "description": "Use a :goodwill: ability from an :adult: at this location, ignoring :goodwill: counters. It cannot be refused by :goodwill: Refusel, but counts towards that ability's 'Once per Loop' limit."
      }
    ]
  },
  {
    "id": "sacredTree",
    "name": "Sacred Tree",
    "edition": "Rei",
    "paranoiaLimit": 4,
    "tags": [
      "tree"
    ],
    "startLocation": [
      "Shrine"
    ],
    "forbiddenLocation": [
      "City",
      "Hospital",
      "School"
    ],
    "abilities": [
      {
        "type": "passive",
        "description": "Each turn, the Leader may move 1 counter on this character to another character at this location. If this character has :goodwill: Refusel, the Mastermind must also do this during the Mastermind's ability step."
      }
    ]
  }
],
 ...[
  {
    "id": "crusader",
    "name": "Crusader",
    "tags": [
      "man",
      "adult"
    ],
    "edition": "Supernatural",
    "paranoiaLimit": 4,
    "startLocation": [
      "Shrine"
    ],
    "abilities": [
      {
        "type": "passive",
        "description": "If this character gains :supernaturalTag: his :goodwill: abilities are automatically refused."
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "Kill any character in the same location. If that character is not :supernaturalTag:, the Protagonists lose."
      }
    ]
  }
],
 ...[
  {
    "id": "mimic",
    "name": "Mimic",
    "edition": "Fan",
    "paranoiaLimit": 2,
    "tags": [
      "adult",
      "man"
    ],
    "startLocation": [
      "City"
    ],
    "forbiddenLocation": [],
    "abilities": [
      {
        "type": "active",
        "goodwillRank": 2,
        "timesPerLoop": 2,
        "description": "Use an Available (counter requirement on target Character met) Goodwill ability from another Character in the same location. This is regarded as the Mimic using the ability."
      }
    ]
  },
  {
    "id": "automaton",
    "name": "Automaton",
    "edition": "Fan",
    "paranoiaLimit": 4,
    "tags": [
      "construct"
    ],
    "startLocation": [
      "City"
    ],
    "forbiddenLocation": [],
    "abilities": [
      {
        "type": "passive",
        "description": "Movement actions do not affect this Character."
      },
      {
        "type": "passive",
        "description": "Day 2 onward: At the start of each day, move to the next clockwise Location."
      },
      {
        "type": "active",
        "goodwillRank": 3,
        "description": "Choose a Character in this Location, then choose a Character in another Location (The target Location must be legal for that Character). Switch their places. Once per Loop"
      }
    ]
  }
]
] as const;
    export const charactersLookup = characters.reduce((p, c) => {
        if (typeof c === 'object' && c !== null && 'id' in c) {
            p[c.id] = c;
        }
        return p;
    }, {} as Record<typeof characters[number]['id'], ReadonlyRecursive<Character>>);
    
    export function isCharacterId(id: unknown): id is keyof typeof charactersLookup {
        return typeof id === 'string' && id in charactersLookup;
    }
    
    ;


export const plots = [

 ...[
  {
    "id": "lostHeart",
    "name": "Lost Heart",
    "roles": {
      "agent": 1,
      "brain": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "At least 1 :intrigue:, 1 :goodwill:, and 1 :paranoia: are placed on the character whose original role is a :person:",
        "description": "The character gains :goodwill: Outburst and :marionette:’s ability."
      }
    ]
  },
  {
    "id": "shadowDemonKing",
    "name": "Shadow Demon King",
    "roles": {
      "twilight": 1
    },
    "rules": []
  },
  {
    "id": "devilsWill",
    "name": "The Devil’s Will",
    "roles": {
      "agent": 1,
      "invader": 1,
      "hider": 1
    },
    "rules": []
  },
  {
    "id": "parallelWorldWar",
    "name": "Parallel World War",
    "roles": {
      "agent": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": ":worldConvergence: hasn‘t occured this loop."
      },
      {
        "type": "Script creation",
        "description": "The :agent: must be the World Convergent culprit."
      }
    ]
  },
  {
    "id": "alienErosion",
    "name": "Alien Erosion",
    "roles": {
      "brain": 1,
      "invader": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "The current world is Normal world and there are at least 2 :intrigue: on the :brain:‘s starting Location"
      },
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "The current world is Abnormal world and there are at least 2 :intrigue: on the :invader:‘s starting Location"
      }
    ]
  },
  {
    "id": "worldDollhouse",
    "name": "The World of Dollhouse",
    "roles": {
      "gossiper": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "All characters whose original role is :person: gain :goodwill: Outburst."
      }
    ]
  },
  {
    "id": "egoWave",
    "name": "Ego Wave",
    "roles": {
      "animus": 1,
      "agitator": 1
    },
    "rules": []
  },
  {
    "id": "closedDoor",
    "name": "The Closed Door",
    "roles": {
      "gossiper": 1,
      "agitator": 1
    },
    "rules": [
      {
        "type": "Optional",
        "timing": [
          "Day Start"
        ],
        "description": "When the Protagonist Leader declares to switch the world, you may refuse it"
      }
    ]
  },
  {
    "id": "moonsideCity",
    "name": "Moonside City",
    "roles": {
      "gossiper": 1,
      "neurotic": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "At least 2 :intrigue: on the City",
        "description": "Switching the world is prohibited except for this plot rule ability."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "At least 2 :intrigue: on the City",
        "description": "Switch the world to Abnormal World."
      }
    ]
  },
  {
    "id": "fanaticSacrifices",
    "name": "Fanatic’s Sacrifices",
    "roles": {
      "animus": 1,
      "fanatic": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "At least three other characters with the same sex as :fanatic: died"
      }
    ]
  },
  {
    "id": "someoneInvites",
    "name": "The One Who Invites",
    "roles": {
      "enchanter": 1
    },
    "rules": []
  }
],
 ...[
  {
    "id": "lightAvenger",
    "name": "Light of the Avenger",
    "roles": {
      "brain": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "2 :intrigue: on the :brain:’s starting location"
      }
    ]
  },
  {
    "id": "placeProtect",
    "name": "A Place to Protect",
    "roles": {
      "keyPerson": 1,
      "cultist": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "2 :intrigue: on the School."
      }
    ]
  },
  {
    "id": "shadowRipper",
    "name": "Shadow of the Ripper",
    "roles": {
      "conspiracyTheorist": 1,
      "serialKiller": 1
    },
    "rules": []
  },
  {
    "id": "unsettlingRumor",
    "name": "An Unsettling Rumor",
    "roles": {
      "conspiracyTheorist": 1
    },
    "rules": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "timesPerLoop": 1,
        "description": "You may place 1 :intrigue: on any location."
      }
    ]
  },
  {
    "id": "hideousScript",
    "name": "A Hideous Script",
    "roles": {
      "conspiracyTheorist": 1,
      "curmudgeon": [
        0,
        2
      ],
      "friend": 1
    },
    "rules": [
      {
        "type": "Script creation",
        "description": "Script writer may choose 0 or 1 or 2 :curmudgeon:s."
      }
    ]
  },
  {
    "id": "murderPlan",
    "name": "Murder Plan",
    "roles": {
      "keyPerson": 1,
      "killer": 1,
      "brain": 1
    },
    "rules": []
  },
  {
    "id": "sealedItem",
    "name": "The Sealed Item",
    "roles": {
      "brain": 1,
      "cultist": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "2 :intrigue: on the Shrine."
      }
    ]
  },
  {
    "id": "signWithMe",
    "name": "Sign with me!",
    "roles": {
      "keyPerson": 1
    },
    "rules": [
      {
        "type": "Script creation",
        "description": ":keyPerson: must be a :girl:."
      },
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "2 :intrigue: on the :keyPerson:."
      }
    ]
  },
  {
    "id": "changeOfFuture",
    "name": "Change of Future",
    "roles": {
      "cultist": 1,
      "timeTraveler": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "˝:butterflyEffect:˝ has occured this loop."
      }
    ]
  },
  {
    "id": "giantTimeBomb",
    "name": "Giant Time Bomb",
    "roles": {
      "witch": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "2 :intrigue: on the :witch:’s starting location."
      }
    ]
  },
  {
    "id": "circleFriends",
    "name": "Circle of Friends",
    "roles": {
      "friend": 2,
      "conspiracyTheorist": 1
    },
    "rules": []
  },
  {
    "id": "loveAffair",
    "name": "A Love Affair",
    "roles": {
      "lover": 1,
      "lovedOne": 1
    },
    "rules": []
  },
  {
    "id": "hiddenFreak",
    "name": "The Hidden Freak",
    "roles": {
      "serialKiller": 1,
      "friend": 1
    },
    "rules": []
  },
  {
    "id": "paranoiaVirus",
    "name": "Paranoia Virus",
    "roles": {
      "conspiracyTheorist": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "All :person:s with at least 3 :paranoia: turn into :serialKiller:s."
      }
    ]
  },
  {
    "id": "threadsFate",
    "name": "Threads of Fate",
    "roles": {},
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "description": "Place 2 :paranoia: on all characters who had :goodwill: last loop."
      }
    ]
  },
  {
    "id": "unknownFactor",
    "name": "Unknown Factor X",
    "roles": {
      "factor": 1
    },
    "rules": []
  }
],
 ...[
  {
    "id": "nobleBloodline",
    "name": "The Noble Bloodline",
    "roles": {
      "keyPerson": 1,
      "vampire": 1
    },
    "rules": [
      {
        "type": "Script creation",
        "description": ":keyPerson: and :vampire: must be of oposing sex."
      }
    ]
  },
  {
    "id": "moonlightBeast",
    "name": "Moonlight Beast",
    "roles": {
      "werewolf": 1
    },
    "rules": []
  },
  {
    "id": "nightMistNightmare",
    "name": "Night Mist Nightmare",
    "roles": {
      "nightmare": 1
    },
    "rules": []
  },
  {
    "id": "OnesFromGrave",
    "name": "The Ones from the Grave",
    "roles": {},
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "All corpses that had the role :person:, :coward: or :showOff: change into having the role of :zombie:"
      }
    ]
  },
  {
    "id": "cursedLand",
    "name": "The Cursed Land",
    "roles": {
      "ghost": 1,
      "showOff": 1
    },
    "rules": [
      {
        "type": "Optional",
        "timing": [
          "Loop Start"
        ],
        "description": "Place a Curse on the :ghost:’s starting location"
      },
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "One or more Location Curses can’t be attached to characters"
      }
    ]
  },
  {
    "id": "thoseHabits",
    "name": "Those with Habits",
    "roles": {
      "ghost": 1,
      "serialKiller": 1,
      "lovedOne": 1
    },
    "rules": []
  },
  {
    "id": "witchsCurse",
    "name": "Witch’s Curse",
    "roles": {
      "conspiracyTheorist": 1,
      "witch": 1
    },
    "rules": [
      {
        "type": "Optional",
        "timing": [
          "Loop Start"
        ],
        "description": "Place a Curse on the :witch:’s startiong location."
      }
    ]
  },
  {
    "id": "keyGirl",
    "name": "The Key Girl",
    "roles": {
      "keyPerson": 1
    },
    "rules": [
      {
        "type": "Script creation",
        "description": "The :keyPerson: must be a :girl:."
      }
    ]
  },
  {
    "id": "monsterIntrigue",
    "name": "Monster Intrigue",
    "roles": {
      "conspiracyTheorist": 1
    },
    "rules": [
      {
        "type": "Optional",
        "timesPerLoop": 2,
        "timesPerDay": 1,
        "timing": [
          "Mastermind Ability"
        ],
        "prerequisite": "A Location where a character with :goodwill: Refusel is.",
        "description": "Place an :intrigue: on one location."
      }
    ]
  },
  {
    "id": "panicObsession",
    "name": "Panic and Obsession",
    "roles": {
      "serialKiller": 1,
      "coward": 1,
      "witch": 1
    },
    "rules": []
  },
  {
    "id": "peopleDontListen",
    "name": "People Who Don’t Listen",
    "roles": {
      "showOff": 1,
      "conspiracyTheorist": 1,
      "coward": 1
    },
    "rules": []
  },
  {
    "id": "choirOuterGod",
    "name": "Choir to the Outer God",
    "roles": {
      "keyPerson": 1,
      "sacrifice": 1,
      "immortalRole": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "At least 5 characters with :intrigue: on them."
      }
    ]
  },
  {
    "id": "sacredWordsDagon",
    "name": "The Sacred Words of Dagon",
    "roles": {
      "keyPerson": 1,
      "cultist": 1,
      "deepOne": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "As manay or more :intrigue: on the Shrine then the Extra Gauge shows.",
        "description": " (If the Extra Gauge is at zero, the Protagonists always lose)"
      }
    ]
  },
  {
    "id": "kingYellow",
    "name": "The King in Yellow",
    "roles": {
      "sacrifice": 1,
      "cultist": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "The Extra Gauge has increased at all this loop."
      }
    ]
  },
  {
    "id": "giantTimeBombAgain",
    "name": "Giant Time Bomb Again",
    "roles": {
      "witch": 1,
      "deepOne": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "2 :intrigue: on the :witch:’s starting location."
      }
    ]
  },
  {
    "id": "bloodyRites",
    "name": "Bloody Rites",
    "roles": {
      "witch": 1,
      "immortalRole": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "As many or more corpses as the Extra Gauge shows.",
        "description": "(If the Extra Gauge is at zero, the Protagonists always lose)"
      }
    ]
  },
  {
    "id": "resistance",
    "name": "The Resistance",
    "roles": {
      "conspiracyTheorist": 1,
      "wizard": 1,
      "serialKiller": 1
    },
    "rules": []
  },
  {
    "id": "peopleWhoSaw",
    "name": "People Who Saw",
    "roles": {
      "conspiracyTheorist": 1,
      "witness": 1
    },
    "rules": []
  },
  {
    "id": "greatRace",
    "name": "The Great Race",
    "roles": {
      "serialKiller": 1,
      "timeTraveler": 1
    },
    "rules": []
  },
  {
    "id": "whispersFromDeep",
    "name": "Whispers from the Deep",
    "roles": {
      "deepOne": 1,
      "paranoiac": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "The :paranoiac: gains all the abilities of the :keyPerson:"
      }
    ]
  },
  {
    "id": "facelessGod",
    "name": "The Faceless God",
    "roles": {
      "wizard": 1,
      "faceless": 1
    },
    "rules": []
  },
  {
    "id": "twistedTruth",
    "name": "Twisted Truth",
    "roles": {
      "paranoiac": 1
    },
    "scriptSpecified": [
      {
        "name": "Extra Plot",
        "type": "plot",
        "addRolesForPlot": true
      }
    ],
    "rules": [
      {
        "type": "Script creation",
        "description": "The :informer: must be included. The script writer then chooses one \"extra\" main plot."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "prerequisite": "The Extra Gauge is on 2 or more.",
        "description": "Druing this loop, the original main plot is replaced by the \"extra\" main plot."
      }
    ]
  }
],
 ...[
  {
    "id": "ancientShrine",
    "name": "Ancient Shrine",
    "roles": {
      "nightmareHaunted": 1,
      "curseGod": 1,
      "GhostHaunted": 1,
      "spellcaster": 1
    },
    "rules": []
  },
  {
    "id": "cursedDoll",
    "name": "The Cursed Doll",
    "roles": {
      "nightmareHaunted": 1,
      "humanDoll": 1,
      "GhostHaunted": 1,
      "spellcaster": 1
    },
    "rules": []
  },
  {
    "id": "fullMoonNight",
    "name": "Full Moon Night",
    "roles": {
      "nightmareHaunted": 1,
      "werewolfHaunted": 1,
      "spellcaster": 1
    },
    "rules": []
  },
  {
    "id": "strangeStory",
    "name": "A Strange Story",
    "roles": {
      "nightmareHaunted": 1,
      "vampireHaunted": 1,
      "monster": 1
    },
    "rules": [
      {
        "type": "Script creation",
        "description": ":vampire and :nightmare: must be the same sex."
      }
    ]
  },
  {
    "id": "primeEvilPlot",
    "name": "Prime Evil",
    "roles": {
      "nightmareHaunted": 1,
      "GhostHaunted": 1,
      "monster": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "The number of surviving characters is 4 or less"
      }
    ]
  },
  {
    "id": "deepHole",
    "name": "The Deep Hole",
    "roles": {
      "monster": 1,
      "horror": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Loop End"
        ],
        "description": "Increase Extra Gauge by X. X is equal to the number of :intrigue: on the City. (Max 3)."
      }
    ]
  },
  {
    "id": "zombieApocalypse",
    "name": "Zombie Apocalypse",
    "roles": {
      "spellcaster": 1,
      "horror": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "A corpse whose original role does not have :afterDeath: activity will be changed to a :zombie: as long as it has at least 1 :intrigue:."
      }
    ]
  },
  {
    "id": "expandingUrbanLegend",
    "name": "Expanding Urban Legend",
    "roles": {
      "poltergeist": 1,
      "horror": 1
    },
    "rules": []
  },
  {
    "id": "hauntedCity",
    "name": "Haunted City",
    "roles": {
      "GhostHaunted": 1,
      "poltergeist": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "There are at least 3 :paranoia: on the :ghost:’s corpse."
      }
    ]
  },
  {
    "id": "dancePartyDead",
    "name": "Dance Party of the Dead",
    "roles": {
      "GhostHaunted": 1,
      "horror": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "There are at least 3 corpses on the location where 1 or more :intrigue: are placed."
      }
    ]
  },
  {
    "id": "maxCrazyGauge",
    "name": "Max Crazy Gauge",
    "roles": {
      "GhostHaunted": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "The number of corpses is 2 or less."
      }
    ]
  },
  {
    "id": "boundaryBetweenLifeDeath",
    "name": "Boundary Between Life and Death",
    "roles": {
      "monster": 1,
      "overlord": 1
    },
    "rules": [
      {
        "type": "Optional",
        "timing": [
          "Day End",
          "First Day"
        ],
        "description": "Kill one character in the same location as :overlord:."
      }
    ]
  }
],
 ...[
  {
    "id": "finalPlan",
    "name": "The Final Plan",
    "roles": {
      "keyPerson": 1,
      "brain": 1,
      "killer": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Day End"
        ],
        "prerequisite": "1 or more :hope: counters on the Key Person.",
        "description": "Traitor(s) become normal Protagonists. If this is true at the end of the final Loop, this carries over to the Final Guess."
      }
    ]
  },
  {
    "id": "sealedConclusion",
    "name": "The Sealed Conclusion",
    "roles": {
      "factor": 1,
      "fragment": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Day End"
        ],
        "prerequisite": "2+ :intrigue: on the Shrine.",
        "description": "When counting :intrigue: on a Location, count :hope: and :despair: on Characters in the same Location as :intrigue:."
      }
    ]
  },
  {
    "id": "worldRebellion",
    "name": "World of Rebellion",
    "roles": {
      "keyPerson": 1,
      "fragment": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "If there are 2+ :intrigue: on the :keyPerson:",
        "description": ":keyPerson: and :fragment: both must be a :girl:."
      }
    ]
  },
  {
    "id": "demonsScript",
    "name": "The Demon’s Script",
    "roles": {
      "watcher": 1,
      "serialKiller": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "If Last Will or The Executioner have triggered in this Loop."
      },
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "At the end of the last Day, if there are 1 or fewer total counters on the Watcher, you may kill the Protagonists."
      }
    ]
  },
  {
    "id": "giantTimeBombYetAgain",
    "name": "Giant Time Bomb Yet Again",
    "roles": {
      "brain": 1,
      "witch": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "2+ :intrigue: on the :witch:'s starting Location."
      }
    ]
  },
  {
    "id": "realMonster",
    "name": "The Real Monster",
    "roles": {
      "serialKiller": 1,
      "secretkeeper": 1,
      "wildcard": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Day End"
        ],
        "prerequisite": "Victory Condition A: There are 5 or more Perished tokens."
      }
    ]
  },
  {
    "id": "keeperMythology",
    "name": "Keeper of Mythology",
    "roles": {
      "influencer": 1,
      "secretkeeper": 1,
      "wildcard": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Goodwill ablility step"
        ],
        "prerequisite": "Victory Condition B: There are 6 or more Acquainted tokens."
      }
    ]
  },
  {
    "id": "trueDetective",
    "name": "I Am the True Detective!",
    "roles": {
      "watcher": 1,
      "secretkeeper": 1,
      "wildcard": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Final Guess"
        ],
        "description": "Protagonist C wins.",
        "prerequisite": "Victory Condition C: Immediately before the Final Guess, Protagonist C correctly identifies the culprit of each :incident:."
      }
    ]
  },
  {
    "id": "mythomaniacSecret",
    "name": "The Mythomaniac's Secret",
    "roles": {
      "conspiracyTheorist": 1
    },
    "rules": [
      {
        "type": "Script creation",
        "description": "The Secretkeeper gains :Goodwill: Refusal. If there is no Secretkeeper during script creation, instead add an additional :killer:, :brain:, or :fragment: to the script. (It does not count as a Role specified by this Plot.)"
      }
    ]
  },
  {
    "id": "socialMediaParanoia",
    "name": "Social Media Paranoia",
    "roles": {
      "serialKiller": 1,
      "influencer": 1,
      "conspiracyTheorist": 1
    },
    "rules": []
  }
],
 ...[
  {
    "id": "tightropePlan",
    "name": "Tightrope Plan",
    "roles": {
      "brain": 1,
      "killer": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "The Extra Gauge is 1 or lower."
      }
    ]
  },
  {
    "id": "dropStrychnine",
    "name": "A Drop of Strychnine",
    "roles": {
      "keyPerson": 1,
      "poisoner": 1,
      "fool": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Incident step"
        ],
        "description": "When determining whether \":serialMurde:,\" or \":suicide:\" triggers, count :intrigue: counters also as :paranoia: counters."
      }
    ]
  },
  {
    "id": "quiltIncidents",
    "name": "A Quilt of Incidents",
    "roles": {
      "fool": 1,
      "conspiracyTheorist": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "The Extra Gauge is 3 or more."
      }
    ]
  },
  {
    "id": "blackSchool",
    "name": "The Black School",
    "roles": {
      "brain": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "There are more than X :intrigue: counters on the School, X is 1 less than the current loop number."
      }
    ]
  },
  {
    "id": "danceFools",
    "name": "Dance of Fools",
    "roles": {
      "friend": 1,
      "fool": 1
    },
    "rules": []
  },
  {
    "id": "isolatedInstitutionPsycho",
    "name": "Isolated Institution Psycho",
    "roles": {
      "conspiracyTheorist": 1,
      "therapist": 1,
      "paranoiac": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "prerequisite": "The Extra Gauge was 2 or less at the end of the previous loop",
        "description": "Increase it by 1."
      }
    ]
  },
  {
    "id": "anAbsoluteWill",
    "name": "An Absolute Will",
    "roles": {
      "obstinate": 1
    },
    "rules": []
  },
  {
    "id": "trickyTwins",
    "name": "Tricky Twins",
    "roles": {
      "twin": 1,
      "paranoiac": 1
    },
    "rules": []
  },
  {
    "id": "smellGunpowder",
    "name": "Smell of Gunpowder",
    "roles": {
      "serialKiller": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "There are a total of 12 or more :paranoia: counters on the remaining charactrs."
      }
    ]
  },
  {
    "id": "masterDetective",
    "name": "I am a Master Detective",
    "roles": {
      "conspiracyTheorist": 1,
      "friend": 1,
      "privateInvestigator": 1
    },
    "rules": []
  },
  {
    "id": "secretRecord",
    "name": "Secret Record",
    "roles": {
      "keyPerson": 1,
      "brain": 1,
      "conspiracyTheorist": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "There are at least 2 :intrigue: counters on the Shrine."
      }
    ]
  },
  {
    "id": "devilsHand",
    "name": "The Devil’s Hand",
    "roles": {
      "keyPerson": 1,
      "cultist": 1,
      "ninja": 1
    },
    "rules": []
  },
  {
    "id": "maleConfrontation",
    "name": "Male Confrontation",
    "roles": {
      "ninja": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "The :ninja: (or its corpse) has at least 2 :intrigue: counters."
      },
      {
        "type": "Script creation",
        "description": "The :ninja: (for this plot) must have the tag man."
      }
    ]
  },
  {
    "id": "fatedConnections",
    "name": "Fated Connections",
    "roles": {
      "conspiracyTheorist": 1,
      "friend": 1,
      "serialKiller": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "prerequisite": "A Character died the last turn.",
        "description": "Chose one of those. Plac any Extra Card on that character. Character(s) with an Extra card has their role changed into a :keyPerson:."
      }
    ]
  },
  {
    "id": "loveHateSpiral",
    "name": "Love-Hate Spiral",
    "roles": {
      "friend": 1,
      "obstinate": 1
    },
    "rules": []
  },
  {
    "id": "showtimeDeath",
    "name": "Showtime of Death",
    "roles": {
      "magician": 1,
      "immortalRole": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "There are 6 or less characters alive."
      }
    ]
  },
  {
    "id": "witchesTeaTime",
    "name": "Witches' Tea Time",
    "roles": {
      "conspiracyTheorist": 1,
      "friend": 1,
      "witch": 2
    },
    "rules": []
  },
  {
    "id": "diceOfGods",
    "name": "Dice of the Gods",
    "roles": {
      "serialKiller": 1,
      "obstinate": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "prerequisite": "A Character died the last turn.",
        "description": "Chose one of those. Plac any Extra Card on that character."
      }
    ]
  },
  {
    "id": "unansweredHeart",
    "name": "Unanswered Heart",
    "roles": {
      "conspiracyTheorist": 1,
      "magician": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "\"Forbid :goodwill:\" has the effect of \"Forbid Movement\""
      }
    ]
  },
  {
    "id": "unsafeTrigger",
    "name": "Unsafe Trigger",
    "roles": {
      "factor": 1
    },
    "rules": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "timesPerLoop": 1,
        "prerequisite": "The :factor: is alive.",
        "description": "You may place 1 Intruge counter on the :factor:’s location"
      }
    ]
  },
  {
    "id": "worshippersApocalypse",
    "name": "Worshippers of the Apocalypse",
    "roles": {
      "prophet": 1
    },
    "rules": [
      {
        "type": "Script creation",
        "description": "There must be at least one :suicide: Incident"
      },
      {
        "type": "Mandatory",
        "timing": [
          "Incident step"
        ],
        "prerequisite": "The Culprit is a :person: and the :prophet: is alive.",
        "description": "When determning whether an Incident triggers, the culprit is regarded as having 1 less than its printed :paranoia: limit."
      }
    ]
  }
],
 ...[
  {
    "id": "forbiddenFuture",
    "name": "The Forbidden Future",
    "roles": {
      "marionetteAHR": 1,
      "storyteller": 1,
      "obstinate|keyPerson": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "Players are in the Light world"
      }
    ]
  },
  {
    "id": "fairyTaleMurderer",
    "name": "Fairy-Tale Murderer",
    "roles": {
      "keyPerson": 1,
      "lullaby": 1,
      "brain": 1
    }
  },
  {
    "id": "motherGooseMystery",
    "name": "Mother Goose Mystery",
    "roles": {
      "marionetteAHR": 1,
      "storyteller": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "There are X or more corpses at the end of the loop, X is the current loop number, to a maximum of 3."
      }
    ]
  },
  {
    "id": "dimensionalMerger",
    "name": "Dimensional Merger",
    "roles": {
      "storyteller": 1,
      "shifter": 1,
      "fragment": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": ":lastWill: or :leftBehind: has triggered this loop."
      }
    ]
  },
  {
    "id": "intoNothingness",
    "name": "Into Nothingness",
    "roles": {
      "obstinate": 1,
      "marionetteAHR": 1,
      "brain": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "(Sum of the Extra Gauge and the number of :intrigue: counters on the :obstinate: added by this Plot) is 3 or more."
      }
    ]
  },
  {
    "id": "jekyllHyde",
    "name": "Jekyll and Hyde",
    "roles": {
      "keyPerson|brain": 1,
      "marionetteAHR": 1
    },
    "rules": []
  },
  {
    "id": "plaguebringer",
    "name": "The Plaguebringer",
    "roles": {
      "piedPiper|gossip": 1
    },
    "rules": []
  },
  {
    "id": "puppeteersStrings",
    "name": "Puppeteer’s Strings",
    "roles": {
      "fragment": 1,
      "gossip": 1,
      "person|serialKiller": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "All types of :goodwill: Refusel become Puppeted :goodwill: Refusel (including mandatory :goodwill: Refusel and that granted by :despair: counters)."
      }
    ]
  },
  {
    "id": "throughLookingGlass",
    "name": "Through the Looking-Glass",
    "roles": {
      "conspiracyTheorist|serialKiller": 1,
      "alice": 1
    },
    "rules": [
      {
        "type": "Script creation",
        "description": ":alice: must be a girl."
      }
    ]
  },
  {
    "id": "crossingWorldLines",
    "name": "Crossing World Lines",
    "roles": {
      "conspiracyTheorist": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "prerequisite": "The loop is even numbered.",
        "description": "The Mastermind adds \":despair: +1\" to hand."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "prerequisite": "The loop is the final loop.",
        "description": "The Protagonists add \":hope: +1\" to hand."
      }
    ]
  },
  {
    "id": "unspeakableHorrors",
    "name": "Unspeakable Horrors",
    "roles": {
      "conspiracyTheorist|obstinate": 1,
      "gossip": 1
    },
    "rules": [
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "If the Extra Gauge is 3 or higher"
      }
    ]
  },
  {
    "id": "hysteriaVirus",
    "name": "Hysteria Virus",
    "roles": {
      "fragment": 1,
      "conspiracyTheorist": 1,
      "gossip": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "While in the Dark World and characters whose original Roles were either :person: or :fragment: and have 2 or more kinds of counters on them",
        "description": "Change role to :serialKiller:s."
      }
    ]
  }
],
 ...[
  {
    "id": "abomination",
    "name": "Abomination",
    "roles": {
      "madGenius": 1,
      "supplicant": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Day End"
        ],
        "prerequisite": ":madGenius: has Body Parts and is alive"
      }
    ]
  },
  {
    "id": "Metamorphosis",
    "name": "Metamorphosis",
    "roles": {
      "potentate": 1,
      "supplicant": 1,
      "lover": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Day End"
        ],
        "prerequisite": ":supplicant: is alive and has the :supernaturalTag: tag"
      }
    ]
  },
  {
    "id": "sporePod",
    "name": "Spore Pod",
    "roles": {
      "facsimile": 1,
      "augur": 1
    },
    "rules": [
      {
        "type": "Optional Loss condition: Tragedy",
        "timing": [
          "Day End"
        ],
        "prerequisite": "Every location contains a :facsimile:."
      }
    ]
  },
  {
    "id": "demonicPortal",
    "name": "Demonic Portal",
    "roles": {
      "potentate": 1,
      "augur": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Last Day",
          "Day End"
        ],
        "prerequisite": "At least two :intrigue: on the shrine."
      }
    ]
  },
  {
    "id": "seed",
    "name": "The Seed",
    "roles": {
      "seeder": 1,
      "serialKiller": 1
    },
    "rules": []
  },
  {
    "id": "infestationSupernatural",
    "name": "Infestation",
    "roles": {
      "facsimile": 2,
      "keyPerson": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "prerequisite": "No :facsimile: are alive.",
        "timing": [
          "Day End"
        ],
        "description": "The :keyPerson:s role changes to :person:."
      }
    ]
  },
  {
    "id": "fatalAttraction",
    "name": "Fatal Attraction",
    "roles": {
      "lover": 3,
      "serialKiller": 1
    },
    "rules": []
  },
  {
    "id": "manifestation",
    "name": "Manifestation",
    "roles": {},
    "rules": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "timesPerLoop": 1,
        "description": "Add 1 :paranoia: to any character."
      }
    ]
  },
  {
    "id": "murderHire",
    "name": "Murder for Hire",
    "roles": {
      "contractKiller": 1,
      "keyPerson": 1
    },
    "rules": []
  },
  {
    "id": "remoteBombing",
    "name": "Remote Bombing",
    "roles": {
      "madGenius": 1
    },
    "rules": [
      {
        "type": "Optional Loss condition: Tragedy",
        "timing": [
          "Day End"
        ],
        "prerequisite": "If there is two :intrigue: on the :madGenius:'s starting location and the :madGenius: is alive and not at that location."
      }
    ]
  },
  {
    "id": "suicideCult",
    "name": "Suicide Cult",
    "roles": {
      "cultMember": 3
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Day End",
          "Last Day"
        ],
        "prerequisite": "If any of the :cultMember:s have no :goodwill:"
      }
    ]
  },
  {
    "id": "twistedThreadsTime",
    "name": "Twisted Threads of Time",
    "roles": {},
    "rules": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "Transfere 1 :intrigue: from a Character to that Character's location or another Character in the same location.",
        "timesPerLoop": 1
      }
    ]
  }
],
 ...[
  {
    "id": "lastMiracle",
    "name": "The Last Miracle",
    "roles": {
      "keyPerson": 1,
      "subHeroine": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "When the Loop ends—if the :keyPerson: is not in the initial starting location of the :subHeroine:, the :keyPerson: dies."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Mastermind Action step"
        ],
        "description": "The Mastermind cannot place any Movement Actions on the :keyPerson:."
      }
    ]
  },
  {
    "id": "sealEvilSpirit",
    "name": "Seal of the Evil Spirit",
    "roles": {
      "brain": 1,
      "possessed": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "2+ :intrigue: on the Shrine."
      }
    ]
  },
  {
    "id": "promiseSmile",
    "name": "Promise of a Smile",
    "roles": {
      "keyPerson": 1,
      "moodMaker": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "The :keyPerson: does not have more than \"X\" :goodwill:. \"X\" is half the number of Days in one Loop, rounded down."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "prerequisite": "\":promiseSmile:\" Loss condition was met in the previous Loop.",
        "description": ":keyPerson: gets +1 :goodwill:."
      }
    ]
  },
  {
    "id": "albumMemories",
    "name": "Album of Memories",
    "roles": {
      "moodMaker": 1,
      "friend": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "All roles have not been revealed."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "description": "The roles of Characters with \"X\" or more :goodwill: are revealed. \"X\" is half the number of Days in one Loop, rounded down."
      }
    ]
  },
  {
    "id": "monsterInsideMe",
    "name": "The Monster Inside Me",
    "roles": {
      "keyPerson": 1
    },
    "rules": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "If the :keyPerson: kills someone, the Protagonists lose."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "prerequisite": "3+ :paranoia:.",
        "description": "Character gains an additional role of \":serialKiller:.\""
      }
    ]
  },
  {
    "id": "noisyClassmates",
    "name": "Noisy Classmates",
    "roles": {
      "friend": 1,
      "moodMaker": 1,
      "troublemaker": 1
    },
    "rules": []
  },
  {
    "id": "missingHighlight",
    "name": "The Missing Highlight",
    "roles": {
      "heavyLovers": 1,
      "moodMaker": 1,
      "conspiracyTheorist": 1
    },
    "rules": []
  },
  {
    "id": "sevenSchoolMysteries",
    "name": "The Seven School Mysteries",
    "roles": {
      "friend": 1,
      "troublemaker": 1,
      "conspiracyTheorist": 1
    },
    "rules": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "prerequisite": "2+ :intrigue: on School.",
        "description": "Place +1 :paranoia: on a Character in School."
      }
    ]
  },
  {
    "id": "behindScenes",
    "name": "Behind the Scenes",
    "roles": {
      "slayer": 1,
      "creature": 1,
      "conspiracyTheorist": 1
    },
    "rules": []
  },
  {
    "id": "howlingMoonlitNight",
    "name": "Howling in the Moonlit Night",
    "roles": {
      "lunatic": 1
    },
    "rules": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "Place +1 :intrigue: counter on any Location or Character (Once per Loop)."
      }
    ]
  },
  {
    "id": "spreadingInfection",
    "name": "The Spreading Infection",
    "roles": {
      "slayer": 1
    },
    "rules": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "3+ :intrigue: on a :person:.",
        "description": "Character becomes the role \":creature:.\""
      }
    ]
  }
]
] as const;
    export const plotsLookup = plots.reduce((p, c) => {
        if (typeof c === 'object' && c !== null && 'id' in c) {
            p[c.id] = c;
        }
        return p;
    }, {} as Record<typeof plots[number]['id'], ReadonlyRecursive<Plot>>);
    
    export function isPlotId(id: unknown): id is keyof typeof plotsLookup {
        return typeof id === 'string' && id in plotsLookup;
    }
    
    ;


export const roles = [

 ...[
  {
    "id": "agent",
    "name": "Agent",
    "abilities": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "On character death"
        ],
        "prerequisite": "This character dies"
      },
      {
        "type": "Mandatory",
        "timing": [
          "After Goodwill Ability used"
        ],
        "description": "Remove 1 :intrigue: in this Location or on any character in this Location"
      }
    ]
  },
  {
    "id": "invader",
    "name": "Invader",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "If there are at least 2 :intrigue: in this location: Switch the world."
      }
    ]
  },
  {
    "id": "twilight",
    "name": "Twilight",
    "goodwillRefusel": "Optional",
    "goodwillOutburst": true,
    "tags": [
      "immortal"
    ],
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "If there are at least 2 :intrigue: in this location: Switch the world."
      }
    ]
  },
  {
    "id": "hider",
    "name": "Hider",
    "goodwillRefusel": "Mandatory",
    "scriptSpecified": [
      {
        "name": "world",
        "type": "text",
        "description": "abnormal or normal"
      }
    ],
    "abilities": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "This character has at least 1 :intrigue:",
        "description": "If this character has at least 1 :intrigue: at Loop End, the tragedy side wins."
      }
    ]
  },
  {
    "id": "gossiper",
    "name": "Gossiper",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "You may place 1 :goodwill: on any character in this location."
      }
    ]
  },
  {
    "id": "fanatic",
    "name": "Fanatic",
    "goodwillOutburst": true,
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "After Goodwill Ability used"
        ],
        "description": "Choose any character in this location and kill it."
      }
    ]
  },
  {
    "id": "animus",
    "name": "Animus",
    "scriptSpecified": [
      {
        "name": "world",
        "type": "text",
        "description": "abnormal or normal"
      }
    ],
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "The sex of this character is reversed."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Incident step"
        ],
        "description": "This character always triggers its incidents (if alive), regardless the amount of :paranoia: on it. After resolving the Incident, the Mastermind declared that “:animus: abilities were used.”."
      }
    ]
  },
  {
    "id": "neurotic",
    "name": "Neurotic",
    "goodwillRefusel": "Mandatory",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Incident step"
        ],
        "description": "When determining whether an Incident, for which this character is the culprit, will occur or not, this character is regarded as having :paranoia: Limit -1."
      }
    ]
  },
  {
    "id": "agitator",
    "name": "Agitator",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Incident trigger"
        ],
        "prerequisite": "This Character resolved an incident",
        "description": "Put 1 :goodwill:, or 1 :paranoia:, or 1 :intrigue: on any character in this location."
      }
    ]
  },
  {
    "id": "enchanter",
    "name": "Enchanter",
    "goodwillOutburst": true,
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "After Goodwill Ability used"
        ],
        "description": "The Mastermind may declare that “:enchanter:‘s abilities were used.” If you do, the next day Leader are prohibited from switching the world and setting action cards."
      }
    ]
  },
  {
    "id": "marionette",
    "name": "Marionette",
    "goodwillOutburst": true,
    "abilities": [
      {
        "type": "Delayed Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "After resolving this character's :goodwill: ability",
        "description": "(optional) The protagonists die during the Day end."
      }
    ]
  },
  {
    "id": "shadow",
    "name": "Shadow",
    "goodwillRefusel": "Mandatory",
    "abilities": [
      {
        "type": "Delayed Loss condition: Protagonists Death",
        "timing": [
          "Incident trigger"
        ],
        "prerequisite": "This Character resolves an incident",
        "description": "(optional) The protagonists die during the Day end."
      }
    ]
  }
],
 ...[
  {
    "id": "person",
    "name": "Person",
    "abilities": []
  },
  {
    "id": "keyPerson",
    "name": "Key Person",
    "abilities": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Always"
        ],
        "prerequisite": "This character dies.",
        "description": "The loop ends immediately."
      }
    ]
  },
  {
    "id": "curmudgeon",
    "name": "Curmudgeon",
    "goodwillRefusel": "Optional",
    "abilities": []
  },
  {
    "id": "killer",
    "name": "Killer",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Day End"
        ],
        "prerequisite": "The :keyPerson: has at least 2 :intrigue: and is in this character‘s location",
        "description": "Kill the :keyPerson:"
      },
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "This character has at least 4 :intrigue:"
      }
    ]
  },
  {
    "id": "brain",
    "name": "Brain",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "You may place 1 :intrigue: on this location or on any character in this location."
      }
    ]
  },
  {
    "id": "cultist",
    "name": "Cultist",
    "goodwillRefusel": "Mandatory",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Card resolve"
        ],
        "description": "You may ignore all Forbid :intrigue: effects on this location and on all characters in this location."
      }
    ]
  },
  {
    "id": "timeTraveler",
    "name": "Time Traveler",
    "tags": [
      "immortal"
    ],
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Card resolve"
        ],
        "description": "Ignore Forbid :goodwill: on this character."
      },
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Day End",
          "Last Day"
        ],
        "prerequisite": "There is 2 or less :goodwill: on this character.",
        "description": "Loop ends"
      }
    ]
  },
  {
    "id": "witch",
    "name": "Witch",
    "goodwillRefusel": "Mandatory",
    "abilities": []
  },
  {
    "id": "friend",
    "name": "Friend",
    "max": 2,
    "abilities": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "This character is dead.",
        "description": "Reveal its role."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "prerequisite": "This role has been revealed",
        "description": "This character gets 1 :goodwill:."
      }
    ]
  },
  {
    "id": "conspiracyTheorist",
    "name": "Conspiracy Theorist",
    "max": 1,
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "You may place 1 :paranoia: on any character in this location."
      }
    ]
  },
  {
    "id": "lover",
    "name": "Lover",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "The :lovedOne: dies",
        "description": "This character gets 6 :paranoia:."
      }
    ]
  },
  {
    "id": "lovedOne",
    "name": "Loved One",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "The :lover: dies",
        "description": "This character gets 6 :paranoia:."
      },
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "This character has at least 3 :paranoia: and at least 1 :intrigue:."
      }
    ]
  },
  {
    "id": "serialKiller",
    "name": "Serial Killer",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "prerequisite": "There is exactly 1 other (living) character in this location",
        "description": "That character dies."
      }
    ]
  },
  {
    "id": "factor",
    "name": "Factor",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "There is at least 2 :intrigue: on the School",
        "description": "This character gains the :conspiracyTheorist:‘s ability, but not its role."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "There is at least 2 :intrigue: on the City",
        "description": "This character gains the :keyPerson:’s ability, but not its role."
      }
    ]
  }
],
 ...[
  {
    "id": "sacrifice",
    "name": "Sacrifice",
    "tags": [
      "immortal"
    ],
    "abilities": [
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "This character has at least 2 :intrigue: and at least 2 :paranoia:",
        "description": "You may kill all characters and the Protagonists."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Incident step"
        ],
        "description": "When determining whether an Incident, for which this character is the culprit, will occour or not, also treat :intrigue: as :paranoia:."
      },
      {
        "type": "Script creation",
        "description": "This character must be the culprit of an incident."
      }
    ]
  },
  {
    "id": "deepOne",
    "name": "Deep One",
    "max": 1,
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "You may place 1 Intruge on this location or on any character in this location."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "When this charcter dies, reveal the role and increast the Extra Gauge 1 step."
      }
    ]
  },
  {
    "id": "wizard",
    "name": "Wizard",
    "max": 1,
    "abilities": [
      {
        "type": "Loss condition: Tragedy",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "This character is dead"
      },
      {
        "type": "Mandatory",
        "timing": [
          "Goodwill ablility step"
        ],
        "description": "When this character’s :goodwill: ability is used, reveal this role after resolution. Then, the leader may increase the Extra Gauge one step."
      }
    ]
  },
  {
    "id": "witness",
    "name": "Witness",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "prerequisite": "This character has 4 or more :paranoia:",
        "description": "This charcter dies, and the Extra Gauge increases with 1 step."
      }
    ]
  },
  {
    "id": "faceless",
    "name": "Faceless",
    "goodwillRefusel": "Optional",
    "tags": [
      "immortal"
    ],
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "The Extra Gauge is 1 or less",
        "description": "This character gains the abilities of a :conspiracyTheorist:."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "the Extra Gauge is 2 or more",
        "description": "This character gains the abilities of a :deepOne:."
      }
    ]
  },
  {
    "id": "vampire",
    "name": "Vampire",
    "goodwillRefusel": "Optional",
    "tags": [
      "immortal"
    ],
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Day End"
        ],
        "prerequisite": "The :keyPerson: has at least 2 :intrigue: and is in this character’s location",
        "description": "You may kill the :keyPerson:."
      },
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "If there are at least 2 corpses in this character’s starting location."
      }
    ]
  },
  {
    "id": "werewolf",
    "name": "Werewolf",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": ":nightMadness: occurred this day"
      },
      {
        "type": "Mandatory",
        "timing": [
          "Mastermind Action step"
        ],
        "description": "The Mastermind cannot place cards on this character."
      }
    ]
  },
  {
    "id": "nightmare",
    "name": "Nightmare",
    "goodwillRefusel": "Optional",
    "tags": [
      "immortal"
    ],
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Day End"
        ],
        "description": "You may kill one character who is in this location."
      },
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "There are 3 ore more :intrigue: on all corpses in total"
      }
    ]
  },
  {
    "id": "ghost",
    "name": "Ghost",
    "max": 1,
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Mastermind Ability"
        ],
        "prerequisite": "This card is a corpse",
        "description": "Palce 1 :paranoia: on any character in this location, or any character in the :ghost:’s starting location."
      }
    ]
  },
  {
    "id": "showOff",
    "name": "Show-Off",
    "tags": [
      "immortal"
    ],
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "prerequisite": "This charcter has more then 2 :paranoia:",
        "description": "This Character loses the :immortal: aspect."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Goodwill ablility step"
        ],
        "prerequisite": "This charcter has more then 2 :paranoia:",
        "description": "This Character gains Mandatory :goodwill: Refusel."
      }
    ]
  },
  {
    "id": "coward",
    "name": "Coward",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Mastermind Ability"
        ],
        "prerequisite": "this charcter has 2 or more :paranoia:",
        "description": "Pick a neigboring location, and move the charcter there."
      }
    ]
  },
  {
    "id": "zombie",
    "name": "Zombie",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "timesPerDay": [
          1,
          {
            "Over all Roles": true
          }
        ],
        "description": "If there is a location where there are more :zombies: than non-:zombies:, kill one character in that location (reminder: a corpse is no longer considered as a character)."
      },
      {
        "type": "Optional",
        "timing": [
          "Day End"
        ],
        "timesPerDay": [
          1,
          {
            "Over all Roles": true
          }
        ],
        "description": "You may move one :zombie: corpse to a neighboring location."
      }
    ]
  }
],
 ...[
  {
    "id": "nightmareHaunted",
    "name": "Nightmare (Haunted Stage)",
    "goodwillRefusel": "Mandatory",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "description": "Kill any other character in this location. If there is no other character, kill this character."
      }
    ]
  },
  {
    "id": "curseGod",
    "name": "Curse God",
    "tags": [
      "afterdeath"
    ],
    "abilities": [
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "Character is dead & At least 2 :intrigue: on this corpse"
      }
    ]
  },
  {
    "id": "humanDoll",
    "name": "Human Doll",
    "tags": [
      "afterdeath"
    ],
    "abilities": [
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Mastermind Ability"
        ],
        "prerequisite": "If there are at least 2 :intrigue: in this location, and at least 1 :paranoia: on this card"
      },
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "On character death"
        ],
        "prerequisite": "When this character dies: Put 1 :intrigue: in this location."
      }
    ]
  },
  {
    "id": "werewolfHaunted",
    "name": "Werewolf (Haunted Stage)",
    "goodwillRefusel": "Mandatory",
    "abilities": [
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "Day end on the fifth day"
      },
      {
        "type": "Mandatory",
        "timing": [
          "Mastermind Action step"
        ],
        "description": "The Mastermind cannot set an action card on this card."
      }
    ]
  },
  {
    "id": "vampireHaunted",
    "name": "Vampire (Haunted Stage)",
    "tags": [
      "afterdeath"
    ],
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Day End"
        ],
        "description": "Kill one character of the opposite sex in this location who has at least 1 :paranoia: and 1 :intrigue:."
      },
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "If there are 3 or more corpses of the opposite sex in this location"
      }
    ]
  },
  {
    "id": "GhostHaunted",
    "name": "Ghost (Haunted Stage)",
    "tags": [
      "afterdeath"
    ],
    "max": 2,
    "abilities": []
  },
  {
    "id": "monster",
    "name": "Monster",
    "tags": [
      "immortal"
    ],
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "When this role is to be reveald"
        ],
        "description": "Increase the Extra Gauge by 2."
      }
    ]
  },
  {
    "id": "spellcaster",
    "name": "Spellcaster",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "Place 1 :intrigue: or :paranoia: on one corpse in this location."
      }
    ]
  },
  {
    "id": "ZombieHaunted",
    "name": "Zombie (Haunted Stage)",
    "tags": [
      "afterdeath"
    ],
    "goodwillRefusel": "Mandatory",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "timesPerDay": [
          1,
          {
            "Over all Roles": true
          }
        ],
        "description": "If there is a location where there are more :zombie:s than non-:zombie:s, kill one character in that location, then increase the Extra Gauge by 1 (reminder: a corpse is no longer considered as a character)."
      }
    ]
  },
  {
    "id": "poltergeist",
    "name": "Poltergeist",
    "max": 1,
    "goodwillRefusel": "Mandatory",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "timesPerDay": 1,
        "description": "Move any card in this location to any location."
      }
    ]
  },
  {
    "id": "horror",
    "name": "Horror",
    "max": 1,
    "tags": [
      "afterdeath"
    ],
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "You may place 1 :paranoia: on any character in this location."
      }
    ]
  },
  {
    "id": "overlord",
    "name": "Overlord",
    "tags": [
      "afterdeath"
    ],
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "prerequisite": "This character is dead",
        "description": "Choose any other card in this location. If it is a corpse, it will be revived, If it is a character, it will be killed."
      }
    ]
  }
],
 ...[
  {
    "id": "watcher",
    "name": "Watcher",
    "tags": [
      "immortal"
    ],
    "max": 1,
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Incident trigger"
        ],
        "prerequisite": "In the same Location as an Incident triggered by a living Character who has 1+ :despair:",
        "description": "The :incident: always triggers, regardless of what other counters the Character has."
      }
    ]
  },
  {
    "id": "influencer",
    "name": "Influencer",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "prerequisite": "This character dies.",
        "description": "Place 1 :paranoia: on each Character who shares this Character's starting Location."
      },
      {
        "type": "Mandatory",
        "timesPerLoop": 1,
        "timing": [
          "Day End"
        ],
        "prerequisite": "This character's Goodwill ability has been triggered.",
        "description": "Place 1 :paranoia: and 1 :goodwill: on each Character who shares this Character's starting Location."
      }
    ]
  },
  {
    "id": "secretkeeper",
    "name": "Secretkeeper",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "prerequisite": "This character has died, or their Goodwill ability has been triggered.",
        "description": "Reveal this Character's Role."
      },
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "This character's Role has been revealed, and it's currently the 1st or 2nd Loop.",
        "description": "The Mastermind can only play 1 Action card on the following Day. At the end of that Day, or this Day if it's already the final Day, kill the Protagonists."
      }
    ]
  },
  {
    "id": "wildcard",
    "name": "Wildcard",
    "tags": [
      "immortal"
    ],
    "max": 1,
    "goodwillRefusel": "Mandatory",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Day Start"
        ],
        "prerequisite": "The current Day is a multiple of 3",
        "description": "This Character gains the abilities of :conspiracyTheorist:, :brain:, and :serialKiller:."
      },
      {
        "type": "Script creation",
        "description": "This Character must be the Culprit of an Incident."
      }
    ]
  }
],
 ...[
  {
    "id": "poisoner",
    "name": "Poisoner",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "prerequisite": "the Extra Gauge is on 2 or more",
        "timesPerLoop": 1,
        "description": "One charcters in the same location dies."
      },
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "The Extra Gauge is on 4 or more."
      }
    ]
  },
  {
    "id": "fool",
    "name": "Fool",
    "max": 1,
    "abilities": [
      {
        "type": "Script creation",
        "description": "This character must be the culprit of an Incident"
      },
      {
        "type": "Mandatory",
        "timing": [
          "Incident step"
        ],
        "description": "After this character has triggered an Incident, remove all :paranoia: counters from this card."
      }
    ]
  },
  {
    "id": "privateInvestigator",
    "name": "Private Investigator",
    "tags": [
      "immortal"
    ],
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "This character can never be a culprit."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Incident step"
        ],
        "prerequisite": "the Extra Gauge is 0, and the culprit is in this location",
        "description": "The Incident triggers regardless of the number of :paranoia: counters on the culprit."
      }
    ]
  },
  {
    "id": "paranoiac",
    "name": "Paranoiac",
    "goodwillRefusel": "Mandatory",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "You may place 1 :intrigue: counter on this location or an any character in this location."
      }
    ]
  },
  {
    "id": "twin",
    "name": "Twin",
    "abilities": [
      {
        "type": "Script creation",
        "description": "This character must be the culprit of an Incident."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Incident trigger"
        ],
        "description": "When this character triggers an Incident, it is considered as being on the diagonally opposit location."
      }
    ]
  },
  {
    "id": "obstinate",
    "name": "Obstinate",
    "goodwillRefusel": "Mandatory",
    "abilities": [
      {
        "type": "Script creation",
        "description": "This character must be the culprit of an Incident."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Incident step"
        ],
        "description": "This character always triggers its Incidents (if alive), regardless of the amount of :paranoia: counters on it."
      }
    ]
  },
  {
    "id": "therapist",
    "name": "Therapist",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Mastermind Ability"
        ],
        "prerequisite": "The Extra Gauge is 1 or above",
        "description": "Remove 1 :paranoia: counter from any other character in this location."
      }
    ]
  },
  {
    "id": "magician",
    "name": "Magician",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "timesPerLoop": [
          1,
          {
            "Over all Roles": true
          }
        ],
        "description": "You may move one character with at least one :paranoia: counter from this location to an adjacent location (not diagonal)."
      },
      {
        "type": "Mandatory",
        "timing": [
          "On character death"
        ],
        "description": "Remove all :paranoia: counters from its corpse."
      }
    ]
  },
  {
    "id": "ninja",
    "name": "Ninja",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "When this role is to be reveald"
        ],
        "description": "You may, insead of saying the truth, state any other non-:person: role that is in this script"
      },
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "There is any charcter with at least 2 :intrigue: Counters in this location"
      }
    ]
  },
  {
    "id": "prophet",
    "name": "Prophet",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Mastermind Action step"
        ],
        "description": "The Mastermind cannot place cards on this character."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Incident step"
        ],
        "prerequisite": "The culprit of an incident that would trigger is in another location",
        "description": "That incident does not trigger, regardless of the number of :paranoia: conters on the culprit."
      }
    ]
  },
  {
    "id": "immortalRole",
    "name": "Immortal",
    "tags": [
      "immortal"
    ],
    "abilities": []
  }
],
 ...[
  {
    "id": "marionetteAHR",
    "name": "Marionette",
    "goodwillRefusel": "Puppeted",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "After Goodwill Ability used"
        ],
        "prerequisite": "If there are two or more kinds of counters on this character",
        "description": "This character dies and you trigger a warp."
      }
    ]
  },
  {
    "id": "lullaby",
    "name": "Lullaby",
    "goodwillRefusel": "Puppeted",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "After Goodwill Ability used"
        ],
        "description": "The target of that ability dies."
      },
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "timesPerLoop": 1,
        "description": "Place 1 :paranoia: or :goodwill: counter on a character at this character’s location."
      },
      {
        "type": "Optional Loss condition: Protagonists Death",
        "prerequisite": "4 or more kinds of counters on this character",
        "timing": [
          "Day End"
        ]
      }
    ]
  },
  {
    "id": "storyteller",
    "name": "Storyteller",
    "tags": [
      "immortal"
    ],
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "prerequisite": "If the Extra Gauge is 1 or higher",
        "description": "Move 1 counter between two other characters at this character’s location."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "prerequisite": "If the Extra Gauge was 3 or more at the end of the previous loop",
        "description": "The Protagonists add \":hope: +1\" to hand."
      }
    ]
  },
  {
    "id": "shifter",
    "name": "Shifter",
    "tags": [
      "immortal"
    ],
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Always"
        ],
        "description": "Ignore Forbid :goodwill: cards played on this character."
      },
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "If there are 2 or fewer kinds of counters on this character"
      }
    ]
  },
  {
    "id": "alice",
    "name": "Alice",
    "abilities": [
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "timing": [
          "Loop End"
        ],
        "prerequisite": "If this character is dead"
      },
      {
        "type": "Mandatory",
        "timing": [
          "After Goodwill Ability used"
        ],
        "timesPerLoop": 1,
        "prerequisite": "If the Extra Gauge is 1 or higher",
        "description": "Place 1 :hope: counter on each other character at this character’s location."
      }
    ]
  },
  {
    "id": "fragment",
    "name": "Fragment",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "prerequisite": "If this character was dead at the end of the previous loop",
        "description": "The Mastermind adds \":despair: +1\" to hand."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Loop Start"
        ],
        "prerequisite": "If this character was alive and had 2 or more :goodwill: at the end of the previous loop",
        "description": "The Protagonists add \":hope: +1\" to hand."
      }
    ]
  },
  {
    "id": "piedPiper",
    "name": "Pied Piper",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "timesPerLoop": 1,
        "prerequisite": "If the Extra Gauge is 2 or higher",
        "description": "One character at this character’s location dies."
      },
      {
        "type": "Optional",
        "timing": [
          "Day End"
        ],
        "description": "You may place 1 :intrigue: counter on a corpse at this character’s location."
      },
      {
        "type": "Optional Loss condition: Protagonists Death",
        "timing": [
          "Day End"
        ],
        "prerequisite": "If there are 3 or more :intrigue: counters on all corpses combined after using this roles other ability."
      }
    ]
  },
  {
    "id": "gossip",
    "name": "Gossip",
    "max": 1,
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "You may place 1 :goodwill: counter on a character at this character’s location."
      },
      {
        "type": "Mandatory",
        "timing": [
          "On character death"
        ],
        "description": "Place 1 :despair: counter on a character at this character’s location, and you may trigger a Warp."
      }
    ]
  }
],
 ...[
  {
    "id": "augur",
    "name": "Augur",
    "abilities": [
      {
        "type": "Optional",
        "description": "Add one :paranoia: to a character in this location.",
        "timing": [
          "Mastermind Ability"
        ]
      }
    ],
    "max": 1
  },
  {
    "id": "contractKiller",
    "name": "Contract Killer",
    "abilities": [
      {
        "type": "Optional",
        "description": "Kill another Character in this location that has 2 or more :intrigue:.",
        "timing": [
          "Mastermind Ability"
        ]
      }
    ],
    "goodwillRefusel": "Mandatory"
  },
  {
    "id": "cultMember",
    "name": "Cult Member",
    "goodwillRefusel": "Mandatory",
    "abilities": [
      {
        "timing": [
          "Always"
        ],
        "type": "Mandatory",
        "description": "Ignore Forbid :goodwill: for this character."
      }
    ]
  },
  {
    "id": "madGenius",
    "name": "Mad Genius",
    "goodwillRefusel": "Optional",
    "max": 1,
    "abilities": [
      {
        "type": "Optional",
        "description": ":madGenius: acquirs Body Parts.",
        "timing": [
          "Day End"
        ],
        "prerequisite": "In the same location as a dead character."
      }
    ]
  },
  {
    "id": "potentate",
    "name": "Potentate",
    "goodwillRefusel": "Mandatory",
    "max": 1,
    "tags": [
      "supernaturalTag"
    ],
    "abilities": [
      {
        "type": "Optional",
        "description": "Transfer 1 :intrigue: from a character to the characers location or another character in the same location.",
        "timing": [
          "Mastermind Ability"
        ]
      }
    ]
  },
  {
    "id": "seeder",
    "name": "Seeder",
    "goodwillRefusel": "Mandatory",
    "max": 1,
    "tags": [
      "supernaturalTag"
    ],
    "abilities": [
      {
        "type": "Loss condition: Tragedy",
        "prerequisite": ":seeder: is killed.",
        "timing": [
          "On character death"
        ]
      },
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "timesPerLoop": 1,
        "description": "Add one :paranoia: to another non-:supernaturalTag: chracter in the same Location and change the Characters role to :keyPerson:. Teh :seeder: looses all abilities and Effects after using this ability."
      }
    ]
  },
  {
    "id": "supplicant",
    "name": "Supplicant",
    "abilities": [
      {
        "type": "Mandatory",
        "prerequisite": "The :supplicant: has 1 :intrigue: and is in the same location as the :lover:",
        "description": "The :supplicant: gainst :supernaturalTag: tag.",
        "timing": [
          "Day End"
        ]
      }
    ]
  },
  {
    "id": "facsimile",
    "name": "Facsimile",
    "tags": [
      "supernaturalTag"
    ],
    "abilities": [
      {
        "type": "Mandatory",
        "prerequisite": "If half or more of the characters in this location are :facsimile:s",
        "description": "All characters in the location become :facsimile:s.",
        "timing": [
          "Day End"
        ]
      },
      {
        "type": "Mandatory",
        "prerequisite": "The :facsimile: is in the same location as the :keyPerson:",
        "description": "The :facsimile: is Killed.",
        "timing": [
          "Day End"
        ]
      }
    ]
  }
],
 ...[
  {
    "id": "subHeroine",
    "name": "Sub Heroine",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Card resolve"
        ],
        "description": "Ignore :goodwill: +1 and :goodwill: +2 Actions set on this character."
      }
    ]
  },
  {
    "id": "creature",
    "name": "Creature",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Day End"
        ],
        "description": "You may kill one Character in the same location who has 1+ :intrigue: Counter(s) on them, unless a :slayer: is in the same Location."
      },
      {
        "type": "Optional",
        "timing": [
          "Day End"
        ],
        "prerequisite": "The :creature: has at least 5 :intrigue:",
        "description": "Kill the Protagonist."
      }
    ]
  },
  {
    "id": "possessed",
    "name": "Possessed",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Card resolve"
        ],
        "description": "Ignore all :intrigue: Counters placed on the Location where this Character is, as well as on any other Characters in the same Location."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Card resolve"
        ],
        "description": "Ignore \"Forbid Movement\" Actions on this Character."
      }
    ]
  },
  {
    "id": "slayer",
    "name": "Slayer",
    "max": 1,
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "description": "If the :slayer: is alone with the :creature:, the :creature: dies."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Mastermind Action step"
        ],
        "description": "Remove one :intrigue: Counter from a Character in the same Location."
      }
    ]
  },
  {
    "id": "heavyLovers",
    "name": "Heavy Lovers",
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Day End"
        ],
        "prerequisite": "3+ :paranoia:, or 1+ :intrigue:",
        "description": "Kill the Protagonist."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "prerequisite": "There is a Character of the same sex with 3+ :goodwill: in the same area.",
        "description": "Kill that Character. You may also place up to 1 :intrigue: and 1 :paranoia: counter each on yourself."
      }
    ]
  },
  {
    "id": "moodMaker",
    "name": "Mood Maker",
    "max": 1,
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "Place 1 :goodwill: counter on one other character in the same Location."
      }
    ]
  },
  {
    "id": "troublemaker",
    "name": "Troublemaker",
    "max": 1,
    "abilities": [
      {
        "type": "Optional",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "Remove 1 :goodwill: counter on one other character in the same Location."
      }
    ]
  },
  {
    "id": "lunatic",
    "name": "Lunatic",
    "goodwillRefusel": "Optional",
    "abilities": [
      {
        "type": "Mandatory",
        "timing": [
          "Mastermind Ability"
        ],
        "description": "Place 1 :intrigue: or 1 :paranoia: on this Character."
      },
      {
        "type": "Mandatory",
        "timing": [
          "Day End"
        ],
        "prerequisite": "This role has been revealed",
        "description": "Kill one Character in the same Location (Once per Loop)."
      }
    ]
  }
]
] as const;
    export const rolesLookup = roles.reduce((p, c) => {
        if (typeof c === 'object' && c !== null && 'id' in c) {
            p[c.id] = c;
        }
        return p;
    }, {} as Record<typeof roles[number]['id'], ReadonlyRecursive<Role>>);
    
    export function isRoleId(id: unknown): id is keyof typeof rolesLookup {
        return typeof id === 'string' && id in rolesLookup;
    }
    
    ;


export const tragedys = [

 ...[
  {
    "id": "anotherHorizon",
    "name": "Another Horizon",
    "mainPlots": [
      "lostHeart",
      "shadowDemonKing",
      "devilsWill",
      "parallelWorldWar",
      "alienErosion"
    ],
    "subPlots": [
      "worldDollhouse",
      "egoWave",
      "closedDoor",
      "threadsFate",
      "moonsideCity",
      "fanaticSacrifices",
      "someoneInvites"
    ],
    "incidents": [
      "insaneMurder",
      "brainwashing",
      "missingPerson",
      "hospitalIncident",
      "driftingToAnotherWorld",
      "assassination",
      "worldEnd",
      "worldConvergence",
      "smallForce",
      "breakthrough",
      "confession"
    ],
    "aditionalRoles": [
      "shadow"
    ],
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 2,
    "extraRules": [
      {
        "name": "World Switch",
        "description": "Protagonist Leader may request to switch the world during the Day Start"
      },
      {
        "name": "Extra Gauge",
        "description": "Indicate World, 1: Normal and intial World, 2: Abnormal World."
      },
      {
        "name": "Abnormal World",
        "description": "The numbers of :goodwill: counter need to reach :paranoia: Limit to trigger the incident. Also sufficient numbers of :paranoia: needed to activate the character’s :goodwill: ability."
      },
      {
        "name": " :goodwill: Outburst",
        "description": "Mastermind able to use the character’s :goodwill: abilities whose role has :goodwill: Outburst."
      }
    ]
  }
],
 ...[
  {
    "id": "firstSteps",
    "name": "First Steps",
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 1,
    "mainPlots": [
      "murderPlan",
      "lightAvenger",
      "placeProtect"
    ],
    "subPlots": [
      "shadowRipper",
      "unsettlingRumor",
      "hideousScript"
    ],
    "incidents": [
      "murder",
      "increasingUnease",
      "suicide",
      "hospitalIncident",
      "farawayMurder",
      "missingPerson",
      "spreading"
    ],
    "extraRules": []
  },
  {
    "id": "basicTragedy",
    "name": "Basic Tragedy",
    "mainPlots": [
      "murderPlan",
      "sealedItem",
      "signWithMe",
      "changeOfFuture",
      "giantTimeBomb"
    ],
    "subPlots": [
      "circleFriends",
      "loveAffair",
      "hiddenFreak",
      "unsettlingRumor",
      "paranoiaVirus",
      "threadsFate",
      "unknownFactor"
    ],
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 2,
    "extraRules": [],
    "incidents": [
      "butterflyEffect",
      "farawayMurder",
      "foulEvil",
      "hospitalIncident",
      "increasingUnease",
      "missingPerson",
      "murder",
      "spreading",
      "suicide"
    ]
  }
],
 ...[
  {
    "id": "primeEvil",
    "name": "Prime Evil",
    "mainPlots": [
      "nobleBloodline",
      "moonlightBeast",
      "nightMistNightmare",
      "OnesFromGrave",
      "cursedLand"
    ],
    "subPlots": [
      "thoseHabits",
      "loveAffair",
      "witchsCurse",
      "keyGirl",
      "monsterIntrigue",
      "panicObsession",
      "peopleDontListen"
    ],
    "aditionalRoles": [
      "zombie"
    ],
    "incidents": [
      "sacrilegiousMurder",
      "increasingUnease",
      "missingPerson",
      "evilContamination",
      "executioner",
      "darkRumor",
      "barricade",
      "nightMadness",
      "awakenedCurse",
      "fountainFilth",
      "evangeliumDead"
    ],
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 2,
    "extraRules": [
      {
        "name": "Victims",
        "description": "Every :inrigue: on locations counts as a corpse with the role :person:. If such a corpse is revived by the power of the :alien:, it is simply removed from the board."
      },
      {
        "name": "Mob Incidents",
        "description": "These Incidents do not specify a character as a culprit, but a location. If there are enough corpses (specified by the incident) on the specific location, the incident happens."
      },
      {
        "name": "Curse cards",
        "description": "Extra cards are treated as Curse cards. They can be placed on a location or be attached to characters and corpses. At the end of Day, before everything else, each curse is handeld. On a location the mastermind attaches a curse to any character in that location. Characters may have more than one curse. Characters with curses are killed (at the same time so a character that got a curse this step is not killed.) and the curse moves to the location. At the end of each loop, all Curses are removed."
      }
    ]
  },
  {
    "id": "cosmicMythology",
    "name": "Cosmic Mythology",
    "mainPlots": [
      "choirOuterGod",
      "sacredWordsDagon",
      "kingYellow",
      "giantTimeBombAgain",
      "bloodyRites"
    ],
    "subPlots": [
      "unsettlingRumor",
      "resistance",
      "peopleWhoSaw",
      "greatRace",
      "whispersFromDeep",
      "facelessGod",
      "twistedTruth"
    ],
    "incidents": [
      "insaneMurder",
      "massSuicide",
      "missingPerson",
      "increasingUnease",
      "evilContamination",
      "hospitalIncident",
      "uproar",
      "fireDemise",
      "houndDogScent",
      "discovery",
      "executioner"
    ],
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 2,
    "extraRules": [
      {
        "name": "Extra Gauge: Mythos knowlege",
        "description": "1. The Extra gauge starts on zero and dose not reset between loops. 2. Every time :goodwill: ability is refused, immediately increase the Extra Gauge. 3. The Extra Gauge unlooks spells for the characters (Level in parenthises behind the spell name)."
      },
      {
        "name": "Spell of Sympathy (1)",
        "description": "At the start of the first day, the Leader may place 2 :goodwill: on any one character of his choice."
      },
      {
        "name": "Reminiscence of the Forefathers (2)",
        "description": "At the end of the loop, the Protagonists get to know the first subplot."
      },
      {
        "name": "Ancient Seal (3)",
        "description": "The Protagonist’s \"Forbid :intrigue:\" cards are no longer nullified if more then one of them are played in the same day."
      },
      {
        "name": "Madness (4)",
        "description": "At the end of the Day the Protagonists die. This happens after everything else. No more loops can be played. Go to Final Guess."
      }
    ]
  }
],
 ...[
  {
    "id": "hauntedStage",
    "name": "Haunted Stage",
    "mainPlots": [
      "ancientShrine",
      "cursedDoll",
      "fullMoonNight",
      "strangeStory",
      "primeEvilPlot"
    ],
    "subPlots": [
      "deepHole",
      "zombieApocalypse",
      "expandingUrbanLegend",
      "hauntedCity",
      "dancePartyDead",
      "maxCrazyGauge",
      "boundaryBetweenLifeDeath"
    ],
    "aditionalRoles": [
      "ZombieHaunted"
    ],
    "incidents": [
      "serialMurder",
      "increasingUnease",
      "massSuicide",
      "sacrificeHaunted",
      "hospitalIncident",
      "blasphemy",
      "monsterLiberation",
      "hundredDemonsNight",
      "curse",
      "infestation",
      "repeatingNightmare"
    ],
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 2,
    "extraRules": [
      {
        "name": "After Death",
        "description": "Protagonist can place cards on the corpse, and use its :goodwill: abilities. However, there is a penalty for corpses that do not have After Death activity. Set Extra Gauge at 0 at the start of the game. Extra Gauge doesn’t reset after loop end."
      },
      {
        "name": "Extra Gauge Effect",
        "description": "Madness [Extra Gauge 4 or more]: Decrease the Extra Gauge by 4 and kill the Protagonists at the end of the day."
      },
      {
        "name": "Extra Gauge Increase factors",
        "description": "Action card set on a corpse that has no :afterDeath: activity (+1) Declaration of using :goodwill: abilities of corpse that has no After Death activity (+1) Using :goodwill: ability to corpse that has no :afterDeath: activity (+1) Subplot :deepHole: at the end of the loop (+ 0-3) Role Ability of :monster: (+2) Role Ability of :zombie: (+1) Incident Effect (:sacrifice:, :repeatingNightmare:) (+1)."
      }
    ]
  }
],
 ...[
  {
    "id": "lastLiar",
    "name": "Last Liar",
    "maximumRoles": {
      "serialKiller": 1
    },
    "mainPlots": [
      "finalPlan",
      "sealedConclusion",
      "worldRebellion",
      "demonsScript",
      "giantTimeBombYetAgain"
    ],
    "subPlots": [
      "realMonster",
      "keeperMythology",
      "trueDetective",
      "crossingWorldLines",
      "unsafeTrigger",
      "socialMediaParanoia",
      "mythomaniacSecret"
    ],
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 2,
    "extraRules": [],
    "incidents": [
      "murder",
      "increasingUnease",
      "missingPerson",
      "hospitalIncident",
      "executioner",
      "distortion",
      "lastWill",
      "confession",
      "spreading",
      "lightHope",
      "murkDespair"
    ]
  }
],
 ...[
  {
    "id": "mysteryCircle",
    "name": "Mystery Circle",
    "mainPlots": [
      "murderPlan",
      "tightropePlan",
      "dropStrychnine",
      "quiltIncidents",
      "blackSchool"
    ],
    "subPlots": [
      "hiddenFreak",
      "danceFools",
      "isolatedInstitutionPsycho",
      "anAbsoluteWill",
      "trickyTwins",
      "smellGunpowder",
      "masterDetective"
    ],
    "incidents": [
      "serialMurder",
      "hospitalIncident",
      "portent",
      "increasingUnease",
      "terrorism",
      "bestialMurder",
      "suicide",
      "suspiciousLetter",
      "fakedSuicide",
      "closedCircle",
      "silverBullet"
    ],
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 2,
    "extraRules": [
      {
        "name": "Extra Gauge: Number of Incidents",
        "description": "During \"removal and placment of counters\" set the Extra Gauge to 0. For each Incident that occurse, increase the Extra Gauge by 1. This is done regardless of whether the Event itself has any effect or not."
      },
      {
        "name": "Extra Cads",
        "description": "During the \"removal and placement of counters\" step during Loop start also remove all Extra cards. Then, place the cards according to plot rules (if applicable)."
      }
    ]
  },
  {
    "id": "midnightZone",
    "name": "Midnight Zone",
    "mainPlots": [
      "sealedItem",
      "secretRecord",
      "devilsHand",
      "maleConfrontation",
      "fatedConnections"
    ],
    "subPlots": [
      "loveHateSpiral",
      "showtimeDeath",
      "witchesTeaTime",
      "diceOfGods",
      "unansweredHeart",
      "unsafeTrigger",
      "worshippersApocalypse"
    ],
    "incidents": [
      "serialMurder",
      "missingPerson",
      "suicide",
      "conspiracies",
      "increasingUnease",
      "hospitalIncident",
      "uproar",
      "fakeIncident",
      "breakthrough",
      "fakedSuicide",
      "confession"
    ],
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 2,
    "extraRules": [
      {
        "name": "Extra Cads",
        "description": "During the \"removal and placement of counters\" step during Loop start also remove all Extra cards. Then, place the cards according to plot rules (if applicable)."
      }
    ]
  }
],
 ...[
  {
    "id": "anotherHorizonR",
    "name": "Another Horizon R",
    "mainPlots": [
      "forbiddenFuture",
      "fairyTaleMurderer",
      "motherGooseMystery",
      "dimensionalMerger",
      "intoNothingness"
    ],
    "subPlots": [
      "jekyllHyde",
      "plaguebringer",
      "puppeteersStrings",
      "throughLookingGlass",
      "crossingWorldLines",
      "unspeakableHorrors",
      "hysteriaVirus"
    ],
    "incidents": [
      "crimePassion",
      "dimensionalDistortion",
      "dimensionalPerversion",
      "dimensionalFracture",
      "leftBehind",
      "phantasmalIncident",
      "hospitalIncident",
      "lastWill",
      "singularity",
      "seepingDaylight",
      "murkDespair"
    ],
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 2,
    "extraRules": [
      {
        "name": "Warping",
        "description": "Many abilities and effects in this Set “trigger a Warp”. If one or more Warps are triggered on a given day, at the end of that day, increase the Extra Gauge by 1. This increase happens before any other effects during the Day end phase, including Mandatory effects. Note that even if multiple Warps are triggered on the same day, the Gauge still only increases by 1. The Mastermind is not obliged to announce when a Warp is triggered. As such, if a Warp is triggered, but the loop ends before the end of that day, they can hide the fact that it triggered at all."
      },
      {
        "name": "Extra Gauge",
        "description": "At the start of each loop, set the Extra Gauge to 0. Indicate World, even: Light World, odd: Dark World."
      },
      {
        "name": " Goodwill & Wrarp",
        "description": "In addition to effects that trigger a Warp, a Warp is automatically triggered after each time any player, Protagonist or Mastermind, uses a 1x∞ :goodwill: ability. (Thematically these abilities have a powerful effect on the world, causing instability in the timeline.)"
      },
      {
        "name": "Change of Heart",
        "description": "When determining if an Incident triggers, :goodwill: is counted instead of :paranoia:. When determining if a :goodwill: ability can be used, :paranoia: is counted instead of :goodwill:."
      },
      {
        "name": "Puppeted :goodwill: Refusel",
        "description": "Puppeted :goodwill: Refusel is a new form of :goodwill: Refusel. In addition to providing the basic effect of :goodwill: Refusel, the Mastermind may use the :goodwill: abilities of a character with Puppeted :goodwill: Refusel during the Mastermind abilities phase, provided that there are sufficient counters on that character. When they do, they must announce what ability is being used, and any targets that ability has, if applicable."
      },
      {
        "name": "Dual Roles",
        "description": "Some plots have dual roles. These roles are always on the same character. The first role is in the Light World, and the second role is in the Dark World. The character’s role is determined by the world they are in."
      },
      {
        "name": "Changes to Starting Action Cards",
        "description": "The Protagonists additionally each have a :paranoia: +2 (1x∞) card, and the Mastermind additionally has a :goodwill: +1 card. Furthermore, the Mastermind also adds a Despair +1 (1x∞) card to their hand for the first loop only. (On later loops, they only gain it if an effect allows them to do so.)"
      }
    ]
  }
],
 ...[
  {
    "id": "supernatural",
    "name": "Supernatural",
    "mainPlots": [
      "abomination",
      "Metamorphosis",
      "sporePod",
      "demonicPortal",
      "seed"
    ],
    "subPlots": [
      "infestationSupernatural",
      "fatalAttraction",
      "manifestation",
      "murderHire",
      "remoteBombing",
      "suicideCult",
      "twistedThreadsTime"
    ],
    "incidents": [
      "abductions",
      "crimePassionSupernatural",
      "graveRobbing",
      "indoctrination",
      "proselytizing",
      "ritualSacrifice",
      "suicide",
      "massHysteria",
      "schoolShooting",
      "darkLesson",
      "publicMeltdown",
      "fireBrimstone",
      "bombThreat"
    ],
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 2
  }
],
 ...[
  {
    "id": "visualNovel",
    "name": "Visual Novel",
    "mainPlots": [
      "lastMiracle",
      "sealEvilSpirit",
      "promiseSmile",
      "albumMemories",
      "monsterInsideMe"
    ],
    "subPlots": [
      "noisyClassmates",
      "missingHighlight",
      "sevenSchoolMysteries",
      "behindScenes",
      "howlingMoonlitNight",
      "hiddenFreak",
      "spreadingInfection"
    ],
    "numberOfMainPlots": 1,
    "numberOfSubPlots": 2,
    "extraRules": [],
    "incidents": [
      "murder",
      "increasingUnease",
      "parting",
      "foulEvil",
      "exposure",
      "terrorism",
      "friction",
      "closedCircle",
      "routeBranching",
      "relocation",
      "heartPoundingTimeBomb"
    ]
  }
]
] as const;
    export const tragedysLookup = tragedys.reduce((p, c) => {
        if (typeof c === 'object' && c !== null && 'id' in c) {
            p[c.id] = c;
        }
        return p;
    }, {} as Record<typeof tragedys[number]['id'], ReadonlyRecursive<Tragedy>>);
    
    export function isTragedyId(id: unknown): id is keyof typeof tragedysLookup {
        return typeof id === 'string' && id in tragedysLookup;
    }
    
    ;


export const incidents = [

 ...[
  {
    "id": "driftingToAnotherWorld",
    "name": "Drifting to Another World",
    "effect": [
      {
        "prerequisite": "1 :intrigue: on the School",
        "description": "Everyone in the School dies."
      },
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "prerequisite": "2 :intrigue: on the School"
      }
    ]
  },
  {
    "id": "brainwashing",
    "name": "Brainwashing",
    "effect": [
      {
        "description": "Place 2 Paranoia on a character of your choice, and 2 :goodwill: on another character of your choice."
      }
    ]
  },
  {
    "id": "assassination",
    "name": "Assassination",
    "effect": [
      {
        "description": "Kill any other character in this location or on the diagonally opposite location. When determining whether this incident occurs or not, count :intrigue: instead of :paranoia:."
      }
    ]
  },
  {
    "id": "worldEnd",
    "name": "World End",
    "effect": [
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "prerequisite": "In Normal world",
        "description": "All characters die."
      }
    ]
  },
  {
    "id": "worldConvergence",
    "name": "World Convergence",
    "effect": [
      {
        "description": "Switch world to Normal world. The world cannot be switched for the remainder of this loop"
      }
    ]
  },
  {
    "id": "smallForce",
    "name": "Small Force",
    "effect": [
      {
        "description": "The Protagonist Leader chooses any character and 1 :goodwill:, or 1 :paranoia:, or 1 :intrigue:. Put one chosen counter on that character. When determining whether this incident occurs or not, the culprit is regarded as having :paranoia: Limit -1."
      }
    ]
  }
],
 ...[
  {
    "id": "murder",
    "name": "Murder",
    "effect": [
      {
        "description": "One (1) other character in culprit’s Location dies"
      }
    ]
  },
  {
    "id": "increasingUnease",
    "name": "Increasing Unease",
    "effect": [
      {
        "description": "Place 2 :paranoia: on any character, then 1 :intrigue: on any other character."
      }
    ]
  },
  {
    "id": "foulEvil",
    "name": "Foul Evil",
    "effect": [
      {
        "description": "Place 2 :intrigue: on the Shrine."
      }
    ]
  },
  {
    "id": "suicide",
    "name": "Suicide",
    "effect": [
      {
        "description": "The culprit dies."
      }
    ]
  },
  {
    "id": "hospitalIncident",
    "name": "Hospital Incident",
    "effect": [
      {
        "prerequisite": "1 :intrigue: on the Hospital",
        "description": "Everyone in the Hospital dies."
      },
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "prerequisite": "2 :intrigue: on the Hospital"
      }
    ]
  },
  {
    "id": "farawayMurder",
    "name": "Faraway Murder",
    "effect": [
      {
        "description": "One character with at least 2 :intrigue: dies."
      }
    ]
  },
  {
    "id": "missingPerson",
    "name": "Missing Person",
    "effect": [
      {
        "description": "Move culprit to any Location. Put 1 :intrigue: on that Location."
      }
    ]
  },
  {
    "id": "spreading",
    "name": "Spreading",
    "effect": [
      {
        "description": "Remove 2 :goodwill: (or 1 if they only have that) from a character, and then add 2 :goodwill: to another character."
      }
    ]
  },
  {
    "id": "butterflyEffect",
    "name": "Butterfly Effect",
    "effect": [
      {
        "description": "Put any counter on any character in culprit’s Location."
      }
    ]
  }
],
 ...[
  {
    "id": "sacrilegiousMurder",
    "name": "Sacrilegious Murder",
    "effect": [
      {
        "description": "Either kill another charcter in the same location as the culprit, or place an :intrigue: on the culprit’s location."
      }
    ]
  },
  {
    "id": "evilContamination",
    "name": "Evil Contamination",
    "effect": [
      {
        "description": "Place 2 :intrigue: on the Shrine."
      }
    ]
  },
  {
    "id": "executioner",
    "name": "The Executioner",
    "effect": [
      {
        "description": "The leader chooses one character. That charcter is killed."
      }
    ]
  },
  {
    "id": "darkRumor",
    "name": "Dark Rumor",
    "effect": [
      {
        "description": "Attach a Curse on the culprit."
      }
    ]
  },
  {
    "id": "barricade",
    "name": "Barricade",
    "effect": [
      {
        "description": "For each other character in cluprit’s location, pick any other location and move the character there."
      }
    ]
  },
  {
    "id": "nightMadness",
    "name": "Night of Madness",
    "mob": 0,
    "effect": [
      {
        "type": "Delayed Loss condition: Protagonists Death",
        "prerequisite": "6 or more zombies when this incident occurs.",
        "description": "The Protagonists will die after the day has ended."
      }
    ]
  },
  {
    "id": "awakenedCurse",
    "name": "Awakened Curse",
    "mob": 1,
    "effect": [
      {
        "description": "Place a curse on the culprit’s location."
      }
    ]
  },
  {
    "id": "fountainFilth",
    "name": "Fountain of Filth",
    "mob": 2,
    "effect": [
      {
        "description": "Place 2 :paranoia: on any one character, and an :intrigue: on any location."
      }
    ]
  },
  {
    "id": "evangeliumDead",
    "name": "Evangelium of the Dead",
    "mob": 2,
    "effect": [
      {
        "description": "Kill all characters in the culprits location."
      },
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "prerequisite": "After that, if that location has 5 or more corpses"
      }
    ]
  },
  {
    "id": "insaneMurder",
    "name": "Insane Murder",
    "effect": [
      {
        "description": "Kill any one character (including culprit) in the same location as the culprit."
      }
    ]
  },
  {
    "id": "massSuicide",
    "name": "Mass Suicide",
    "effect": [
      {
        "prerequisite": "1 :intrigue: on the culprit",
        "description": "All characters in the culprit’s location are killed."
      }
    ]
  },
  {
    "id": "fireDemise",
    "name": "Fire of Demise",
    "effect": [
      {
        "timesPerGame": 1,
        "type": "Mandatory Loss condition: Protagonists Death",
        "description": "All characters and the Protagonists are killed."
      }
    ]
  },
  {
    "id": "houndDogScent",
    "name": "Hound Dog Scent",
    "effect": [
      {
        "type": "Delayed Loss condition: Protagonists Death",
        "description": "When determinig whether this incident occours or not, count :intrigue: instead of :paranoia:. For the rest of the loop, if another Incident occours, the Protagonists are killed after the Incident step."
      }
    ]
  },
  {
    "id": "discovery",
    "name": "Discovery",
    "effect": [
      {
        "description": "Increase the Extra Gauge 1 step."
      }
    ]
  }
],
 ...[
  {
    "id": "sacrificeHaunted",
    "name": "Sacrifice",
    "effect": [
      {
        "description": "[When determining whether this Incident triggers or not, treat the culprit’s :paranoia: limit as 2 less than its printed limit.] Increase Extra Gauge by 1, then the Protagonist Leader chooses one character. That character is killed."
      }
    ]
  },
  {
    "id": "blasphemy",
    "name": "Blasphemy",
    "effect": [
      {
        "description": "Blasphemy\tPlace 1 :paranoia: and 1 :intrigue: on any corpse."
      }
    ]
  },
  {
    "id": "monsterLiberation",
    "name": "Monster Liberation",
    "effect": [
      {
        "description": "Put an Extra card on the culprit’s location. Thereafter, the card is treated as a character whose title is Demon Beast and gains Nightmare’s role."
      }
    ]
  },
  {
    "id": "hundredDemonsNight",
    "name": "Hundred Demons Night",
    "effect": [
      {
        "prerequisite": "Culprit is dead",
        "description": "At least 1 :intrigue: on the shrine: Increase Extra Gauge by 4."
      }
    ]
  },
  {
    "id": "curse",
    "name": "Curse",
    "effect": [
      {
        "prerequisite": "Culprit is dead",
        "description": "Move any card on the culprit’s location to any other location."
      }
    ]
  },
  {
    "id": "infestation",
    "name": "Infestation",
    "effect": [
      {
        "prerequisite": "Culprit is dead",
        "description": "Place 2 :intrigue: on the culprit’s location."
      }
    ]
  },
  {
    "id": "repeatingNightmare",
    "name": "Repeating Nightmare",
    "effect": [
      {
        "prerequisite": "Culprit is dead",
        "description": "Increase the Extra Gauge by 1 and revive the culprit."
      }
    ]
  }
],
 ...[
  {
    "id": "distortion",
    "name": "Distortion",
    "effect": [
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "prerequisite": "Culprit's starting Location has 2+ :intrigue:"
      },
      {
        "prerequisite": "Culprit's starting Location does not have 2+ :intrigue:",
        "description": "Place 2 :intrigue: on that Location."
      }
    ]
  },
  {
    "id": "lightHope",
    "name": "The Light of Hope",
    "effect": [
      {
        "description": "The Leader chooses a character. Place 1 :Hope: counter on that character. When determining if this Incident triggers, refer to the number of :Goodwill: counters on the culprit instead of the original type of counter."
      }
    ]
  }
],
 ...[
  {
    "id": "serialMurder",
    "name": "Serial Murder",
    "repeatedCulprit": true,
    "effect": [
      {
        "description": "One other character in the culprit’s location dies. The same character may be the culprit of several Serial Murder Incidents."
      }
    ]
  },
  {
    "id": "portent",
    "name": "Portent",
    "effect": [
      {
        "description": "[When determning wether this Incident triggers or not, treat the culprit’s :paranoia: limit ats 1 less then its printed limit] Put 1 :paranoia: counter on any character in the culprit’s location."
      }
    ]
  },
  {
    "id": "terrorism",
    "name": "Terrorism",
    "effect": [
      {
        "prerequisite": "1 :intrigue: on the City",
        "description": "Everyone in the City dies."
      },
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "prerequisite": "2 :intrigue: on the City"
      }
    ]
  },
  {
    "id": "bestialMurder",
    "name": "Bestial Murder",
    "effect": [
      {
        "description": "[When determinig wether this Incident triggers or not, treat the culprit’s :paranoia: limit as 1 more than its printed limit.] Resolve \":sserialMurder:\" and \":increasingUnease:\" in that order. Then increase the Extra Gauge by 1 more step."
      }
    ]
  },
  {
    "id": "suspiciousLetter",
    "name": "A Suspicious Letter",
    "effect": [
      {
        "description": "Move any character in the culprit’s location to any location. If the character actually changed location, that character cannot be moved the next day."
      }
    ]
  },
  {
    "id": "fakedSuicide",
    "name": "Faked Suicide",
    "effect": [
      {
        "description": "Set an Extra Card on the culprit. The Protagonists may not play any cards on character(s) with an Extra Card."
      }
    ]
  },
  {
    "id": "closedCircle",
    "name": "Closed Circle",
    "effect": [
      {
        "description": "Reveal the culprit’s locationto. For 3 days, including the day the incident occurred, any movement to or from that location is nullified."
      }
    ]
  },
  {
    "id": "silverBullet",
    "name": "The Silver Bullet",
    "effect": [
      {
        "description": "The loop ends after this Incident step (resulting in a Protagonist victory unless any loss condition is fullifilled). This Incident dose not increase the Extra Gauge."
      }
    ]
  },
  {
    "id": "conspiracies",
    "name": "Conspiracies",
    "effect": [
      {
        "description": "Resolve either a :serialMurder: or a :missingPerson: Incident. Check :intrigue: instead of :paranoia: conters to trigger the Incident."
      }
    ]
  },
  {
    "id": "uproar",
    "name": "Uproar",
    "effect": [
      {
        "prerequisite": "1 :intrigue: on the School",
        "description": "Everyone in the School dies."
      },
      {
        "prerequisite": "1 :intrigue: on the City",
        "description": "Everyone in the City dies."
      }
    ]
  },
  {
    "id": "fakeIncident",
    "name": "Fake Incident",
    "effect": [
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "prerequisite": "2 :intrigue: on the culprit’s starting location"
      }
    ],
    "faked": true
  },
  {
    "id": "breakthrough",
    "name": "Breakthrough",
    "effect": [
      {
        "description": "The Protagonist Leader chooses one location or character, and removes 2 :intrigue: counters from there."
      }
    ]
  },
  {
    "id": "confession",
    "name": "Confession",
    "effect": [
      {
        "description": "Reveal the culprit and the culprit’s role."
      }
    ]
  }
],
 ...[
  {
    "id": "crimePassion",
    "name": "Crime of Passion",
    "effect": [
      {
        "description": "One other character at the culprit’s location dies, if possible. When determining if this Incident triggers, treat the culprit’s :paranoia: Limit as 1 lower."
      }
    ]
  },
  {
    "id": "dimensionalDistortion",
    "name": "Dimensional Distortion",
    "effect": [
      {
        "description": "You may trigger a Warp. When determining if this Incident triggers, it always triggers regardless of the number of counters on the culprit."
      }
    ]
  },
  {
    "id": "dimensionalPerversion",
    "name": "Dimensional Perversion",
    "effect": [
      {
        "description": "You may trigger a Warp. Place 2 :paranoia: counters on any character, and 2 :goodwill: counters on any other character."
      }
    ]
  },
  {
    "id": "dimensionalFracture",
    "name": "Dimensional Fracture",
    "effect": [
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "prerequisite": "If there are 3 or more kinds of counters on the culprit"
      }
    ]
  },
  {
    "id": "leftBehind",
    "name": "Left Behind",
    "effect": [
      {
        "description": "Place 1 :intrigue: counter on a character at the culprit’s location, then move the culprit to any location."
      }
    ]
  },
  {
    "id": "phantasmalIncident",
    "name": "Phantasmal Incident",
    "effect": [
      {
        "description": "Resolve the effect of Crime of Passion, Dimensional Perversion, or Left Behind. When determining if this Incident triggers, refer to the number of :intrigue: counters on the culprit instead of the original type of counter."
      }
    ]
  },
  {
    "id": "lastWill",
    "name": "Last Will",
    "effect": [
      {
        "description": "The culprit dies. At the start of the next loop, the Protagonists add \"Hope +1\" to hand."
      }
    ]
  },
  {
    "id": "singularity",
    "name": "The Singularity",
    "effect": [
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "prerequisite": "If the players are in the Light World and if this is the first time this game that this Incident triggered."
      },
      {
        "prerequisite": "If the players are in the Light World",
        "description": "Trigger a Warp."
      },
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "prerequisite": "If the players are in the Dark World and there is 1 or more :intrigue: counter on the culprit’s starting location"
      }
    ]
  },
  {
    "id": "seepingDaylight",
    "name": "Seeping Daylight",
    "effect": [
      {
        "description": "The Leader chooses a character. Place 1 Hope counter on that character."
      }
    ]
  },
  {
    "id": "murkDespair",
    "name": "The Murk of Despair",
    "effect": [
      {
        "description": "Place 1 Despair counter on any character."
      }
    ]
  }
],
 ...[
  {
    "id": "abductions",
    "name": "Abductions",
    "effect": [
      {
        "description": "Move a Character from his/her location to another location. Move a second Character from his/her location to another location."
      }
    ]
  },
  {
    "id": "crimePassionSupernatural",
    "name": "Crime of Passion",
    "effect": [
      {
        "description": "Kill another Character in the Culprit's location."
      }
    ]
  },
  {
    "id": "graveRobbing",
    "name": "Grave Robbing",
    "effect": [
      {
        "description": "Place two :intrigue: on the Shrine. Move a Character from the Shrine to the School, if possible."
      }
    ]
  },
  {
    "id": "indoctrination",
    "name": "Indoctrination",
    "effect": [
      {
        "description": "Remove all :goodwill: from a Character and replace with a mix of :paranoia: or :intrigue: that equals the removed :goodwill:."
      }
    ]
  },
  {
    "id": "proselytizing",
    "name": "Proselytizing",
    "effect": [
      {
        "description": "Add one :intrigue: to a Character and one :intrigue: to that Character's location."
      }
    ]
  },
  {
    "id": "ritualSacrifice",
    "name": "Ritual Sacrifice",
    "effect": [
      {
        "description": "The Character with the highest :goodwill: is killed. If no Character has the highest, this Incident doesn't trigger."
      }
    ]
  },
  {
    "id": "massHysteria",
    "name": "Mass Hysteria",
    "effect": [
      {
        "description": "Add 1 :paranoia: to every character."
      }
    ]
  },
  {
    "id": "schoolShooting",
    "name": "School Shooting",
    "effect": [
      {
        "prerequisite": "If at least 1 :intrigue: on the Culprit",
        "description": "All Characters in School are killed."
      }
    ]
  },
  {
    "id": "darkLesson",
    "name": "Dark Lesson",
    "effect": [
      {
        "description": "Add an :intrigue: to all Characters at the Culprit's location."
      }
    ]
  },
  {
    "id": "publicMeltdown",
    "name": "Public Meltdown",
    "effect": [
      {
        "description": "Reveal the role of the Culprit."
      }
    ]
  },
  {
    "id": "fireBrimstone",
    "name": "Fire and Brimstone",
    "effect": [
      {
        "description": "Kill all Characters in all Locations except the Shrine."
      }
    ]
  },
  {
    "id": "bombThreat",
    "name": "Bomb Threat",
    "effect": [
      {
        "description": "The Leader chooses a location. All Characters from that Location Move to the Location clockwise from their current Location."
      }
    ]
  }
],
 ...[
  {
    "id": "Diagonal Destruction",
    "name": "Diagonal Destruction",
    "effect": [
      {
        "type": "Loss condition: Tragedy",
        "prerequisite": "If there is at least 2 :intrigue: on the culprit's location.",
        "description": "Everyone in the diagonally opposite location dies and protagonists lose"
      }
    ]
  },
  {
    "id": "Diagonal Destruction 3",
    "name": "Diagonal Destruction 3",
    "effect": [
      {
        "description": "everyone in the diagonally opposite location dies"
      },
      {
        "description": "The incident happens at :paranoia: limit -1"
      },
      {
        "type": "Mandatory Loss condition: Protagonists Death",
        "prerequisite": "If 3 or more people die in this (a single) incident, the protagonists are killed."
      }
    ]
  },
  {
    "id": "Diagonal Destruction 2",
    "name": "Diagonal Destruction 2",
    "effect": [
      {
        "description": "Everyone in the diagonally opposite location of the culprit dies"
      }
    ]
  }
],
 ...[
  {
    "id": "parting",
    "name": "Parting",
    "effect": [
      {
        "description": "Move the Culprit to any Location."
      }
    ]
  },
  {
    "id": "exposure",
    "name": "Exposure",
    "effect": [
      {
        "description": "Choose one: 1. Remove up to 2 (cumulative total) :goodwill: counters from characters in the same Location as the Culprit. 2. Place up to 2 (cumulative total) :goodwill: counters on characters in the same Location as the Culprit."
      }
    ]
  },
  {
    "id": "friction",
    "name": "Friction",
    "effect": [
      {
        "prerequisite": "Culprit must have at least 1+ :goodwill:.",
        "description": "Remove all :goodwill: counters from one Character in the same Location as the Culprit."
      }
    ]
  },
  {
    "id": "routeBranching",
    "name": "Route Branching",
    "effect": [
      {
        "prerequisite": "The Culprit must have enough :goodwill: to use a :goodwill: Ability.",
        "description": "The Culprit's role changes to Key Person for the rest of that Loop."
      }
    ]
  },
  {
    "id": "relocation",
    "name": "Relocation",
    "effect": [
      {
        "description": "The Culprit is treated as if they are not in any Location, and cannot have Action cards placed on them."
      }
    ]
  },
  {
    "id": "heartPoundingTimeBomb",
    "name": "Heart-Pounding Time Bomb",
    "effect": [
      {
        "prerequisite": "The Culprit must not have any :goodwill: on them when this Incident occurs.",
        "description": "Prepare the EX Gauge, starting at 0. From that Day onward, the EX Gauge increases by 1 at each Day End phase. When it reaches 3, the :goodwill: counters on all Characters decreases by 2. When a :goodwill: counter is placed on the Culprit, the EX Counter resets to 0."
      }
    ]
  }
]
] as const;
    export const incidentsLookup = incidents.reduce((p, c) => {
        if (typeof c === 'object' && c !== null && 'id' in c) {
            p[c.id] = c;
        }
        return p;
    }, {} as Record<typeof incidents[number]['id'], ReadonlyRecursive<Incident>>);
    
    export function isIncidentId(id: unknown): id is keyof typeof incidentsLookup {
        return typeof id === 'string' && id in incidentsLookup;
    }
    
    ;


export const scripts = [

 ...[
  {
    "title": "Actually, I Am…",
    "creator": "translated by megakun",
    "set": [
      {
        "name": "Another Horizon",
        "number": 1
      }
    ],
    "tragedySet": "anotherHorizon",
    "mainPlot": [
      "shadowDemonKing"
    ],
    "subPlots": [
      "worldDollhouse",
      "threadsFate"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 3
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "boyStudent": "person",
      "richStudent": "person",
      "shrineMaiden": "person",
      "doctor": "person",
      "classRep": "person",
      "alien": "person",
      "popIdol": "person",
      "journalist": "gossiper",
      "henchman": "twilight"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "confession",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "confession",
        "culprit": "popIdol"
      },
      {
        "day": 3,
        "incident": "confession",
        "culprit": "alien"
      },
      {
        "day": 4,
        "incident": "confession",
        "culprit": "classRep"
      },
      {
        "day": 5,
        "incident": "confession",
        "culprit": "shrineMaiden"
      }
    ],
    "victory-conditions": "",
    "mastermindHints": "",
    "story": "This is the story of somthig eles"
  },
  {
    "title": "I‘m A Normal Human",
    "creator": "translated by megakun",
    "set": [
      {
        "name": "Another Horizon",
        "number": 2
      }
    ],
    "tragedySet": "anotherHorizon",
    "mainPlot": [
      "parallelWorldWar"
    ],
    "subPlots": [
      "threadsFate",
      "worldDollhouse"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 3
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "girlStudent": "person",
      "shrineMaiden": "person",
      "informer": "person",
      "doctor": "person",
      "alien": "person",
      "godlyBeing": [
        "agent",
        {
          "enters on loop": 2
        }
      ],
      "popIdol": "person",
      "boss": [
        "gossiper",
        {
          "Turf": "Hospital"
        }
      ]
    },
    "incidents": [
      {
        "day": 1,
        "incident": "confession",
        "culprit": "alien"
      },
      {
        "day": 2,
        "incident": "worldConvergence",
        "culprit": "godlyBeing"
      },
      {
        "day": 3,
        "incident": "confession",
        "culprit": "girlStudent"
      },
      {
        "day": 4,
        "incident": "confession",
        "culprit": "popIdol"
      },
      {
        "day": 5,
        "incident": "confession",
        "culprit": "informer"
      },
      {
        "day": 6,
        "incident": "confession",
        "culprit": "doctor"
      }
    ],
    "victory-conditions": "",
    "mastermindHints": "",
    "story": ""
  },
  {
    "title": "Packed Tragedy",
    "creator": "translated by megakun",
    "set": [
      {
        "name": "Another Horizon",
        "number": 3
      }
    ],
    "tragedySet": "anotherHorizon",
    "mainPlot": [
      "lostHeart"
    ],
    "subPlots": [
      "egoWave",
      "moonsideCity"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 7
      }
    ],
    "daysPerLoop": 2,
    "cast": {
      "boyStudent": "gossiper",
      "girlStudent": "agitator",
      "richStudent": "neurotic",
      "policeOfficer": "agent",
      "doctor": "person",
      "patient": "brain",
      "journalist": "person",
      "henchman": [
        "animus",
        {
          "world": "abnormal"
        }
      ],
      "illusion": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "smallForce",
        "culprit": "policeOfficer"
      },
      {
        "day": 2,
        "incident": "hospitalIncident",
        "culprit": "richStudent"
      }
    ],
    "victory-conditions": "",
    "mastermindHints": "",
    "story": ""
  },
  {
    "title": "I Hope This Reaches You, The Only One in the World",
    "creator": "Bakafire",
    "set": [
      {
        "name": "Another Horizon",
        "number": 4
      }
    ],
    "tragedySet": "anotherHorizon",
    "mainPlot": [
      "shadowDemonKing"
    ],
    "subPlots": [
      "egoWave",
      "moonsideCity"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 5
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "boyStudent": "person",
      "richStudent": "gossiper",
      "officeWorker": "person",
      "patient": "neurotic",
      "mysteryBoy": "agent",
      "popIdol": "twilight",
      "journalist": "person",
      "scientist": "agitator",
      "illusion": [
        "animus",
        {
          "world": "normal"
        }
      ]
    },
    "incidents": [
      {
        "day": 1,
        "incident": "worldEnd",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "worldConvergence",
        "culprit": "mysteryBoy"
      },
      {
        "day": 3,
        "incident": "brainwashing",
        "culprit": "patient"
      },
      {
        "day": 4,
        "incident": "assassination",
        "culprit": "journalist"
      },
      {
        "day": 5,
        "incident": "driftingToAnotherWorld",
        "culprit": "illusion"
      },
      {
        "day": 6,
        "incident": "missingPerson",
        "culprit": "officeWorker"
      }
    ],
    "victory-conditions": "",
    "mastermindHints": "",
    "story": ""
  },
  {
    "title": "Fictional Collapse",
    "creator": "translated by megakun",
    "set": [
      {
        "name": "Another Horizon",
        "number": 5
      }
    ],
    "tragedySet": "anotherHorizon",
    "mainPlot": [
      "parallelWorldWar"
    ],
    "subPlots": [
      "fanaticSacrifices",
      "egoWave"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 5
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "boyStudent": "person",
      "richStudent": "person",
      "shrineMaiden": "person",
      "officeWorker": "person",
      "informer": "agitator",
      "doctor": [
        "animus",
        {
          "world": "abnormal"
        }
      ],
      "classRep": "person",
      "godlyBeing": [
        "agent",
        {
          "enters on loop": 3
        }
      ],
      "popIdol": "fanatic",
      "illusion": [
        "animus",
        {
          "world": "normal"
        }
      ]
    },
    "incidents": [
      {
        "day": 3,
        "incident": "worldConvergence",
        "culprit": "doctor"
      },
      {
        "day": 4,
        "incident": "worldConvergence",
        "culprit": "godlyBeing"
      },
      {
        "day": 6,
        "incident": "worldEnd",
        "culprit": "illusion"
      }
    ],
    "victory-conditions": "",
    "mastermindHints": "",
    "story": ""
  },
  {
    "title": "Beyond Praying Stage",
    "creator": "translated by andrewshen123",
    "set": [
      {
        "name": "Another Horizon",
        "number": 5
      }
    ],
    "tragedySet": "anotherHorizon",
    "mainPlot": [
      "parallelWorldWar"
    ],
    "subPlots": [
      "fanaticSacrifices",
      "egoWave"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 5
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "boyStudent": "person",
      "richStudent": "person",
      "shrineMaiden": "person",
      "officeWorker": "person",
      "informer": "agitator",
      "doctor": [
        "animus",
        {
          "world": "abnormal"
        }
      ],
      "classRep": "person",
      "godlyBeing": [
        "agent",
        {
          "enters on loop": 3
        }
      ],
      "popIdol": "agitator",
      "illusion": [
        "animus",
        {
          "world": "normal"
        }
      ]
    },
    "incidents": [
      {
        "day": 3,
        "incident": "worldConvergence",
        "culprit": "doctor"
      },
      {
        "day": 4,
        "incident": "worldConvergence",
        "culprit": "godlyBeing"
      },
      {
        "day": 6,
        "incident": "worldEnd",
        "culprit": "illusion"
      }
    ],
    "victory-conditions": "",
    "mastermindHints": "",
    "story": ""
  },
  {
    "title": "Unknown",
    "creator": "Hallycon translated by megakun",
    "tragedySet": "anotherHorizon",
    "mainPlot": [
      "shadowDemonKing"
    ],
    "subPlots": [
      "fanaticSacrifices",
      "egoWave"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 4
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "doctor": "person",
      "patient": "person",
      "policeOfficer": "person",
      "officeWorker": "fanatic",
      "informer": [
        "animus",
        {
          "world": "normal"
        }
      ],
      "girlStudent": "person",
      "richStudent": "agitator",
      "shrineMaiden": "twilight",
      "classRep": [
        "animus",
        {
          "world": "abnormal"
        }
      ]
    },
    "incidents": [
      {
        "day": 3,
        "incident": "assassination",
        "culprit": "classRep"
      },
      {
        "day": 4,
        "incident": "worldEnd",
        "culprit": "patient"
      },
      {
        "day": 5,
        "incident": "smallForce",
        "culprit": "richStudent"
      },
      {
        "day": 6,
        "incident": "insaneMurder",
        "culprit": "policeOfficer"
      }
    ],
    "victory-conditions": "",
    "mastermindHints": "",
    "story": ""
  }
],
 ...[
  {
    "title": "The First Script",
    "creator": "BakaFire",
    "set": [
      {
        "name": "Tragedy Looper",
        "number": 1
      }
    ],
    "tragedySet": "firstSteps",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 1
      }
    ],
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "shadowRipper"
    ],
    "cast": {
      "boyStudent": "person",
      "girlStudent": "keyPerson",
      "shrineMaiden": "serialKiller",
      "policeOfficer": "conspiracyTheorist",
      "officeWorker": "killer",
      "doctor": "brain"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "murder",
        "culprit": "officeWorker"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "girlStudent"
      }
    ],
    "victory-conditions": "See Tragedy Looper Mastermind Handbook",
    "story": "See Tragedy Looper Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper Mastermind Handbook"
  },
  {
    "title": "The First Script (NT)",
    "creator": "BakaFire",
    "set": [
      {
        "name": "New Tragedies",
        "number": 1
      }
    ],
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 1
      }
    ],
    "tragedySet": "firstSteps",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "shadowRipper"
    ],
    "daysPerLoop": 3,
    "cast": {
      "girlStudent": "keyPerson",
      "officeWorker": "killer",
      "doctor": "brain",
      "popIdol": "conspiracyTheorist",
      "shrineMaiden": "serialKiller",
      "boyStudent": "person"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "girlStudent"
      }
    ],
    "description": "This script is designed to be used directly after having explained the game, and everyone, both Mastermind and Protagonists, plays Tragedy Looper for the first time. So, you can play it fairly relaxed and focus on getting everything right. As it’s a practice script, it’s not very difficult. Don’t worry about winning or losing.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Killing the :keyPerson: (:girlStudent:) \n1. At any day end, have :shrineMaiden: and :girlStudent: alone in a location, triggering the :serialKiller:’s effect to kill the :girlStudent:, triggering the :keyPerson:’s loss condition immediately. \n2. At any day end, have 2 or more :intrigue: on the :girlStudent: in the same location as the :officeWorker:, triggering the :murder:er’s :keyPerson: kill effect, and thus the :keyPerson:’s loss condition immediately. \n3. At end of day 3, have 3 or more :paranoia: on the :girlStudent:, triggering her :suicide:, and thus triggering the :keyPerson:’s loss condition immediately. \n\nKilling the Protagonists \n• At any day end, have 4 or more :intrigue: on the Offce Worker, triggering the :murder:er’s Protagonist kill effect.",
    "story": "Welcome to the First Script. We’ll start with a simple tragedy to try out what you have learned. \n\nA girl has learned a secret. And to silence her, some organization has sent out an assassin to silence her. Her best friend, a :popIdol:, isn’t much help, due to being a bit paranoid. To make matters worse, there’s also a serial killer in town. Do you think the heroes will be able to defend the :youngGirl:?",
    "mastermindHints": "Aim to kill the :girlStudent:. The possible ways to kill her are listed in the “Victory Conditions” below. Just playing a card on the :girlStudent: will have people wonder what card it is, so play a card on her every single day. \n\nIn the first loop, the Protagonists have no idea of what’s happening. It’s recommended to just move the :girlStudent: to the :shrineMaiden: (:serialKiller:). \n\nFrom the second loop and on, the Protagonists will be wary of the :serialKiller:. Use that to get the :girlStudent: to commit suicide. Use the :conspiracyTheorist:’s ability to place :paranoia:, and the card :paranoia: +1, to get her to 3 :paranoia: by the third day. \n\nIn the third loop, you’ll have to aim for every possible way to kill her. You can place 2 :intrigue: on her to get the :officeWorker: to kill her or put 4 :intrigue: on the :officeWorker: to kill the Protagonists. (Remember, you can’t move the :officeWorker: to the School as he is not allowed there.) Finally, use the :brain:’s power when you can.\n\nHelping the Protagonists\n\nIf the Protagonists seem to struggle as to what to do, tell them this: “Winning the first loop is next to impossible. What you want to do is gather information. This is best done by playing :goodwill: +1 or :goodwill: +2 on some characters to use their abilities.” Also, to use the Reference sheet to figure out what occurred and possibly how it occurred.\n\nA PLAY GUIDE FOR “THE FIRST SCRIPT” \n\nAs this is the very first script, we give you suggestions on how to play out the script. Try it out the first time you play the script. \n\n(1) First loop, Day 1: Card playing step\nThe very first round, play the following cards: \n• :girlStudent:: Vertical Movement \n• :officeWorker:: :intrigue: +1 \n• :doctor:: :paranoia: +1 \nAfter the cards resolve, if the result is that the :girlStudent: is alone with the :shrineMaiden:, you’ve won, go to Bad Ending 1. If not, go to (2) \n\n(2) Day 1 to 3: Mastermind ability step\nIf the :popIdol: and the :girlStudent: are in the same location, place an :paranoia: token on the :girlStudent: (using the Idol’s :conspiracyTheorist: ability). If the :doctor: and the :officeWorker: or the :girlStudent: are in the same area, place one :intrigue: on the :officeWorker: or :girlStudent: (using the :doctor:’s :brain: ability) \n• Check the Bad Ending list; if any are fulfilled, you’ve won. But if it’s the third day, and nothing has happened, the Protagonists have won. Otherwise, go to (3).\n\n(3) Day 2 or 3: Card playing phase\nBelow you will find several tactics depending on the situation. They are in priority order. Also, if you can combine 2 tactics, do so. \n• Tactic 1: Kill the :girlStudent: with the :serialKiller: (if the :serialKiller: is divulged, the players will hamper this): Move the :shrineMaiden: and the :girlStudent: so that they end up together. You can use this tactic even if the :serialKiller: is divulged if you have a card play to spare or if it’s the only way out. \n• Tactic 2: :suicide: the :girlStudent: (she must have at least 1 :paranoia: if it’s day 2, and at least 2 if it’s day 3): Put an :paranoia: +1 on the :girlStudent:, move the :popIdol: to the :girlStudent:, and put Forbid :goodwill: on the :boyStudent:. \n• Tactic 3: Kill the Protagonists with the :murder:er (:officeWorker: must have at least 1 :intrigue:, hopefully 2, if it’s day 3): Put :intrigue: +1 or +2 on the :officeWorker: and move the :doctor: to where the :officeWorker: is. \n• Tactic 4: Kill the :girlStudent: with the :murder:er: Put :intrigue: +1, :intrigue: +2 on the :girlStudent:, or move her to the :officeWorker:. Move the :officeWorker: to where the :girlStudent: is (remember, he cannot go to the School!). Move the :doctor: to the :girlStudent:’s location. \nIf either tactic is fulfilled, no need to do anything in (2) and go to the Bad Ending in question. If neither is fulfilled, go to (2).  \n\n(4) Second loop, Day 1: Card playing phase\nIf the :serialKiller: hasn’t been divulged, go to (1). \nIf the :serialKiller: has been divulged, do the following: \n• Play :paranoia: +1 on the :girlStudent:, Horizontal Movement on the Idol, Vertical Movement on the :doctor:. Then go to (2). \n\n(5) Third loop, Day 1: Card playing phase \nTry to fight it out your way in loop 3. If you are at a loss as to what to do, check (3). \n\nBad Ending 1: Meeting at night. \nCondition: If the :shrineMaiden: and the :girlStudent: are alone in one location \nDon’t do anything during the Mastermind ability step. Then, when the day ends, the :girlStudent: dies (turn her sideways) and tell the Protagonists she’s dead, they’ve lost, and the loop ends. \n\nBad Ending 2: So, you were the ones snooping around! \nCondition: There are 4 :intrigue: counters on the :officeWorker: \nIf it’s already a sure thing, you don’t need to do anything during the Mastermind ability step. If you don’t have enough :intrigue:, but can use the :brain:’s ability, then do so. Then, at the end of the day, tell the Protagonists that they are dead, they’ve lost, and the loop ends. \n\nBad Ending 3: Who was the assassin? \nCondition: If the :girlStudent: has 2 :intrigue: on her and is in the same location as the :officeWorker: \nDon’t do anything during the Mastermind ability step if she already has enough :intrigue:, however, if she’s with the :doctor:, do place an :intrigue: on her to get the :officeWorker: to kill her at day end. Tell the Protagonists she’s dead, they’ve lost, and the loop ends. \n\nBad Ending 4: Couldn’t stand the strain. \nCondition: If the :girlStudent: has 3 or more :paranoia: on day 3 \nIf it’s already a sure thing, don’t use any Mastermind ability to put another :paranoia: on her. If you need the :conspiracyTheorist: to put the third :paranoia: on her, do so. Then, during the Incident step, she dies. Tell the Protagonists that she’s dead, they’ve lost, and the loop ends. \n\nAfter the ending, if this was the first loop, reset the board and go to (4). \nIf this was the second loop, reset the board and go to (5) \nIf this was the third loop, you’ve won!"
  },
  {
    "title": "In the Godless Temple",
    "creator": "Kaijin",
    "set": [
      {
        "name": "New Tragedies",
        "number": 2
      }
    ],
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 1
      }
    ],
    "tragedySet": "firstSteps",
    "mainPlot": [
      "lightAvenger"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "daysPerLoop": 4,
    "cast": {
      "alien": "brain",
      "policeOfficer": "conspiracyTheorist",
      "officeWorker": "curmudgeon",
      "patient": "friend",
      "shrineMaiden": "person",
      "popIdol": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "officeWorker"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "shrineMaiden"
      }
    ],
    "specialRules": [
      ""
    ],
    "description": "In the Godless Temple is an advanced training script, to be played after the First Script. This is a perfect script to learn how to use :intrigue: counters. After The First Script and this, you’ll get the hang of most victory tactics for the Mastermind.",
    "story": "A decrepit shrine at the outskirts of town. Whatever is hallowed there, it’s not a god. And it wants revenge. Revenge on the :shrineMaiden: that summoned it and consequently all humans. \n\nThis being confuses people and pushes the ill will of the broken :shrineMaiden: and less-than-good-tempered people onto the Protagonists. Will the heroes be able to stop this incarnation of darkness and evil from getting its revenge?",
    "victory-conditions": "1. At loop end, have 2 :intrigue: on the Shrine, triggering the loss condition of :lightAvenger:.\n\n2. At end of day 3, have at least 1 :intrigue: on the Hospital and at least 2 :paranoia: on the shrineMaiden, triggering the :hospitalIncident:, killing the :patient:, and triggering the :friend:’s loss condition at loop end.\n\n3. At end of day 3, have at least 2 :intrigue: on the Hospital and at least 2 :paranoia: on the :shrineMaiden:, triggering the :hospitalIncident:, killing the Protagonists.",
    "mastermindHints": "The basic way to win is to have 2 :intrigue: on the Shrine at the end of the loop. It’s good to also have 2 :intrigue: on the City, so that you can hide who the :brain: is. When you use the :brain:’s ability, see to it that the :officeWorker: and the :alien: are in the same area.\n\nAnother way to win is the :hospitalIncident:, while having 1 or more :intrigue: on the Hospital, killing the :patient: who is the :friend:, triggering a win condition for you.\n\nIn either case, you should get the :officeWorker: to trigger :missingPerson: to help place :intrigue:. Use the conspiracyTheorist: ability from Day 1. But if you think you can win with 2 :intrigue: on the Shrine, don’t aim for the :hospitalIncident:. It’s best to hide who the culprit is there, and to place a lot of :paranoia: on them once you need to.\n\nA PLAY GUIDE FOR “IN THE GODLESS TEMPLE”\n\nWe present a briefer play-through guide for this script.\n\nDay 1: Card playing phase\n\nOn loop 1, play these cards:\n\n• City: :intrigue: +2\n\n• Shrine: :intrigue: +1\n\n• Hospital: Any card (mainly to bluff)\n\nOn loop 2, play any of the below:\n\n• City or Shrine: :intrigue: +1, :intrigue: +2, or any other card\n\n• Hospital: :intrigue: +1 or any other card\n\n• :officeWorker: :paranoia: +1\n\n• :shrineMaiden: Vertical Movement\n\nDay 1-4: Mastermind ability step\n\n• If it’s day 1 or 2, and the :officeWorker: and :policeOfficer: are in the same area, place an :paranoia: token on the :officeWorker:.\n\n• If it’s day 1, 2 or 3, and the :shrineMaiden: and :policeOfficer: are in the same area, and you have 1 or more :intrigue: on the Hospital, put an :paranoia: on the :shrineMaiden:.\n\n• If the :alien: and :policeOfficer: are together at the City or Shrine, and you have 1 or less :intrigue: on that location, place one :intrigue: there.\n\n• If the :alien: is in the Shrine, and you really need to, place 1 :intrigue: there.\n\nDay 2-4: Card playing phase\n\nPlay cards or move, as needed:\n\n• City or Shrine: :intrigue: +1, :intrigue: +2 or any other card.\n\n• Hospital: :intrigue: +1 or any other card.\n\n• :shrineMaiden: :paranoia: +1, Forbid :goodwill:, or move her away from the Shrine.\n\n• :alien: Move it to Shrine or City.\n\n• :policeOfficer: Move to same location as :shrineMaiden: or :officeWorker:.\n\n• :officeWorker: :paranoia: +1 or move to same location as :alien:.\n\n• :popIdol: Forbid :goodwill: or move her so she can’t use her :goodwill: ability.\n\n• :patient: Don’t place anything on him."
  },
  {
    "title": "Prevailing Secrecy",
    "creator": "BakaFire",
    "set": [
      {
        "name": "Tragedy Looper",
        "number": 2
      }
    ],
    "tragedySet": "firstSteps",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 1
      },
      {
        "numberOfLoops": 3,
        "difficulty": 3
      }
    ],
    "mainPlot": [
      "placeProtect"
    ],
    "subPlots": [
      "unsettlingRumor"
    ],
    "cast": {
      "boyStudent": "cultist",
      "girlStudent": "person",
      "shrineMaiden": "conspiracyTheorist",
      "officeWorker": "person",
      "doctor": "keyPerson",
      "patient": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "shrineMaiden"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "boyStudent"
      },
      {
        "day": 5,
        "incident": "farawayMurder",
        "culprit": "patient"
      }
    ],
    "victory-conditions": "See Tragedy Looper Mastermind Handbook",
    "story": "See Tragedy Looper Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper Mastermind Handbook"
  },
  {
    "title": "Young Women’s Battlefield",
    "creator": "Gayusu",
    "set": [
      {
        "name": "Tragedy Looper",
        "number": 3
      }
    ],
    "tragedySet": "basicTragedy",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 2
      },
      {
        "numberOfLoops": 3,
        "difficulty": 4
      }
    ],
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "loveAffair",
      "hiddenFreak"
    ],
    "cast": {
      "boyStudent": "person",
      "girlStudent": "friend",
      "classRep": "lovedOne",
      "shrineMaiden": "keyPerson",
      "policeOfficer": "person",
      "officeWorker": "lover",
      "informer": "serialKiller",
      "patient": "person",
      "nurse": "person"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "foulEvil",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "classRep"
      },
      {
        "day": 6,
        "incident": "suicide",
        "culprit": "girlStudent"
      }
    ],
    "victory-conditions": "See Tragedy Looper Mastermind Handbook",
    "story": "See Tragedy Looper Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper Mastermind Handbook"
  },
  {
    "title": "Magical Girls' Superiority",
    "creator": "M.Hydrome",
    "set": [
      {
        "name": "New Tragedies",
        "number": 3
      }
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 2
      },
      {
        "numberOfLoops": 3,
        "difficulty": 3
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "unsettlingRumor",
      "hiddenFreak"
    ],
    "daysPerLoop": 5,
    "cast": {
      "popIdol": "keyPerson",
      "doctor": "conspiracyTheorist",
      "boyStudent": "serialKiller",
      "patient": "person",
      "teacher": "person",
      "richStudent": "person",
      "shrineMaiden": "friend"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "patient"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "shrineMaiden"
      },
      {
        "day": 5,
        "incident": "missingPerson",
        "culprit": "boyStudent"
      }
    ],
    "specialRules": [
      ""
    ],
    "victory-conditions": "This is a script recommended for those who are playing :basicTragedy: for the first time. This is where it begins for real. Now you have 2 Subplots and seven people in the cast. Also, there are 5 days each loop!\n\nThat said, the difficulty is still set to very low. Protagonists who come from :firstSteps: won’t feel super confused and will be able to enjoy the extra additions well enough. It’s not difficult to play as Mastermind either. But beware of the Final Guess. If all roles are clear, then you will lose, and that is more stressful than it may seem.",
    "story": "For boys and girls, the world is a constant battlefield. Each tiny little disturbance will shake their inexperienced souls. Especially in this town, where seductors lure with the promise of magic powers – without explaining the consequences!\n\nThe school was bubbling with talk. “Spells” and “magic”, and everyone knows that you CAN become a magical girl, if you just sign the contract. Eventually, the :popIdol: will sign, and she will die. Can the Protagonists stop this tragedy from happening?",
    "mastermindHints": "The best way to win is through the Hospital :horror: and to place :intrigue: on the Idol. This reveals the least information.\n\nStart day 1 and 2 with :paranoia: +1 on the :patient:, and a bluff and :intrigue: +1 on the Shrine and Hospital respectively.\n\nAlso from day 1, use the :conspiracyTheorist: and trigger :spreading: :paranoia:. If you get some :intrigue: on the Hospital, put 2 :paranoia: on the :shrineMaiden:, and trigger the Hospital :horror:.\n\nOn day 3, you can use the Unsettling Rumor effect to put the second :intrigue: on the Hospital and kill the Protagonists.\n\nIf you can’t seem to get any :intrigue: on the Hospital, put instead 2 :intrigue: on the Idol and if possible, also on the Shrine, :richStudent:, and shrineMaiden. For this, :spreading: :paranoia: and :unsettlingRumor: will help you.\n\nHide the :serialKiller: as long as you can and use him to kill the :keyPerson: as your last resort. Killing the :friend: will make the Final Guess easy for the Protagonists, so avoid that as much as possible.\n\nThe Final Guess can easily become your downfall. Either you hide the :conspiracyTheorist: or the :friend:. Try to make it look like a :murder:er or a :brain: instead.\n\nVictory conditions\n1. At any day end, have the popIdol and the :boyStudent: alone in a location, triggering the :serialKiller: effect and thus the :keyPerson:’s loss condition immediately.\n\n2. At end of day 3, have at least 1 :intrigue: on the Hospital, at least 2 :paranoia: on the shrineMaiden, and the popIdol in the Hospital, triggering Hospital :horror:, killing the popIdol and triggering the :keyPerson:’s loss condition immediately.\n\n3. At loop end, have 2 :intrigue: on the popIdol, triggering the loss condition for “Sign with Me!”.\n\n4. At any day end, have the shrineMaiden and the :boyStudent: alone in a location, triggering the :serialKiller: effect and thus the :friend:’s loss condition at loop end.\n\n5. At end of day 3, have at least 1 :intrigue: on the Hospital, at least 2 :paranoia: on the shrineMaiden who should be in the Hospital, triggering the Hospital :horror:, killing the shrineMaiden, and triggering the :friend:’s loss condition at loop end.\n\n6. At the end of day 3, have at least 2 :intrigue: on the Hospital, at least 2 :paranoia: on the shrineMaiden, triggering the Hospital :horror: killing the Protagonists."
  },
  {
    "title": "The Cat Box",
    "creator": "GEnd",
    "set": [
      {
        "name": "New Tragedies",
        "number": 4
      }
    ],
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 5
      },
      {
        "numberOfLoops": 4,
        "difficulty": 3
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "giantTimeBomb"
    ],
    "subPlots": [
      "loveAffair",
      "unknownFactor"
    ],
    "daysPerLoop": 5,
    "cast": {
      "nurse": "witch",
      "alien": "lover",
      "officeWorker": "lovedOne",
      "blackCat": "factor",
      "richStudent": "person",
      "popIdol": "person",
      "soldier": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "missingPerson",
        "culprit": "officeWorker"
      },
      {
        "day": 5,
        "incident": "missingPerson",
        "culprit": "alien"
      }
    ],
    "description": "The Cat Box is a perfect script to play as your second :basicTragedy: script. The complexity is slightly higher, and there are now 8 characters in the cast. The idea of this script is to introduce two new things. \n\nThe first is the :mysteryBoy: and the :blackCat:; both a bit strange. It’s a good chance to get to know rather complicated characters. But don’t fret; neither of them will mess up things too badly. \n\nThe second is the trick to find the :witch:, who has mandatory :goodwill: Refusel. This will have the Protagonists learn how to use this technique.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1. At loop end, have 2 :intrigue: on the Hospital, triggering the loss condition of “:giantTimeBomb:.”\n\n2. At any day end, have 3 or more :paranoia: and 1 or more :intrigue: on the :officeWorker:, triggering the :lovedOne:'’s Protagonist kill.",
    "story": "The witch laughs and the cat familiar meows. A mistake in a ritual has summoned a strange existence from another world. And to make matters worse, it fell in love with a human. \n\nThe witch decides to burn the entire town to hide the evidence. But to do that, she needs to stay hidden. Time is of the essence: kill the witch and shut the lid on this box forever.",
    "mastermindHints": "You can win with either 2 :intrigue: on the Hospital or the ability of the :lovedOne:. Your first choice should be the former, but the latter hides more information. \n\nFirst day on loop 1 should be an :intrigue: +2 on the School, an :intrigue: +1 on the Hospital, and a bluff card on the Shrine. Then put an :paranoia: on the :richStudent: using the :conspiracyTheorist: and kill her with :suicide:. This is to give the impression that she might be the :witch:! \n\nAfter that, try triggering both :missingPerson: Incidents by putting out :paranoia:. Use the :conspiracyTheorist:’s and :factor:’s abilities (Subplot “Unknown :factor:”: Putting 2 :intrigue: on the School makes the :blackCat: able to act as a :conspiracyTheorist:!) when you need. Putting :paranoia: on the :officeWorker: is effective both for :missingPerson: and for the :lovedOne: ability. \n\nFinally, if you can get 2 :intrigue: on the School, Shrine, and Hospital, the first loop is perfected. You can try to get that result in future loops too, but it’ll be hard with Forbid :intrigue: and trying to prevent the Protagonists from finding the :witch: with :goodwill: Refusel. \n\nIt’s super easy to hide the :factor:, so you don’t need to worry about the Final Guess."
  },
  {
    "title": "Crushed by the Hospital Building in Doronoki",
    "creator": "ロキルス",
    "set": [
      {
        "name": "New Tragedies",
        "number": 6
      }
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 4
      },
      {
        "numberOfLoops": 4,
        "difficulty": 5
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "paranoiaVirus",
      "unknownFactor"
    ],
    "daysPerLoop": 6,
    "cast": {
      "informer": "keyPerson",
      "classRep": "killer",
      "boss": [
        "brain",
        {
          "Turf": "Hospital"
        }
      ],
      "transferStudent": [
        "conspiracyTheorist",
        {
          "enters on day": 5
        }
      ],
      "boyStudent": "factor",
      "shrineMaiden": "person",
      "alien": "person",
      "officeWorker": "person",
      "henchman": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "hospitalIncident",
        "culprit": "henchman"
      },
      {
        "day": 5,
        "incident": "farawayMurder",
        "culprit": "transferStudent"
      }
    ],
    "description": "The special additions in this script are the presence of the powerful :boss: character and the capricious :transferStudent:. Have fun with the slightly higher complexity and the rather hard-to-use-well characters.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Note: Due to Subplot, :paranoiaVirus:, the :shrineMaiden:, :alien:, :officeWorker:, and :henchman: become :serialKiller:s while they have 3 or more :paranoia: on them. \n\n1. At any day end, have the Informant alone in a location with any of the :serialKiller:s, activating that ability, and triggering the loss condition for the :keyPerson: immediately. \n\n2. At any day end, have at least 2 :intrigue: on the Informant in the same location as the :classRep:, activating the :murder:er’s ability and thus triggering the :keyPerson:’s loss condition immediately. \n\n3. At the end of day 4, have the Informant in the Hospital, at least 1 :intrigue: on the Hospital, and at least 1 :paranoia: on the :henchman:, triggering the Hospital :horror:, killing the Informant, and triggering the loss condition of the :keyPerson: immediately. \n\n4. At the end of day 6, have at least 2 :paranoia: on the :transferStudent:, triggering the :farawayMurder:, and at least 2 :intrigue: on the Informant, choosing her to be the target, triggering the loss condition for the :keyPerson: immediately. \n\n5. Having at least 2 :intrigue: on the City, causing the :factor: (:boyStudent:) to gain the :keyPerson:’s effect, and applying any of (1) to (4) on the :factor: instead of the Informant. \n\n6. At the end of day 2, having at least 2 :intrigue: on the City, causing the :factor: to gain the :keyPerson:’s effect, and having at least 2 :paranoia: on the :boyStudent:, triggering :suicide:, and triggering the loss condition of the :keyPerson: immediately. \n\n7. At the end of day 4, have at least 2 :intrigue: on the Hospital and at least 1 :paranoia: on the :henchman:, triggering the Hospital :horror:’s Protagonist kill. \n\n8. At any day end, have at least 4 :intrigue: on the :classRep:, triggering the :murder:er’s Protagonist kill.",
    "story": "The hospital was empty, a ruin. Shut down by a combination of unfortunate events and political decisions. Forgotten, and abandoned. \n\nBut it harbored a secret. Here, in this very building, they had researched a new type of virus. One woman who worked as an informant had gotten ahold of this information, and the former CEO of the hospital, the :boss:, decided to silence her once and for all. Dedicated to his task, the :boss: plans to send his :henchman: to eliminate the evidence by destroying the building entirely, together with the boy who had contracted the virus. \n\nBut, one day a girl shows up, transferred back to the school in town. A girl with ties to the hospital and a burning hatred, accelerating the looming tragedy. \n\nCan the players outmaneuver the :henchman: and the vengeful girl?",
    "mastermindHints": "The :henchman: should start in the Hospital in every loop. \n\nThe first thing to aim for is a :factor: kill. First loop, :intrigue: +2 :intrigue: on the School, :intrigue: +1 on the City and a bluff on the Hospital. If the :boss: is in the City, use him to place the second :intrigue: on the City there, and :suicide: the :factor:. Use the same cards in Loop 2 but interchange them. \n\nIf that gets stopped, then you should aim for a :hospitalIorror:. The :boss: can spread :intrigue: here and there. The :goodwill: effects of the :henchman: and :alien: will get in your way. Spread out :paranoia: everywhere to make it hard to pinpoint the culprit. \n\nIf that also gets stopped, then it’s the :transferStudent:. With the :conspiracyTheorist:’s ability, you will for sure be able to activate :farawayMurder:, so have 2 :intrigue: on the :informant: (and :boyStudent:). \n\nWith the :paranoiaVirus:, you might activate a lot of :serialKiller:s:, but beware so you don’t get trapped in that. You can use that to get a win if you need. \n\nFor the Final Guess, you need to hide the :murder:er. Don’t use the :murder:er’s ability unless you can safely do so. "
  },
  {
    "title": "Those with Antibodies (NT)",
    "creator": "Satoru Sawamura",
    "set": [
      {
        "name": "New Tragedies",
        "number": 7
      }
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 4
      },
      {
        "numberOfLoops": 4,
        "difficulty": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "paranoiaVirus",
      "threadsFate"
    ],
    "daysPerLoop": 4,
    "cast": {
      "shrineMaiden": "cultist",
      "informer": "timeTraveler",
      "richStudent": "conspiracyTheorist",
      "classRep": "person",
      "officeWorker": "person",
      "forensicSpecialist": "person",
      "doctor": "person",
      "soldier": "person",
      "henchman": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "butterflyEffect",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "foulEvil",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "spreading",
        "culprit": "doctor"
      },
      {
        "day": 4,
        "incident": "missingPerson",
        "culprit": "forensicSpecialist"
      }
    ],
    "description": "In each loop of this script, the Mastermind has a fool-proof way to win. That means that the Protagonists have no other choice than to find out what is happening and aim for the Final Guess. It’s a puzzle to solve. But not a regular, decent puzzle. And it’s a question of whether the Protagonists can notice this and find the answer.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "* Note: Due to Subplot, :paranoiaVirus:, the :classRep:, :officeWorker:, :forensicSpecialist:, :doctor:, :soldier:, and :henchman: become :serialKiller:s while they have 3 or more :paranoia: on them. \n\n1. At the end of day 1, have at least 1 :paranoia: on the :richStudent:, triggering the :butterflyEffect:, thus triggering the loss condition in “Changing the Future” at loop end. \n\n2. End the final day with 2 or less :goodwill: tokens on the Informant, triggering the loss condition of the :timeTraveler:.",
    "story": "Cascading viruses—this is the future that we have no way to avoid. An insignificant start, small as the fluuttering of a butterfly’s wing, soon weaves its threads into a spiral, gradually turning it into an unavoidable maelstrom. The Protagonists cannot escape. They cannot change the future. Whatever plan of change they may have, it’s fruitless. Pointless. Completely in vain. \n\nThey must accept the future to live on and grasp the possibility of escaping the virus. Yes. That is the answer. They must find Those with Antibodies.",
    "mastermindHints": "The :henchman: should always start at the School.\n\nUnless you mess up badly, you should be able to win in every loop. In loop 1, put :paranoia: on :richStudent: and the :henchman:, and Forbid :goodwill: on the :forensicSpecialist:. Activate the :butterflyEffect:, if necessary, by using the :conspiracyTheorist:’s ability, and place a :goodwill: token on the :richStudent:. In this way, you’ll be able to trigger the :butterflyEffect: in all subsequent loops. \n\nAfter that, place :intrigue: tokens on the Shrine by the :foulEvil: effect, and delay the discovery of the Main Plot by, for example, placing :intrigue: on a girl. It’s recommended to use Forbid :goodwill: somewhere every single round. \n\nOne winning strategy for the Protagonists will be to try to use the effect from :paranoiaVirus:, that :person:s change into :serialKiller:s with enough :paranoia: counters, and by this method, discover which characters don’t change and which don’t die. :threadsFate: can also be used to pile up the :paranoia: tokens. So, once the Protagonists catch this one, make their lives miserable with :paranoia: -1 and Forbid :paranoia:."
  },
  {
    "title": "The Assassin from the Future",
    "creator": "unun",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 3
      },
      {
        "name": "New Tragedies",
        "number": 5
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "hiddenFreak",
      "circleFriends"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 3
      },
      {
        "numberOfLoops": 4,
        "difficulty": 5
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "girlStudent": "person",
      "classRep": "friend",
      "shrineMaiden": "friend",
      "alien": "person",
      "informer": "conspiracyTheorist",
      "popIdol": "cultist",
      "journalist": "person",
      "doctor": "serialKiller",
      "patient": "timeTraveler"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "butterflyEffect",
        "culprit": "classRep"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "doctor"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "informer"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "Un Rerum",
    "creator": "GaRSoBaG",
    "set": [
      {
        "name": "New Tragedies",
        "number": 8
      }
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "circleFriends",
      "threadsFate"
    ],
    "daysPerLoop": 6,
    "cast": {
      "ai": "brain",
      "richStudent": "cultist",
      "alien": "friend",
      "officeWorker": "friend",
      "blackCat": "conspiracyTheorist",
      "mysteryBoy": "lover",
      "classRep": "person",
      "forensicSpecialist": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "blackCat"
      },
      {
        "day": 2,
        "incident": "foulEvil",
        "culprit": "ai"
      },
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "mysteryBoy"
      },
      {
        "day": 4,
        "incident": "spreading",
        "culprit": "officeWorker"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "classRep"
      },
      {
        "day": 6,
        "incident": "murder",
        "culprit": "alien"
      }
    ],
    "description": "“un rerum”’s specialty is its usage of :threadsFate:. Horribly enough, if you fail in loop 3, you won’t be able to win the last loop. Also, the :ai: appears, and you’ll need to use that one’s abilities.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1. At loop end, have 2 :intrigue: on the Shrine, triggering the loss condition for “:sealedItem:”. \n\n2. At the end of day 6, have the :officeWorker: in the same location as the :alien:, and at least 2 :paranoia: on the :alien:, triggering Homicide, and thus triggering the loss condition of :friend: at loop end.",
    "story": "In a corner of the city, a lot of electronics have started blinking. One advanced computer, said to be able to change the world, has become self-aware. And it tries to replace reality with supernaturality, truly changing the world. The last hope IS the Shrine; that area has not yet fallen victim to this new order. If that one falls, then the entire city is lost",
    "mastermindHints": "Thanks to the :blackCat:, you can win by placing that needed :intrigue: on the Shrine. \n\nOn day 1, move the :richStudent: vertically, play :intrigue: +1 on the Shrine, and :intrigue: +2 on the City. If the Shrine’s :intrigue: is blocked, then you’ll have to use the :cultist:’s power to unblock it. After that, place :paranoia: tokens to trigger Transfer :friend:ship and :butterflyEffect:. \n\nIn loops 2 and 3, you’ll probably have :paranoia: on the :ai: and the :mysteryBoy:. If you trigger :foulEvil: or :missingPerson:, you’ll win. Specifically, in loop 2, the Protagonists will probably place :goodwill: tokens on the :mysteryBoy:, so you’ll mostly win by :missingPerson: in loop 3. \n\nIt’s also important to place :goodwill: on the :ai: and :mysteryBoy: with Transfer :friend:ship and :butterflyEffect:. If you start a loop with :paranoia: on both, place :intrigue: +2 on the :ai:, :intrigue: +1 on the Shrine, and :paranoia: +1 on the :mysteryBoy:, and you’ll have it. \n\nIf you avoid using :goodwill: Refusel on the :ai:, it’ll take time for the Protagonists to realize she’s the :brain:. Use the :goodwill: Refusel with wisdom. \n\nFor the Final Guess, hide either the :friend: or :conspiracyTheorist:."
  },
  {
    "title": "Lesser of Two Evils",
    "creator": "GEnd",
    "set": [
      {
        "name": "Tragedy Looper",
        "number": 4
      }
    ],
    "tragedySet": "basicTragedy",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 3
      },
      {
        "numberOfLoops": 3,
        "difficulty": 4
      }
    ],
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "hiddenFreak",
      "unknownFactor"
    ],
    "cast": {
      "boyStudent": "person",
      "girlStudent": "person",
      "richStudent": "brain",
      "shrineMaiden": "friend",
      "officeWorker": "serialKiller",
      "informer": "person",
      "journalist": "factor",
      "patient": "person",
      "nurse": "cultist"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "missingPerson",
        "culprit": "nurse"
      },
      {
        "day": 5,
        "incident": "missingPerson",
        "culprit": "boyStudent"
      },
      {
        "day": 7,
        "incident": "suicide",
        "culprit": "journalist"
      }
    ],
    "victory-conditions": "See Tragedy Looper Mastermind Handbook",
    "story": "See Tragedy Looper Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper Mastermind Handbook"
  },
  {
    "title": "The Secret That Was Kept",
    "creator": "BakaFire",
    "set": [
      {
        "name": "Tragedy Looper",
        "number": 5
      }
    ],
    "tragedySet": "basicTragedy",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 3
      },
      {
        "numberOfLoops": 3,
        "difficulty": 5
      }
    ],
    "mainPlot": [
      "giantTimeBomb"
    ],
    "subPlots": [
      "threadsFate",
      "circleFriends"
    ],
    "cast": {
      "richStudent": "witch",
      "classRep": "person",
      "shrineMaiden": "person",
      "alien": "friend",
      "officeWorker": "friend",
      "informer": "conspiracyTheorist",
      "popIdol": "person",
      "journalist": "person",
      "patient": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "hospitalIncident",
        "culprit": "journalist"
      },
      {
        "day": 6,
        "incident": "spreading",
        "culprit": "shrineMaiden"
      },
      {
        "day": 7,
        "incident": "foulEvil",
        "culprit": "popIdol"
      }
    ],
    "victory-conditions": "See Tragedy Looper Mastermind Handbook",
    "story": "See Tragedy Looper Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper Mastermind Handbook"
  },
  {
    "title": "The Future of the Gods",
    "creator": "Nightly Moonfire group",
    "set": [
      {
        "name": "Tragedy Looper",
        "number": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      }
    ],
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "hiddenFreak",
      "loveAffair"
    ],
    "cast": {
      "boyStudent": "timeTraveler",
      "richStudent": "person",
      "shrineMaiden": "cultist",
      "godlyBeing": [
        "lovedOne",
        {
          "enters on loop": 3
        }
      ],
      "policeOfficer": "person",
      "officeWorker": "serialKiller",
      "popIdol": "lover",
      "patient": "person",
      "nurse": "friend"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "popIdol"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "shrineMaiden"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "policeOfficer"
      },
      {
        "day": 7,
        "incident": "foulEvil",
        "culprit": "patient"
      }
    ],
    "victory-conditions": "See Tragedy Looper Mastermind Handbook",
    "story": "See Tragedy Looper Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper Mastermind Handbook"
  },
  {
    "title": "Mirror Passcode",
    "creator": "M. Hydromel",
    "set": [
      {
        "name": "Tragedy Looper",
        "number": 7
      }
    ],
    "tragedySet": "basicTragedy",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      },
      {
        "numberOfLoops": 3,
        "difficulty": 6
      }
    ],
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "unknownFactor",
      "paranoiaVirus"
    ],
    "cast": {
      "boyStudent": "person",
      "girlStudent": "keyPerson",
      "richStudent": "factor",
      "mysteryBoy": "cultist",
      "officeWorker": "person",
      "informer": "conspiracyTheorist",
      "journalist": "person",
      "patient": "person",
      "nurse": "person"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "journalist"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "mysteryBoy"
      },
      {
        "day": 7,
        "incident": "murder",
        "culprit": "boyStudent"
      }
    ],
    "victory-conditions": "See Tragedy Looper Mastermind Handbook",
    "story": "See Tragedy Looper Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper Mastermind Handbook"
  },
  {
    "title": "Those with Antibodies",
    "creator": "Satoshi Sawamura",
    "set": [
      {
        "name": "Tragedy Looper",
        "number": 8
      }
    ],
    "tragedySet": "basicTragedy",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 5
      },
      {
        "numberOfLoops": 4,
        "difficulty": 6
      }
    ],
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "threadsFate",
      "paranoiaVirus"
    ],
    "cast": {
      "girlStudent": "person",
      "richStudent": "conspiracyTheorist",
      "classRep": "person",
      "shrineMaiden": "cultist",
      "policeOfficer": "person",
      "officeWorker": "person",
      "informer": "conspiracyTheorist",
      "doctor": "person",
      "patient": "person",
      "henchman": "timeTraveler"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "butterflyEffect",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "foulEvil",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "spreading",
        "culprit": "doctor"
      },
      {
        "day": 4,
        "incident": "missingPerson",
        "culprit": "policeOfficer"
      }
    ],
    "victory-conditions": "See Tragedy Looper Mastermind Handbook",
    "story": "See Tragedy Looper Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper Mastermind Handbook"
  },
  {
    "title": "Prologue",
    "creator": "BakaFire",
    "set": [
      {
        "name": "Tragedy Looper",
        "number": 9
      },
      {
        "name": "New Tragedies",
        "number": 9
      }
    ],
    "tragedySet": "basicTragedy",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 5
      },
      {
        "numberOfLoops": 4,
        "difficulty": 7
      }
    ],
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "circleFriends",
      "loveAffair"
    ],
    "cast": {
      "boyStudent": "lover",
      "girlStudent": "lovedOne",
      "richStudent": "killer",
      "shrineMaiden": "keyPerson",
      "policeOfficer": "conspiracyTheorist",
      "officeWorker": "person",
      "informer": "friend",
      "doctor": "brain",
      "patient": "friend"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "girlStudent"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "boyStudent"
      },
      {
        "day": 7,
        "incident": "murder",
        "culprit": "policeOfficer"
      }
    ],
    "victory-conditions": "See Tragedy Looper Mastermind Handbook",
    "story": "See Tragedy Looper Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper Mastermind Handbook"
  },
  {
    "title": "Neverending Happy & Sad Story",
    "creator": "BF + 3G",
    "set": [
      {
        "name": "Tragedy Looper",
        "number": 10
      },
      {
        "name": "New Tragedies",
        "number": 10
      }
    ],
    "tragedySet": "basicTragedy",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 8
      }
    ],
    "mainPlot": [
      "giantTimeBomb"
    ],
    "subPlots": [
      "unsettlingRumor",
      "loveAffair"
    ],
    "cast": {
      "girlStudent": "person",
      "richStudent": "lovedOne",
      "classRep": "person",
      "mysteryBoy": "brain",
      "alien": "person",
      "godlyBeing": [
        "witch",
        {
          "enters on loop": 4
        }
      ],
      "officeWorker": "person",
      "popIdol": "person",
      "boss": [
        "conspiracyTheorist",
        {
          "Turf": "School"
        }
      ],
      "patient": "lover",
      "nurse": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "butterflyEffect",
        "culprit": "classRep"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "alien"
      },
      {
        "day": 4,
        "incident": "missingPerson",
        "culprit": "officeWorker"
      },
      {
        "day": 5,
        "incident": "missingPerson",
        "culprit": "nurse"
      },
      {
        "day": 6,
        "incident": "missingPerson",
        "culprit": "patient"
      }
    ],
    "specialRules": [
      "Mastermind removes \"Forbid :goodwill:\" from his hand. It cannot be used in any loop"
    ],
    "victory-conditions": "See Tragedy Looper Mastermind Handbook",
    "story": "See Tragedy Looper Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper Mastermind Handbook"
  },
  {
    "title": "The Illusion Under the Cherry Tree",
    "creator": "BakaFire",
    "set": [
      {
        "name": "New Tragedies",
        "number": 11
      }
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "unsettlingRumor",
      "hiddenFreak"
    ],
    "daysPerLoop": 7,
    "cast": {
      "doctor": "brain",
      "boyStudent": "cultist",
      "godlyBeing": [
        "conspiracyTheorist",
        {
          "enters on loop": 3
        }
      ],
      "richStudent": "serialKiller",
      "illusion": "friend",
      "shrineMaiden": "person",
      "officeWorker": "person",
      "informer": "person",
      "scientist": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "shrineMaiden"
      },
      {
        "day": 4,
        "incident": "butterflyEffect",
        "culprit": "scientist"
      },
      {
        "day": 5,
        "incident": "missingPerson",
        "culprit": "illusion"
      },
      {
        "day": 7,
        "incident": "farawayMurder",
        "culprit": "richStudent"
      }
    ],
    "description": "This script is for players who are used to Tragedy Looper. The Protagonists should have at least 2 or 3 games under their belt. It circles around the uncanny abilities of the :illusion:. Enjoy the weirdness that changes the very point of many of the Action cards.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1. At loop end, have 2 :intrigue: on the Shrine, triggering the loss condition for “:sealedItem:”. \n\n2. At any day end, have the :richStudent: alone with the :illusion:, triggering the :serialKiller:’s ability and thus triggering the loss condition of the :friend: at loop end. \n\n3. At the end of day 7, have at least 1 :paranoia: on the :richStudent:, triggering :farawayMurder:, and at least 2 :intrigue: on the :illusion:, selecting her as a target, triggering the loss condition of the :friend: at loop end.",
    "story": "She has materialized in this world to protect the Seal at the Shrine. And by chance, she becomes friends to the Protagonists. The enemies are many: A fanatic who has lost his mind, am artistic killer, and an amnesiac god. The Protagonists must fight with her to protect the Seal, helped by her strange abilities. But the :illusion: will fade in the end. It will fade. :hope:fully, it will not do so in vain. And beautifully, under the Cherry Tree.",
    "mastermindHints": "You’ll aim for :intrigue: at the Shrine by using several methods, such as :unsettlingRumor:, :missingPerson:, and Action cards, as well as a killing of the :illusion: by the :serialKiller: or :farawayMurder:. It’s easiest if you don’t put all your eggs in one basket though. \n\nAs the :illusion: is there, the setting of Action cards on the board is much more powerful. For example, assume that you’ve set :intrigue: +1 on the Shrine and :paranoia: +1 on the School. If the :illusion: is in the School, you’ll get 1 :paranoia: on her, which looks good, as well as an unsettling :intrigue: on the Shrine. Try to use this as a way of confusing the Protagonists as much as you can. Bluntly put, you can put an Action card on the board where the :illusion: is, every single day. \n\nWinning by :farawayMurder: is a safe bet. The Protagonists will probably pump :goodwill: on the :illusion: to remove her from the board. You can’t completely stop that with Forbid :goodwill: but try to mess with that as much as you can. \n\nFor the Final Guess, hide the :conspiracyTheorist: or the :brain:. Once the :godlyBeing: enters in loop 3, it’s easy to hide either of them. "
  },
  {
    "title": "A Little Friend",
    "creator": "BakaFire+R",
    "set": [
      {
        "name": "New Tragedies",
        "number": 12
      },
      {
        "name": "Script Collection 2",
        "number": 1
      }
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      },
      {
        "numberOfLoops": 5,
        "difficulty": 3
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "hiddenFreak",
      "paranoiaVirus"
    ],
    "daysPerLoop": 5,
    "cast": {
      "godlyBeing": [
        "cultist",
        {
          "enters on loop": 3
        }
      ],
      "journalist": "timeTraveler",
      "classRep": "serialKiller",
      "youngGirl": "friend",
      "richStudent": "conspiracyTheorist",
      "blackCat": "person",
      "alien": "person",
      "informer": "person",
      "forensicSpecialist": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "youngGirl"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "informer"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "classRep"
      }
    ],
    "description": "This is a script for players who have played at least once. It’s made to make full use of the abilities of the :youngGirl: and is a good script to get to know her usefulness.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "* Note: Due to Subplot, :paranoiaVirus:, the :alien:, :blackCat:, Informant, and :forensicSpecialist: become :serialKiller:s while they have 3 or more :paranoia: on them. \n\n1. At the end of day 5, have at least 2 :paranoia: on the :classRep:, triggering the :butterflyEffect:, thus triggering the loss condition of “Changing the Future” at loop end. \n\n2. At loop end, have 2 or less :goodwill: on the :journalist:, triggering the loss condition of the :timeTraveler: at loop end. \n\n3. At any day end, have the :youngGirl: alone with the :classRep:, triggering the :serialKiller:’s ability, thus triggering the loss condition of the :friend: at loop end. \n\n4. At any day end, have at least 3 :paranoia: on :alien:, :blackCat:, Informant, or :forensicSpecialist:, triggering the effect of :paranoiaVirus:, turning them into :serialKiller:s, and alone with the :youngGirl:, triggering the loss condition of the :friend: at loop end.",
    "story": "There was a serial killer who threatened to kill a girl. A visitor from the future comes back to create killers to stop the first killer from succeeding. Things got out of control. \n\nThe Protagonists must reveal the visitor’s identity and persuade him to stop. And they will need the power of the young girl. She and only she can help them!",
    "mastermindHints": "The first loop, aim to win by killing the :friend: (:youngGirl:). Put a Horizontal Movement on the :richStudent:, and :paranoia: +1 on the :youngGirl: and :classRep:. Then put an :paranoia: on the :richStudent: using the :conspiracyTheorist:’s ability, trigger :spreading: :paranoia:, and put two :paranoia: on the :classRep: and an :intrigue: on the :alien:. This way, you can kill the :friend: all the while hiding a lot of other information. If you succeed, just keep hiding information for all remaining loops. It’s important to keep the :classRep:’s :paranoia: at 3 or more. If you fail, keep going in loop 2 as per below, and try to hide the Main Plot. \n\nFrom loop 2 and on, keep killing the :friend:. If her power is used on day 1, use that to put :intrigue: on the Shrine with :missingPerson:. Generally, it’s easy to win, but the :serialKiller:s are hard to contain, and things are hard to hide. Try as much as you can to hide the :journalist:’s role of :timeTraveler: and try to keep the Main Plot hidden. It’s easy to camouflage the Main Plot as anything but Premeditated :murder:. Put :intrigue: on the Shrine and on the girls, to keep things fuzzy. \n\nFor the Final Guess, keep either Main Plot, the :paranoiaVirus: Subplot, who the first :serialKiller: is, or who the :timeTraveler: and the :conspiracyTheorist: are, hidden."
  },
  {
    "title": "Fall-Sakura Gathering",
    "creator": "Kyokei",
    "set": [
      {
        "name": "New Tragedies",
        "number": 13
      },
      {
        "name": "Script Collection 2",
        "number": 2
      }
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 3
      },
      {
        "numberOfLoops": 5,
        "difficulty": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "loveAffair",
      "hiddenFreak"
    ],
    "daysPerLoop": 5,
    "cast": {
      "sacredTree": "keyPerson",
      "soldier": "killer",
      "policeOfficer": "brain",
      "doctor": "lover",
      "classRep": "lovedOne",
      "shrineMaiden": "serialKiller",
      "sectFounder": "friend",
      "copycat": "killer",
      "mysteryBoy": "conspiracyTheorist"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "classRep"
      },
      {
        "day": 4,
        "incident": "butterflyEffect",
        "culprit": "doctor"
      },
      {
        "day": 5,
        "incident": "increasingUnease",
        "culprit": "sectFounder"
      }
    ],
    "description": " This script is for players who have played at least once. It’s specifically designed to highlight the use three of the characters that originally came with the Second Script Collection: :copycat:, :sacredTree:, and :prophet:. ",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1. At any day end, let the :shrineMaiden: be alone with the :sacredTree:, or have at least 2 :intrigue: on the :sacredTree: and in the same location as the :soldier: or :copycat:. This kills the :sacredTree: and triggers the loss condition for the :keyPerson: immediately. \n\n2. At any day end, let the :shrineMaiden: be alone with the :prophet:, triggering the :serialKiller:’s ability, and thus the loss condition for :friend: at loop end. \n\n3. At any day end, have at least 1 :intrigue: and at least 3 :paranoia: on the :classRep: (:lovedOne:), or at least 4 :intrigue: on the :soldier: or :copycat: (:murder:ers), triggering a Protagonist kill.",
    "story": "A giant cherry tree in the middle of the shrine. It has been used to keep evil powers away for centuries and is said to bloom in full even in fall. Hence, the worshipping of this tree has a long history in this little town, and for generations, this local religion has been passed down since the founder. But that's far from the truth. Long ago, a warlord hid a large sum of money under the tree and died, and this religious lie has been kept alive to protect the treasure. And some individuals, who have found out this truth, are coming to town. They have fooled the shrine maiden and made her insane. They lured a young boy into town and pushed him down the path of evil. Their goal is to cut down the cherry tree and take the treasure for themselves. The Protagonists must find out their scheme and protect the secret. ",
    "mastermindHints": "In loop 1, put a Vertical Movement on the :prophet: and end everything straight away. The remaining two actions can be Forbid :goodwill: and Horizontal Movement on the :copycat:. If you by some reason should fail to end it there, put some :paranoia: on the :prophet: with the :conspiracyTheorist:’s ability. \n\nFrom loop 2 and on, start aiming for a :lovedOne: victory. However, for that, the :suicide: on day 2 is a crutch. The best is if :classRep: has exactly 1 :paranoia: on day 2. \n\nThe :spreading: :paranoia: of the :prophet: has extreme explosive power. If the :classRep: lives, put 1 :intrigue: and 1 :paranoia: on her, and you’ve basically won. Or you could put 2 :intrigue: on the :sacredTree: and kill it with the :murder:er. It should be easy if you use the :conspiracyTheorist:’s ability. \n\nIt looks as if it’s easy to win, but the roles are as easily revealed too, so take great care. If you put cards on the two :murder:ers, and total 4 :intrigue: on them, you can win on pure luck, but you’ll end up revealing both :murder:ers. If you put :intrigue: on the :sacredTree: outside of :spreading: :paranoia:, you won’t have time for much more, and it’s hard to win by just that. \n\nIt’s next to hopeless to hide much for the Final Guess. You need to stop the deaths of the :friend:, :lovedOne:, and :lover:, and try to desperately cloud who are the :murder:ers and :brain:. "
  },
  {
    "title": "Thunder in the City",
    "creator": "Redless",
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 0
      }
    ],
    "tragedySet": "firstSteps",
    "mainPlot": [
      "lightAvenger"
    ],
    "subPlots": [
      "unsettlingRumor"
    ],
    "daysPerLoop": 5,
    "cast": {
      "popIdol": "brain",
      "girlStudent": "conspiracyTheorist",
      "journalist": "person",
      "patient": "person",
      "shrineMaiden": "person",
      "boyStudent": "person",
      "richStudent": "person"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "murder",
        "culprit": "richStudent"
      },
      {
        "day": 5,
        "incident": "missingPerson",
        "culprit": "shrineMaiden"
      }
    ],
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "This script is in an awkward spot, since it requires a fair bit of skill to solve and because it's very long, yet it's a :firstSteps: script. the kind of people who would like this script would definitely be good at this game, and if that's the case, why play a :firststeps: script with them? maybe if some people haven't played it before and some have? anyways, this script is meant to introduce the players to deep bluffing and trickery, 'coverups' in other words. to do that, we have the :brain: and :increasingUnease: subplot to obfuscate who the :brain: is. loop 1, you should try to place an :intrigue: on the city, school, and hospital. I'd put the 2 on the city since it's most important and they're less likely to block it, and one on the hospital to make it easier to set the incident off (if you definitely don't want the incident, you can also do school). then, you want to set off the :hospitalIncident: by placing a :paranoia: AND using the contheorist's ability on the :boyStudent: on day 3, to surprise the protagonists, while placing :paranoia: on other people to bluff the culprits. alternately, if they blocked on hospital or something, try to get them to block there again for the free school :intrigue:, move the :journalist: or :popIdol: over (:journalist: probably better) and/or trigger :missingPerson: so that you can go for the :placeProtect: bluff. in future loops, try to confuse the protagonists about who and where the :brain: is, and who does the :hospitalIncident:. the presence of the :murder: incident makes it harder to bluff that RMD does the :hospitalIncident:, but makes it easier to bluff place to protect and ultimately a hideous script. slowly, more and more things will be revealed over the course of five loops. it's subtle, it's a marathon, and it requires GOOD play from the protagonists to have any chance at all, but I hope and think it does a suitable job introducing the more trickery focused side of looper."
  },
  {
    "title": "A Cruel Shrine Maiden's Thesis",
    "creator": "Redless",
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 0
      },
      {
        "numberOfLoops": 4,
        "difficulty": 0
      }
    ],
    "tragedySet": "firstSteps",
    "mainPlot": [
      "placeProtect"
    ],
    "subPlots": [
      "shadowRipper"
    ],
    "daysPerLoop": 4,
    "cast": {
      "nurse": "keyPerson",
      "popIdol": "cultist",
      "girlStudent": "conspiracyTheorist",
      "shrineMaiden": "serialKiller",
      "classRep": "person",
      "patient": "person",
      "officeWorker": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "missingPerson",
        "culprit": "nurse"
      }
    ],
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "This whole script revolves around :missingPerson:. It will let you drag the :nurse: into the maiden's path, and also let you stack some more :intrigue: on the school. It's your \"easy out\", everything else in the script lives in service of it. The first day you should aim to serial kill the nurse. LOL! Gotem. Another loop, drop :paranoia: on the OW, and move the :girlStudent: left. By doing this, you will be in a good spot to MP. If people don't die, stack the school and set to work adding :intrigue:. If people do die, try to position the :shrineMaiden: alone so that you can MP the nurse over. Another loop, try and stack people in the school and obfuscate who exactly the :cultist: is. Finally, try and win the final loop via :missingPerson: shenanigans, which is surprisingly hard to do. You might consider playing this instead of the first script, or in place of the second script if your players don't mind you crushing their spirits by doing the same BS strat that characterizes the first script, a second time in a row. I believe this is very balanced. But of course you have to contend with the repetition of :serialKiller: if you're playing this after the first script. "
  },
  {
    "title": "Trouble in Paradise",
    "creator": "chewonki",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 0
      },
      {
        "numberOfLoops": 5,
        "difficulty": 0
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "hiddenFreak",
      "loveAffair"
    ],
    "daysPerLoop": 4,
    "cast": {
      "richStudent": "keyPerson",
      "alien": "serialKiller",
      "godlyBeing": [
        "friend",
        {
          "enters on loop": 3
        }
      ],
      "henchman": "lover",
      "patient": "lovedOne",
      "mysteryBoy": "person",
      "classRep": "person",
      "doctor": "person",
      "popIdol": "person",
      "informer": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "foulEvil",
        "culprit": "patient"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "henchman"
      }
    ],
    "description": "",
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": ""
  },
  {
    "title": "Tough to Name",
    "creator": "Hal",
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 0
      },
      {
        "numberOfLoops": 4,
        "difficulty": 0
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "giantTimeBomb"
    ],
    "subPlots": [
      "loveAffair",
      "hiddenFreak"
    ],
    "daysPerLoop": 6,
    "cast": {
      "popIdol": "witch",
      "patient": "lover",
      "boyStudent": "lovedOne",
      "shrineMaiden": "serialKiller",
      "godlyBeing": [
        "friend",
        {
          "enters on loop": 2
        }
      ],
      "nurse": "person",
      "journalist": "person",
      "officeWorker": "person",
      "richStudent": "person",
      "classRep": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "journalist"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "shrineMaiden"
      },
      {
        "day": 6,
        "incident": "suicide",
        "culprit": "patient"
      }
    ],
    "description": "",
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "The witch might get discovered quite early - the :popIdol:'s goodwill ability is a tempting one, so her goodwill refusel will probably be outed in the first loop. Given this, Max (our mastermind) decided to try and win through intrigue on the City in day one, and spread about some intrigue and paranoia elsewhere so it wasn't too obvious.\n\nThe hospital incident is another way for the mastermind to win, especially coupled with increasing unease. But you don't have many ways of getting intrigue out, and no cultist to block you forbid intrigues. The only one you do have is :missingPerson:, and that's quite one use as the culprit reveals themselves. Once the :godlyBeing: is in play, the players could stack goodwill on them too to get the ability to remove intrigue, and to discover the culprit. So the hospital incident looks quite tough to pull off. On the plus side, the culprit is the serial killer, so she won't get offed. It did trigger with one intrigue in the hospital in loop 2 for us, which isn't great for the mastermind, as it might reveal the :patient: as the lover. It killed the patient and the boy student, which was a happy coincidence for Max.\n\nThe :shrineMaiden: serial killer is a good secret weapon for loop 2, if you can avoid her being discovered. The :godlyBeing: appears in the shrine in loop 2, so if the protagonists don't move the two apart, you can kill the friend and spend the rest of the loop misleading them.\n\nI'm not sure what the best role to try and hide is for the mastermind. My suspicion is that it's the lover/loved one. You have the option of pulling a win out of the bag on the last day using the suicide on the patient, but it looks like you might have trouble surviving the final guess if you did that. Maybe you can get away with hiding the :witch:, but you'd have to be lucky I think."
  },
  {
    "title": "Was it Just the Wind?",
    "creator": "Tuxian",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      },
      {
        "numberOfLoops": 3,
        "difficulty": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "threadsFate",
      "unsettlingRumor"
    ],
    "daysPerLoop": 7,
    "cast": {
      "illusion": "keyPerson",
      "alien": "killer",
      "journalist": "brain",
      "shrineMaiden": "conspiracyTheorist",
      "patient": "person",
      "doctor": "person",
      "popIdol": "person",
      "richStudent": "person",
      "classRep": "person",
      "henchman": "person",
      "mysteryBoy": "factor"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "butterflyEffect",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "shrineMaiden"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "henchman"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "alien"
      },
      {
        "day": 5,
        "incident": "farawayMurder",
        "culprit": "patient"
      },
      {
        "day": 6,
        "incident": "spreading",
        "culprit": "doctor"
      },
      {
        "day": 7,
        "incident": "suicide",
        "culprit": "mysteryBoy"
      }
    ],
    "description": "",
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "Loop 1, activate the :factor: as fast as possible, bluff Incidents to keep the Protagonists guessing and putting out fires. Kill :factor: with either :farawayMurder: (preferred) or :suicide:. Ignore the :illusion: to make them ignore it as well.\n\nLoop 2, attempt to kill the :illusion: using the :killer:. Gather several people together to hide who the :killer: might be.\n\nLoop 3, use :farawayMurder: to kill the :illusion: or activated :factor:, or :suicide: the :factor:.\n\nLoop 4, if playing, use whatever you need to.\n\nHide the :brain: by using the Unsettling Rumor, and hide the :killer: by having a crowd around the :illusion:. Remember the :illusion: can be used to bluff a different main plot because she gets tokens when you play a card on a Location, you can also use this to your advantage by bluffing something where she is and moving her to a location where she would get :intrigue:.\n\n[BGG Log 1](https://boardgamegeek.com/thread/1548338) and [BGG Log 2](https://boardgamegeek.com/thread/1550297) are two logs of this script played where the Mastermind wins, so you can study those if you’re trying to optimize "
  },
  {
    "title": "Tofu Murder Case",
    "creator": "じーちゃん",
    "source": "https://web.archive.org/web/20171025123847/http://g-chan.dip.jp/square/archives/2015/12/rooper_x_02.html",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 0
      }
    ],
    "tragedySet": "firstSteps",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "daysPerLoop": 5,
    "cast": {
      "popIdol": "keyPerson",
      "girlStudent": "killer",
      "policeOfficer": "brain",
      "richStudent": "conspiracyTheorist",
      "patient": "curmudgeon",
      "shrineMaiden": "friend",
      "boyStudent": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "shrineMaiden"
      },
      {
        "day": 3,
        "incident": "murder",
        "culprit": "richStudent"
      },
      {
        "day": 5,
        "incident": "increasingUnease",
        "culprit": "patient"
      }
    ],
    "description": "This :firstSteps: scenario was made for players who have never played Tragedy Looper before. However, it's not particularly easy in terms of difficulty. In fact, it's somewhat difficult. I think that even newcomers can enjoy it, but I would not recommend it to people who aren't good at logic puzzles, or if they're new to board games in general (well, in this case, they probably won't be playing Tragedy Looper right now to begin with). This scenario is intended to be played with table talk allowed, but you can forbid it if the players are experienced. Since a tofu-minded lady commits murders in this scenario, I titled it \"Tofu :murder: Case.\"\n\nThis scenario consists of four Loops, but the first three Loops will allow you to experienced the core element of Tragedy Looper—information gathering. The main focus of this is to learn how to deduce clues based on what's happening in the board, such as \\\"The board was like this when we died, so this must be the Plot.\\\"\n\nBefore entering the final Loop, the Mastermind will defeat the Protagonists while guiding them to reveal the Main Plot, Sub Plot, all Roles, and all Incident Culprits (Since :firstSteps: doesn't have a Final Guess, you won't lost just for revealing everything).\n\nThen, in the final Loop, the Protagonists will have a guessing game with the Mastermind based on the information that's been revealed. The aim is for the Protagonists to play in a way that avoids the Defeat Condition when they have all the information at hand.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1. Kill the :keyPerson: (:killer:, :murder:)\n2. Kill the :friend: (:suicide:, :murder:)\n3. Kill the Protagonists (:killer:)",
    "story": "",
    "mastermindHints": "First Loop\n\nThe goal is the won by putting 4 :intrigue: on the :killer:, which is the most difficult option. Since you can get 1 :intrigue: by triggering :increasingUnease: on the 5th Day, you only need to put 3 :intrigue: on the :girlStudent:.\n\nDay 1\n- Pop Idol: :intrigue:+1\n- Patient: :paranoia:+1\n- :girlStudent Student: :paranoia:+1 (this is a bluff, so you can use others if you want)\n\nPlace :paranoia:+1 on the :patient: every turn to trigger the :increasingUnease: Incident. The Protagonists have a total of 3 :paranoia:-1 cards, so you can reach the :paranoia: limit on the final Day if you put :paranoia:+1 out every turn. \n\nHowever, this time, there is a :popIdol:. Since there is a possibility of her removing :paranoia: via her mandatory, if she accumulates :goodwill: on her, you can counter it with repeated Forbid :goodwill:, preventing her from moving to the Hospital, or simply place a 4th :intrigue:. Additionally, placing it on :popIdol:, :girlStudent:, and :patient: is also a setup for the next Loop, so it's recommended to place it on these three Characters.\n\nDay 2\n- :girlStudent Student: :intrigue:+1\n- Patient: :paranoia:+1\n- Pop Idol: :paranoia:+1 (this is a bluff, so you can use others if you want)\n\nContinuing from the previous Day, place :paranoia: on the :patient:. Then place :intrigue:+1 on the :girlStudent:. This is the day when :suicide: occurs, but if the Mastermind does not place any :paranoia:, the :shrineMaiden: shouldn't hit her required limit.\n\nDay 3\n- :girlStudent Student: :intrigue:+2\n- Patient: :paranoia:+1\n- Pop Idol: Move to the same position as :richStudent:\n\nAt this point, it's important to place :intrigue:+2 while moving the :richStudent:. If :intrigue:+2 is blocked, your options for beating the Protagonists will be greatly narrowed (I think the only option then is to tput 2 :intrigue: on the :keyPerson: and kill him with the :killer:).\n\nAs insurance in case :intrigue:+2 is nullified, move :popIdol: (:keyPerson:) towards :richStudent: (:conspiracyTheorist:). By doing this, the Daughter, who is a :conspiracyTheorist:, will be abl to put a Paranoid counter on herself, trigger :murder:, kill the :keyPerson:, and defeat the Protagonists.\n\nIf :intrigue:+2 is successfully placed on the :girlStudent: (:killer:), don't use :conspiracyTheorist:'s mandatory ability and continue instead with \\\"No Incident occured.\\\"\n\nDay 4\n- :girlStudent Student: :intrigue:+1\n- Patient: :paranoia:+1\n- Pop Idol: :paranoia:+1 (this is a bluff, so you can use others if you want)\n\nWhen the :girlStudent: gets 4 :intrigue:, kill the Protagonists to end the Loop. However, the Protagonists will likely be wary of her since she has three :intrigue:, so they will likely place Forbid :intrigue:. It's a good idea to place :paranoia: so that the :popIdol: also reaches her Limit, so it makes it difficult to determine if the final Day's Culprit is the :popIdol: or the :patient:.\n\nDay 5\n- :girlStudent Student: :intrigue:+1\n- Patient: :paranoia:+1\n- Pop Idol: :paranoia:+1 (this is a bluff, so you can use others if you want)\n\nSame as Day 4. If everything goes according to plan, the :girlStudent: should have 3 :intrigue: on her. Now, trigger :increasingUnease: (which should be unstoppable) to put the 4th :intrigue: on the :killer:, and kill the Protagonists at the end of the turn to end the Loop.\n\n2nd Loop\n\nThe strategy for the 2nd Loop is to aim for a one-turn kill, or kill the :keyPerson: with the :killer: if you can't get a one-turn kill.\n\nDay 1\n- Pop Idol: :intrigue:+2\n- :girlStudent Student: Move to the City\n- Patient: :paranoia:+1\n\nLet's aim for a one-turn kill!\nSince you're placing cards on the same characters as in the previous Loop, and, according to the plot, the Protagonists lost the previous Loop due to the :killer:'s mandatory :intrigue: 4 ability, the Protagonists will probably place Forbid :intrigue: on the :girlStudent: (:killer:). Well, they may move the :popIdol: to avoid the risk of a one-turn kill, but if they, you can chase them down with the :killer: the next Day or later.\n\nDay 2 onward\n\nSo, did you get the one-turn kill? Well, you're reading this on Day 2, so probably not (lol). From hereon, use :policeOfficer: (:brain:)'s mandatory ability aggressively and aim to put :intrigue: on the :keyPerson: and :killer:.\n\nThere's no Final Guess in the First Step set, so there's no reason to be stingy with mandatory role abilities. It's also a good idea to aim to kill the :shrineMaiden: (:friend:), or :popIdol: (:keyPerson:) with the :richStudent: :murder: Incident on the 3rd Day.\n\n3rd Day\n\nFor the third Loop, try to kill the :shrineMaiden:.\n\nDay 1\n- Shrine Maiden: :paranoia:+1\n- :richStudent:: Move up or down\n- Pop Idol: :intrigue:+1\n\nIf the :richStudent: safely reaches the Shrine and is able to join the :shrineMaiden:, use her mandatory :conspiracyTheorist: ability to place :paranoia: on the :shrineMaiden:.\n\nDay 2\n- Shrine Maiden: :paranoia:+1\n- :boyStudent: Move left or right (avoid going to the Shrine)\n- Pop Idol: :intrigue:+1\n\nIf everything goes as planned up until now, the :shrineMaiden: (:friend:) will kill herself, so this Loop will end. If that doesn't work, try killing the :shrineMaiden: or :popIdol: on the third Day with :richStudent:'s :murder: Incident (if you miss this, too, you'll be in a lot of trouble lol).\n\n4th Loop\n\nIf you have been following the Hints, all Culprits and Roles should be revealed by now. Therefore, in the final 4th Loop, it'll be a battle of wits between the Mastermind and the Protagonists.\n\nGood luck!"
  },
  {
    "title": "I'm Dying to Cause a Tragedy!",
    "creator": "RooP",
    "source": "http://asuwa.mistysky.net/rooper/rooper.cgi",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 0
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "hiddenFreak",
      "threadsFate"
    ],
    "daysPerLoop": 5,
    "cast": {
      "doctor": "keyPerson",
      "informer": "killer",
      "officeWorker": "brain",
      "patient": "serialKiller",
      "richStudent": "friend",
      "boyStudent": "person",
      "girlStudent": "person",
      "shrineMaiden": "person",
      "policeOfficer": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "murder",
        "culprit": "shrineMaiden"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "doctor"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "officeWorker"
      }
    ],
    "description": "Doctor! I have no choice but to kill you!\nIf that ojou-sama has such a tofu mentality, she has no choice but to die!\nWho said that office worker is merely an ordinary person?!",
    "specialRules": [
      "Table talk is not allowed. There is a Final Guess."
    ],
    "victory-conditions": "- Killing the :keyPerson:—:murder:, :suicide:, :hospitalIncident:, :serialKiller:, and :killer:.\n- Kill the Protagaonists—:hospitalIncident:, :killer:\n- Kill the :friend:—:murder:, :hospitalIncident:, :serialKiller:",
    "story": "A man who lost his daughter to a medical error swore revenge. \n\nHe gives him a knife, hiding it in a condolence gift. \"You're dying to kill someone, aren't you? Now's your chance.\"\n\nHe hands the woman a heavy suitcase. \"You can get anything you want as long you have enough money, right? Information, even a life.\"\n\nHe offers the cursed sword to the shrine. \"I want it exorcised within two days. It's dangerous if it gets rushed? That's not my concern, just get it done.\"\n\nHe looks into his eyes that are tormented by guilt. \"If you really feel bad about what you've done, can't you just apologize by dying?\"\n\nHe whispers to the young lady, who's surrounded by her clique. \"Hey, I have an interesting story for you. Care to listen?\"\n\nFinally, he ends it with his own hands. \"Let's go out with a bang.\"",
    "mastermindHints": "For the first Loop, you'll focus on the three students at the School, and you'll be able to avoid the Hospital. If there's no particular movement, you should be good.\n\nFor the second Loop, aim to kill the :richStudent: with a :murder: or the :serialKiller:.\n\nFor the third Loop, you'll be forced to choose between working behind the scenes with the Hospital, or working behind the scenes with the :doctor:. Furthermore, the :doctor: can be forced to choose between :paranoia: and :intrigue: until the third Day. The rest is flexible. The roles you need to hide are the :killer: and the :brain:. Use your ability after gathering a large number of people first."
  },
  {
    "title": "Escort Mission",
    "creator": "davidchaeB",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 0
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "paranoiaVirus",
      "threadsFate"
    ],
    "daysPerLoop": 6,
    "cast": {
      "richStudent": "keyPerson",
      "popIdol": "killer",
      "boss": [
        "brain",
        {
          "Turf": "School"
        }
      ],
      "classRep": "conspiracyTheorist",
      "henchman": "person",
      "nurse": "person",
      "informer": "person",
      "mysteryBoy": "factor"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "murder",
        "culprit": "classRep"
      },
      {
        "day": 4,
        "incident": "butterflyEffect",
        "culprit": "mysteryBoy"
      },
      {
        "day": 5,
        "incident": "farawayMurder",
        "culprit": "nurse"
      },
      {
        "day": 6,
        "incident": "missingPerson",
        "culprit": "richStudent"
      }
    ],
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "You have very strong power play. They can ruin it with Threads into Virus."
  }
],
 ...[
  {
    "title": "Secret Cat Walk",
    "creator": "GEnd",
    "set": [
      {
        "name": "Cosmic Evil",
        "number": 1
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "paranoiaVirus",
      "unknownFactor"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 4
      },
      {
        "numberOfLoops": 4,
        "difficulty": 6
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "boyStudent": "person",
      "richStudent": "cultist",
      "teacher": "conspiracyTheorist",
      "shrineMaiden": "person",
      "blackCat": "factor",
      "officeWorker": "person",
      "informer": "brain",
      "patient": "person",
      "soldier": "person"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "hospitalIncident",
        "culprit": "shrineMaiden"
      },
      {
        "day": 6,
        "incident": "butterflyEffect",
        "culprit": "blackCat"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "story": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Cosmic Evil Mastermind Handbook"
  },
  {
    "title": "Master Thief Seven Tails",
    "creator": "BakaFire",
    "set": [
      {
        "name": "Cosmic Evil",
        "number": 2
      }
    ],
    "tragedySet": "primeEvil",
    "mainPlot": [
      "cursedLand"
    ],
    "subPlots": [
      "panicObsession",
      "keyGirl"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 3
      },
      {
        "numberOfLoops": 3,
        "difficulty": 5
      }
    ],
    "daysPerLoop": 4,
    "cast": {
      "boyStudent": "witch",
      "richStudent": "ghost",
      "classRep": "coward",
      "shrineMaiden": "keyPerson",
      "policeOfficer": "showOff",
      "officeWorker": "serialKiller"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "sacrilegiousMurder",
        "culprit": "classRep"
      },
      {
        "day": 4,
        "incident": "evangeliumDead",
        "culprit": "School"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "story": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Cosmic Evil Mastermind Handbook"
  },
  {
    "title": "Vamp",
    "creator": "ENTEI",
    "set": [
      {
        "name": "Cosmic Evil",
        "number": 3
      }
    ],
    "tragedySet": "primeEvil",
    "mainPlot": [
      "nobleBloodline"
    ],
    "subPlots": [
      "thoseHabits",
      "witchsCurse"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 5
      },
      {
        "numberOfLoops": 4,
        "difficulty": 6
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "boyStudent": "person",
      "girlStudent": "keyPerson",
      "classRep": "person",
      "shrineMaiden": "lovedOne",
      "alien": "conspiracyTheorist",
      "policeOfficer": "ghost",
      "officeWorker": "vampire",
      "popIdol": "witch",
      "patient": "serialKiller"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "fountainFilth",
        "culprit": "Hospital"
      },
      {
        "day": 4,
        "incident": "nightMadness",
        "culprit": "City"
      },
      {
        "day": 5,
        "incident": "sacrilegiousMurder",
        "culprit": "boyStudent"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "story": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Cosmic Evil Mastermind Handbook"
  },
  {
    "title": "Zombi Hero",
    "creator": "GEnd",
    "set": [
      {
        "name": "Cosmic Evil",
        "number": 4
      }
    ],
    "tragedySet": "primeEvil",
    "mainPlot": [
      "OnesFromGrave"
    ],
    "subPlots": [
      "loveAffair",
      "peopleDontListen"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 5
      },
      {
        "numberOfLoops": 4,
        "difficulty": 6
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "girlStudent": "person",
      "mysteryBoy": "zombie",
      "transferStudent": [
        "lover",
        {
          "enters on day": 5
        }
      ],
      "shrineMaiden": "person",
      "officeWorker": "person",
      "informer": "showOff",
      "journalist": "person",
      "patient": "lovedOne",
      "nurse": "conspiracyTheorist",
      "henchman": "coward"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "evilContamination",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "journalist"
      },
      {
        "day": 4,
        "incident": "nightMadness",
        "culprit": "Shrine"
      },
      {
        "day": 5,
        "incident": "sacrilegiousMurder",
        "culprit": "patient"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "story": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Cosmic Evil Mastermind Handbook"
  },
  {
    "title": "Ultimate Halloween",
    "creator": "BakaFire",
    "set": [
      {
        "name": "Cosmic Evil",
        "number": 5
      }
    ],
    "tragedySet": "primeEvil",
    "mainPlot": [
      "moonlightBeast"
    ],
    "subPlots": [
      "monsterIntrigue",
      "peopleDontListen"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 6
      },
      {
        "numberOfLoops": 4,
        "difficulty": 7
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "girlStudent": "person",
      "mysteryBoy": "nightmare",
      "teacher": "person",
      "transferStudent": [
        "coward",
        {
          "enters on day": 5
        }
      ],
      "alien": "person",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 2
        }
      ],
      "popIdol": "person",
      "forensicSpecialist": "conspiracyTheorist",
      "soldier": "showOff",
      "patient": "werewolf"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "awakenedCurse",
        "culprit": "School"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "popIdol"
      },
      {
        "day": 3,
        "incident": "executioner",
        "culprit": "girlStudent"
      },
      {
        "day": 4,
        "incident": "sacrilegiousMurder",
        "culprit": "soldier"
      },
      {
        "day": 5,
        "incident": "nightMadness",
        "culprit": "Hospital"
      },
      {
        "day": 6,
        "incident": "missingPerson",
        "culprit": "mysteryBoy"
      }
    ],
    "specialRules": [
      "Mastermind removes \"Forbid :goodwill:\" from his hand. It cannot be used in any loop."
    ],
    "victory-conditions": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "story": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Cosmic Evil Mastermind Handbook"
  },
  {
    "title": "Banquet School Life",
    "creator": "BakaFire",
    "set": [
      {
        "name": "Cosmic Evil",
        "number": 6
      }
    ],
    "tragedySet": "cosmicMythology",
    "mainPlot": [
      "giantTimeBombAgain"
    ],
    "subPlots": [
      "resistance",
      "unsettlingRumor"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 3
      },
      {
        "numberOfLoops": 3,
        "difficulty": 5
      }
    ],
    "daysPerLoop": 4,
    "cast": {
      "boyStudent": "conspiracyTheorist",
      "girlStudent": "witch",
      "richStudent": "serialKiller",
      "shrineMaiden": "person",
      "policeOfficer": "person",
      "officeWorker": "wizard",
      "doctor": "deepOne"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "evilContamination",
        "culprit": "shrineMaiden"
      },
      {
        "day": 4,
        "incident": "insaneMurder",
        "culprit": "boyStudent"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "story": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Cosmic Evil Mastermind Handbook"
  },
  {
    "title": "Eyes Without Vision",
    "creator": "Nightly Moonfire Group",
    "set": [
      {
        "name": "Cosmic Evil",
        "number": 7
      }
    ],
    "tragedySet": "cosmicMythology",
    "mainPlot": [
      "bloodyRites"
    ],
    "subPlots": [
      "peopleWhoSaw",
      "greatRace"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 5
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "girlStudent": "witness",
      "richStudent": "person",
      "classRep": "witch",
      "shrineMaiden": "person",
      "alien": "person",
      "officeWorker": "conspiracyTheorist",
      "informer": "timeTraveler",
      "doctor": "serialKiller",
      "patient": "immortalRole"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "doctor"
      },
      {
        "day": 3,
        "incident": "houndDogScent",
        "culprit": "alien"
      },
      {
        "day": 4,
        "incident": "executioner",
        "culprit": "girlStudent"
      },
      {
        "day": 5,
        "incident": "insaneMurder",
        "culprit": "officeWorker"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "story": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Cosmic Evil Mastermind Handbook"
  },
  {
    "title": "Bella Stellarum",
    "creator": "Gaijin",
    "set": [
      {
        "name": "Cosmic Evil",
        "number": 8
      }
    ],
    "tragedySet": "cosmicMythology",
    "mainPlot": [
      "kingYellow"
    ],
    "subPlots": [
      [
        "twistedTruth",
        {
          "Extra Plot": "bloodyRites"
        }
      ],
      "whispersFromDeep"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 4
      },
      {
        "numberOfLoops": 4,
        "difficulty": 6
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "classRep": "sacrifice",
      "mysteryBoy": "serialKiller",
      "godlyBeing": [
        "paranoiac",
        {
          "enters on loop": 2
        }
      ],
      "officeWorker": "cultist",
      "informer": "person",
      "journalist": "person",
      "popIdol": "person",
      "patient": "immortalRole",
      "nurse": "paranoiac"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "executioner",
        "culprit": "patient"
      },
      {
        "day": 2,
        "incident": "discovery",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "insaneMurder",
        "culprit": "classRep"
      },
      {
        "day": 5,
        "incident": "uproar",
        "culprit": "godlyBeing"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "story": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Cosmic Evil Mastermind Handbook"
  },
  {
    "title": "An Ordinary Day In Misuka City",
    "creator": "Rokirusu",
    "set": [
      {
        "name": "Cosmic Evil",
        "number": 9
      }
    ],
    "tragedySet": "cosmicMythology",
    "mainPlot": [
      "sacredWordsDagon"
    ],
    "subPlots": [
      "facelessGod",
      [
        "twistedTruth",
        {
          "Extra Plot": "giantTimeBombAgain"
        }
      ]
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 7
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "girlStudent": "person",
      "richStudent": "faceless",
      "mysteryBoy": "witch",
      "alien": "wizard",
      "officeWorker": "cultist",
      "informer": "deepOne",
      "journalist": "keyPerson",
      "patient": "paranoiac"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "insaneMurder",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "fireDemise",
        "culprit": "alien"
      },
      {
        "day": 4,
        "incident": "uproar",
        "culprit": "patient"
      },
      {
        "day": 6,
        "incident": "evilContamination",
        "culprit": "informer"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "story": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Cosmic Evil Mastermind Handbook"
  },
  {
    "title": "Cosmic Insignificance",
    "creator": "M. Hydromel and her automatons",
    "set": [
      {
        "name": "Cosmic Evil",
        "number": 10
      }
    ],
    "tragedySet": "cosmicMythology",
    "mainPlot": [
      "kingYellow"
    ],
    "subPlots": [
      "facelessGod",
      "resistance"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 8
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "classRep": "person",
      "mysteryBoy": "immortalRole",
      "alien": "person",
      "godlyBeing": [
        "sacrifice",
        {
          "enters on loop": 5
        }
      ],
      "illusion": "serialKiller",
      "blackCat": "cultist",
      "officeWorker": "person",
      "journalist": "wizard",
      "nurse": "faceless",
      "henchman": "conspiracyTheorist"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "discovery",
        "culprit": "henchman"
      },
      {
        "day": 2,
        "incident": "insaneMurder",
        "culprit": "blackCat"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "journalist"
      },
      {
        "day": 5,
        "incident": "evilContamination",
        "culprit": "classRep"
      },
      {
        "day": 6,
        "incident": "executioner",
        "culprit": "godlyBeing"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "story": "See Tragedy Looper: Cosmic Evil Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Cosmic Evil Mastermind Handbook"
  }
],
 ...[
  {
    "title": "孤独ノ隠レンボ (Lonely Hide and Seek)",
    "creator": "siro_xx",
    "set": [
      {
        "name": "Haunted Stage",
        "number": 1
      }
    ],
    "tragedySet": "hauntedStage",
    "mainPlot": [
      "cursedDoll"
    ],
    "subPlots": [
      "expandingUrbanLegend",
      "maxCrazyGauge"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 3
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "boyStudent": "spellcaster",
      "girlStudent": "person",
      "richStudent": "nightmareHaunted",
      "shrineMaiden": "humanDoll",
      "policeOfficer": "GhostHaunted",
      "officeWorker": "person",
      "informer": "poltergeist",
      "doctor": "horror",
      "patient": "GhostHaunted"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "curse",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "blasphemy",
        "culprit": "girlStudent"
      },
      {
        "day": 5,
        "incident": "infestation",
        "culprit": "shrineMaiden"
      }
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": ""
  },
  {
    "title": "ジブン観察日記 (Self-Observation Diary)",
    "creator": "siro_xx",
    "set": [
      {
        "name": "Haunted Stage",
        "number": 2
      }
    ],
    "tragedySet": "hauntedStage",
    "mainPlot": [
      "primeEvilPlot"
    ],
    "subPlots": [
      "zombieApocalypse",
      "deepHole"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 4
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "boyStudent": "spellcaster",
      "girlStudent": "horror",
      "richStudent": "person",
      "shrineMaiden": "monster",
      "policeOfficer": "person",
      "officeWorker": "person",
      "informer": "person",
      "doctor": "nightmareHaunted",
      "patient": "GhostHaunted"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "sacrificeHaunted",
        "culprit": "patient"
      },
      {
        "day": 4,
        "incident": "monsterLiberation",
        "culprit": "boyStudent"
      },
      {
        "day": 5,
        "incident": "hundredDemonsNight",
        "culprit": "officeWorker"
      }
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": ""
  },
  {
    "title": "ニセモノ注意報 (Imposter Advisory)",
    "creator": "siro_xx",
    "set": [
      {
        "name": "Haunted Stage",
        "number": 3
      }
    ],
    "tragedySet": "hauntedStage",
    "mainPlot": [
      "fullMoonNight"
    ],
    "subPlots": [
      "boundaryBetweenLifeDeath",
      "deepHole"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 4
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "boyStudent": "horror",
      "girlStudent": "person",
      "richStudent": "spellcaster",
      "shrineMaiden": "werewolfHaunted",
      "policeOfficer": "monster",
      "officeWorker": "nightmareHaunted",
      "informer": "overlord",
      "doctor": "monster",
      "patient": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "blasphemy",
        "culprit": "girlStudent"
      },
      {
        "day": 5,
        "incident": "serialMurder",
        "culprit": "boyStudent"
      }
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": ""
  },
  {
    "title": "完全犯罪ラブレター (Perfect Crime Love Letter)",
    "creator": "siro_xx",
    "set": [
      {
        "name": "Haunted Stage",
        "number": 4
      }
    ],
    "tragedySet": "hauntedStage",
    "mainPlot": [
      "fullMoonNight"
    ],
    "subPlots": [
      "zombieApocalypse",
      "deepHole"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 4
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "boyStudent": "nightmareHaunted",
      "girlStudent": "werewolfHaunted",
      "richStudent": "spellcaster",
      "shrineMaiden": "spellcaster",
      "policeOfficer": "person",
      "officeWorker": "person",
      "informer": "horror",
      "doctor": "person",
      "patient": "monster"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "sacrificeHaunted",
        "culprit": "boyStudent"
      },
      {
        "day": 2,
        "incident": "infestation",
        "culprit": "officeWorker"
      },
      {
        "day": 5,
        "incident": "repeatingNightmare",
        "culprit": "girlStudent"
      }
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": ""
  },
  {
    "title": "猿マネ椅子盗りゲーム (Aimless Imitation Musical Chairs)",
    "creator": "siro_xx",
    "set": [
      {
        "name": "Haunted Stage",
        "number": 5
      }
    ],
    "tragedySet": "hauntedStage",
    "mainPlot": [
      "primeEvilPlot"
    ],
    "subPlots": [
      "boundaryBetweenLifeDeath",
      "dancePartyDead"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 5
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "boyStudent": "horror",
      "girlStudent": "nightmareHaunted",
      "richStudent": "person",
      "shrineMaiden": "monster",
      "policeOfficer": "GhostHaunted",
      "officeWorker": "person",
      "informer": "person",
      "doctor": "GhostHaunted",
      "patient": "overlord"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "monsterLiberation",
        "culprit": "shrineMaiden"
      },
      {
        "day": 3,
        "incident": "blasphemy",
        "culprit": "girlStudent"
      },
      {
        "day": 4,
        "incident": "infestation",
        "culprit": "patient"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "boyStudent"
      }
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": ""
  },
  {
    "title": "代行天使 (Proxy Angel)",
    "creator": "Translated by megakun",
    "set": [
      {
        "name": "Haunted Stage",
        "number": 6
      }
    ],
    "tragedySet": "hauntedStage",
    "mainPlot": [
      "ancientShrine"
    ],
    "subPlots": [
      "maxCrazyGauge",
      "expandingUrbanLegend"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 7
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "boyStudent": "spellcaster",
      "richStudent": "person",
      "shrineMaiden": "person",
      "policeOfficer": "nightmareHaunted",
      "patient": "person",
      "popIdol": "horror",
      "journalist": "person",
      "henchman": "GhostHaunted",
      "scientist": "curseGod"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "sacrificeHaunted",
        "culprit": "shrineMaiden"
      },
      {
        "day": 2,
        "incident": "sacrificeHaunted",
        "culprit": "boyStudent"
      },
      {
        "day": 3,
        "incident": "sacrificeHaunted",
        "culprit": "popIdol"
      },
      {
        "day": 4,
        "incident": "sacrificeHaunted",
        "culprit": "richStudent"
      },
      {
        "day": 5,
        "incident": "sacrificeHaunted",
        "culprit": "henchman"
      }
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": ""
  },
  {
    "title": "蘇る幻影（マボロシ） (Reviving illusion)",
    "creator": "Translated by megakun",
    "set": [
      {
        "name": "Haunted Stage",
        "number": 7
      }
    ],
    "tragedySet": "hauntedStage",
    "mainPlot": [
      "primeEvilPlot"
    ],
    "subPlots": [
      "deepHole",
      "dancePartyDead"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 1
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "boyStudent": "person",
      "doctor": "person",
      "patient": "monster",
      "classRep": "person",
      "alien": "person",
      "popIdol": "GhostHaunted",
      "journalist": "horror",
      "boss": [
        "nightmareHaunted",
        {
          "Turf": "School"
        }
      ],
      "illusion": "GhostHaunted"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "journalist"
      },
      {
        "day": 4,
        "incident": "curse",
        "culprit": "boyStudent"
      },
      {
        "day": 5,
        "incident": "repeatingNightmare",
        "culprit": "illusion"
      },
      {
        "day": 6,
        "incident": "monsterLiberation",
        "culprit": "patient"
      }
    ],
    "specialRules": [
      "Mastermind remove \"Forbid :goodwill:\" from his hand. It cannot be used on any loop"
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": ""
  },
  {
    "title": "幻想は都市で死ぬ (illusions Die in the City)",
    "creator": "Translated by megakun",
    "set": [
      {
        "name": "Haunted Stage",
        "number": 8
      }
    ],
    "tragedySet": "hauntedStage",
    "mainPlot": [
      "fullMoonNight"
    ],
    "subPlots": [
      "deepHole",
      "maxCrazyGauge"
    ],
    "difficultySets": [
      {
        "difficulty": 1,
        "numberOfLoops": 4
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "boyStudent": "werewolfHaunted",
      "shrineMaiden": "person",
      "officeWorker": "GhostHaunted",
      "informer": "horror",
      "mysteryBoy": "curseGod",
      "popIdol": "nightmareHaunted",
      "journalist": "spellcaster",
      "scientist": "monster",
      "illusion": "person"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "curse",
        "culprit": "boyStudent"
      },
      {
        "day": 5,
        "incident": "repeatingNightmare",
        "culprit": "shrineMaiden"
      },
      {
        "day": 6,
        "incident": "blasphemy",
        "culprit": "scientist"
      }
    ],
    "specialRules": [
      "Mastermind remove \"Forbid :goodwill:\" from his hand. It cannot be used on any loop"
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": ""
  }
],
 ...[
  {
    "title": "The Demonic Password Game",
    "creator": "Mew2mi",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 8
      }
    ],
    "tragedySet": "lastLiar",
    "mainPlot": [
      "demonsScript"
    ],
    "subPlots": [
      "realMonster",
      "keeperMythology"
    ],
    "daysPerLoop": 7,
    "cast": {
      "ai": "watcher",
      "shrineMaiden": "serialKiller",
      "officeWorker": "secretkeeper",
      "youngGirl": "secretkeeper",
      "blackCat": "wildcard",
      "popIdol": "influencer",
      "doctor": "person",
      "richStudent": "person",
      "girlStudent": "person",
      "journalist": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "confession",
        "culprit": "youngGirl"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "journalist"
      },
      {
        "day": 3,
        "incident": "murkDespair",
        "culprit": "doctor"
      },
      {
        "day": 4,
        "incident": "spreading",
        "culprit": "officeWorker"
      },
      {
        "day": 5,
        "incident": "murder",
        "culprit": "girlStudent"
      },
      {
        "day": 7,
        "incident": "lastWill",
        "culprit": "blackCat"
      }
    ],
    "description": "This is a script where the Mastermind will always win at the end of the Loop, but how he wins each loop will be very important. That said, while this looks like an impossible script at a first glance, it is not, as all  3 Protagonists do not care about winning the end of the Loop, as none of their win conditions require them to get there.",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "-Secretkeepers dying/using their abilities on loops 1 and 2.\n-End of Loop always triggers from Last Will Black Cat.",
    "story": "This is a story you might find familiar, but the demons feeding on the chaos, hope and despair in this world would beg to differ. Taking the form of a common feline, it feeds on the Shrine Maiden's jealousy towards her very popular and influential sister, whispering in her ears those dear to the Pop Idol. With no heroes in sight and nobody to save this world, it is doomed and destroyed. But from the ruins, a third demon emerges. One that feeds on the cycle of hope and despair. Sensing that there is still hope somewhere in these ruins, it approaches what appears to be a dying detective, grasping desperately onto a laptop. A grin appears on its deformed face. \"4 attempts\", it says. \"I will let you experience this tragedy from its beginnings, 4 times. At the end, I will ask of you the password to this PC that you have found. Guess it correctly, and it will contain all the answers you need to save this world. Guess it wrong, and this PC will forever disappear from this reality\". Its grin extends, showing sharp teeth and all 8 of its eyes burst in red flames. What appears to be a hand extends from its shadowy body. \"Deal?\"",
    "mastermindHints": "Protagonist A will win by killing 5 Characters using the Serial Killer, Murder Incident and the Wildcard. Protagonist B will win by using 6 Goodwill abilities, which shouldn't be too easy, as everyone else will be working against this. Protagonist C's choices will impact the game the most. While they are not a Traitor, their gameplan is still the same as if they were, where they will be placing Paranoia around and trying to sabotage the rest.\nAs the Mastermind, for the first 2 loops, focus on Confession, don't place any cards on the Office Worker and try to end both the loops as fast as possible. If the Protagonists get the full 28 days, your odds of winning are very slim. The best way to accomplish that is to either kill off the Secretkeepers using murder/Serial Killer, or to goad Protagonist B into triggering their abilities by placing your cards very carefully each day. Where you place your cards is more important than which cards you place most of the time.\nOn Loop 3 and onwards, you need to fully focus on stopping protagonist A and B's win conditions. Since none of the Protagonists have a way to figure out who is traitor for sure and who isn't due to the plots combination, they will be fully sabotaging each other. If things have went badly, then you will win loop 3 at the end of the loop without 2 intrigue on the shrine and with 2+ different counters on the A.I, at which point Protagonist B will fully know that they are a Traitor, and if they haven't already, will fully focus on winning on the final loop. That is when it will be hardest for you to stop them, as you will need the help of both A and C. Thankfully, you do have 1 card up your sleeve - clever use of the AI. As both A and C should be interested in using it due to the listed incidents, this is your chance kill off some characters that have not used their abilities and if you succeed, all you have left to worry about is the A.I. meeting with the Serial Killer, or it using Confession/Last Will on itself and blocking off Protagonist A's win condition, which at this point should be either 3/5 or 4/5. If you succeed at both of these, you have 80% chance of victory, as the Watcher is very hard to find in this script, since its ability is not required for victory.\nDo note that with very clever use of the AI, protagonist C might be able to figure out all the roles easily, so be very careful with the AI, it's a double edged sword."
  }
],
 ...[
  {
    "title": "Machina Solar Cogwheel",
    "creator": "M.Hydromel",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 1
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "loveAffair",
      "circleFriends"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 4
      },
      {
        "numberOfLoops": 4,
        "difficulty": 5
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "richStudent": "conspiracyTheorist",
      "classRep": "person",
      "alien": "friend",
      "informer": "lovedOne",
      "forensicSpecialist": "friend",
      "ai": "timeTraveler",
      "patient": "cultist",
      "scientist": "lover"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "murder",
        "culprit": "informer"
      },
      {
        "day": 4,
        "incident": "butterflyEffect",
        "culprit": "ai"
      },
      {
        "day": 5,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 6,
        "incident": "hospitalIncident",
        "culprit": "scientist"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "Traditional Ensemble Murder",
    "creator": "BakaFire",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 2
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "unsettlingRumor",
      "hiddenFreak"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 3
      },
      {
        "numberOfLoops": 4,
        "difficulty": 4
      },
      {
        "numberOfLoops": 3,
        "difficulty": 6
      }
    ],
    "daysPerLoop": 7,
    "cast": {
      "boyStudent": "serialKiller",
      "girlStudent": "keyPerson",
      "richStudent": "killer",
      "shrineMaiden": "friend",
      "policeOfficer": "brain",
      "officeWorker": "person",
      "informer": "person",
      "doctor": "conspiracyTheorist",
      "patient": "cultist"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "patient"
      },
      {
        "day": 4,
        "incident": "hospitalIncident",
        "culprit": "shrineMaiden"
      },
      {
        "day": 5,
        "incident": "missingPerson",
        "culprit": "boyStudent"
      },
      {
        "day": 7,
        "incident": "murder",
        "culprit": "officeWorker"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "The Future Assassin",
    "creator": "unun",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 3
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "hiddenFreak",
      "circleFriends"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 3
      },
      {
        "numberOfLoops": 4,
        "difficulty": 5
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "girlStudent": "person",
      "classRep": "friend",
      "shrineMaiden": "friend",
      "alien": "person",
      "informer": "conspiracyTheorist",
      "popIdol": "cultist",
      "journalist": "person",
      "doctor": "serialKiller",
      "patient": "timeTraveler"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "butterflyEffect",
        "culprit": "classRep"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "doctor"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "informer"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "Romance Antithesis",
    "creator": "BakaFire",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 4
      }
    ],
    "tragedySet": "midnightZone",
    "mainPlot": [
      "maleConfrontation"
    ],
    "subPlots": [
      "unsafeTrigger",
      "loveHateSpiral"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 2
      },
      {
        "numberOfLoops": 3,
        "difficulty": 4
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "boyStudent": "person",
      "girlStudent": "obstinate",
      "richStudent": "friend",
      "shrineMaiden": "person",
      "alien": "factor",
      "policeOfficer": "person",
      "officeWorker": "ninja"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "confession",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "confession",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "girlStudent"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "In the Hands of Evil Fate",
    "creator": "Bodogetta",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 5
      }
    ],
    "tragedySet": "midnightZone",
    "mainPlot": [
      "secretRecord"
    ],
    "subPlots": [
      "diceOfGods",
      "unsafeTrigger"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 3
      },
      {
        "numberOfLoops": 3,
        "difficulty": 5
      }
    ],
    "daysPerLoop": 4,
    "cast": {
      "boyStudent": "obstinate",
      "richStudent": "person",
      "shrineMaiden": "person",
      "alien": "conspiracyTheorist",
      "godlyBeing": [
        "brain",
        {
          "enters on loop": 2
        }
      ],
      "policeOfficer": "serialKiller",
      "officeWorker": "factor",
      "doctor": "person",
      "patient": "keyPerson"
    },
    "incidents": [
      {
        "day": 2,
        "incident": [
          "fakeIncident",
          "hospitalIncident"
        ],
        "culprit": "boyStudent"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "serialMurder",
        "culprit": "doctor"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "Singer of the Heretic Idol",
    "creator": "Satoshi Sawamura",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 6
      }
    ],
    "tragedySet": "midnightZone",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "loveHateSpiral",
      "worshippersApocalypse"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 8,
        "difficulty": 4
      },
      {
        "numberOfLoops": 7,
        "difficulty": 5
      },
      {
        "numberOfLoops": 6,
        "difficulty": 6
      }
    ],
    "daysPerLoop": 4,
    "cast": {
      "boyStudent": "obstinate",
      "classRep": "friend",
      "mysteryBoy": "immortalRole",
      "shrineMaiden": "person",
      "illusion": "brain",
      "popIdol": "person",
      "journalist": "prophet",
      "doctor": "cultist",
      "henchman": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "popIdol"
      },
      {
        "day": 2,
        "incident": [
          "fakeIncident",
          "breakthrough"
        ],
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": [
          "fakeIncident",
          "uproar"
        ],
        "culprit": "classRep"
      },
      {
        "day": 4,
        "incident": [
          "fakeIncident",
          "hospitalIncident"
        ],
        "culprit": "boyStudent"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "Spinning Duplicate Spiral",
    "creator": "Gaijin",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 7
      }
    ],
    "tragedySet": "midnightZone",
    "mainPlot": [
      "fatedConnections"
    ],
    "subPlots": [
      "unansweredHeart",
      "showtimeDeath"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 7
      }
    ],
    "daysPerLoop": 6,
    "cast": {
      "boyStudent": "magician",
      "richStudent": "person",
      "mysteryBoy": "prophet",
      "shrineMaiden": "serialKiller",
      "policeOfficer": "magician",
      "popIdol": "friend",
      "journalist": "conspiracyTheorist",
      "doctor": "person",
      "patient": "immortalRole"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "fakedSuicide",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "missingPerson",
        "culprit": "richStudent"
      },
      {
        "day": 5,
        "incident": "uproar",
        "culprit": "journalist"
      },
      {
        "day": 6,
        "incident": "confession",
        "culprit": "patient"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "Obsession Detection",
    "creator": "BakaFire",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 8
      }
    ],
    "tragedySet": "mysteryCircle",
    "mainPlot": [
      "blackSchool"
    ],
    "subPlots": [
      "trickyTwins",
      "danceFools"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 2
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "boyStudent": "brain",
      "richStudent": "fool",
      "shrineMaiden": "person",
      "alien": "twin",
      "policeOfficer": "paranoiac",
      "officeWorker": "person",
      "journalist": "person",
      "patient": "friend"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "portent",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "silverBullet",
        "culprit": "policeOfficer"
      },
      {
        "day": 4,
        "incident": "hospitalIncident",
        "culprit": "shrineMaiden"
      },
      {
        "day": 5,
        "incident": "serialMurder",
        "culprit": "alien"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "The Temple of Poisoned Loneliness",
    "creator": "GEnd",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 9
      }
    ],
    "tragedySet": "mysteryCircle",
    "mainPlot": [
      "quiltIncidents"
    ],
    "subPlots": [
      "anAbsoluteWill",
      "trickyTwins"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 3
      },
      {
        "numberOfLoops": 3,
        "difficulty": 5
      }
    ],
    "daysPerLoop": 5,
    "cast": {
      "boyStudent": "paranoiac",
      "girlStudent": "person",
      "richStudent": "person",
      "shrineMaiden": "twin",
      "officeWorker": "person",
      "journalist": "conspiracyTheorist",
      "popIdol": "person",
      "patient": "fool",
      "nurse": "obstinate"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "nurse"
      },
      {
        "day": 2,
        "incident": "serialMurder",
        "culprit": "popIdol"
      },
      {
        "day": 3,
        "incident": "serialMurder",
        "culprit": "shrineMaiden"
      },
      {
        "day": 4,
        "incident": "serialMurder",
        "culprit": "patient"
      },
      {
        "day": 5,
        "incident": "serialMurder",
        "culprit": "shrineMaiden"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "The Unknowns of the Tragedies in the Academic City",
    "creator": "Izayoi",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 10
      }
    ],
    "tragedySet": "mysteryCircle",
    "mainPlot": [
      "dropStrychnine"
    ],
    "subPlots": [
      "masterDetective",
      "hiddenFreak"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      }
    ],
    "daysPerLoop": 7,
    "cast": {
      "girlStudent": "friend",
      "richStudent": "privateInvestigator",
      "shrineMaiden": "poisoner",
      "alien": "keyPerson",
      "informer": "person",
      "popIdol": "friend",
      "boss": [
        "conspiracyTheorist",
        {
          "Turf": "School"
        }
      ],
      "doctor": "fool",
      "patient": "serialKiller"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "fakedSuicide",
        "culprit": "shrineMaiden"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "girlStudent"
      },
      {
        "day": 5,
        "incident": "bestialMurder",
        "culprit": "boss"
      },
      {
        "day": 6,
        "incident": "serialMurder",
        "culprit": "doctor"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "The Festival of Fools",
    "creator": "Satoshi Sawamura",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 11
      }
    ],
    "tragedySet": "mysteryCircle",
    "mainPlot": [
      "quiltIncidents"
    ],
    "subPlots": [
      "danceFools",
      "isolatedInstitutionPsycho"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 6,
        "difficulty": 6
      }
    ],
    "daysPerLoop": 4,
    "cast": {
      "richStudent": "fool",
      "classRep": "person",
      "shrineMaiden": "paranoiac",
      "policeOfficer": "therapist",
      "officeWorker": "friend",
      "journalist": "person",
      "doctor": "person",
      "patient": "person",
      "henchman": "conspiracyTheorist"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suspiciousLetter",
        "culprit": "classRep"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "patient"
      },
      {
        "day": 4,
        "incident": "portent",
        "culprit": "richStudent"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  },
  {
    "title": "Bag of Risks",
    "creator": "Mew2mi",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 7
      },
      {
        "numberOfLoops": 5,
        "difficulty": 5
      }
    ],
    "tragedySet": "mysteryCircle",
    "mainPlot": [
      "tightropePlan"
    ],
    "subPlots": [
      "hiddenFreak",
      "masterDetective"
    ],
    "daysPerLoop": 7,
    "cast": {
      "boss": [
        "brain",
        {
          "Turf": "School"
        }
      ],
      "nurse": "killer",
      "journalist": "serialKiller",
      "boyStudent": "friend",
      "alien": "friend",
      "informer": "conspiracyTheorist",
      "transferStudent": [
        "privateInvestigator",
        {
          "enters on day": 5
        }
      ],
      "godlyBeing": [
        "person",
        {
          "enters on loop": 4
        }
      ],
      "teacher": "person",
      "popIdol": "person",
      "classRep": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "terrorism",
        "culprit": "teacher"
      },
      {
        "day": 3,
        "incident": "silverBullet",
        "culprit": "popIdol"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "alien"
      },
      {
        "day": 6,
        "incident": "serialMurder",
        "culprit": "classRep"
      },
      {
        "day": 7,
        "incident": "bestialMurder",
        "culprit": "boss"
      }
    ],
    "description": "This is not a script for the faintest of hearts. It is very hard on both the mastermind and the protagonists. For both the mastermind and the protagonists, it will require tons of knowledge about Mystery Circle. Additionally, this includes tricky characters like the :boss:, :godlyBeing: and :transferStudent:. Every character has a role to play and the slightest misplay from the mastermind could spell their doom, and every misdeduction by the Protagonists could end up being deadly.",
    "specialRules": [
      "None."
    ],
    "victory-conditions": "Protagonists die:\n- Day 2 :terrorism:.\n- :killer: ability (4 :intrigue: on :nurse:).\nLoop End:\n- :friend: has died (:boyStudent:/:alien:)\n- :tightropePlan: (Extra Gauge at 1 or less)",
    "story": "Constant gunfire is being reported in both the school and the city streets. The yakuza have spread their influence and they will not surrender their territory to anyone, no matter the sacrifices.\nOrchestrated by an elusive figure and her partner in crime, these shootings aim to hold control over the Sinzuwa Ward, using the yakuza as a convenient cover up. Spreading misinformation by controlling the media and getting rid of those who try to uncover the truth, they rule the area with an iron fist, a cloak and a dagger.\nBut not everyone is willing to remain silent. The newly transferred student has quickly grasped the situation with the help of otherworldly assistance and has cooked up a plan on how to use their own secrecy against them. Will she be able to prevail with the help of the protagonists, or will the party fail to uncover the truth?",
    "mastermindHints": "The Protagonists only have 3 paths towards victory. They either have to cause :terrorism: (without a :friend: dying) + :serialMurder:, or they have to cause :bestialMurder: (using :paranoia: and/or the :privateInvestigator:), or they have to win through the final guess. As the first option is highly unlikely, it becomes your job to try and hide the roles as well as possible, while also trying your hardest not to give away the main plot.\n\nThe main plot can be hidden easily if you give away the :brain: and/or the CT. :terrorism: and :silverBullet: are your bread and butter, and you need to threaten them every loop. For the first loop, try to trigger (and hopefully hide the culprit in the process) :silverBullet:. Cover up the main plot with :blackSchool:. For the second loop, try to either cause :terrorism: with 1 :intrigue: and kill a :friend:, or 2 :intrigue: and end the loop there. If that fails, then you should still be able to cause :silverBullet:, but you might be unable to hide the main plot in that case. To avoid that, you could try to get away with killing a :friend: (ideally the :alien:) instead, which would hide the main plot. The third loop is the most important one. It becomes very hard to cover up the main plot, so it might be a good idea to use your CT to force :silverBullet: and end the loop early. \n\nIf you have ended Loops 1, 2 and 3 on Day 2/3, then they should be able to figure out the main plot at this point, but they would not know about the :godlyBeing: + :transferStudent: that is awaiting them. If they are smart, they should be able to prevent the loop from ending on Day 2/3, while also getting the :godlyBeing: to 3 :goodwill:. If they succeed in doing so and use its ability to reveal either of the last 2 incident by day 3, your chances of winning nearly evaporate. Using :paranoia: and/or the :journalist:, they should be able to easily use whichever incident they need to trigger. Your final :hope: lies in the protagonists' weakness to cooperate. Even if they do get one of them, you might be able to get them to kill a :friend:. However depending on which abilities you have ended up using, that might also be the final role they need to win with the final guess, so you need to be extremely careful even then, as they should have been able to find the :serialKiller:, :brain:, one of the friends and/or the CT.\n\nHiding the :killer: is your best bet, however you might need to pop his ability on the last day in an emergency. If you have not used their ability, he is the primary role to hide in this script. Try to spread :intrigue: on random persons when possible to distract them from the :nurse:."
  },
  {
    "title": "Milestone",
    "creator": "AKITAGAWA Detective Bureau",
    "set": [
      {
        "name": "Midnight Circle",
        "number": 12
      }
    ],
    "tragedySet": "mysteryCircle",
    "mainPlot": [
      "tightropePlan"
    ],
    "subPlots": [
      "anAbsoluteWill",
      "masterDetective"
    ],
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 6
      },
      {
        "numberOfLoops": 4,
        "difficulty": 8
      }
    ],
    "daysPerLoop": 7,
    "cast": {
      "girlStudent": "conspiracyTheorist",
      "richStudent": "killer",
      "mysteryBoy": "poisoner",
      "alien": "obstinate",
      "policeOfficer": "privateInvestigator",
      "informer": "person",
      "popIdol": "friend",
      "patient": "brain",
      "scientist": "person",
      "henchman": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "hospitalIncident",
        "culprit": "informer"
      },
      {
        "day": 2,
        "incident": "terrorism",
        "culprit": "alien"
      },
      {
        "day": 4,
        "incident": "portent",
        "culprit": "henchman"
      },
      {
        "day": 5,
        "incident": "bestialMurder",
        "culprit": "girlStudent"
      },
      {
        "day": 6,
        "incident": "silverBullet",
        "culprit": "scientist"
      },
      {
        "day": 7,
        "incident": "bestialMurder",
        "culprit": "richStudent"
      }
    ],
    "victory-conditions": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "story": "See Tragedy Looper: Midnight Circle Mastermind Handbook",
    "mastermindHints": "See Tragedy Looper: Midnight Circle Mastermind Handbook"
  }
],
 ...[
  {
    "title": "Beyond Praying Stage",
    "creator": "NAOKI",
    "description": "A tragedy set in a world where the boundary between reality and dreams is blurred, exploring themes of identity and existentialism.",
    "set": [
      {
        "name": "Rei",
        "number": 1
      }
    ],
    "tragedySet": "anotherHorizonR",
    "mainPlot": [
      "intoNothingness"
    ],
    "subPlots": [
      "jekyllHyde",
      "throughLookingGlass"
    ],
    "difficultySets": [
      {
        "difficulty": 3,
        "numberOfLoops": 3
      }
    ],
    "daysPerLoop": 4,
    "cast": {
      "girlStudent": "alice",
      "richStudent": "keyPerson|brain",
      "classRep": "person",
      "shrineMaiden": "marionetteAHR",
      "officeWorker": "marionetteAHR",
      "popIdol": "obstinate",
      "partTimer": "brain",
      "soldier": "conspiracyTheorist|serialKiller"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "lastWill",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "crimePassion",
        "culprit": "popIdol"
      }
    ],
    "victory-conditions": "",
    "mastermindHints": "",
    "story": ""
  }
],
 ...[
  {
    "title": "The School Tragedy",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 7
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/7",
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 1
      }
    ],
    "tragedySet": "firstSteps",
    "mainPlot": [
      "placeProtect"
    ],
    "subPlots": [
      "unsettlingRumor"
    ],
    "daysPerLoop": 2,
    "cast": {
      "classRep": "keyPerson",
      "officeWorker": "cultist",
      "popIdol": "conspiracyTheorist",
      "shrineMaiden": "person",
      "richStudent": "person",
      "boyStudent": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "missingPerson",
        "culprit": "popIdol"
      },
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "classRep"
      }
    ],
    "description": "This is a practice script that focuses on :intrigue: to the board, with an emphasis on understanding the effects of :intrigue:.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "On the first day of the first loop, set :paranoia:+1 to :popIdol:, :paranoia:+1 to :boyStudent:, and :intrigue:+1 to School.\n\nUse :popIdol:'s :conspiracyTheorist: ability to activate :missingPerson:, and place 2 :intrigue: on the School on the first day to win. Use :unsettlingRumor: too, if necessary.\n\nIf you can successfully get :intrigue: on the Locations, place a bluff on the second Day to avoid giving away information. If you can get :intrigue:+2 on Locations other than School, that'll be a powerful CS. However, don't use the :cultist:'s :mandatory ability.\n\nIn the second Loop, they'll likely be on guard against :paranoia: on the :popIdol:, so instead take advantage of that opportunity to trigger :classRep:'s :suicide:.\n\nOn Day 1, put :paranoia:+1 on the :classRep:, move to :popIdol:, and place Forbid :goodwill: on the :boyStudent:. Use :conspiracyTheorist:'s :mandatory ability as well to hit the :classRep:'s :paranoia: limit on the first day.\n\nThe third Loop will be a tough game. Which Incident will you trigger, and where will you place your cards to achieve it? You're now in a battle of wits with the Protagonists. Good luck."
  },
  {
    "title": "Time Traveler on the Run",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 8
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/8",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 2
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "unknownFactor",
      "unsettlingRumor"
    ],
    "daysPerLoop": 4,
    "cast": {
      "patient": "cultist",
      "informer": "timeTraveler",
      "girlStudent": "factor",
      "richStudent": "conspiracyTheorist",
      "doctor": "person",
      "alien": "person",
      "shrineMaiden": "person",
      "officeWorker": "person",
      "policeOfficer": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "hospitalIncident",
        "culprit": "girlStudent"
      },
      {
        "day": 3,
        "incident": "spreading",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "foulEvil",
        "culprit": "shrineMaiden"
      }
    ],
    "description": "This script is aimed at beginner Masterminds and Protagonists who have never played Basic before, or at most a couple times.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "First, win the first Loop with a :hospitalIncident:. Place :intrigue:+2 on the Hospital, :intrigue:+1 on the School, and :paranoia:+1 on the :girlStudent:. If Forbid :intrigue: is placed on the Hospital, nullify it with the :cultist:. Then, in the Mastermind's :mandatory phase, use :unsettlingRumor: to give :intrigue:+1 to the School, and give :conspiracyTheorist:'s ability to the :factor:. \n\nUse :conspiracyTheorist:'s ability to give :paranoia:+1 to the :girlStudent:, and then use the original :conspiracyTheorist:'s ability to give :paranoia: to the :girlStudent: again, triggering a :hospitalIncident: and ending the Loop.\n\nBy using a large amount of abilities, you'll be bombarding the Protagonists with a flood of information. But if they take the time to organize the information, they'll see that both Sub Plots are already confirmed. Take enough time in the Time Spiral immediately afterward to make the Protagonists realize this fact. Doing so will prevent :goodwill: from being placed on the :informer:. Since this is a script where only the Time Travel can win without the :hospitalIncident:, it's important to reveal the Sub Plot here and make them think that it's pointless to place :goodwill: on the :informer:.\n\nIn the second Loop, aim for :foulEvil:. Triggering it won't affect the outcome of the game, but by doing so, you can make the Main Plot appear sealed offand lure the Protagonists away. If they start looking for the :timeTraveler: from the second Loop, it will be very tough for the mastermind, so this lure is also a necessary way to buy time."
  },
  {
    "title": "From the Nothing, with Love",
    "creator": "Ryo.Shin.",
    "set": [
      {
        "name": "Sangeki",
        "number": 89
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/89",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "circleFriends",
      "unsettlingRumor"
    ],
    "daysPerLoop": 3,
    "cast": {
      "henchman": "cultist",
      "blackCat": "timeTraveler",
      "patient": "friend",
      "ai": "friend",
      "informer": "conspiracyTheorist",
      "doctor": "person",
      "alien": "person",
      "classRep": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "spreading",
        "culprit": "blackCat"
      },
      {
        "day": 2,
        "incident": "hospitalIncident",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "ai"
      }
    ],
    "description": "A gimmick-breaking script that utilizes the :ai: The key is whether you can get through the :hospitalIncident: successfully. The difficulty here is quite high. The special rules are a balance adjustment (as a result of prioritizing the short nature of the script, and that we have limited the Protagonists' moves to avoid stressing them out).\n\nTo lower the difficulty, you can:\n- Add more Loops\n- Remove the :classRep:\n\n(Title is a Project Itoh/007 reference)",
    "specialRules": [
      "1. Mastermind can't use \"Forbid :goodwill:\" or \"Vertical Movement.\"\n2. :henchman: always starts in the City.\n3. The Mastermind can't place :intrigue: or :paranoia: on a certain character known only to them (but the Protagonists can). This rule applies to the same character in every Loop."
    ],
    "victory-conditions": "",
    "story": "A sickly genius created a brilliant artificial intelligence. Capable of even learning languages, it was truly a concious being created by mankind. He pushed himself to continue development, despite his frail health. He and the :ai: were truly one and the same.\n\nHowever, it was a technology that could shake the balance of power in the world. A spy from a certain country got ahold of this information, and is plotting to wipe both the Professor and his creation from this world. The Professor's technology will greatly change the coming era—we cannot let the world fall into madness. The Protagonists leap through time and take action to prevent this tragedy.\n\nAll the :ai: can do now is pray for peace for humanity.\n\n\nFrom the Nothing, with Love",
    "mastermindHints": "Special rule: Mastermind can't place :intrigue: or :paranoia: on the :ai: in any Loop.\n\nIf the Protagonists don't notice the gimmick, the Mastermind can kill the :friend: in the Hospital and win.\n\nHospital gimmick:\nOn the first dya, place :intrigue:+1 on the Hospital, :paranoia:+1 on the :henchman:, and :intrigue:+2 (main plot bluff) on the :alien:, :classRep:, or Shrine. The :henchman: is the Culprit of the :hospitalIncident:, and the :informer: is a :conspiracyTheorist:, so you can definitely cause a :hospitalIncident:. Normally, the :patient: can only move on the third Day, so if you put :intrigue: on the Hospital with :unsettlingRumor:, you can quickly kill the :patient: (:friend:) and win.\n\nTo survive the :hospitalIncident:, the Protagonists need the power of the :ai: After killing the :ai: (with a :mandatory Ability or an Incident), use :goodwill:+2 on the :ai: and :goodwill:+1 on the :doctor: in the Loop, which will trigger :spreading: and put the :ai:'s :goodwill: on the :doctor:. Then, you can use the :doctor:'s :goodwill: 3 ability on the first Day, allowing the :patient: to escape (:henchman: can't be used here because his ability ignores :goodwill:).\n\nIf you trigger :unsettlingRumor: with the :informer:, you'll get :circle:friend:s: in return, so you will expose the second :friend:.\n\nFinal Guess:\nThis will depend on whether you cleared the Hospital gimmick. If the :ai:'s :friend: has not been revealed, there's no difference between it and the :conspiracyTheorist:, and the Main Plot can't be determined, so you're safe. If the Protagonists are able to crack the gimmick, the Mastermind has no way to win other than exposing the :timeTraveler:. There are only three candidates (:alien:, :blackCat:, and :classRep:), and since there's time to kill them with the :hospitalIncident: or via the :alien:, or accumulating :goodwill: on the third Day, they'll be revealed pretty quickly.\n\nBy the way, the title and \\\"AI\\\" (\\\"love\\\") are puns. Hehe."
  },
  {
    "title": "A Perfect Day for Suicide",
    "creator": "竹美香",
    "set": [
      {
        "name": "Sangeki",
        "number": 57
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/57",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "circleFriends",
      "loveAffair"
    ],
    "daysPerLoop": 4,
    "cast": {
      "littleSister": "keyPerson",
      "policeOfficer": "killer",
      "servant": "brain",
      "richStudent": "friend",
      "youngGirl": "friend",
      "sacredTree": "conspiracyTheorist",
      "officeWorker": "lover",
      "patient": "lovedOne"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "patient"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "youngGirl"
      }
    ],
    "description": "Since I wanted to use the :littleSister:, I made a script in which the :youngGirl: :friend: will definitely die if I didn't utlize her.\n\nThe :littleSister:'s :goodwill: Location restriction seems strict, so I set it up so that she only needs to be in the School with either the :servant: or the :policeOfficer:, and it makes it easier to accumulate :goodwill: at the :sacredTree:.\n\nI improvised this, so I'm not sure about the difficulty!",
    "specialRules": [
      ":goodwill: actions can't be used. \n\n(Banning Move Diagonal will reduce the difficulty.)"
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "If you keep placing :paranoia: on the :youngGirl:, she will definitely trigger :suicide:. :policeOfficer: and :servant: ignores :goodwill:, so you can't get out unless you use the :littleSister:'s ability on the :youngGirl:.\n\nGuessing the Role is also difficult if you don't use the :littleSister:, as they won't know the :key:person:, :conspiracyTheorist:, :killer:, or :brain:.\n\nIt may be better to trigger :suicide: early and reveal the two Sub Plots, but it's better the reveal :friend:, :lover:, and :lovedOne: early and make them realize that the only way to escape is by fighting."
  },
  {
    "title": "Leave it to Lil' Sis!",
    "creator": "れう",
    "set": [
      {
        "name": "Sangeki",
        "number": 24
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/24",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "giantTimeBomb"
    ],
    "subPlots": [
      "threadsFate",
      "circleFriends"
    ],
    "daysPerLoop": 7,
    "cast": {
      "henchman": "witch",
      "godlyBeing": [
        "friend",
        {
          "enters on loop": 3
        }
      ],
      "littleSister": "friend",
      "policeOfficer": "conspiracyTheorist",
      "richStudent": "person",
      "shrineMaiden": "person",
      "officeWorker": "person",
      "forensicSpecialist": "person",
      "blackCat": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "shrineMaiden"
      },
      {
        "day": 2,
        "incident": "murder",
        "culprit": "policeOfficer"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "blackCat"
      },
      {
        "day": 4,
        "incident": "spreading",
        "culprit": "richStudent"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "officeWorker"
      },
      {
        "day": 6,
        "incident": "farawayMurder",
        "culprit": "forensicSpecialist"
      },
      {
        "day": 7,
        "incident": "missingPerson",
        "culprit": "henchman"
      }
    ],
    "description": "This script is more like PP, which has many violent crimes. The Protagonists will prevent tragedies with the help of their :littleSister:.",
    "specialRules": [
      "The Mastermind can't set Actions cards on a certain secret, predetermined character in any Loop."
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "Special secret rule: The Mastermind can't place Action cards on the :littleSister:.\n\nThe Mastermind can't set Actions on the :littleSister:, so therefore she can use :adult: :mandatory abilities at will. The :forensicSpecialist: can adjust :paranoia: and identify the role of a corpse, the :policeOfficer: can put an Escort Counter on yourself as a :friend:, and if they  use the :henchman:'s ability to stop the Incidents, the Protagonists can escape the Loop.\n\nThe Mastermind's basic stragegy is to place the :henchman: at the Shrine and accumulate 2 :intrigue: while bluffing the :hospitalIncident:. The :henchman: is adjustable, so change his starting position if you think you'll be discovered. In that case, however, be careful about hiding the :conspiracyTheorist: for the Final Guess.\n\nBasically, you can win by putting :paranoia: on :henchman:, but if he is killed by the :serialKiller:, or your :littleSister: is used to activate the :henchman:'s ability, it'll be a very tough battle. :faraway:murder: is strong because if forces you to choose between Locations for :intrigue:, but it can also be hindered by the :policeOfficer:'s Escort counter, so be careful.\n\n:butterflyEffect: can be used for bluffing and CS, and it's also strong if you can put :goodwill: on the Culprit for the next Loop."
  },
  {
    "title": "Battle Between Women",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 2
      }
    ],
    "source": "http://www.boardgame.work/sangeki/kyakuhon/detail.php?id=1006",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 1
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "hiddenFreak",
      "threadsFate"
    ],
    "daysPerLoop": 4,
    "cast": {
      "popIdol": "keyPerson",
      "girlStudent": "serialKiller",
      "nurse": "friend",
      "shrineMaiden": "person",
      "officeWorker": "person",
      "classRep": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "officeWorker"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "shrineMaiden"
      },
      {
        "day": 4,
        "incident": "murder",
        "culprit": "girlStudent"
      }
    ],
    "description": "This script is aimed at Masterminds playing :basicTragedy: for the time. The combination of rules provides a stable means of victory, making easy to use even for beginner Masterminds. However, although it's meant for practice, it's still difficult for the Protagonists to win.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1. \n- Kill the :key:person: (:serialKiller:, :murder:)\n2.\n- :signWithMe: (place 2 :intrigue: on :key:person:, :increasing:paranoia: x2)",
    "story": "",
    "mastermindHints": "In each Loop, it's recommended to aim for the following victory conditions.\n- 1st Loop: Kill the :key:person: with the :serialKiller:.\n- 2nd Loop: :increasing:paranoia: to give the :key:person: :intrigue:+2.\n- 3rd Loop: Same as the 2nd Loop.\n- 4th Loop: Use :intrigue:+2 or kill with the :serialKiller:\n\nIn the first Loop, kill the :key:person: with the :serialKiller: for an instant win. If it doesn't end on the first Day, keep putting Forbid :goodwill: on the :officeWorker: to prevent them from using his :mandatory :goodwill: ability.\n\nThe second Loop will probably start with :threadsFate:, giving the :officeWorker: :paranoia:+2. If that happens, you can be sure to trigger two :increasing:paranoia: Incidents, place two :intrigue: on the :popIdol:, and win.\n\nIf you can't make the :officeWorker: trigger :increasing:paranoia:, you'll have to predict the Action cards. \n\nSet a card on the :popIdol: every day. It's recommended to place a Move or :intrigue: on her. At the same time, load :paranoia: on the :shrineMaiden: and :girlStudent: to trigger an Incident. You can win if you can get :intrigue:+2 through the :popIdol:, so do your best to win the guessing game.\n\nHide the :friend: for the Final Guess. It can be exposed by the :shrineMaiden:, but you will be busy with :paranoia: exchanges, so you don't need to be too careful. You should be more careful of the :serialKiller:. In addition to the :nurse:, the :shrineMaiden: and :classRep: are your :friend: candidates, so if they find out that these two are not the :friend:s, you're screwed. Be careful if the Protagonists start utilizing the :serialKiller:.\n\nPlacement Guide:\n\nFirst Loop\nFirst Day:\n- :classRep: Move Vertical\n- :popIdol: Move Horizontal\n- Shrine: :intrigue:+1\n\nIf the :officeWorker: gets :paranoia:+2 from :threadsFate:\nFirst Day:\n- :officeWorker: :paranoia:+1\n- :popIdol: Move Horizontal\n\nSecond Day:\n- :shrineMaiden: :paranoia:+1\n\nFinal Loop:\nFirst Day\n- :shrineMaiden: :paranoia:+1\n- :girlStudent: :paranoia:+1\n- :popIdol: Move Horizontal\n\nSecond Day\n- :shrineMaiden: :paranoia:+1\n- :girlStudent: :paranoia:+1"
  },
  {
    "title": "Spreading Paranoia and Trust",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 100
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/100",
    "difficultySets": [
      {
        "numberOfLoops": 6,
        "difficulty": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "threadsFate",
      "unknownFactor"
    ],
    "daysPerLoop": 1,
    "cast": {
      "patient": "cultist",
      "sacredTree": "timeTraveler",
      "ai": "factor",
      "alien": "person",
      "journalist": "person",
      "informer": "person",
      "officeWorker": "person",
      "richStudent": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "hospitalIncident",
        "culprit": "officeWorker"
      }
    ],
    "description": "This is a one-day script with a strange special rules. Interaction with :threadsFate: will cause :paranoia: and :goodwill: to explosively pile up. Enjoy a dynamic game unlike anything you'd expect from a one-day script.",
    "specialRules": [
      "Mastermind can't use Forbid :goodwill: or :paranoia:-1. :paranoia: counters also count as :goodwill: counters."
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "Characters who place :paranoia: counters will have +2 :paranoia: at the start of subsequent Loops due to the special rule and :threadsFate:. Make sure to put :paranoia:+1 on :officeWorker: and :intrigue:+2 on Hospital every Loop. This will ensure that you can trigger :hospitalIncident: from the second Loop onward.\n\nSince the :patient: is a :cultist:, force through :intrigue:+2 and try to kill the Protagonists. \n\nYou can't cause an Incident in the first Loop, but you can manage that with the :timeTraveler:. If the :officeWorker: is hit with :paranoia:-1, there's a risk of the script collapsing, so place cards on the Shrine and :richStudent: to distract them from the :officeWorker:.\n\nThe Protagonists can only win with the Final Guess, but the key to victory is the :alien: and :informer:. :alien:'s role is to break the :timeTraveler:. If you leave them alone, they'll use the :mandatory :murder: ability right away, so always move them away from the Shrine. If the :timeTraveler: is broken, the :informer:'s :goodwill: ability will give them the last piece of information. That is, they'll find out the :ai:'s role and that everyone else is a :person:.\n\nIf you get this far, the Protagonists will win the Final Guess."
  },
  {
    "title": "Unstable Life",
    "creator": "Ryo.Shin.",
    "set": [
      {
        "name": "Sangeki",
        "number": 90
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/90",
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 1
      }
    ],
    "tragedySet": "firstSteps",
    "mainPlot": [
      "placeProtect"
    ],
    "subPlots": [
      "shadowRipper"
    ],
    "daysPerLoop": 2,
    "cast": {
      "richStudent": "keyPerson",
      "henchman": "cultist",
      "popIdol": "conspiracyTheorist",
      "doctor": "serialKiller",
      "teacher": "person",
      "blackCat": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "missingPerson",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "murder",
        "culprit": "popIdol"
      }
    ],
    "description": "This is short, easy-to-play script for beginners that will show them the joys of Tragedy Looper. The Protagonists must deduce the Culprit in this classic :murder: mystery, and the roles of each Character in order to achieve a :breakthrough: and clear the game. If you can figure out a way to kill the :popIdol: with a :serialKiller:, you'll have even more of a fun time.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1. Kill the :key:person:\n2. Place 2 :intrigue: on the School (:placeProtect:).",
    "story": "Welcome to a world of tragedy.\n\nThe key to escaping your days of captivity lie in the School, and that innocent, lovely girl. However, evil is lurking  behind a wavering heart.\n\nBe careful. She could disappear in an instant.",
    "mastermindHints": "For the :henchman:'s starting Location, I recommend the School or an adjacent board.\n\nIn the first Loop, we'll use :missingPerson: to move the :richStudent: and then have the :serialKiller: kill her. Place Horizontal Movement on the :popIdol:, :paranoia:+1 on the :richStudent:, and a bluff, then send the :richStudent: to the Hospital with :missingPerson: to win.\n\nFrom the second Loop onward, aim to either win via :murder: or put :intrigue:+2 on the School while handling the :serialKiller:.\n\nThe :blackCat: is just a diversion and don't do much harm this time. Once the :key:person: is revealed, the main plot \\\":lightAvenger:\\\" will be negated, so keep in mind that you don't need to worry about the Shrine's :intrigue:.\n\nWinning via :murder: is done by placing Forbid Movement or :paranoia:+1 on the :popIdol:, and the School's :intrigue: is handled by using Forbid :intrigue:. If you put the :henchman: on the School, you can always win by :intrigue: with the :cultist:'s :mandatory ability. However, if the :cultist: is revealed, the Protagonists will interfere and make placing :intrigue: on the School difficult.\n\nThe best resistance the Mastermind can offer on Day 1 is probably School :intrigue:+2 with the :henchman:, placing Horizontal Movement on the :popIdol:, and :paranoia:+1 on the :richStudent:. Although, this can be overcome by moving the :serialKiller: and the :cultist: to the same place.\n\nThe final Loop will be a guessing game. Ideally, you don't want to narrow down the :cultist: candidates, but if there are multiple Forbid moves active, it'll be difficult for the Mastermind to win. Try to predict the Movement actions so that you setup meetings between the :richStudent: and the :doctor:/:popIdol:."
  },
  {
    "title": "She's Here",
    "creator": "Ryo.Shin.",
    "set": [
      {
        "name": "Sangeki",
        "number": 91
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/91",
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 2
      }
    ],
    "tragedySet": "firstSteps",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "shadowRipper"
    ],
    "daysPerLoop": 3,
    "cast": {
      "girlStudent": "keyPerson",
      "policeOfficer": "killer",
      "henchman": "brain",
      "alien": "conspiracyTheorist",
      "transferStudent": [
        "serialKiller",
        {
          "enters on day": 2
        }
      ],
      "officeWorker": "person",
      "forensicSpecialist": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "henchman"
      },
      {
        "day": 2,
        "incident": "farawayMurder",
        "culprit": "policeOfficer"
      },
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "transferStudent"
      }
    ],
    "description": "This is a short :firstSteps: script without any difficult gimmicks or PP, and is designed to be beginner-:friend:ly. The behavior of the :henchman: and the :transferStudent: holds the key.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1. Kill the :key:person: (via :killer:, :serialKiller:, or :faraway:murder:)\n2. Kill the Protagonists (:killer:)",
    "story": "A female student happens to :witness: a :policeOfficer: commiting a crime. He seems to have hidden connections with a criminal organization, and now he's teamed up with his associates to try and silence her.\n\nWill the police help her?\nIn the midst of her fear, a :transferStudent: approaches her and offers to help. Can she really be trusted?",
    "mastermindHints": "In the first Loop, place Horizontal Movement on the :policeOfficer:, :intrigue:+2 on the :girlStudent:, and Vertical Movement on the :alien:, then use the :killer: to kill the :girlStudent:. If you want to take care of Forbid :intrigue:, you can start the :henchman: in the School, trigger :increasing:paranoia: with :paranoia:+2, and place a second :intrigue: on the :girlStudent: with the :brain:'s ability.\n\nIn the second Loop, you'll also aim to kill the :girlStudent:, but :faraway:murder: should make you wary of :intrigue: on the :girlStudent:. Start the :henchman: in the Shrine, trigger :increasing:paranoia: via the :conspiracyTheorist:, move the :henchman:, and aim to put :intrigue: on the :girlStudent: via the :brain:'s ability. After that, aim to kill her with :faraway:murder: or the :killer:.\n\nOn the second Day, the :transferStudent: will arrive. She can reduce :intrigue: with 2 :goodwill:, so the Protagonists will gladly make use of her. Don't worry, though, because she's the :serialKiller:. If there is no :henchman: in the School, she'll gladly get to work.\n\nThe final Loop will be tough, but the Mastermind have some weapons at his disposal.\n- Accumulate :intrigue: on the :killer: by having the :henchman: start in the City\n- Use :missingPerson: on :transferStudent: on the 3rd Day.\n\nAim to kil the :key:person: or the Protagonists. There are advantages to placing :henchman: anywhere other than the Hospital.\n- Shrine: Easy to trigger :increasing:paranoia:\n- School: Can place :intrigue: on the :girlStudent:\n- City: Can place :intrigue: on the :policeOfficer:\n\nThe Protagonists are not defenseless, either, and have weapons such as:\n- Using the :alien: or :serialKiller: to kill the :brain:/:killer:\n- Controlling :intrigue: by using the :forensicSpecialist:'s or the Tranfer Student:'s ability.\n\nIf the Protagonists let the :girlStudent: escape to the Shrine, it'll be difficult for the :killer: or :serialKiller: to reach her. Make good use of powerful actions such as :paranoia:-1 or Forbid Movement."
  },
  {
    "title": "The Truth Behind the Rumors",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 8
      }
    ],
    "source": "http://www.boardgame.work/sangeki/kyakuhon/detail.php?id=1005",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 5
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "giantTimeBomb"
    ],
    "subPlots": [
      "unsettlingRumor",
      "unknownFactor"
    ],
    "daysPerLoop": 6,
    "cast": {
      "officeWorker": "witch",
      "girlStudent": "conspiracyTheorist",
      "teacher": "factor",
      "soldier": "person",
      "informer": "person",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 2
        }
      ],
      "richStudent": "person"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "murder",
        "culprit": "richStudent"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "girlStudent"
      },
      {
        "day": 6,
        "incident": "suicide",
        "culprit": "teacher"
      }
    ],
    "description": "This is a script that heavily utilizes the plot \"Disturbing Rumors.\" This plot is amazing!",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1\n\n    :giantTimeBomb:\n\n    Place 2 :intrigue: in the City\n\n2\n\n    Kill the Protagonists\n\n    :hospitalIncident:",
    "story": "",
    "mastermindHints": "The Mastermind's way to win is to either kill the Protagonists with :hospitalIncident:, or to place two :intrigue: on the City. With the strong support of :unsettlingRumor:, you're almost certain to achieve one of these.\n\nIn the first Loop, you want to win with the :giantTimeBomb: if possible. On the first Day, place :intrigue:+2 and :intrigue:+1 on the City. If the :intrigue: on the City goes through, place the second one with :unsettlingRumor: on the last Day to guarantee victory. If the City is blocked, there is no choice but to aim for a :hospitalIncident: victory.\n\nIf you are aiming for :hospitalIncident:, you don't need to hide the :conspiracyTheorist:. Use the :mandatory ability as much as you can and place :paranoia:+1 as well. Even if you trigger the :hospitalIncident:, the more troublesome Culprit candidates you can make for the Protagonists, the better.\n\nThe :soldier: is a threat to the Mastermind. Your :hospitalIncident: might be blocked, or your :factor: might commit :suicide:. This will lead straight to your defeat, so be careful not to use the :mandatory :goodwill: ability.\n\nThe :godlyBeing: is also tricky. In order to prevent the :suicide: Culprit from being revealed, increase the number of suspects for the :hospitalIncident: as much as possible.\n\nOf course, the :factor: is hiding in the Final Guess. To avoid it being revealed via :murder: on the third Day, place :intrigue: in the City with :unsettlingRumor: on the last Day. Also, don't trigger :suicide: on the last Day.\n\nPlacement guide (same each Loop):\n\nFirst Day:\n\nHospital - :intrigue:+2\n\nCity - :intrigue:+1"
  },
  {
    "title": "The Hidden Time Traveler",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 7
      }
    ],
    "source": "http://www.boardgame.work/sangeki/kyakuhon/detail.php?id=1002",
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 5
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "circleFriends",
      "threadsFate"
    ],
    "daysPerLoop": 5,
    "cast": {
      "shrineMaiden": "cultist",
      "informer": "timeTraveler",
      "popIdol": "friend",
      "classRep": "friend",
      "teacher": "conspiracyTheorist",
      "alien": "person",
      "forensicSpecialist": "person",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 5
        }
      ],
      "girlStudent": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "butterflyEffect",
        "culprit": "forensicSpecialist"
      },
      {
        "day": 3,
        "incident": "murder",
        "culprit": "teacher"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "classRep"
      },
      {
        "day": 5,
        "incident": "suicide",
        "culprit": "informer"
      }
    ],
    "description": "This script is a little long considering its difficulty. The three rules and many of the Roles are relatively easy to crack, but the :timeTraveler: is difficult to pinpoint, with one exception.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "The main way to win is to kill the :friend:. Since the :teacher: is a :conspiracyTheorist: and the :murder: culprit, you should prioritize killing your :classRep:. You should avoid using :timeTraveler:'s ability to win, but if you have no other choice, you can use it.\n\nThe :forensicSpecialist:'s :goodwill: ability is a trap. Once you have :goodwill:, you can put a lot of pressure on your opponent via :forensicSpecialist: with :threadsFate: and :butterflyEffect:. If :butterflyEffect: occurs, place a :goodwill: counter on :forensicSpecialist: and aim for the same in the next Loop.\n\n:suicide: is the Achilles' heel of this script. If the Protagonists try to cause this Incident, it'll be a very difficult situation. Fortunately, there are very few situations where the :informer:'s :mandatory ability can be used, so you will only have one chance to accidentally trigger the :suicide:."
  },
  {
    "title": "He's a Person",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 5
      }
    ],
    "source": "http://www.boardgame.work/sangeki/kyakuhon/detail.php?id=1003",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "circleFriends",
      "loveAffair"
    ],
    "daysPerLoop": 5,
    "cast": {
      "alien": "keyPerson",
      "ai": "friend",
      "richStudent": "friend",
      "teacher": "conspiracyTheorist",
      "transferStudent": [
        "lover",
        {
          "enters on day": 2
        }
      ],
      "patient": "lovedOne",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 2
        }
      ],
      "officeWorker": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "spreading",
        "culprit": "ai"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "teacher"
      },
      {
        "day": 3,
        "incident": "murder",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "transferStudent"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "patient"
      }
    ],
    "description": "This is for Protagonists who have experienced Tragedy Looper a few times already. It features a lock gimmick that makes extensive use of the Sub Plot, and how it conceals the Main Plot.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1.\n- Kill the Protagonists (:lovedOne:'s :mandatory)\n\n2.\n- Kill a :friend: (:ai:'s :goodwill: ability, :alien:'s :goodwill: ability, :murder:)\n\n3.\n- :signWithMe: (place 2 :intrigue: on :key:person:)",
    "story": "",
    "mastermindHints": "Use the :conspiracyTheorist:'s :mandatory ability every day. There is no need to hide it. The absolute goal is to cause Increase :paranoia: and :transferStudent:'s :suicide:. However, :murder: on the third Day must not occur.\n\nUse :increasing:paranoia: to supply :intrigue: and :transferStudent:'s :suicide: to supply :paranoia: to the :patient:, and aim to have the :lovedOne: kill the Protagonists in every Loop.\n\nSince no elements of the Main Plot are required up to this point, identifying the Main Plot will be the focus of the game.\n\nThe Main Plot will be identified when all characters use their :mandatory :goodwill: abilities.\n\nThe script only has eight characters, five of whom are Sub Plot Roles, and the :officeWorker: is a :person:, so only two can be assigned to the Main Plot Role. Therefore, once the :officeWorker: is exposed as the :person:, the :murder:Plan: will be removed from the list of candidates.\n\nOf the remaining four Main Plots, :goodwill: Refusal is part of all plots except :signWithMe:. If the Protagonists discover that there is no :goodwill: Refusal in this script, the Main Plot and even the role assignments will be confirmed.\n\nKeep track of which characters have not used their :mandatory :goodwill: abilities yet in the game, and be on guard to prevent at least one character from using their :mandatory :goodwill: ability throughout the game. The most likely candidate is :godlyBeing:.\n\nHere's a template for the cards, but it's not :mandatory. There will be situations where you'll have to improvise, especially as the game nears the end.\n\nIf :goodwill: is not on the :ai:, then:\n\nDay 1:\n- :teacher:—:paranoia:+1\n- :alien:—Forbid :goodwill:\n\nDay 2:\n- :teacher:—:paranoia:+1\n- :transferStudent:—:paranoia:+1\n\nDay 3:\n- :transferStudent:—:paranoia:+1\n\nDay 4:\n- :transferStudent:—:paranoia:+1\n\nIf :goodwill: is on the :ai: via the :friend: ability:\nDay 1:\n- :teacher:—:paranoia:+2\n- :ai:—:intrigue:+2\n\nDay 2:\n- :teacher:—:paranoia:+1\n- :transferStudent:—:paranoia:+1\n\nDay 3:\n- :transferStudent:—:paranoia:+1\n\nDay 4:\n- :transferStudent:—:paranoia:+1"
  },
  {
    "title": "A Hideous Cast",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 102
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/102",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 5
      }
    ],
    "tragedySet": "firstSteps",
    "mainPlot": [
      "lightAvenger"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "daysPerLoop": 1,
    "cast": {
      "patient": "brain",
      "scientist": "conspiracyTheorist",
      "ai": "curmudgeon",
      "officeWorker": "friend",
      "littleSister": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "farawayMurder",
        "culprit": "scientist"
      }
    ],
    "description": ":hideousScript: has the unique feature of allowing you to reduce the number of Roles. This script focuses on that feature to the full extent. It's compact and not too difficult, so please enjoy it as a break from the more difficult Tragedies.",
    "specialRules": [
      "The Protagonists may make a Final Guess."
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "Make sure you place a :paranoia: counter on the :scientist: each Loop. Then, place cards according to the guideline here.\n\n- Hospital—:intrigue:+1\n- :officeWorker:—:intrigue:+2\n- :scientist:—:paranoia:+1\n\nIf the Hospital's :intrigue: passes, you can definitely cause a Tragedy in the Main Plot, and if the :officeWorker:'s :intrigue: passes, you can also trigger :faraway:murder:.\n\nProtagonists cannot escape the Loop, but they can go to the Final Guess due to the special rules. And by taking advantage of the Defeat Conditions and the PP related to them, you can reveal all the roles.\n\nIf you lose in the Main Plot, you can reveal the :brain:, and if you lose in the Incident, you can reveal the :friend:. By placing :paranoia:-1 on the :scientist:, you can also reveal the :conspiracyTheorist:. \n\nOnce you have revealed that much, all that remains is the placement of the :curmudgeon:. That can be determined by the characteristics of the :littleSister: and :ai:"
  },
  {
    "title": "Heresy Prevails in the Classroom",
    "creator": "Ryo.Shin.",
    "set": [
      {
        "name": "Sangeki",
        "number": 88
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/88",
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 4
      }
    ],
    "tragedySet": "firstSteps",
    "mainPlot": [
      "placeProtect"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "daysPerLoop": 3,
    "cast": {
      "shrineMaiden": "keyPerson",
      "patient": "cultist",
      "doctor": "conspiracyTheorist",
      "boyStudent": "curmudgeon",
      "richStudent": "curmudgeon",
      "soldier": "friend",
      "alien": "person",
      "informer": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "shrineMaiden"
      },
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "soldier"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "richStudent"
      }
    ],
    "description": "I wanted to write a short, medium difficulty script using :firstSteps:. The key is to use the :mandatory :goodwill: abilities of the :soldier: and :alien:.\n\nDifficulty adjustments:\n\nTo lower the difficulty:\n- Change the number of Loops to 4 (2★)\n- Change the Culprit on the 3rd Day to :boyStudent: (3★)\n- Change :boyStudent: to :person: (3.5★)\n\nTo increase the difficulty:\n- Special Rule: Protagonists can't use :paranoia:-1 (6★)\nOr forbid table talk.\n\nIf the Protagonists are experienced and can make good deductions and moves, 3 Loops will be a good match. For beginners, 4 Loops is recommended.",
    "specialRules": [
      "Mastermind can't use \"Forbid :goodwill:.\""
    ],
    "victory-conditions": "1. Kill the :key:person: (:shrineMaiden:)\n2. Kill the :friend: (:soldier:)\n3. Put 2 :intrigue: on the School\n4. Kill the Protagonists (:hospitalIncident:)",
    "story": "An evil teaching has quietly been :spreading: amongst the youth... One after another, they're succumbing to mental illness. The cult responsible is also plotting a terrorist attack on the Hospital by manipulating students. A :soldier: is tasked with the investigation, and together with the Protagonists and even an :alien: from another world who all share the same goal, he sets out to thwart the evil scheme.",
    "mastermindHints": "In the first Loop, aim for the :shrineMaiden:'s :suicide:. Place Horizontal Movement on the :doctor:, :paranoia:+1 on the :shrineMaiden:, and :paranoia:+1 on :richStudent:, then use the :conspiracyTheorist:'s :mandatory to put :paranoia: on the :shrineMaiden: and win.\n\nIn the second Loop, aim for 2 :intrigue: on the School. Place :paranoia:+1 on the :soldier:, :intrigue:+2 on the Hospital,and :intrigue:+1 on the School. You can use the :cultist:'s :mandatory ability as much as you want. Combined with the :soldier:'s :missingPerson: Incident on the second Day, you can put :intrigue: on the School. If the School's :intrigue: is blocked, trigger a :hospitalIncident: instead to win. If that happens, it's better to not reveal your :friend: (if the :friend: dies in the second Loop, the Protagonists have a way to win in the third Loop).\n\nMake sure to trigger the :hospitalIncident: in the final Loop. The Protagonists will also notice the :cultist:s and realize that Forbid :intrigue: on the Hospital is useless.\n\nThe :shrineMaiden:'s :suicide: is easily prevent, but thanks to the :cultist:s and the :richStudent:, you can easily win via :hospitalIncident:. Use the :conspiracyTheorist: to increase the number of :hospitalIncident: Culprit candidates as much as possible.\n\nThe way for the Protagonists to escape the Loop is to:\n1. Kill :richStudent: with the :alien:'s :mandatory, or\n2. become :immortal: with the :soldier:'s :mandatory. \nHowever, 1 requires identifying the :hospitalIncident:'s Culprit. 2 does not require revealing that Culprit, but in order to avoid involving the :soldier: in the :hospitalIncident:, it's necessary to use the :doctor: and :soldier:'s abilities to take advantage of :missingPerson:, or to secure one move by using the :friend:'s :goodwill: 1 :mandatory. As the Mastermind, you should crush anyone who gives easy answers such as \\\"Use the :soldier: for an easy victory LOL.\\\"\n\nFor a brute force solution, it's also possible to win by using :paranoia:-1 on the :richStudent: for three days straight. The quickest solution for the Protagonists is to use :alien: and :paranoia:-1 to shut down the :hospitalIncident: Culprit candidates.\n\nAs a Mastermind, this is a bit annoying, but since I created this with the intent of it being a smooth :firstSteps: script, I consider this a correct solution."
  },
  {
    "title": "The Academy Tragedy",
    "creator": "れう",
    "set": [
      {
        "name": "Sangeki",
        "number": 28
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/28",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "circleFriends",
      "unsettlingRumor"
    ],
    "daysPerLoop": 4,
    "cast": {
      "doctor": "brain",
      "popIdol": "cultist",
      "classRep": "friend",
      "transferStudent": [
        "friend",
        {
          "enters on day": 3
        }
      ],
      "girlStudent": "conspiracyTheorist",
      "mysteryBoy": "serialKiller",
      "shrineMaiden": "person",
      "officeWorker": "person",
      "informer": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "shrineMaiden"
      },
      {
        "day": 2,
        "incident": "hospitalIncident",
        "culprit": "classRep"
      },
      {
        "day": 4,
        "incident": "missingPerson",
        "culprit": "transferStudent"
      }
    ],
    "description": "This script is for beginner to intermediate players. It becomes much easier to win once you understand the rules and who the culprit is.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "1st Loop\nPlace :intrigue: on the :girlStudent:, City, and Shrine. Basically you want to end the game via :sealedItem: or by killing the :friend:, but it's best to hide the fact that :mysteryBoy: is a :serialKiller:.\n\n2nd Loop\nFrom hereon, we'll rely on Incidents and :serialKiller:s. The Culprit of a crime can usually be prevent by placing :paranoia: -1. The :girlStudent:'s ability is offset by the :conspiracyTheorist:'s ability.\n\nIf :increasing:paranoia: is successful, placing :intrigue: on both the Hospital and Shrine, combined with :hospitalIncident:, will almost always guarantee victory, but you'll need the :conspiracyTheorist:.\n\nThe Protagonists can be stopped dead in their tracks by placing :goodwill: +2 on the :girlStudent: (Mastermind needs :shrineMaiden: :paranoia: +1 to cause :increasing:paranoia:, and the :girlStudent: is guaranteed to move, so you can't use Forbid :goodwill:).\n\nIf the :conspiracyTheorist: is revealed and the Culprit is exposed, the Incident will easily be stopped, along with your chances of winning the Final Guess. Be careful.\n\nDeciding on whether to use the :doctor:'s Ability depends mainly on how much information the Protagonists have acquired, but generally it's better to hide him.\n\nIn :basicTragedy:, the only role from a Sub-Plot that has :goodwill: Refusel is the :factor:.\n　・The :popIdol: is revealed because of :goodwill: Refusel.\n　・The Loop ends with two :intrigue: in the City.\nIn this case, it's better not to use the :doctor: since it might hint at the Main Plot \\\":giantTimeBomb:.\\\" On the other hand, if the :intrigue: onto City isn't successful, Refusel may be a better option here since it gives you two options for :cultist:s.\n\nHowever, it's easier to win if you hide your :brain: amongst the :person:s, so you might as well use it when you don't want the :doctor: to use his :mandatory ability.\n\n3rd Loop\nUse :transferStudent:'s :missingPerson: and :unsettlingRumor: to go for the win. However, you should avoid using :unsettlingRumor: up to this point.\n\nIf you hint at placing :intrigue:+1 on the Shrine and :intrigue:+2 on the Hospital in the previous Loop, you can snatch a victory with :unsettlingRumor:. Just be wary of the :shrineMaiden:'s :goodwill: ability.\n\n4th Loop\nMost of the roles will have been exposed by now, but try to hide the :brain: and the :conspiracyTheorist:. If the Protagonists get the optimal solution at this point, you won't be able to win, so in the end you should aim for a :serialKiller: strike or the :brain:/:cultist:'s :mandatory ability.\n\nThe Protagonists can win by:\n\nPlaving Forbid :intrigue: on the Shrine, :paranoia:-1 on :classRep: and :transferStudent:, and not meeting up with the :conspiracyTheorist: (or by using :goodwill:+2 so it doesn't matter if they do).\n\nAnd finally, locking the movement of the :brain: and the :cultist:, and preventing the :serialKiller: from killing your :friend:s."
  },
  {
    "title": "When You Wish Upon A Star",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 9
      }
    ],
    "source": "http://www.boardgame.work/sangeki/kyakuhon/detail.php?id=1010",
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 5
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "circleFriends",
      "threadsFate"
    ],
    "daysPerLoop": 6,
    "cast": {
      "henchman": "cultist",
      "littleSister": "timeTraveler",
      "illusion": "friend",
      "ai": "friend",
      "policeOfficer": "conspiracyTheorist",
      "nurse": "person",
      "officeWorker": "person",
      "informer": "person",
      "boyStudent": "person",
      "richStudent": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "officeWorker"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "nurse"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "henchman"
      }
    ],
    "description": "Note: There are two :illusion:s in this script, but due to limitations, the second :friend: is displayed as \\\":ai:\\\" Treat it as an :illusion: instead.\n\nRegarding the special rule, be sure to explain that the script adheres to the principle that Roles are cast without excess or deficiency. The :illusion: is not a :mysteryBoy: or :copycat:.\n\nThis script has the cast of the stage play, \"Tragedy Looper Be Playing Stage Game.\" The number of Loops is short, and because one Sub Plot is exposed from the beginning due to the special rule, this scenario can be played in a relatively short amount of time.",
    "specialRules": [
      "Illusions are Friends."
    ],
    "victory-conditions": "1. \n:changeOfFuture: (triggers :butterflyEffect:)\n2.\n:timeTraveler:'s ability",
    "story": "",
    "mastermindHints": "The Mastermind's means of victory are :butterflyEffect: and the :timeTraveler:'s abilities, but :butterflyEffect: is the main one. :butterflyEffect: is a victory condition, so you should aim to trigger it, but it's important to note that you can place :goodwill: on Incident Culprits. Starting with :richStudent:, it's worth placing :goodwill: on any Incident Culprit.\n\nIt's easy to trigger :increasingUnease:, but don't trigger it at all in the first Loop. It's important to prevent the Culprit from being revealed.\n\nIn fact, if the Culprit is identified, Incidents will be prevented retardless of the :paranoia: amount. The key to this is the :illusion: and the :littleSister:. Also, the :policeOfficer:'s :goodwill: 4 ability is a bit troublesome.\n\nThe Protagonists' winning stragedy is to stop the :henchman:'s Incident with :littleSister:.  It may seem difficult at first glance, but it can be achieved just by putting 2 :goodwill: on the :illusion:. Once :illusion: has 3 :goodwill:, they can use the :goodwill: 3 abilities of the two Illusions in succesion to lure all the characters to the :littleSister:. After that, they can accumulate 5 :goodwill: with the :littleSister:, call the :henchman:, and politely ask him not to cause an Incident, thus securing the Protagonists' victory.\n\nPlacement template:\nFirst Loop:\n- Start of Loop—:henchman: in City\nFirst Day:\n- :richStudent:—Move Horizontally\n- :officeWorker:—:paranoia:+1\n\n:officeWorker: starts with 2 :paranoia:\n- Start of Loop—:henchman: in School\nFirst Day:\n- :nurse:—Move Horizontal\n- :officeWorker:—:paranoia:+1\nSecond Day:\n- :officeWorker:—:paranoia:+1"
  },
  {
    "title": "A Will.",
    "creator": "比良坂右京",
    "set": [
      {
        "name": "Sangeki",
        "number": 53
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/53",
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 4
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "unsettlingRumor",
      "loveAffair"
    ],
    "daysPerLoop": 5,
    "cast": {
      "patient": "cultist",
      "partTimer": "timeTraveler",
      "informer": "conspiracyTheorist",
      "doctor": "lover",
      "classRep": "lovedOne",
      "richStudent": "person",
      "teacher": "person",
      "officeWorker": "person",
      "popIdol": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "butterflyEffect",
        "culprit": "popIdol"
      },
      {
        "day": 5,
        "incident": "foulEvil",
        "culprit": "teacher"
      }
    ],
    "description": "A script that's aimed at beginner or intermediate players. This script is more focused on CS than PP, and teaches the Protagonists the importance of activating :mandatory :goodwill: abilities.",
    "specialRules": [
      "Mastermind can't use \"Forbid :goodwill:.\""
    ],
    "victory-conditions": "- The :timeTraveler: has two or fewer :goodwill: at the end of the Loop.\n- :butterflyEffect:\n- :hospitalIncident:\n- :lovedOne: kills Protagonists",
    "story": "The strange device was :leftBehind:, along with a corpse. The young man had died, leaving behind a mysterious object capable of time travel.\n\"…Though, you didn't know, did you? That I… came from the future. Keep it a secret, okay?\"",
    "mastermindHints": "Basically, CS with :sealedItem: or :signWithMe:. Also, don't use :cultist:'s :mandatory ability, and don't aim to win with :timeTraveler: until it's discovered. If you can kill the :partTimer: on the 4th Day, you can win as long as there's no :goodwill: counter via :richStudent: or :popIdol:, but since it's :paranoia:, prepare it as a backup plan. When the :partTimer:'s :mandatory ability is activated, the Main Plot will be discovered. Also, both Sub Plots are discovered when the :lover: dies due to the :informer:'s ability, or the :hospitalIncident:.\n\nTaking the above into consideration, act as follows.\n\nFirst Loop, win via :butterflyEffect:. You want to increase of number of CS rules as much as possible. You want to place two :intrigue: on the Shrine and Hospital, or :intrigue: on :richStudent: and :popIdol:. You want to trigger :increasing:paranoia: and :foulEvil:.\n\nSecond Loop, you'll want to win via :hospitalIncident:. You can use the ability from :unsettlingRumor:. If you can place two :intrigue:, kill the Protagonists directly. If you can only place one, kill them via :lovedOne:. Either way, place an :intrigue: on the :classRep: when setting the Action card on the third Day, and after :lovedOne: is discovered, it's easy to place Forbid :intrigue:.\n\nThird Loop, if the Main Plot has been narrowed down, aim to win with Timr Traveler. At this time, pay close attention not to place a :goodwill: counter, and don't be in the same place as the :popIdol:). If not, aim to win with :lovedOne: or the Main Plot.\n\nFinal Guess is difficult. It's great if you can play in a way that does not confirm the :conspiracyTheorist:. The focus is likely to be on where the :cultist: is hiding. If the :timeTraveler: is discovered, there's a high possibility that the :patient: will be designated as a :cultist: by process of elimination.\n\nAs for the Protagonists, they'll want to place :goodwill: on the :partTimer: to transform him into the :partTimerAlternate: and use her :goodwill: ability."
  },
  {
    "title": "Antivirus Friendship Front",
    "creator": "れう",
    "set": [
      {
        "name": "Sangeki",
        "number": 12
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/12",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "circleFriends",
      "paranoiaVirus"
    ],
    "daysPerLoop": 6,
    "cast": {
      "popIdol": "cultist",
      "patient": "timeTraveler",
      "girlStudent": "friend",
      "richStudent": "friend",
      "doctor": "conspiracyTheorist",
      "officeWorker": "person",
      "informer": "person",
      "shrineMaiden": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "butterflyEffect",
        "culprit": "popIdol"
      },
      {
        "day": 4,
        "incident": "hospitalIncident",
        "culprit": "officeWorker"
      },
      {
        "day": 5,
        "incident": "murder",
        "culprit": "shrineMaiden"
      },
      {
        "day": 6,
        "incident": "murder",
        "culprit": "informer"
      }
    ],
    "description": "This is a simple, easy script that's best suited for first-time players who want to get used to not allowing table talk, or when there are beginners. Also, if they realize that they can use :paranoia:Virus: to deduce the identities of the :conspiracyTheorist: and :timeTraveler:, the Protagonists can win quite easily.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "There are 4 ways to win:\n- Kill a :friend:\n- :butterflyEffect:\n- :hospitalIncident:\n- :timeTraveler:",
    "story": "",
    "mastermindHints": "There are three ways to kill the :friend: :murder:, :hospitalIncident:, or :serialKiller:. Your first priority is to kill the :friend: via the :murder: Incident. However, if you stack :paranoia: to trigger an Incident, the :officeWorker:, :shrineMaiden:, or :informer: will become :serialKiller:s, and the Roles will easily be deduced by process of elimination, making the Final Guess even harder for you. Therefore, even if you want to trigger an Incident, you should always be careful about the number of :paranoia: you place.\n\nIf you want to win with :butterflyEffect:, you will not be able to trigger an Incident without the :conspiracyTheorist: if the Culprit is revealed, so prepare a Culprite candidate and don't forget to place :paranoia: on it as well.\n\nBe careful with the :hospitalIncident:, as it will reveal the identity of the :timeTraveler:.\n\nThe Roles you want to hide are one of the :friend:s, and the :conspiracyTheorist:. Therefore, try to avoid creating a :serialKiller: via :paranoia:Virus: as much as possible."
  },
  {
    "title": "What if a Man Fell into the World of Yuri?",
    "creator": "白獅子のケイ",
    "set": [
      {
        "name": "Sangeki",
        "number": 72
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/72",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "loveAffair",
      "paranoiaVirus"
    ],
    "daysPerLoop": 6,
    "cast": {
      "transferStudent": [
        "keyPerson",
        {
          "enters on day": 3
        }
      ],
      "nurse": "lover",
      "richStudent": "lovedOne",
      "henchman": "conspiracyTheorist",
      "girlStudent": "person",
      "popIdol": "person",
      "shrineMaiden": "person",
      "alien": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "butterflyEffect",
        "culprit": "shrineMaiden"
      },
      {
        "day": 4,
        "incident": "butterflyEffect",
        "culprit": "popIdol"
      },
      {
        "day": 5,
        "incident": "increasingUnease",
        "culprit": "transferStudent"
      },
      {
        "day": 6,
        "incident": "murder",
        "culprit": "nurse"
      }
    ],
    "description": "I intended for this to be a relatively beginner's script. Be careful of the :serialKiller: who appears out of nowhere.",
    "specialRules": [
      "Is Table Talk is prohibited, increase the Loop count by 1."
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "- Kill the Protagonists via :lovedOne:\n- Hide the :lover: well\n- Kill the :transferStudent: with the girls\n\nSpread :intrigue: and :paranoia: with :richStudent: and :henchman:'s Incidents, and aim to kill the Protagonists with :lovedOne:'s effect.\n\nFrom the first Loop, kill the Protagonists and :transferStudent: early on with :lovedOne: or a :person: with 3 Paraoia to end the Loop.\n\nUse :conspiracyTheorist:'s :mandatory ability every day. The priority of :paranoia: is to trigger an Incident, so it makes sense for her to dish out plenty of :paranoia:.\n\nDay 1:\n- :richStudent:—:paranoia:+1\n- :shrineMaiden:—:paranoia:+1\n- :girlStudent:—Forbid :goodwill:\n\nIf :increasing:paranoia: occurs, put :intrigue: on :richStudent: and :paranoia: on the :popIdol: or :shrineMaiden:.\n\nIf an Incident occurs on the second day, put :intrigue: on :richStudent: and :paranoia: on the girl. Make as many :girl:s as possible have at least 2 :paranoia:.\n\nOn the third Day, the :transferStudent: will appear. On the third or fourth Day, place :intrigue:+2 on the :transferStudent:. If an Incident occurs, put :paranoia: on the :shrineMaiden:.\n\nDay 4\nIf you did not place :intrigue:+2 on the third Day, try placing :intrigue: today. If an Incident occurs, place :paranoia: if the :transferStudent: does not have 2 :intrigue:, otherwise place :intrigue:.\n\nDay 5/6\nIf the :transferStudent: is at 2 :intrigue:, place :intrigue: on oen of the :girl:s, and prepare as many :girl:s with 1 :intrigue: and 3 :paranoia: as possible. If they're not placed, use :paranoia: and Movement to kill the :transferStudent: or the Protagonists.\n\n[Weaknesses of this script]\nIf they can move the :henchman: well and prevent the occurence of an Incident, they have a good chance at breaking out of the Loop.\n\nIn the Final Guess, the Main Plot forces the player to choose between :murder:Plan: or :signWithMe:, whie trying to find out who the :lover: is. However, if all the :girl:s have acted as :serialKiller: at least once, the Main Plot is confirmed, and it becomes clear that the :lover: can only be the :nurse:, so it will be difficult for the Mastermind to win."
  },
  {
    "title": "The Assassin Blade's Target",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 59
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/59",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 3
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "circleFriends",
      "threadsFate"
    ],
    "daysPerLoop": 3,
    "cast": {
      "informer": "keyPerson",
      "popIdol": "killer",
      "soldier": "brain",
      "officeWorker": "friend",
      "teacher": "friend",
      "boyStudent": "conspiracyTheorist",
      "blackCat": "person",
      "patient": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "murder",
        "culprit": "officeWorker"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "boyStudent"
      },
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "popIdol"
      }
    ],
    "description": "The concept of this script is the first day :murder:. There are pros and cons to either starting or not starting the :murder:, so the intentions of the Mastermind and Protagonists become inter:twin:ed. When they inter:twin:e in such an exquisite way, will the outcome be a peaceful everyday life, or a tragedy...?",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Kill the :key:person:, basically.",
    "story": "",
    "mastermindHints": "In the first Loop, place :intrigue:+2 on the :key:person: on the first Day. :increasing:paranoia: is almost guaranteed, and the Missin :person:'s Culprit is the :killer:, so as long as you can get :intrigue: on the :key:person:, you'll be able to meet the defeat conditions without any problems. It's okay if the :conspiracyTheorist: gets exposed.\n\nFrom the second Loop onward, the game will be a struggle for the firt Day's :murder:. On the first Day, be sure to place Action cards on the :officeWorker: and :informer:. It depends on the flow of the game whether you go for preventing a :murder:, or triggering it.\n\nThe advantage of triggering the :murder: is, of course, that you can aim to kill the :key:person:. If you cause a :murder:, you can use up to three movement cards on the first Day. Also, if you hide the :brain:, the  :friend: can also be considered as a :murder: target. \n\nThe advantage of not triggering the :murder: is that you can prevent the :killer: from being killed in a :murder:. If you don't trigger it, use Move Vertical or :paranoia:-1 on the :officeWorker:. Even if the Protagonists use :paranoia:+1, you can avoid a fatal injury if the Incident occurs in the Hospital.\n\nFor the Final Guess, you should hide the :brain: or the second :friend:. If you hide the :brain:, the :friend: will be the target of the :murder: on the first Day. Use Movement to earn yourself a Loop. If you hide the :friend:, the importance of the :killer:'s ability to kill the Protagonists increases. On this route, it's better not to cause a :murder: on the first Day. That said, in the end, it'll come down to a battle of wits between the Protagonists and you."
  },
  {
    "title": "Overcoming the Death of a Friend",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 58
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/58",
    "difficultySets": [
      {
        "numberOfLoops": 6,
        "difficulty": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "hiddenFreak",
      "circleFriends"
    ],
    "daysPerLoop": 3,
    "cast": {
      "ai": "brain",
      "doctor": "cultist",
      "transferStudent": [
        "serialKiller",
        {
          "enters on day": 2
        }
      ],
      "alien": "friend",
      "sacredTree": "friend",
      "patient": "conspiracyTheorist",
      "copycat": "friend",
      "littleSister": "person",
      "richStudent": "person",
      "henchman": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "henchman"
      },
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "foulEvil",
        "culprit": "patient"
      }
    ],
    "description": "This is a puzzle script in which the Mastermind's actions are greatly restricted. It also contains two concept-level gimmicks, and the Protagonists must discover both of them. It's a bit different style from the standard Tragedy Looper, so it's not recommended for beginners.",
    "specialRules": [
      "Mastermind can't use use any Movement actions, or Forbid :goodwill:."
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": ":henchman: starts in Hospital, and should always be placed in the Hospital as his starting Location.\n\nThis allows you to very reliably trigger :foulEvil: on the third Day, and if it does occur, you're guaranteed victory. It takes some effort for the Protagonists to stop this, so it's impossible for them to immediately clear the game in the early Loops.\n\nFirst, scatter :paranoia: in the early game and move to cause all three Incidents. By setting the initial placement of your :henchman: to the Hospital, you can ensure :increasing:paranoia:, so place :paranoia: on the :patient:.\n\n:missingPerson: will delay the :discovery: of the :serialKiller:. At the same time, stack :intrigue: on the :littleSister: and sime for a CS with :signWithMe:. It's relatively easy to stop :intrigue: on the :littleSister:, so the CS ov :signWithMe: will collapse at that point. Then, place :intrigue:+1 on the Shrine to limit the number of moves the Protagonists can make.\n\nIn the Final Guess, you will be forced to choose between two options: The :conspiracyTheorist: will be the :henchman: or the :patient:, and the :brain: and :cultist: will either be the :doctor: or :ai: Make sure you always refuse the :ai:'s :goodwill: ability. It will be difficult to differentiate between the :brain: and the :cultist:.\n\nThere are ways to reveal both, but they're complete gimmicks, so the Protagonists' imaginations will be tested. Please refer to the steps below.\n\nThe most important point is that the Protagonists kill the two :friend:s. The key to exposing the :conspiracyTheorist: is the characteristic of the :sacredTree:. If you give the :sacredTree: and the :alien: :goodwill:+1 and the Loop starts, you can use :alien:'s :goodwill: ability on the first Day. If you kill the :henchman: with that, the Mastermind will have no choice but to reveal the :conspiracyTheorist:.\n\nAs for the :brain: and :cultist:, you can crack the :cultist: by killing the :patient:, who is the Culprit on the third Day, with :alien:'s :mandatory ability. If you put all your :goodwill: into the :littleSister: and :alien:, you can activate the :doctor:'s :goodwill: ability via the :littleSister: on the second Day, and have the :alien: kill the :patient: on the third Day. In that case, the Mastermind can't win unless he uses the :cultist:'s ability. However, as soon as he does that, the Protagonists are guaranteed a victory in the Final Guess."
  },
  {
    "title": "100-Friends Plan",
    "creator": "白獅子のケイ",
    "set": [
      {
        "name": "Sangeki",
        "number": 71
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/71",
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 5
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "unknownFactor",
      "circleFriends"
    ],
    "daysPerLoop": 6,
    "cast": {
      "boss": [
        "brain",
        {
          "Turf": "School"
        }
      ],
      "doctor": "cultist",
      "teacher": "factor",
      "shrineMaiden": "friend",
      "richStudent": "friend",
      "classRep": "conspiracyTheorist",
      "mysteryBoy": "timeTraveler",
      "transferStudent": [
        "person",
        {
          "enters on day": 2
        }
      ],
      "popIdol": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "missingPerson",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "mysteryBoy"
      },
      {
        "day": 5,
        "incident": "farawayMurder",
        "culprit": "teacher"
      },
      {
        "day": 6,
        "incident": "hospitalIncident",
        "culprit": "transferStudent"
      }
    ],
    "description": "This script uses a variety of ways to defeat the various CS, while the Protagonists unleashes their :goodwill: abilities and puts all their effort into finding :friend:s.",
    "specialRules": [
      "If Table Talk is prohibited, increase the Loop count by 1."
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "In the first Loop, you'll win with the :timeTraveler:'s ability, so don't use the Mastermind's :mandatory ability, don't trigger ann Incident, and spend the Loop quietly placing Movement cards.\n\nFrom the second Loop onward, defeat the Protagonists by mainly killing the :factor: and placing :intrigue: on the Shrine.\n\nDay 1:\n- :richStudent:—:paranoia:+1\n- City—:paranoia:+1 or :intrigue:+1\n- School—:paranoia:+1 or :intrigue:+1"
  },
  {
    "title": "Those Who Overwhelm",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 56
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/56",
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 2
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "threadsFate",
      "hiddenFreak"
    ],
    "daysPerLoop": 4,
    "cast": {
      "transferStudent": [
        "keyPerson",
        {
          "enters on day": 2
        }
      ],
      "blackCat": "serialKiller",
      "ai": "friend",
      "soldier": "person",
      "copycat": "person",
      "boss": [
        "person",
        {
          "Turf": "School"
        }
      ],
      "illusion": "person",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 1
        }
      ]
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "transferStudent"
      },
      {
        "day": 4,
        "incident": "spreading",
        "culprit": "blackCat"
      }
    ],
    "description": "Although it features a large number of eccentric characters, the script is quiet beginner-:friend:ly. On the other hand, the Mastermind will have a difficult time, so they should be experienced.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "",
    "mastermindHints": "In the first Loop, you'll aim for the :transferStudent:, but you can choose either the :intrigue: or the :murder: route. If you aim for :intrigue:, you'll want to place two :intrigue: on either Location as CS. Make the most of the :blackCat: and :illusion:s to confuse the Protagonists.\n\nIf you're aiming for :murder:, use the :serialKiller: in the first Loop. In that case, :intrigue: on characters becomes more important to make the Main Plot look like :murder:Plan:.\n\nFrom the second Loop onward, use the Main Plot and :serialKiller: to put pressure on both the :key:person:'s :murder: and the :intrigue:. If the :friend: is exposed, the :ai: will also be a target. In the third Loop, you'll need to use the :serialKiller:.\n\nIn the Final Guess, the roles of :mysteryBoy: and :copycat: are the key. If you can escape without letting even one of them use their :mandatory :goodwill: ability, you'll have the Final Guess in the bag. It's difficult to hide the :boss: properly because of the :boss:' :mandatory and the :ai:'s :spreading:, but you can silence them with the :serialKiller:. The :serialKiller: will be very useful for both hiding information and winning conditions."
  },
  {
    "title": "Wings Slithering on the Ground",
    "creator": "ぶー太郎",
    "set": [
      {
        "name": "Sangeki",
        "number": 27
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/27",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "unsettlingRumor",
      "unknownFactor"
    ],
    "daysPerLoop": 5,
    "cast": {
      "girlStudent": "cultist",
      "partTimer": "timeTraveler",
      "teacher": "conspiracyTheorist",
      "informer": "factor",
      "classRep": "person",
      "shrineMaiden": "person",
      "officeWorker": "person",
      "patient": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "spreading",
        "culprit": "teacher"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "classRep"
      }
    ],
    "description": "A script that relies too much on one gimmick, but you should be able to put up a decent fight.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "There's only one path to victory—use the :partTimer:'s time travelling ability.\n\nIn other words, the Mastermind's only condition for victory is to kill the :partTimer: on the 4th Day. With this in mind, I've written down the steps for each day.",
    "story": "There are no butterflies here yet, just insects slithering on the ground dreaming of becoming butterflies.",
    "mastermindHints": "On the first Day, put Forbid :goodwill: on the :partTimer:, :paranoia:+1 on the :classRep:, :intrigue:+2 on the School, and use the :teacher:'s :conspiracyTheorist: ability. Move the :girlStudent:, and even if the School has Forbid :intrigue:, activate it on the :teacher: herself (even if the :factor: disguise has failed). If it's known that the :teacher: is not a :factor:, it becomes a multiple choice question.\nIs :paranoia:+1 on the :teacher: a good idea?\n\nThe second Day branches based on the first Day actions. If the :teacher: does not have two :paranoia:, place :paranoia:+1, otherwise leave her alone. \n\n:paranoia:+1 on the :classRep: is a :mandatory.\nIf :paranoia: is 1 on a :partTimer:, place Forbid Paranoid. If the :paranoia: is 0, it's a free action. You can also place Forbid :goodwill:. As a free action, if :intrigue: is not on the School, you can place 1 :intrigue: on the school (it'll be tricky if they know that the :teacher: is the :conspiracyTheorist:, and not the :factor:). \n\nIf you don't have enough :paranoia:, trigger :teacher:'s :conspiracyTheorist: and get a guaranteed :spreading: Incident. The first thing to do is to remove the :partTimer:'s :goodwill:, and the second thing is to remove the :teacher:'s :goodwill:.  If there's a :goodwill: Ban for the :partTimer:, the :teacher:'s :goodwill: should be included.\n\nIt might be a good idea to place :goodwill: on the :officeWorker:. It's important to not unnecessarily reduce the number of :timeTraveler: candidates. This will allow the :partTimer: to have 0 :goodwill: on the third Day, which means the :paranoia: counter will only go up to 1 no matter what happens.\n\nBranching will occur dependong on the number of :paranoia: counters. On the third Day, if the :partTimer: has 1 :paranoia:, place a Forbid :goodwill:. If you're confident that you can win the read, place a Forbid :paranoia:. If the :paranoia: is at 0, then place a Move, :intrigue:+1, or :paranoia:+1.\n\nNow's the time for you to strike.\n\n:classRep: gets :paranoia:+1. \nThe School gets :intrigue:+1, or Forbid :goodwill:, or the :teacher: aims to join up with the :classRep:, or the :teacher: aims to join up with the :partTimer:. Combine these actions.\n\nTo summarize, just be careful not to let your counters reach three in this situation. Since you're surrounded by the :conspiracyTheorist:, :factor:, and :cultist:, it's best to keep it at one or two counters.\n\nOn the 4th Day, though, aim for triggering :increasing:paranoia: and make the :partTimer: to self-destruct at the end of the turn. Use a :paranoia:+1 on :classRep:, :intrigue: or :paranoia:+1 on the :partTimer:, activate :conspiracyTheorist:, and so on to aim for self-destruction.\n\n:classRep:'s :increasing:paranoia: won't occur if the :teacher: escapes and :paranoia:-1 is placed three times, but since they'll think \\\":classRep: has used all of the :paranoia:-1, so the Protagonists :paranoia:-1 won't be used,\\\" the Mastermind's :paranoia:+1 on the 4th Day will definitely go through. Therefore, if you reach the 3rd Day with two counters, you're guaranteed a win.\n\nOn the 5th Day, just place Forbid :goodwill: and Move. You should be guaranteed a win, so try to win.\n\nOn the other hand, if the :partTimer: doesn't self-destruct, or self-destructs on the 3rd Day, you're guaranteed to lose.\n\nIf the :informer:'s :goodwill: ability is activated, respond appropriately. I think it's best to answer with :unsettlingRumor:."
  },
  {
    "title": "A Businessman Wanders into a Place Full of Women",
    "creator": "yurei(ʘᴗʘ✿)",
    "set": [
      {
        "name": "Sangeki",
        "number": 11
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/11",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 2
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "hiddenFreak",
      "loveAffair"
    ],
    "daysPerLoop": 5,
    "cast": {
      "shrineMaiden": "keyPerson",
      "alien": "serialKiller",
      "officeWorker": "friend",
      "classRep": "lover",
      "richStudent": "lovedOne",
      "informer": "person",
      "popIdol": "person"
    },
    "incidents": [
      {
        "day": 3,
        "incident": "butterflyEffect",
        "culprit": "richStudent"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "popIdol"
      }
    ],
    "description": "This is the first script I've ever written.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "",
    "story": "The girls are coming after the :officeWorker:, but the most important thing here is the :shrineMaiden:.",
    "mastermindHints": "First Loop, kill the :friend: with the :serialKiller:.\nSecond Loop, kill the :key:person: with the :serialKiller:.\nThird Loop, use the :hospitalIncident: as a dummy and put :intrigue: on the :key:person:.\nFourth Loop, put :butterflyEffect: to put :intrigue: on the :key:person:.\nIf you can't do that, break through with the :lovedOne:.\n\nIf you're up against an experienced player, 3 Loops shouldn't be a problem."
  },
  {
    "title": "Scathing Report",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 12
      }
    ],
    "source": "http://www.boardgame.work/sangeki/kyakuhon/detail.php?id=1901",
    "difficultySets": [
      {
        "numberOfLoops": 6,
        "difficulty": 8
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "hiddenFreak",
      "threadsFate"
    ],
    "daysPerLoop": 5,
    "cast": {
      "officeWorker": "keyPerson",
      "boss": [
        "killer",
        {
          "Turf": "Hospital"
        }
      ],
      "transferStudent": [
        "brain",
        {
          "enters on day": 3
        }
      ],
      "ai": "serialKiller",
      "journalist": "friend",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 3
        }
      ],
      "patient": "person",
      "soldier": "person",
      "classRep": "person",
      "richStudent": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "officeWorker"
      },
      {
        "day": 2,
        "incident": "butterflyEffect",
        "culprit": "godlyBeing"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "journalist"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "transferStudent"
      },
      {
        "day": 5,
        "incident": "spreading",
        "culprit": "richStudent"
      }
    ],
    "specialRules": [
      ""
    ],
    "description": "This is a very unreasonable script. Although six loops seems long at first glance, the time given to the protagonist is shorter than expected.\nThe final battle is a game of luck, so it is recommended to escape the loop, but you will be required to understand the entire script and place your cards perfectly. ",
    "story": "",
    "mastermindHints": "Up until the third loop, the scriptwriter's means of victory are strictly defined. By following this, you can make the protagonist feel :despair: with each loop.\n\n-1st loop: :murder: of :friend: by :serialKiller:\n\n-2nd loop: :suicide: of :key:person:\n\n-3rd loop onwards: PP1 with :faraway:murder: The 2nd loop is preparation time. By following the template, you can set up a vicious PP from the 3rd loop onwards. However, if you do not place a :goodwill: counter on the :officeWorker: in the 1st loop, the script will fall apart. Keep placing Forbid :goodwill: on the :boss: and encourage the :officeWorker: to use his ability. The 3rd loop is where the real action begins. From this point onwards, keep placing Forbid :goodwill: on the :ai: and :paranoia:+1 on the :journalist: every day to cause :increasing:paranoia:. Put that :paranoia: on :transferStudent: and :intrigue: on the :officeWorker:. By following the template the next day, you can be sure to win with :faraway:murder:. Note that you don't have to place :intrigue: on the :journalist: and :officeWorker: on the 4th day. If the :journalist: doesn't have :paranoia:-1, prepare :intrigue: the next day. By doing so, you can place Forbid :goodwill: on the :ai: on the 4th day as well. Aim for :spreading: to occur on the 4th loop. If :spreading: occurs after the 4th loop, you can prevent the :officeWorker: from padding their :goodwill: and losing the chance to escape the loop. Hide the :killer: in the final battle. Do not refuse the :boss:'s :goodwill: ability.\n\nThe way for the protagonist to win is to cause :spreading: with the :ai: and remove the :goodwill: counter from the :journalist:. The steps to do this are as follows.\n\nFirst, place :paranoia:-1 on the :journalist: until the 2nd day, and place 3 :goodwill: on :richStudent:.\n\nThen, on the third day, make sure :rich:student: is not in the Shrine, and if there is a card on :transferStudent:, put :paranoia:-1 on it. During this time, the :officeWorker: must not have :intrigue:, so keep Forbid :intrigue: on it until the third day. However, if the :officeWorker: is free, try :goodwill:+1 on the :ai:\n\nThen the writer must follow the template for the fourth day, and the :ai: and :rich:student: will be free, so the :ai:'s :goodwill: ability can be used all at once.\n\nNote that you can take care of :spreading: by putting Forbid :intrigue: on the :journalist: here. Thus, by removing the :goodwill: counter from the :journalist: here, you can almost certainly seal :increasing:paranoia: in the next loop.\n\nIf you get this far, the protagonist's victory is almost certain.\n\nFirst Loop\n\nDay 1\n\n\n    :boss:\n    「Forbid :goodwill:」\n    Shrine\n    「:intrigue:+1」\n\nDay 2\n\n\n    :officeWorker:\n    「Vertical Movement」\n    :boss:\n    「Horizontal Movement」\n    \nDay 3\n\n\n    :boss:\n    「Forbid :goodwill:」\n\nDay 4\n\n\n    :boss:\n    「Forbid :goodwill:」\n\nDay 5\n\n\n    :boss:\n    「Forbid :goodwill:」\n    \n2nd Loop\n\nDay 1\n\n\n    :officeWorker:\n    「:paranoia:+1」\n    :ai:\n    「Diagonal Movement」\n    Shrine\n    「:intrigue:+2」\n    \n3rd Loop\n\nDay 1\n\n\n    :ai:\n    「Forbid :goodwill:」\n    :journalist:\n    「:paranoia:+1」\n    :officeWorker:\n    「:intrigue:+1」\n    \nDay 2\n\n\n    :ai:\n    「Forbid :goodwill:」\n    :journalist:\n    「:paranoia:+1」\n    :rich:student:\n    「Vertical Movement」\n\nDay 3\n\n\n    :ai:\n    「Forbid :goodwill:」\n    :journalist:\n    「:paranoia:+1」\n    :transferStudent:\n    「Vertical Movement」\n\nDay 4\n\n\n    :transferStudent:\n    「:paranoia:+1」\n    :officeWorker:\n    「:intrigue:+1」\n    :journalist:\n    「:intrigue:+2」\n\n4th Loop (if the :breakthrough: is discovered)\n\nDay 1\n\n\n    :ai:\n    「Forbid :goodwill:」\n    :journalist:\n    「:paranoia:+1」\n    :richStudent:\n    「:paranoia:+1」\n\nDay 2\n\n\n    :ai:\n    「Forbid :goodwill:」\n    :journalist:\n    「:paranoia:+1」\n    :rich:student:\n    「Vertical Movement」\n\nDay 3\n\n\n    :ai:\n    「Forbid :goodwill:」\n    :journalist:\n    「:paranoia:+1」\n    :transferStudent:\n    「:paranoia:+1」\nDay 4\n\n\n    :transferStudent:\n    「:paranoia:+1」\n    :officeWorker:\n    「:intrigue:+1」\n    :journalist:\n    「:intrigue:+2」\n\n\n\nProtagonists' winning strategy (not Mastermind's strategy)\n\nDay 1\n\n\n    :journalist:\n    「:paranoia:-1」\n    :rich:student:\n    「:goodwill:+2」\n    :officeWorker:\n    「Forbid :intrigue:」\n\nDay 2\n\n\n    :journalist:\n    「:paranoia:-1」\n    :rich:student:\n    「:goodwill:+1」\n\nDay 3\n\n\n    :transferStudent:\n    「:paranoia:-1」\n    :classRep:\n    「:goodwill:+2」\n    :rich:student:\n    「Move to City」\n\nDay 4\n\n\n    :journalist:\n    「:intrigue:Forbid」\n    :ai:\n    「:goodwill:+2」\n    :rich:student:\n    「Move to City」"
  },
  {
    "title": "The Devotion of Mastermind X",
    "creator": "Lapras-hst@パーソン",
    "set": [
      {
        "name": "Sangeki",
        "number": 20
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/20",
    "difficultySets": [
      {
        "numberOfLoops": 6,
        "difficulty": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "circleFriends",
      "threadsFate"
    ],
    "daysPerLoop": 4,
    "cast": {
      "blackCat": "cultist",
      "godlyBeing": [
        "timeTraveler",
        {
          "enters on loop": 2
        }
      ],
      "patient": "friend",
      "richStudent": "friend",
      "transferStudent": [
        "conspiracyTheorist",
        {
          "enters on day": 3
        }
      ],
      "doctor": "person",
      "soldier": "person",
      "boss": [
        "person",
        {
          "Turf": "Hospital"
        }
      ],
      "forensicSpecialist": "person",
      "popIdol": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "spreading",
        "culprit": "boss"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "forensicSpecialist"
      },
      {
        "day": 4,
        "incident": "butterflyEffect",
        "culprit": "blackCat"
      }
    ],
    "description": "This is a script with special rules aimed at intermediate to advanced players who are accustomed to playing Tragedy Looper. I recommend getting the protagonist's consent before playing, as this may ruin the original enjoyment of the game.\n\nAlso, make sure everyone knows beforehand that this is a script in which the protagonist is guaranteed to win if played correctly.",
    "specialRules": [
      "Mastermind can't use \":intrigue: +1,\" \":paranoia: -1,\" or \"Diagonal Movement.\"\n\nThere is no Final Guess.\n\nThere is one Secret Rule known only to the Mastermind."
    ],
    "victory-conditions": "Hidden Special Rule:\n\nThe Mastermind wins if the Protagonists win. However, the Protagonists' optional Loss Condition will be forcibly triggered if other Loss Conditions are not met.",
    "story": "A scriptwriter weaves a tragedy spanning countless timelines. The protagonists are struggling to keep up, failing every time.  However, there is something strange about the world they have wandered into this time.\n\nWhen you find yourself hanging on the edge of :despair:, reach out and grasp the flickering light of :hope:.\n\nAll you need is a flash of inspiration.\n\nDon't give up. Let's start the next loop.",
    "mastermindHints": "Contrary to the usual rules, the screenwriter's true goal is to \"support the Protagonists and free them from the Loop.\"\n\nPlay with a poker face so that your goal is not revealed. We recommend that you keep your communication with the Protagonists to a minimum except for answering their questions, and leave your seat between Loops.\n\nWhen the Public Sheet is presented, the Protagonists may laugh at the limitations on the Mastermind, but they won't be laughing much longer.\n\nBasically, aim for :spreading: and :hospitalIncident: to occur, and for :immortal: to take effect by involving :godlyBeing:. Move :doctor: to City (preparing for :goodwill: and :paranoia: boost), and keep placing :paranoia: on :forensicSpecialist: and :boss:. Move :blackCat: to Hospital, and at the same time, place :intrigue: +2 on Hospital. \n\nAlthough the Protagonists will initially see this as a normal power play and try to thwart it, you should make full use of the abilities of the :conspiracyTheorist: and :cultist:.\n\nIn the unlikely event that :spreading: does occur, remove it from :richStudent: and place it on :doctor:, :godlyBeing:, or :forensicSpecialist:.\n\nIf you have extra hands, place Forbid :goodwill: on :rich:student: or move it to the City (so that you can use :forensicSpecialist: to remove it if :goodwill: is placed). The Protagonists will probably not understand your intentions, and a spiteful Protagonist who is used to the game will sneak through and place :paranoia: or :goodwill: on it, not realizing that they're tying their own noose.\n\nWhen the Culprit behind :butterflyEffect: and role of the :godlyBeing: are revealed, the Protagonists will feel the difficulty of breaking the loop, and the strange discomfort caused by the Mastermind's actions. Once they realize the true purpose behind everything, it's time for telepathic cooperation. Let's break out of this ac:curse:d Loop, together.\n\nTo break the loop, you need to do the following 4 things:\n\n- Cause a :hospitalIncident:\n\n- Move the :patient: out of the Hospital before the Incident occurs\n\n- Involve the :blackCat: in the :hospitalIncident:\n\n- Place 3 :goodwill: on :godlyBeing:\n\nThere are two routes: use :godlyBeing: to remove one :intrigue: from the Hospital, or use the :soldier: to make the Protagonists :immortal:.\n\nIf the role of :rich:student: is exposed due to the whims of the Protagonists, it will be more difficult to break through the Loop. In that case, on the first day, place a Vertical Movement on :doctor:, a Horizontal Movement on :rich:student:, and a :paranoia: +1 on :forensicSpecialist: or :boss: to support them.\n\nIn any case, if the Protagonists and Mastermind work together perfectly, at the end of the last day:\n\n- All characters except :blackCat: will survive and gather in the City\n- All :paranoia: counters will be zero\n- Everyone will have :goodwill: counters on them\n\nYou can do it. Good luck."
  },
  {
    "title": "Unrivaled ★ Little Sister",
    "creator": "KM(キャクホンカミナライ)",
    "set": [
      {
        "name": "Sangeki",
        "number": 94
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/94",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 6
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "paranoiaVirus",
      "unknownFactor"
    ],
    "daysPerLoop": 6,
    "cast": {
      "henchman": "cultist",
      "littleSister": "timeTraveler",
      "ai": "conspiracyTheorist",
      "officeWorker": "factor",
      "informer": "person",
      "journalist": "person",
      "sectFounder": "person",
      "copycat": "person",
      "metaworldDenizen": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "murder",
        "culprit": "journalist"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "copycat"
      },
      {
        "day": 4,
        "incident": "butterflyEffect",
        "culprit": "ai"
      },
      {
        "day": 5,
        "incident": "foulEvil",
        "culprit": "henchman"
      },
      {
        "day": 6,
        "incident": "spreading",
        "culprit": "sectFounder"
      }
    ],
    "description": "This script features a wide selection of relatively new and unusual characters who will use their :goodwill: abilities to overwhelm the Mastermind.",
    "specialRules": [
      "The Leader may use one of their expended Once per Loop cards in the Action phase. When using that card, set another card first, and then swap that card with the expended Once per Loop card just before revealing."
    ],
    "victory-conditions": "",
    "story": "Yo, hello there. I am Kanae. I look down on the world from my seat up here.\n\"Ah, it's Kanae-sama.\"\n\"Thank you, Kanae-sama.\"\n\"Is that Kanae-sama?\"\n\nUm, nice to meet you. I'm Yatsuha, your younger sister.\n\"Yatsuha-sama—!\"\n\"Nice to meet you, Yatsuha-sama—!\"\n\"I want to get to know Yatsuha-sama—!\"\n...\n\nYep, everything looks good. My work is here is done.\n\n\"H-huh?? Kanae-sama—!!??\"",
    "mastermindHints": "It's very easy to trigger :butterflyEffect: via the :ai:, so it's trivial to prevent loop :breakthrough:s. On the other hand, the number of Roles is few, and there are ways to split them up, so the issue becomes trying not to let them get revealed.\n\nIt's also important to organize a CS to increase the number of possible candidate roles. Except for :murder:Plan:, CS are easy to organize, so it's best to go for multiple elements if possible.\n\nOn Day 2, try to aggressively trigger :increasing:paranoia:. With :paranoia:+1 and the :ai:'s :mandatory ability, you can almost always trigger it unless the :copycat: is moved. You can place an :intrigue: counter on :metaworldDenizen: or :littleSister: to set up :signWithMe:\n\nFor Day 5's :foulEvil:, if the :henchman:'s initial placement is a Shrine, the CS will be :sealedItem: and :giantTimeBomb: (of course, Sealed by itself is effective).\n\nOn the other hand, if you start the :henchman: in the City, your goal should be to use the :journalist:'s :murder: Incident to kill the :officeWorker: on Day 1. This choice is the same as admitting that the :officeWorker: is a :factor: when he uses :goodwill:, so you'll either want to score Loops at all cost, or kill him at the beginning when the Protagonists don't know anything and focus all your efforts on the CS with the remaining :henchman:.\n\nAs you approach the Final Guess, try to keep them in the dark about which Main Plot it might be, and be careful to avoid the :copycat: neutralizing all your :person:s. Sometimes, it might be necessary to make a :serialKiller:."
  },
  {
    "title": "Script Maker",
    "creator": "Lapras-hst@パーソン",
    "set": [
      {
        "name": "Sangeki",
        "number": 19
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/19",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 0
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "circleFriends",
      "paranoiaVirus"
    ],
    "daysPerLoop": 5,
    "cast": {
      "doctor": "brain",
      "ai": "cultist",
      "patient": "friend",
      "shrineMaiden": "friend",
      "girlStudent": "conspiracyTheorist",
      "mysteryBoy": "lovedOne",
      "policeOfficer": "person",
      "informer": "person",
      "richStudent": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "murder",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "murder",
        "culprit": "patient"
      },
      {
        "day": 4,
        "incident": "murder",
        "culprit": "shrineMaiden"
      },
      {
        "day": 5,
        "incident": "murder",
        "culprit": "ai"
      }
    ],
    "description": "This is a highly unconventional script in which the Protagonists, who have never created a script before, can help write the script on the spot.\n\nFor the Masterminds, the difficulty is :nightmare: level at 8★ and requires top-tier improvisation skills and on-the-fly thinking.\n\nNo matter which Incident you choose, there are elements you can fight for. Do your best to reach the Final Guess!",
    "specialRules": [
      "At the start of each Loop, the Protagonist Leader declares the name of each Incident for Days 2–5. During that Loop, the effects of each Incident will resolve as declared, to the extent allowed by the rules.\n\nNote: The Incidents are listed as \":murder:\" due to technical limitations, but they are all actually \"???.\""
    ],
    "victory-conditions": "",
    "story": "\"Do you like :basicTragedy:? Good, good. Now I'm curious what you might think of this. Here you go, it's a new :basicTragedy: model. It's pretty good, right? Ahh, come on now, don't be like that. The rules might be unique, but regular :basicTragedy: scripts don't have much room for development, you have to deal with demonic Protagonists, and the Loops are easily broken, nothing good ever comes out them. No good! There's plenty of difficulty in this one (for the Mastermind), so any super genius [Mastermind] can handle it. Please give it a whirl, it'll make a nice sound. Ahh, that's a very nice scream, it's a different kind of energy.\"\n\n\"My favorite part is...\"\n\n\"What?\"\n\n\"The incidents!\"",
    "mastermindHints": "The basic way to win is by placing :intrigue: on the Shrine. At the same time, however, how you prepare your CS and how you hide one of your roles are the important tests for your skill as a Mastermind.\n\nThe CS for the Main Plot is mostly limited to :signWithMe: and the presence of :giantTimeBomb:, but you should adjust to the Sub Plot and how you hide your roles as well.\n\nThe main ways to use Incidents are as follows:\n\n・:murder: Kill a :friend:\n\n・Increasing Paranoia: Activate :lovedOne: and create a :serialKiller:\n\n・:foulEvil: Lucky! Put all your effort into CS.\n\n・:suicide: Kill a :friend:\n\n・:hospitalIncident: Kill a :friend: or the Protagonist.\n\n・:farawayMurder: Kill a :friend:\n\n・:spreading: Interfere with the Protagonists by using :doctor:'s :mandatory :goodwill: ability.\n\n・:butterflyEffect: Various methods"
  },
  {
    "title": "Everyone Gathers to Bully the Tofu-Minded",
    "creator": "Lapras-hst@パーソン",
    "set": [
      {
        "name": "Sangeki",
        "number": 13
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/13",
    "difficultySets": [
      {
        "numberOfLoops": 5,
        "difficulty": 4
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "unknownFactor",
      "paranoiaVirus"
    ],
    "daysPerLoop": 5,
    "cast": {
      "richStudent": "keyPerson",
      "shrineMaiden": "killer",
      "girlStudent": "brain",
      "boyStudent": "factor",
      "teacher": "conspiracyTheorist",
      "mysteryBoy": "serialKiller",
      "classRep": "person",
      "popIdol": "person",
      "transferStudent": [
        "person",
        {
          "enters on day": 3
        }
      ]
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "popIdol"
      },
      {
        "day": 5,
        "incident": "farawayMurder",
        "culprit": "classRep"
      }
    ],
    "description": "A script full of students, where the clique of girls gang up on the unfortunate ojou-sama to bully her to death. If the Protagonist is not familiar with the game, you can ask for their opinion. On the other hand, if you're facing an experienced opponent, 4 Loops is fine.\n\nThe Power Play is quite strong, so you can aim for one-turn kills many times in the first half. If you're lucky, you can even get three in a row. The second half is a tense battle of predicting your opponent's moves. Make full use of not only the :killer:, but :paranoia:Virus: to bully the :richStudent: into the grave.\n\nAlso, even if you change the Main Plot to \\\":signWithMe:\\\" or the Sub Plot to any other plot, the script will still work. Try shuffling the rules and roles as you please. I recommend including a :serialKiller:.",
    "specialRules": [
      "Mastermind can't use \"Forbid :goodwill:.\""
    ],
    "victory-conditions": "",
    "story": "Now that the slightly-standoffish girl broke the girl Code and chummed up to the most popular boy in class, things are about to get dicey. She's incurred the wrath of the entire female body! All bets are off!\n\nHe'll be on her side... right?\n\nThrow in a clumsy :teacher:, a mysterious lone wolf, and a clueless :transferStudent:, the situation is about to explode and drag the entire class down with it!",
    "mastermindHints": "Let's just focus on putting :richStudent: into the grave.\n\nIn the first Loop, if there's no interference, you can just use the :killer:'s :mandatory ability to kill the :richStudent:. If :intrigue: gets blocked, use the :conspiracyTheorist:'s ability to put :paranoia: on the :richStudent: to cause an Incident, then combine it with :brain:'s ability to place 2 more :intrigue: on her, and then finally use the :killer:'s ability to take her down.\n\nIn the second loop, if the Protagonists are obedient enough to block :intrigue:, you can use the aforementioned :brain:/:conspiracyTheorist: combo even if the face down Action cards are in the same positions as the first Loop.\n\nIn the second or third Loop, the Protagonists may decide that the School is dangerous and move :richStudent:. At this point, they'll be suspicious of the :shrineMaiden: moving vertically and move her horizontally instead. Therefore, by s:witch:ing it up and giving :richStudent: and :popIdol: :paranoia:+1 each, an Incident will occur: Two :paranoia: will be sent to :popIdol:, turning her into a :serialKiller: to take our :richStudent:. Another one-turn kill!\n\nIf you're unable to kill :richStudent: on the first day, take the following actions:\n\n- Place :paranoia: on :popIdol: and :classRep: to cause an Incident, or make her a :serialKiller:.\n- Use the :conspiracyTheorist:'s :mandatory ability every day.\n- Place two :intrigue: on :richStudent:\n- Place four :intrigue: on the :shrineMaiden: instead if :richStudent: has Forbid :intrigue: on her.\n- Move each character so that :richStudent: is alone with the :killer: or :serialKiller:.\n\nOn the third Day, if the :popIdol: is a :serialKiller:, you can perform a surprise attack. If the :mysteryBoy: is exposed as a :serialKiller:, the Protagonists will use him aggressively, so be careful.\n\nYou'll want the Final Guess to hide the :factor:, so don't ignore the :boyStudent:'s :mandatory :goodwill: ability.\n\nDay 1 guide:\n\n1st Loop:\n\n\n\n:shrineMaiden: Move Down\n\n:richStudent:: :intrigue:+2\n\n:popIdol: Move Right\n\n2nd Loop:\n\n\n\n:shrineMaiden: Move Down\n\n:richStudent:: :intrigue:+2 or :paranoia:+1\n\n:popIdol: Move Right\n\n2nd–3rd Loop (:richStudent: moves Left)\n\n\n\n:shrineMaiden: Move Down, or :intrigue:+2\n\n:richStudent:: :paranoia:+1\n\n:popIdol: :paranoia:+1\n\n3rd Loop onward:\n\n\n\n:shrineMaiden: Move Down, or :intrigue: +2, +1\n\n:richStudent:: :intrigue: +2, +1, or :paranoia:+1\n\n:popIdol: :paranoia:+1"
  },
  {
    "title": "KILL BOSS: The Looper",
    "creator": "Ryo.Shin.",
    "set": [
      {
        "name": "Sangeki",
        "number": 92
      }
    ],
    "source": "https://sangeki.boardgame.work/scenario/92",
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 0
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "threadsFate",
      "paranoiaVirus"
    ],
    "daysPerLoop": 3,
    "cast": {
      "boss": [
        "keyPerson",
        {
          "Turf": "Hospital"
        }
      ],
      "journalist": "conspiracyTheorist",
      "officeWorker": "person",
      "doctor": "person",
      "patient": "person",
      "nurse": "person",
      "teacher": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "teacher"
      },
      {
        "day": 2,
        "incident": "murder",
        "culprit": "officeWorker"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "nurse"
      }
    ],
    "description": "This is a tribute to the corporate slave TRPG \"Kill :boss:.\"\nIt was made as a light-hearted, fun script.",
    "specialRules": [
      ":key:person: rules:\n\n\n\n1. [Always] This character is also treated as a \":girl:.\"\n\n2. [:mandatory: Upon :key:person:'s death] The Protagonists win and the Loop ends, even if a Defeat Condition is triggered.\n\n3. [Defeat Condition: At the end of Loop]: The :key:person: survives.\n\n\n\n4. The :key:person: is the \":boss:.\""
    ],
    "victory-conditions": "",
    "story": "\"Are you free tomorrow? You can come in tomorrow, right?\"\n\n\"ChatGPT can do a better job than this.\"\n\n\"Young people these days are so...\"\n\n\"Ah, I'll leave this to you then. Can you get it done in three days?\"\n\nI'm sick of this.\nLet's put an end to these monotonous days once and for all.\n\nDay 1: :increasing:paranoia: (:teacher:)\n\nMy workload increases, but my salary remains the same. This is serious, I'll spread the word on social media about this.\n\nDay 2: :murder: (:officeWorker:)\n\nI have no choice, he has to die. Deadlines? No, you should worry about your own deadline instead.\n\nDay 3: :hospitalIncident: (:nurse:)\n\nMedical equipment malfunction? Accidentally gave the wrong dosage? Well, it was an accident, what can you do about it?\n\nFinal Guess\n\nI've had enough! I'm going to tell the news station everything!",
    "mastermindHints": "Try your best to keep the :boss: alive.\n\nThe School is safe because it's far from the Hospital, and the :officeWorker: can't enter it. However, if the Protagonists notice the :threadsFate: and :paranoia:Virus: Sub Plots, it'll be very dangerous to make it out alive.\n\nIn the first Loop, if you use Forbid :goodwill: and Forbid :paranoia:, you should be safe. Interfere with their search for the Incident's Culprit, and pray that you don't get any :goodwill:. Never use the :conspiracyTheorist:'s :mandatory ability.\n\nAfter the second Loop, the :serialKiller: will appear here and there, so avoid being alone with them. Also, if the :officeWorker: has hit his :paranoia: Limit, keep placing Movement cards on the :officeWorker: and the :boss:. Since there are only three Forbid Movement cards, you can avoid :murder: on the second Day.\n\nThe final Loop is pure hell, so brace yourself. If the Protagonists noticed :threadsFate: and the :paranoia:Virus: Sub Plots, they'll have scattered :goodwill: in the previous Loop. The Mastermind can only fight back with :paranoia:-1 and Forbid :paranoia:, but the Protagonists have plenty of :paranoia: and :intrigue: with three :paranoia:+1, and the :mandatory abilities of the :journalist: and :doctor:. Grit your teeth and do your best to survive.\n\nFor the Final Guess, the :conspiracyTheorist: will be the focus, so try to hide her if you can afford it...\n\nI think the Mastermind's chances of winning are slim, so you might want to consider cutting out the Final Guess altogether."
  },
  {
    "title": "Evil Contaminated Realm",
    "creator": "ペンスキー",
    "set": [
      {
        "name": "Sangeki",
        "number": 6
      }
    ],
    "source": "http://www.boardgame.work/sangeki/kyakuhon/detail.php?id=1004",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 4
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "circleFriends",
      "paranoiaVirus"
    ],
    "daysPerLoop": 6,
    "cast": {
      "boyStudent": "brain",
      "nurse": "cultist",
      "alien": "friend",
      "richStudent": "friend",
      "forensicSpecialist": "conspiracyTheorist",
      "soldier": "person",
      "policeOfficer": "person",
      "officeWorker": "person",
      "teacher": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "teacher"
      },
      {
        "day": 5,
        "incident": "foulEvil",
        "culprit": "boyStudent"
      },
      {
        "day": 6,
        "incident": "increasingUnease",
        "culprit": "officeWorker"
      }
    ],
    "description": "This script can be developed in two ways: CS-based and PP-based. It is up to the scriptwriter to decide which one to use, so please choose according to your preference.\n\nEither way, it is important to identify the culprit of the incident. The :policeOfficer: will be very useful.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "This script can be developed in two ways: CS-based and PP-based. It is up to the scriptwriter to decide which one to use, so please choose according to your preference.\n\nIn either case, it is important to identify the culprit of the incident. The :policeOfficer: will be very useful.",
    "story": "",
    "mastermindHints": "The scriptwriter's means of victory are killing :sealedItem: and :friend:. However, since the use of :paranoiaVirus: is essential to killing :friend:, we will first aim to win with :sealedItem:.\n\nIn the case of CS axis, CS of the main plot is very important.\n\nPut :intrigue: on boards other than City and :alien: to expand the possibilities of the main plot. For that, incidents are a strong support. Since you want to cause all incidents while concealing the culprit, scatter :paranoia: with all your might. You can also use :conspiracyTheorist: from the beginning. Be careful of the movement of the :policeOfficer: so that you do not narrow down the culprit of :foulEvil:.\n\nIn the case of PP axis, use :cultist: to win the first loop. If you pass :intrigue: to Shrine, it is better not to cause :foulEvil:. The first loop is very stable, but since you want to hide the :cultist: until the final battle, you need to move in such a way that the :alien:'s :goodwill: ability is never used. Use Forbid :goodwill: at full power, but be careful of :richStudent: and :forensicSpecialist:'s :goodwill: abilities.\n\nAs the game progresses, it becomes more difficult to win with :sealedItem:. Then it's time to use :paranoia:Virus:.\n\nOn the final day, leave the :person: and :friend: alone and aim for a surprise attack by turning them into a :serialKiller: with :increasing:paranoia:. If the :person: has one :paranoia: counter on them, they can be a candidate, so it is important to spread :paranoia:.\n\nIn the final battle, hide the :cultist:. It is a role that is difficult to find, but it may be revealed by :nurse:'s trait, :paranoia:Virus:, :forensicSpecialist:, etc."
  }
],
 ...[],
 ...[
  {
    "cast": {
      "girlStudent": "keyPerson",
      "henchman": "cultist",
      "classRep": "conspiracyTheorist",
      "boyStudent": "person",
      "richStudent": "curmudgeon",
      "patient": "friend"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "patient"
      },
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "girlStudent"
      }
    ],
    "title": "Tainted Candy",
    "source": "https://web.archive.org/web/20211027004127/http://www.tragedylooperscripts.com/scripts/1",
    "mainPlot": [
      "placeProtect"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "story": "The patient overdosed on illegal drugs and is becoming suicidal. Meanwhile, the henchman is a dealer planning to spread the same drugs throughout the school. If the classRep visits the patient in the Hospital, her reprimands spur him towards suicide. The Rich Man’s Daughter is a drug user who dislikes the Protagonists attempts to get in her business. On Day 3 the girlStudent has a bad trip and jumps off a tall building.",
    "tragedySet": "firstSteps",
    "mastermindHints": "",
    "specialRules": [
      "Special Setup: The henchman starts in the City."
    ],
    "victory-conditions": "\n\ntest comment\n    Alan Tran",
    "creator": "ProdigalPlaneswalker",
    "daysPerLoop": 3,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      },
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 1
      }
    ]
  },
  {
    "cast": {
      "boyStudent": "cultist",
      "classRep": "brain",
      "richStudent": "person",
      "illusion": "prophet",
      "officeWorker": "magician",
      "popIdol": "person",
      "patient": "immortalRole",
      "henchman": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "popIdol"
      },
      {
        "day": 3,
        "incident": "uproar",
        "culprit": "patient"
      },
      {
        "day": 4,
        "incident": "hospitalIncident",
        "culprit": "classRep"
      },
      {
        "day": 5,
        "incident": "confession",
        "culprit": "henchman"
      }
    ],
    "title": "Shell Game",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "worshippersApocalypse",
      "showtimeDeath"
    ],
    "story": "",
    "tragedySet": "midnightZone",
    "mastermindHints": "Note: The Day 5 incident is actually a Fake Incident!\n\nEvery location cares about having :intrigue: on it, and you have to play cards on locations to move the illusion, who has a really important role. Use this information to leave the protagonists guessing about which location is important or what you actually want to do with the illusion.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Kill 2 characters (Uproar, Hospital Incident)\nKill the Protagonists (Hospital Incident)\n2 :intrigue: on the Shrine (Sealed Item)\n2 :intrigue: on the henchman's starting location\n\n",
    "creator": "Iammars",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 5
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 102
      }
    ]
  },
  {
    "cast": {
      "girlStudent": "person",
      "richStudent": "person",
      "classRep": "keyPerson",
      "alien": "killer",
      "policeOfficer": "person",
      "officeWorker": "person",
      "popIdol": "friend",
      "doctor": "brain",
      "patient": "friend",
      "henchman": "conspiracyTheorist"
    },
    "incidents": [
      {
        "day": 4,
        "incident": "hospitalIncident",
        "culprit": "girlStudent"
      },
      {
        "day": 5,
        "incident": "suicide",
        "culprit": "classRep"
      }
    ],
    "title": "Terror is Ripe",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "paranoiaVirus",
      "circleFriends"
    ],
    "story": "The night is dark and full of terrors. It’s not safe to go alone anymore. Something has inspired a regime of terror, fear, death. The terror that is experienced by people now… it is something to behold. They are so afraid of dying that they will kill for it. It’s… hilarious.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Go in full :paranoia: on as many characters as possible to cover until the Hospital Incident triggers. If it does, you win the loop no matter what. In the meantime, best get killing, right? The whole point of this script is to slaughter as many characters as possible and leave the Protagonists over the carnage with the loop lost. In the end, the Killer, Brain, and Friend (popIdol) are easy to hide, so just keep 1 or 2 of them secret and the Final Guess should be yours.",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "1) Kill the Key Person (classRep)\n            There's the Hospital Incident, the Killer, and the Suicide. Really, you choose on this one.\n2) Kill a Friend (patient, popIdol)\n           Triggering the Hospital Incident will always kill the patient, so that makes things much easier. Alternatively, turning the Persons into Serial Killers will make it easy too.\n3) Kill the Protagonists\n           Use either the Killers power to kill them, or blow up the Hospital. It's easy enough to trigger the Hospital Incident, so players may attempt to prevent the placing of :intrigue: there, but the fact that the Brain starts there makes it a powerful tool the Protagonists will want to move immediately.\n\nThis was a good script. Difficulty 8/10 makes sense for 3 loops, but 4 is a bit easier. It's fairly balanced. We had the protagonists win on the final guess. \n    Emily Forster\n\nOne of the better ones on this site. 2 Thumbs up!!!\n    sevento",
    "creator": "O'Malley",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 8,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 103
      }
    ]
  },
  {
    "cast": {
      "henchman": "timeTraveler",
      "popIdol": "cultist",
      "richStudent": "factor",
      "nurse": "conspiracyTheorist",
      "mysteryBoy": "killer",
      "informer": "person",
      "officeWorker": "person",
      "girlStudent": "person",
      "alien": "person",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 2
        }
      ],
      "shrineMaiden": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "murder",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "butterflyEffect",
        "culprit": "girlStudent"
      },
      {
        "day": 5,
        "incident": "increasingUnease",
        "culprit": "godlyBeing"
      },
      {
        "day": 6,
        "incident": "foulEvil",
        "culprit": "alien"
      }
    ],
    "title": "To End This World",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "unknownFactor",
      "unsettlingRumor"
    ],
    "story": "He jumps back in time, far from the future. A pleasant and happy time, this person watched as it was ruined for him and him alone. Now, he seeks to undo it. Only one person can save this world now, and they aren't ready to take responsibility yet. They need assistance, they need support. Their destiny will be realized, one way or another.One knife, and he will rise. The future will become his, and it is ruined for the rest of humanity. All it takes is one.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Too lazy right now. I'll edit later.",
    "specialRules": [
      "N/A"
    ],
    "victory-conditions": "Too lazy right now. I'll edit later.\n\nThis script is extremely difficult for the protagonists and it's almost impossible. It's very difficult to stop the Butterfly Effect from occurring, though I believe it's possible to do so with perfect information. I won as the MM from Butterfly Effect all 4 loops. There's also no way for the protagonists to figure out who the Time Traveler is unless the MM messes up.\n    Emily Forster\n\nThank you for the comment – I will adjust. \n    O'Malley\n\nChanged the culprit for the Butterfly Effect from the RMD (who is also the Factor which allows her to place :paranoia: on herself if the School has :intrigue:) to the girlStudent, making it much harder to trigger. In addition, swapped out the policeOfficer for the shrineMaiden so her revealing ability can help the Protagonists.\n\nStill should be difficult to discover the henchman, but I hope it isn't AS hard. It should be easier to win in the loops but harder in the Final Guess.\n    O'Malley",
    "creator": "O'Malley",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 8,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 104
      }
    ]
  },
  {
    "cast": {
      "journalist": "ninja",
      "girlStudent": "friend",
      "shrineMaiden": "obstinate",
      "patient": "conspiracyTheorist",
      "boyStudent": "magician",
      "mysteryBoy": "keyPerson",
      "officeWorker": "person",
      "doctor": "person",
      "alien": "person",
      "henchman": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "patient"
      },
      {
        "day": 3,
        "incident": "serialMurder",
        "culprit": "henchman"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "girlStudent"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "officeWorker"
      },
      {
        "day": 6,
        "incident": "serialMurder",
        "culprit": "shrineMaiden"
      },
      {
        "day": 7,
        "incident": "serialMurder",
        "culprit": "shrineMaiden"
      }
    ],
    "title": "The Greatness Reborn",
    "mainPlot": [
      "maleConfrontation"
    ],
    "subPlots": [
      "loveHateSpiral",
      "unansweredHeart"
    ],
    "story": "",
    "tragedySet": "midnightZone",
    "mastermindHints": "",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Ninja (journalist) has 2 :intrigue:, Friend (girlStudent) dies, Killing Key Person (mysteryBoy), Hospital Incident\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 105
      }
    ]
  },
  {
    "cast": {
      "henchman": "brain",
      "godlyBeing": [
        "paranoiac",
        {
          "enters on loop": 2
        }
      ],
      "richStudent": "conspiracyTheorist",
      "ai": "therapist",
      "policeOfficer": "obstinate",
      "mysteryBoy": "friend",
      "classRep": "person",
      "nurse": "person",
      "shrineMaiden": "person",
      "patient": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "closedCircle",
        "culprit": "policeOfficer"
      },
      {
        "day": 2,
        "incident": "suspiciousLetter",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "closedCircle",
        "culprit": "ai"
      },
      {
        "day": 5,
        "incident": "suicide",
        "culprit": "mysteryBoy"
      },
      {
        "day": 6,
        "incident": "serialMurder",
        "culprit": "godlyBeing"
      },
      {
        "day": 7,
        "incident": "hospitalIncident",
        "culprit": "classRep"
      }
    ],
    "title": "The Greatness Vroomed",
    "mainPlot": [
      "blackSchool"
    ],
    "subPlots": [
      "isolatedInstitutionPsycho",
      "anAbsoluteWill"
    ],
    "story": "",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "Be careful not to let the therapist be revealed too easily. Also, try to bluff a private investigator for the first incident and play forbid :goodwill: on the mysteryBoy and/or godlyBeing in loops 2-4 to prevent that information from getting out.",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Black School: :intrigue: on school is equal to or more than loop #-1, level 2 hospital incident, killing Friend (mysteryBoy)\n\nThis script has been played once with 4 loops, protagonists won on the final guess.\n    Justin Karp",
    "creator": "Justin Karp",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "difficulty": 3,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 106
      }
    ]
  },
  {
    "cast": {
      "godlyBeing": [
        "timeTraveler",
        {
          "enters on loop": 2
        }
      ],
      "shrineMaiden": "cultist",
      "boyStudent": "serialKiller",
      "policeOfficer": "friend",
      "alien": "person",
      "popIdol": "person",
      "officeWorker": "person",
      "richStudent": "person",
      "classRep": "person",
      "doctor": "person",
      "patient": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "missingPerson",
        "culprit": "boyStudent"
      },
      {
        "day": 3,
        "incident": "butterflyEffect",
        "culprit": "godlyBeing"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "shrineMaiden"
      },
      {
        "day": 6,
        "incident": "foulEvil",
        "culprit": "policeOfficer"
      }
    ],
    "title": "Lords of Space-Time",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "hiddenFreak",
      "threadsFate"
    ],
    "story": "A fun and very interesting coverup script. This always Misleads the Protagonists.\n\nLords of Space-Time.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Always have 2 :intrigue: on the Shrine. The Protagonists CAN'T win in the first two loops, as the godlyBeing will never have 2 :goodwill:. With this, and the fact that there is a Cultist, will lead them to believe the main plot is The Sealed Item. So long as they still think this, you've won. Once the godlyBeing arrives, the game changes. While you still have to make sure the Shrine has 2 :intrigue:, triggering the Butterfly Effect is now a priority. Try killing the Friend after they place :goodwill: on the godlyBeing to trigger the Butterfly Effect next loop. Winning is easy enough therefore. But beware - if they realize the Main Plot is Change of Future, the Final Guess will be VERY hard on you.",
    "specialRules": [
      "N/A"
    ],
    "victory-conditions": "Time Traveler (godlyBeing) has 2 or less :goodwill: at the end of the loop.\nAt the end of the loop, if the Butterfly Effect has triggered this loop.\nAt the end of the loop, the Friend (policeOfficer) was killed.\n\nForgot to put in Incidents - adding\n    O'Malley\n\nThe MM information here is in conflict with a couple of things. First of all, you say that the MM will win the first two loops because the godlyBeing isn't there, but you have the godlyBeing listed as \"godlyBeing (2)\" indicating that the godlyBeing comes in at the beginning of loop 2. Also, the rule for the Time Traveler is Optional: Day end last day, not Loss condition: Loop end, so when the Protagonists lose from this, they lose at day end, not loop end and they know that they lose from this and not anything else. So all of the hints about the :intrigue: on the Shrine don't really work. Finally, it's very hard to get the Butterfly Effect to occur the first time the godlyBeing shows up because there are no MM abilities to help and the Protagonists just have to play one :paranoia: -1 to block the godlyBeing from getting to :paranoia: limit by day 3. \n    Emily Forster\n\nThis script is broken because the godlyBeing is not allowed to use their ability when they are not in play.\n    Justin Karp",
    "creator": "O'Malley + Koya",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 8,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 108
      }
    ]
  },
  {
    "cast": {
      "popIdol": "keyPerson",
      "classRep": "friend",
      "alien": "friend",
      "informer": "conspiracyTheorist",
      "godlyBeing": [
        "lover",
        {
          "enters on loop": 2
        }
      ],
      "nurse": "lovedOne",
      "mysteryBoy": "cultist",
      "journalist": "person",
      "patient": "person",
      "henchman": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "foulEvil",
        "culprit": "popIdol"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "classRep"
      },
      {
        "day": 4,
        "incident": "butterflyEffect",
        "culprit": "journalist"
      },
      {
        "notTragedySpecified": true,
        "day": 5,
        "incident": "Diagonal Destruction",
        "culprit": "henchman"
      },
      {
        "day": 6,
        "incident": "farawayMurder",
        "culprit": "informer"
      }
    ],
    "title": "The Death of Greatness",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "circleFriends",
      "loveAffair"
    ],
    "story": "An exciting script with lots of twists and turns.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Try your best.",
    "specialRules": [
      "New incident: Diagonal Destruction: If there is at least 2 :intrigue: on the culprit's location, everyone in the diagonally opposite location dies and protagonists lose."
    ],
    "victory-conditions": "Diagonal Destruction: 2 :intrigue: on culprit's location, Friend dies (alien or classRep), Key Person dies (popIdol), Key Person has 2 :intrigue: (popIdol), Loved One has 3 :paranoia: and 1 :intrigue: (nurse)\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 109
      }
    ]
  },
  {
    "cast": {
      "mysteryBoy": "privateInvestigator",
      "scientist": "fool",
      "boss": [
        "conspiracyTheorist",
        {
          "Turf": "School"
        }
      ],
      "henchman": "paranoiac",
      "classRep": "twin",
      "doctor": "friend",
      "policeOfficer": "person",
      "forensicSpecialist": "person",
      "popIdol": "person",
      "girlStudent": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "closedCircle",
        "culprit": "classRep"
      },
      {
        "day": 2,
        "incident": "serialMurder",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "scientist"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "popIdol"
      },
      {
        "day": 5,
        "incident": "serialMurder",
        "culprit": "henchman"
      },
      {
        "day": 6,
        "incident": "silverBullet",
        "culprit": "policeOfficer"
      },
      {
        "day": 7,
        "incident": "serialMurder",
        "culprit": "henchman"
      },
      {
        "day": 8,
        "incident": "suicide",
        "culprit": "doctor"
      }
    ],
    "title": "Who Died? And Why?",
    "mainPlot": [
      "quiltIncidents"
    ],
    "subPlots": [
      "trickyTwins",
      "danceFools"
    ],
    "story": "YOU CAN READ THIS TO YOUR PLAYERS:\n\"Hello. If you have came here, we told you about our note detailing the upcoming... events. you are most likely in the understanding that no matter how hard you try, this mystery is far too difficult for any of our experts to solve. We have no idea what's going on. We've entitled this case therefore by the most prominent question, \"Who died? And why?\" because we need your help to answer them. We'll need your assistance in uncovering what roles and culprits there are, and convicting whoever is behind this. Judging by the patterns of crime recently, some mastermind is behind all of this. This, if we can find out information, might be the time we catch him.  Good luck.\"\n-Signed, someone... somewhere",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "If you are playing this scenario, you should have TONS of experience. Go with your gut and really follow the basic rules. Don't give the Protagonists information, always try to win with the same loss conditions, and recognize them yourself - I'm too lazy right now.",
    "specialRules": [
      "N/A"
    ],
    "victory-conditions": "Said I was too lazy.\n\nThis script is impossible to win during the loops, but it's not a terrible script because the protagonists can win on the final guess. We played 4 loops and the protagonists won on the final guess. I actually thought this was a very enjoyable script to play for both sides. The PI/Twin combo was very exciting and a big surprise for the protagonists.\n    Justin Karp",
    "creator": "O'Malley",
    "daysPerLoop": 8,
    "difficultySets": [
      {
        "difficulty": 11,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 110
      }
    ]
  },
  {
    "cast": {
      "richStudent": "brain",
      "boss": [
        "conspiracyTheorist",
        {
          "Turf": "School"
        }
      ],
      "doctor": "killer",
      "scientist": "paranoiac",
      "godlyBeing": [
        "therapist",
        {
          "enters on loop": 2
        }
      ],
      "policeOfficer": "serialKiller",
      "mysteryBoy": "friend",
      "alien": "person",
      "journalist": "person",
      "boyStudent": "person",
      "popIdol": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "silverBullet",
        "culprit": "policeOfficer"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "boyStudent"
      },
      {
        "day": 5,
        "incident": "fakedSuicide",
        "culprit": "doctor"
      },
      {
        "day": 6,
        "incident": "bestialMurder",
        "culprit": "scientist"
      }
    ],
    "title": "Big Brother",
    "mainPlot": [
      "tightropePlan"
    ],
    "subPlots": [
      "isolatedInstitutionPsycho",
      "smellGunpowder"
    ],
    "story": "",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "Not sure yet",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Extra Gauge is 1 or lower at Loop End, 12 or more :paranoia: on remaining characters at Loop End, Friend dead at Loop End, Killer ability\n\n",
    "creator": "Emily Forster",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 8,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 111
      }
    ]
  },
  {
    "cast": {
      "popIdol": "keyPerson",
      "godlyBeing": [
        "poisoner",
        {
          "enters on loop": 2
        }
      ],
      "patient": "obstinate",
      "alien": "fool",
      "richStudent": "paranoiac",
      "henchman": "twin",
      "mysteryBoy": "killer",
      "doctor": "person",
      "journalist": "person",
      "informer": "person",
      "boyStudent": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "serialMurder",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "alien"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "patient"
      },
      {
        "day": 6,
        "incident": "serialMurder",
        "culprit": "henchman"
      }
    ],
    "title": "The Revival of Greatness",
    "mainPlot": [
      "dropStrychnine"
    ],
    "subPlots": [
      "anAbsoluteWill",
      "trickyTwins"
    ],
    "story": "An exciting revival of the greatness.",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "Force the increasing unease on day 1 by forbid :goodwill: on the boyStudent.",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Killing Key Person (popIdol), Level 2 Hospital Incident, Poisoner Ability (godlyBeing)\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 7,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 112
      }
    ]
  },
  {
    "cast": {
      "doctor": "showOff",
      "patient": "serialKiller",
      "alien": "lovedOne",
      "shrineMaiden": "person",
      "godlyBeing": [
        "ghost",
        {
          "enters on loop": 3
        }
      ],
      "popIdol": "person",
      "policeOfficer": "witch",
      "journalist": "person",
      "informer": "person",
      "richStudent": "person",
      "henchman": "conspiracyTheorist"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "sacrilegiousMurder",
        "culprit": "journalist"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "popIdol"
      },
      {
        "day": 3,
        "incident": "darkRumor",
        "culprit": "henchman"
      },
      {
        "day": 4,
        "incident": "awakenedCurse",
        "culprit": "Hospital"
      },
      {
        "day": 5,
        "incident": "missingPerson",
        "culprit": "alien"
      },
      {
        "day": 6,
        "incident": "nightMadness",
        "culprit": "School"
      }
    ],
    "title": "Evil Primes",
    "mainPlot": [
      "cursedLand"
    ],
    "subPlots": [
      "thoseHabits",
      "witchsCurse"
    ],
    "story": "I tried writing a Prime Evil script. You can find the relevant info for Plots, Sub-Plots, Roles, and Incidents in the Cosmic Evil Player's Handbook on Board Game Geek's Cosmic Evil page as of my writing this script, and I haven't used any of the new characters so it should all be playable with the base game materials. Note the changes to the policeOfficer and informer's abilities as given in the Prime Evil Summary Sheet, which you can also find on Board Game Geek's Cosmic Evil page.",
    "tragedySet": "primeEvil",
    "mastermindHints": "Pretend the henchman is the curse-layer? Once the godlyBeing comes down, start putting down two curses. Try to get as many curses up and running as possible to be able to kill protagonists. Use Increasing Unease for getting :paranoia: on the doctor, to get him murdered, or the alien, to get the protagonists murdered. Try to have as many other potential death conditions as possible when you kill off the protagonists (Vampire, Werewolf, Nightmare, Night of Madness).",
    "specialRules": [
      ""
    ],
    "victory-conditions": "The Cursed Land rule for killing Protagonists; Loved One kills Protagonists\n\n",
    "creator": "Augustin-Louis Cauchy",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 7,
        "numberOfLoops": 3
      },
      {
        "difficulty": 7,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 113
      }
    ]
  },
  {
    "cast": {
      "shrineMaiden": "keyPerson",
      "alien": "cultist",
      "girlStudent": "deepOne",
      "classRep": "conspiracyTheorist",
      "informer": "faceless",
      "officeWorker": "wizard",
      "journalist": "person",
      "richStudent": "person",
      "boyStudent": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "boyStudent"
      },
      {
        "day": 3,
        "incident": "evilContamination",
        "culprit": "alien"
      },
      {
        "day": 4,
        "incident": "insaneMurder",
        "culprit": "informer"
      },
      {
        "day": 5,
        "incident": "massSuicide",
        "culprit": "girlStudent"
      }
    ],
    "title": "Eldritch Knowledge",
    "mainPlot": [
      "sacredWordsDagon"
    ],
    "subPlots": [
      "unsettlingRumor",
      "facelessGod"
    ],
    "story": "I tried writing a :cosmicMythology: script. You can find the relevant info for Plots, Sub-Plots, Roles, and Incidents in the Cosmic Evil Player's Handbook on Board Game Geek's Cosmic Evil page as of my writing this script, and I haven't used any of the new characters so it should all be playable with the base game materials. Note the changes to the informer's ability as given in the :cosmicMythology: Summary Sheet, which you can also find on Board Game Geek's Cosmic Evil page.",
    "tragedySet": "cosmicMythology",
    "mastermindHints": "If/when the protagonists get to 3 on the Extra Gauge, try to kill of the :deepOne: to finish them off, or the :faceless:/:deepOne: :goodwill: Refusel? You can effectively decide whether or not :massSuicide: does anything by placing or not placing :intrigue: on the :girlStudent: with the :girlStudent: on Day 5. Be careful of the shrineMaiden's ability to remove :intrigue: from the Shrine. Don't forget the Unsettling Rumor ability!",
    "specialRules": [
      ""
    ],
    "victory-conditions": "At least as much :intrigue: on Shrine as the Extra Gauge shows (end of loop); Key Person dies (immediate); Wizard dies (end of loop); Protagonists die from Extra Gauge being at 4 (end of day); Loop loss from Extra Gauge Being at 4 (start of loop)\n\n",
    "creator": "Augustin-Louis Cauchy",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 7,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 114
      }
    ]
  },
  {
    "cast": {
      "journalist": "ninja",
      "richStudent": "friend",
      "policeOfficer": "obstinate",
      "illusion": "factor",
      "forensicSpecialist": "person",
      "scientist": "person",
      "shrineMaiden": "person",
      "classRep": "person",
      "nurse": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "policeOfficer"
      },
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "scientist"
      },
      {
        "day": 3,
        "incident": "conspiracies",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "serialMurder",
        "culprit": "classRep"
      },
      {
        "day": 5,
        "incident": [
          "fakeIncident",
          "uproar"
        ],
        "culprit": "forensicSpecialist"
      }
    ],
    "title": "They're Out to Get You",
    "mainPlot": [
      "maleConfrontation"
    ],
    "subPlots": [
      "loveHateSpiral",
      "unsafeTrigger"
    ],
    "story": "",
    "tragedySet": "midnightZone",
    "mastermindHints": "Mastermind secret rule: The :intrigue: from Increasing Unease cannot be placed on the journalist.",
    "specialRules": [
      "The Mastermind has a secret rule that dictates part of his/her play."
    ],
    "victory-conditions": "2 :intrigue: on journalist at end of loop, kill classRep or illusion while 2 :intrigue: on City via Ninja ability or Conspiracies, Fake Incident\n\n",
    "creator": "Emily Forster",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 115
      }
    ]
  },
  {
    "cast": {
      "policeOfficer": "serialKiller",
      "henchman": "conspiracyTheorist",
      "classRep": "witch",
      "alien": "witch",
      "shrineMaiden": "friend",
      "patient": "friend",
      "officeWorker": "obstinate",
      "informer": "person",
      "journalist": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "missingPerson",
        "culprit": "henchman"
      },
      {
        "day": 2,
        "incident": "fakedSuicide",
        "culprit": "journalist"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "patient"
      },
      {
        "day": 4,
        "incident": [
          "fakeIncident",
          "confession"
        ],
        "culprit": "classRep"
      },
      {
        "day": 5,
        "incident": [
          "fakeIncident",
          "breakthrough"
        ],
        "culprit": "alien"
      },
      {
        "notTragedySpecified": true,
        "day": 6,
        "incident": "Diagonal Destruction 2",
        "culprit": "officeWorker"
      }
    ],
    "title": "The Greatest Greatness",
    "mainPlot": [
      "fatedConnections"
    ],
    "subPlots": [
      "witchesTeaTime",
      "loveHateSpiral"
    ],
    "story": "Idk",
    "tragedySet": "midnightZone",
    "mastermindHints": "",
    "specialRules": [
      "New incident: Diagonal Destruction 2: Everyone in the diagonally opposite location of the culprit dies"
    ],
    "victory-conditions": "Key person dies from Fated Connections, Friend dies (alien or patient), Fake Incident\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 8,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 116
      }
    ]
  },
  {
    "cast": {
      "henchman": "conspiracyTheorist",
      "alien": "fool",
      "classRep": "serialKiller",
      "shrineMaiden": "paranoiac",
      "richStudent": "therapist",
      "mysteryBoy": "poisoner",
      "journalist": "person",
      "patient": "person",
      "policeOfficer": "person",
      "officeWorker": "person",
      "doctor": "person",
      "informer": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "classRep"
      },
      {
        "day": 3,
        "incident": "serialMurder",
        "culprit": "henchman"
      },
      {
        "day": 4,
        "incident": "suspiciousLetter",
        "culprit": "shrineMaiden"
      },
      {
        "day": 5,
        "incident": "portent",
        "culprit": "alien"
      },
      {
        "day": 6,
        "incident": "silverBullet",
        "culprit": "informer"
      }
    ],
    "title": "Vroom Vroom Greatness",
    "mainPlot": [
      "quiltIncidents"
    ],
    "subPlots": [
      "smellGunpowder",
      "isolatedInstitutionPsycho"
    ],
    "story": "A chance for the greatness to vroom.",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "Be careful what you do.",
    "specialRules": [
      "Protagonists know when the incidents are and who does them except for day 5 but not which incident occurs on each day. The incident list is in the culprit list for the mastermind."
    ],
    "victory-conditions": "Smell of Gunpowder: 12 or more :paranoia: on the remaining characters, A Quilt of Incidents: Extra Gauge is on 3 or more, Poisoner (mysteryBoy)\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 7,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 117
      }
    ]
  },
  {
    "cast": {
      "girlStudent": "conspiracyTheorist",
      "richStudent": "keyPerson",
      "alien": "brain",
      "officeWorker": "magician",
      "shrineMaiden": "magician",
      "popIdol": "immortalRole",
      "patient": "person",
      "policeOfficer": "person",
      "boyStudent": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "serialMurder",
        "culprit": "boyStudent"
      },
      {
        "day": 2,
        "incident": "conspiracies",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "confession",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "serialMurder",
        "culprit": "boyStudent"
      },
      {
        "day": 5,
        "incident": "uproar",
        "culprit": "policeOfficer"
      },
      {
        "day": 6,
        "incident": "suicide",
        "culprit": "patient"
      }
    ],
    "title": "Night at the Vroomsbury",
    "mainPlot": [
      "secretRecord"
    ],
    "subPlots": [
      "showtimeDeath",
      "unansweredHeart"
    ],
    "story": "",
    "tragedySet": "midnightZone",
    "mastermindHints": "",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Secret Record Victory: Magician(s) or Brain is revealed (officeWorker, shrineMaiden, alien); Showtime of Death Victory: 6 or fewer characters left alive; Key Person dies (Rich Man’s Daughter)\n\n",
    "creator": "Augustin-Louis Cauchy",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 6,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 118
      }
    ]
  },
  {
    "cast": {
      "alien": "person",
      "boyStudent": "person",
      "forensicSpecialist": "person",
      "journalist": "serialKiller",
      "mysteryBoy": "cultist",
      "nurse": "friend",
      "patient": "person",
      "policeOfficer": "witch",
      "popIdol": "person",
      "richStudent": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "nurse"
      },
      {
        "day": 3,
        "incident": "spreading",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "popIdol"
      },
      {
        "day": 6,
        "incident": "hospitalIncident",
        "culprit": "patient"
      }
    ],
    "title": "The Medea Syndrome",
    "mainPlot": [
      "giantTimeBomb"
    ],
    "subPlots": [
      "hiddenFreak",
      "threadsFate"
    ],
    "story": "",
    "tragedySet": "basicTragedy",
    "mastermindHints": "This is a script geared toward Protagonists still a bit unused to Tragedy Looper. We have few roles, so we need to hide at least the Witch as long as we can, else it's gg. On the other hand, Protagonists will need to focus on :goodwill: abilities, and the policeOfficer may never reveal his mandatory refusel. For the first loop, try using the Cultist to kill the nurse via Faraway Murder. Don't be afraid of over using his ability, he would have been discovered anyway pretty soon. \nThen, 2nd loop, Threads of Fate will make triggering Incidents considerably easier. Keep killing the nurse if you can, or try a steady win via Hospital Incident. It's up to you. 3rd loop is a battle of wits. If you succeed, you may still lose at The Final Guess unless you managed to hide the Witch so be careful.\nThe Cultist is your best friend. Beware, the Protagonists will try to kill him with the Serial Killer. If you can, try to keep the journalist's role concelead at least for the first loop; that will make your plays a lot easier. Use Spreading to choose a character you want :paranoia: on next loop. Faraway Murder is your midgame major threat, and your best bet to kill the nurse.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1. At least 2 :intrigue: on the City (main plot: Giant Time Bomb victory condition)\n2. Kill the Friend (nurse, using Serial Killer's ability, Hospital Incident, Faraway Murder)\n3. Kill the Protagonists (using Hospital Incident)\n\n",
    "creator": "Elmore Maji",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 126
      }
    ]
  },
  {
    "cast": {
      "girlStudent": "keyPerson",
      "boyStudent": "killer",
      "informer": "brain",
      "classRep": "friend",
      "policeOfficer": "friend",
      "popIdol": "conspiracyTheorist",
      "patient": "person",
      "richStudent": "person",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 3
        }
      ],
      "henchman": "person"
    },
    "incidents": [
      {
        "notTragedySpecified": true,
        "day": 1,
        "incident": "Diagonal Destruction 3",
        "culprit": "henchman"
      },
      {
        "notTragedySpecified": true,
        "day": 2,
        "incident": "Diagonal Destruction 3",
        "culprit": "boyStudent"
      },
      {
        "notTragedySpecified": true,
        "day": 3,
        "incident": "Diagonal Destruction 3",
        "culprit": "girlStudent"
      },
      {
        "notTragedySpecified": true,
        "day": 4,
        "incident": "Diagonal Destruction 3",
        "culprit": "policeOfficer"
      },
      {
        "notTragedySpecified": true,
        "day": 5,
        "incident": "Diagonal Destruction 3",
        "culprit": "godlyBeing"
      },
      {
        "notTragedySpecified": true,
        "day": 6,
        "incident": "Diagonal Destruction 3",
        "culprit": "patient"
      }
    ],
    "title": "Diagonal Vroom",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "threadsFate",
      "circleFriends"
    ],
    "story": "Idk",
    "tragedySet": "basicTragedy",
    "mastermindHints": "?",
    "specialRules": [
      "Diagonal Destruction 3: everyone in the diagonally opposite location dies, the incident happens at :paranoia: limit -1, if 3 or more people die in a single incident, the protagonists are killed"
    ],
    "victory-conditions": "Key Person (boyStudent) dies\nFriend (classRep or policeOfficer) dies\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 129
      }
    ]
  },
  {
    "cast": {
      "informer": "keyPerson",
      "policeOfficer": "poisoner",
      "godlyBeing": [
        "fool",
        {
          "enters on loop": 2
        }
      ],
      "henchman": "twin",
      "richStudent": "paranoiac",
      "boyStudent": "serialKiller",
      "girlStudent": "friend",
      "mysteryBoy": "privateInvestigator",
      "classRep": "person",
      "officeWorker": "person",
      "transferStudent": [
        "person",
        {
          "enters on day": 4
        }
      ]
    },
    "incidents": [
      {
        "day": 1,
        "incident": "closedCircle",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "serialMurder",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "girlStudent"
      },
      {
        "day": 4,
        "incident": "serialMurder",
        "culprit": "henchman"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "policeOfficer"
      },
      {
        "day": 6,
        "incident": "serialMurder",
        "culprit": "godlyBeing"
      }
    ],
    "title": "Zexal?",
    "mainPlot": [
      "dropStrychnine"
    ],
    "subPlots": [
      "trickyTwins",
      "hiddenFreak"
    ],
    "story": "The Zexal has now taken over the world.",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "None",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Killing Key Person: informer\nKilling Friend: girlStudent\nLevel 2 Hospital Incident\nPoisoner Ability (Extra Gauge at 4)\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 134
      }
    ]
  },
  {
    "cast": {
      "soldier": "sacrifice",
      "ai": "cultist",
      "richStudent": "conspiracyTheorist",
      "scientist": "witness",
      "policeOfficer": "deepOne",
      "informer": "paranoiac",
      "transferStudent": [
        "person",
        {
          "enters on day": 4
        }
      ],
      "popIdol": "person",
      "boyStudent": "person",
      "officeWorker": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "insaneMurder",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "massSuicide",
        "culprit": "officeWorker"
      },
      {
        "day": 5,
        "incident": "uproar",
        "culprit": "soldier"
      },
      {
        "day": 6,
        "incident": "discovery",
        "culprit": "transferStudent"
      }
    ],
    "title": "Metamorphosis",
    "mainPlot": [
      "kingYellow"
    ],
    "subPlots": [
      "peopleWhoSaw",
      "whispersFromDeep"
    ],
    "story": "",
    "tragedySet": "cosmicMythology",
    "mastermindHints": "Loop 1, try to kill the informer from the Insane Murder. Keep trying to kill the informer in later loops, but you'll most likely win from increasing the extra gauge after loop 1. Try to bluff Choir to the Outer God at least in Loop 2.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Key Person (and :paranoia:c) ability. Sacrifice Ability. Increase the extra gauge by Witness, Deep One, or Discovery.\n\n",
    "creator": "Emily Forster",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 135
      }
    ]
  },
  {
    "cast": {
      "popIdol": "keyPerson",
      "policeOfficer": "brain",
      "informer": "killer",
      "forensicSpecialist": "twin",
      "scientist": "paranoiac",
      "alien": "fool",
      "shrineMaiden": "friend",
      "mysteryBoy": "obstinate",
      "richStudent": "person",
      "patient": "person"
    },
    "incidents": [
      {
        "notTragedySpecified": true,
        "day": 1,
        "incident": "Diagonal Destruction 3",
        "culprit": "mysteryBoy"
      },
      {
        "notTragedySpecified": true,
        "day": 2,
        "incident": "Diagonal Destruction 3",
        "culprit": "richStudent"
      },
      {
        "notTragedySpecified": true,
        "day": 3,
        "incident": "Diagonal Destruction 3",
        "culprit": "forensicSpecialist"
      },
      {
        "notTragedySpecified": true,
        "day": 4,
        "incident": "Diagonal Destruction 3",
        "culprit": "alien"
      },
      {
        "notTragedySpecified": true,
        "day": 5,
        "incident": "Diagonal Destruction 3",
        "culprit": "informer"
      },
      {
        "notTragedySpecified": true,
        "day": 6,
        "incident": "Diagonal Destruction 3",
        "culprit": "scientist"
      }
    ],
    "title": "Mystery Vroom",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "trickyTwins",
      "danceFools"
    ],
    "story": "?",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "?",
    "specialRules": [
      "Special Incident: Diagonal Destruction 3 (the culprit must kill up to 2 people in the diagonally opposite location)"
    ],
    "victory-conditions": "Kill Key Person: popIdol\nKill Friend: shrineMaiden\nKiller Ability (informer)\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 141
      }
    ]
  },
  {
    "cast": {
      "journalist": "keyPerson",
      "forensicSpecialist": "poisoner",
      "alien": "fool",
      "classRep": "paranoiac",
      "officeWorker": "conspiracyTheorist",
      "patient": "therapist",
      "doctor": "serialKiller",
      "nurse": "person",
      "henchman": "person",
      "policeOfficer": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "forensicSpecialist"
      },
      {
        "day": 2,
        "incident": "closedCircle",
        "culprit": "alien"
      },
      {
        "day": 3,
        "incident": "serialMurder",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "patient"
      },
      {
        "day": 5,
        "incident": "terrorism",
        "culprit": "nurse"
      },
      {
        "day": 6,
        "incident": "hospitalIncident",
        "culprit": "policeOfficer"
      }
    ],
    "title": "New Vroom",
    "mainPlot": [
      "dropStrychnine"
    ],
    "subPlots": [
      "isolatedInstitutionPsycho",
      "smellGunpowder"
    ],
    "story": "A vroom",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "?",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Killing Key Person: forensicSpecialist\nLevel 2 Terrorism\nLevel 2 Hospital Incident\n4 Extra Gauge Poisoner Ability\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 142
      }
    ]
  },
  {
    "cast": {
      "scientist": "keyPerson",
      "journalist": "sacrifice",
      "ai": "immortalRole",
      "transferStudent": [
        "serialKiller",
        {
          "enters on day": 3
        }
      ],
      "richStudent": "timeTraveler",
      "blackCat": "deepOne",
      "informer": "paranoiac",
      "teacher": "person",
      "shrineMaiden": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "evilContamination",
        "culprit": "scientist"
      },
      {
        "day": 4,
        "incident": "fireDemise",
        "culprit": "journalist"
      },
      {
        "day": 5,
        "incident": "uproar",
        "culprit": "teacher"
      }
    ],
    "title": "Cosmic Vroomology",
    "mainPlot": [
      "choirOuterGod"
    ],
    "subPlots": [
      "greatRace",
      "whispersFromDeep"
    ],
    "story": "",
    "tragedySet": "cosmicMythology",
    "mastermindHints": "",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Kill Key Person or :paranoia:c with Serial Killer or Uproar. Fire of Demise. Sacrifice ability. Time Traveler ability. 5 characters with :intrigue: at end of loop.\n\n",
    "creator": "Emily Karp",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 143
      }
    ]
  },
  {
    "cast": {
      "doctor": "timeTraveler",
      "alien": "serialKiller",
      "officeWorker": "lovedOne",
      "girlStudent": "lover",
      "richStudent": "friend",
      "boss": [
        "cultist",
        {
          "Turf": "Hospital"
        }
      ],
      "policeOfficer": "person",
      "patient": "person",
      "mysteryBoy": "conspiracyTheorist"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "foulEvil",
        "culprit": "boss"
      },
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "alien"
      },
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "doctor"
      },
      {
        "day": 5,
        "incident": "spreading",
        "culprit": "girlStudent"
      },
      {
        "day": 6,
        "incident": "butterflyEffect",
        "culprit": "patient"
      }
    ],
    "title": "Last Gift of the God of Freedom",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "loveAffair",
      "hiddenFreak"
    ],
    "story": "He's locked up, and he's going to ruin everything. He has to be set free to avert the tragedy that's about to occur.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Here are a few crazy things you can do to win:\n1. hide the serial killer loop 1 (and 2 if they let you) by moving her into the school and suiciding her using the conspiracy theorist (second loop you may need to move Mboy up instead). Then win via patient shenanigans, but use :intrigue: cards and missing person to bluff giant time bomb, sealed item, and/or sign with me\n2. use increasing unease to set off either the loved one, spreading, or butterfly effect, depending on what you need. spreading is good because you can remove :goodwill: from the time traveler (great). loved one is obviously insanely good. and butterfly effect sometimes needs that little extra push.\n3. try to kill the girlStudent (lover) before the RMD (friend). then you can autowin with the officeWorker's ability without showing the friend first. This doesn't apply if they already :goodwill:ed OW of course.\n4. bluff that the informer, cop, and patient are all cultists. you can lock your protags out of this info until they start pressuring you actively by threatening to win. remember you can use spreading to help deny this info as well. they should be strapped for :goodwill: between all the crazy abilities that are on the loose (doc, alien, OW, mboy, these two). Anyways, this is your final guess mainstay.\n5. don't forget to move the doctor out of the hospital. if you aggressively move him orthogonally to the hospital it's absolutely impossible for them to get him back inside. ideally you do this day 1 of later loops, then they're forced to use his 3-cost ability to deal with butterfly effect. You should basically always do this for the first two loops.\n6. use doctor aggressively if you need to, to obfuscate the serial killer. if you can hide SK for 2 loops you're really set\n7. the officeWorker and alien's movement restrictions make him a lot harder to kill. it takes a pretty huge amount of card investment to actually use his ability, and you typically can't do so while keeping the contheorist hidden either.\n8. Missing person can allow you to add :intrigue: to locations (useful for bluffing), kill the RMD (another wincon), and prevent the protagonists from figuring out the time traveler with the serial killer.\n9. the most important thing you can do is hide the alien-SK from them for as long as possible.\n10. That said, if the SK dies and the mysteryBoy is already gone, you're in kind of an awkward position. So prioritize saving the mysteryBoy in later loops.",
    "specialRules": [
      "Know that there is no godlyBeing in this script."
    ],
    "victory-conditions": "Butterfly Effect. doctor doesn't have enough :goodwill: (use spreading to help with this). Kill the friend. Loved one kills protagonists.\n\nThis script was inspired by the doctor's 3-cost :goodwill: ability. How could I make it crucial for the protagonists? This motivated doctor as time traveler and alien as serial killer, and patient doing butterfly effect. I then added the other stuff. It's a bit overkill honestly. Here are all the gimmicks in this script:\n\ngimmicks:\n1. the patient needs to leave the hospital in order to stop BFE\n2. spreading can ruin the doctor and motivate an alternate wincon\n3. the serial killer suicides early, making it difficult to pick out roles\n4. mboy is contheorist, leaking both subplots but making for a vicious contender\n5. officeWorker as loved one unfucks the role\n6. missing person works to cause or interrupt serial kills, and to facilitate bluffing loop 1\n\nThe one time I tested this, I was running the following changes: informer instead of boss, informer does hospital incident instead of foul evil. I'm thinking of switching the girlStudent and officeWorker too, but I'm not sure if that's too imbalanced.\n\nThe lesson of this script for me is that I may have tried to cram too many tricks and gimmicks in. Maybe it's better to write scripts with \"theses\" if that makes sense. Very tightly focused explorations of a concept. That's not what this is lol. Good luck.\n    boyStudent",
    "creator": "Redless",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 5
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 157
      }
    ]
  },
  {
    "cast": {
      "patient": "person",
      "doctor": "curmudgeon",
      "officeWorker": "person",
      "classRep": "conspiracyTheorist",
      "mysteryBoy": "cultist",
      "girlStudent": "friend",
      "informer": "person",
      "godlyBeing": [
        "brain",
        {
          "enters on loop": 3
        }
      ]
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "classRep"
      },
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "patient"
      }
    ],
    "title": "Love, or a Nightmare?",
    "mainPlot": [
      "lightAvenger"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "story": "Whispers of an ancient evil emanate from the Shrine, the stars are aligned, the fateful day approaches. Echoes of fear manifest in those vulnerable minds, manifesting in panic, while a vessel of the Dark Ones arrives in order to ensure their coming is set in stone. Meanwhile, a love triangle has developed in the school, centering around the mysterious new student. However, he is far from what he seems, and the battleground of love forming around him may brew desperate acts with fatal consequences. Can the Protagonists save the students from one another and protect the world from the darkness that claws at its seams?",
    "tragedySet": "firstSteps",
    "mastermindHints": "Recommended for experienced Protagonists and Masterminds that are not yet ready for Basic Tragedy. This script heavily relies on the Mastermind's ability to cover-up and bluff, as there are only two ways for the Mastermind to win, and both can be blocked relatively easily by the Protagonists. You do have some tools at your disposal, however.\n-The informer and officeWorker are good early game bait for the Protagonists to waste time on. Use them either as decoys or false leads as you play it out.\n-Be aggressive in using the abilities at your disposal in the beginning. Incidents are very important in this script, particularly on the first day. Use the combined abilities of your cards, the Conspiracy Theorist, and the Curmudgeon doctor to stack up :paranoia: and keep the Protagonists guessing as to the culprits of the incidents.\n-Between placing 2 :intrigue: on the School and placing 2 :intrigue: on the girlStudent (Friend), the Cultist's presence will quickly become apparent, which you can use to your advantage when covering up the main plot. However, if the Protagonists discover that the mysteryBoy is the Cultist, it will destroy all cover-ups related to A Place to Protect. Be prepared for this.\n-Once the godlyBeing arrives in Loop 3, how you want to use it will depend on how close the Protagonists are to figuring your Plots out. If they are near certain as to what they are, go loud with its Brain ability. You should be able to secure the win and prepare for the battle that is the last loop. Alternatively, if they are still unsure, use it only to secure the win, and preferably when there's room to deceive. Be aware that particularly experienced Protagonists may predict the arrival of the godlyBeing if you focus on placing :intrigue: on the Shrine.",
    "specialRules": [
      "N/A"
    ],
    "victory-conditions": "(1) [Loop End]: The girlStudent (Friend) is dead. (Faraway Murder)\n(2) [Loop End]: 2 :intrigue: on the Shrine. (Light of the Avenger)\n\n",
    "creator": "CipherCybr",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 159
      }
    ]
  },
  {
    "cast": {
      "patient": "conspiracyTheorist",
      "nurse": "keyPerson",
      "henchman": "brain",
      "popIdol": "killer",
      "policeOfficer": "person",
      "girlStudent": "friend",
      "shrineMaiden": "person",
      "classRep": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "spreading",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "murder",
        "culprit": "popIdol"
      },
      {
        "day": 4,
        "incident": "hospitalIncident",
        "culprit": "patient"
      }
    ],
    "title": "A Decadent Rebellion",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "story": "Young love often signaled a turbulent time of passion and change. Unfortunately for this city, when the boy became a patient at a mental ward, it also signaled the birth of a terrifying plan to elope, and the only thing in the lovers' way? The very nurse that had admitted him. And with the charm and resources of a very successful popIdol enrolled in an all-girls school? \"Collateral damage\" is all but certain, should the dark pair successfully enact their plan. But they are not outside of the public eye. A humble man of nine-to-fives is aware of the danger they pose, and must keep his secret, lest the lovers force him to take it to the grave.",
    "tragedySet": "firstSteps",
    "mastermindHints": "This is a very tricky :firstSteps: script for Protagonist and Mastermind alike. You have a lot of tools to achieve your victory each loop, and there are several big threats that will draw the attention of the Protagonists very early on. However, your job is far from easy. While you are able to pull off some interesting plays off the special rule, including preventing movement and forcing the Protagonists into a catch-22, the School is a safe zone against some of your plays, especially if the :popIdol: ends up there. There are also 3 potentially useful :goodwill: 2 abilities, and none of them have :goodwill: Refusel, so expect these to be used to full effect, on top of the :friend: (:officeWorker:) potentially being revealed before you can win using them. Finally, it is highly likely that the main plot will be discovered quickly, so you must take every opportunity to conceal the identities of your :keyPerson: (:nurse:) and :killer: (:popIdol:) during every loop but the last. Your most common route to victory early on will involve the :hospitalIncident:, using the :conspiracyTheorist: as the culprit and your cards, you can easily pull off 2 :paranoia: on the patient. Between killing the :friend: via the :murder: incident and a :intrigue: turbo combo (Day 1: +2 :intrigue: on :killer:, then +1 :intrigue: from :brain:), you also have a couple of backup plans in case things go awry. If the Protagonists' strategy relies on a specific :goodwill: ability (notably the :nurse: or :classRep:), you can have the :henchman: steal 2 :goodwill: from that character using the :spreading: incident, placing it on a useless :goodwill: ability (as his incident has already triggered).",
    "specialRules": [
      "[Always]: Characters with <:boy:> or <:man:> cannot move into the School by any means."
    ],
    "victory-conditions": "(1) The nurse is dead (:keyPerson:) [:killer:'s Ability, :murder: Incident, :hospitalIncident:] ; (2) The :friend: (:officeWorker:) is dead at the end of the loop [:murder: Incident, :hospitalIncident:] ; (3) The Protagonists are killed [:killer:'s Ability, :hospitalIncident:].\n\n",
    "creator": "CipherCybr",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 161
      }
    ]
  },
  {
    "cast": {
      "shrineMaiden": "friend",
      "boyStudent": "conspiracyTheorist",
      "girlStudent": "person",
      "richStudent": "person",
      "officeWorker": "cultist",
      "informer": "keyPerson",
      "doctor": "curmudgeon",
      "nurse": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "doctor"
      },
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "shrineMaiden"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "officeWorker"
      }
    ],
    "title": "The World Evervolving",
    "mainPlot": [
      "placeProtect"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "story": "She was the key. At least, the humble maiden of the shrine was made the key to this Pandora's Box. From anywhere else, it looked like a regular place of worship. Step within the sacred grounds, however, and a world twisting and contorting would greet the eyes of the unwary. Having discovered this phenomenon, the Protagonists must work with and protect a woman interested in the shifting shrine, alongside the key, lest the rotations spread beyond the shrine.",
    "tragedySet": "firstSteps",
    "mastermindHints": "//EFFECTS OF SPECIAL RULE// Day 1: Place 1 :paranoia: on all characters in the Shrine. // Day 2: Replace all :paranoia: on all characters in the Shrine with :goodwill:, and place 1 :goodwill: on each character that had :paranoia: replaced. // Day 3: Replace all :goodwill: on all characters in the Shrine with :intrigue:, and place 1 :intrigue: on each character that had :goodwill: replaced. // Day 4: All characters with at least 2 :intrigue: in the Shrine die. // MASTERMIND HINTS: First of all, kudos to those that get the reference. This script is as much chaos for the Mastermind as it is for the Protagonists. You should aim to get the Friend killed on Loop 1 and 2 (Special Rule and Faraway Murder), and try to save the Key Person's death and the A Place to Protect loss condition for the last two loops. Be very aware of how the board will change with the moving Shrine, and be especially careful of movement restrictions on certain characters. One way to quickly kill someone is to place +2 :intrigue: on someone and then send them to the Shrine, but the Protagonists will not fall for this twice. It is possible for the Protagonists to exploit the Special Rule on Day 2 to gain more :goodwill: on specific characters, so be prepared to counter this. With so much potential :paranoia: on the board, it can be fairly easy to cover up the culprits of Incidents in the first couple of loops. On the final loop, aim to trigger Missing Person and move the shrineMaiden to the School, and take advantage of the age old catch 22: +1 :intrigue: on the School, or +2 somewhere worse. The key to victory here is to take advantage of how much you can bamboozle the Protagonists with an ever-shifting board and a terrifying amount of :paranoia: and :intrigue: generation.",
    "specialRules": [
      "[At Day End]: Move the Shrine anti-clockwise on the board and switch it with the Location in the slot it moves to (Example: Night 1 - Switch the Shrine and Hospital). Then, an effect (only known by Mastermind) is applied to all characters in that location."
    ],
    "victory-conditions": "The shrineMaiden (Friend) is dead: Special Rule, Faraway Murder // The informer (Key Person) is dead: Special Rule, Faraway Murder // 2 :intrigue: on the School (A Place to Protect).\n\n",
    "creator": "CipherCybr",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 162
      }
    ]
  },
  {
    "cast": {
      "girlStudent": "keyPerson",
      "shrineMaiden": "killer",
      "richStudent": "brain",
      "boyStudent": "conspiracyTheorist",
      "officeWorker": "serialKiller",
      "nurse": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "murder",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "girlStudent"
      }
    ],
    "title": "United We Fall",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "shadowRipper"
    ],
    "story": "Alternative first script.",
    "tragedySet": "firstSteps",
    "mastermindHints": "",
    "specialRules": [
      "Final Guess is allowed"
    ],
    "victory-conditions": "(a) Killing the Key Person (Killer / Serial Killer / Murder / Suicide)\n(b) 4 :intrigue: in Killer\n\n",
    "creator": "ChrisMaxheart",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 2
      },
      {
        "difficulty": 0,
        "numberOfLoops": 3
      },
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 163
      }
    ]
  },
  {
    "cast": {
      "doctor": "person",
      "patient": "conspiracyTheorist",
      "shrineMaiden": "brain",
      "illusion": "serialKiller",
      "officeWorker": "person",
      "mysteryBoy": "keyPerson",
      "classRep": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "murder",
        "culprit": "patient"
      },
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "mysteryBoy"
      }
    ],
    "title": "Those from Beyond",
    "mainPlot": [
      "lightAvenger"
    ],
    "subPlots": [
      "shadowRipper"
    ],
    "story": "From the dark they come, living shadows pressed into human form, their hunger for living souls insatiable. The brother, a former forerunner now living life peacefully as a human, and the sister, bloodthirsty and seeking vengeance for being blocked from our world by her sibling. With her summoning by an unwitting shrineMaiden, tendrils flow outward from that holy place, touching many with a desire to release the essence of the living for her consumption and empowerment, in the only way they know how: death and desecration.",
    "tragedySet": "firstSteps",
    "mastermindHints": "IMPORTANT: You need the expansion character illusion to play this script. A tricky script with an overarching win condition, but with a few nasty surprises for the Protagonists to disrupt certain strategies. Your main focus will be :intrigue: on the Shrine: once that is achieved, damage control to prevent any more information falling into the hands of the Protagonists is key. It might be tempting to have the mysteryBoy (Key Person) killed by the illusion (Serial Killer) for a quick loop end, however it will reveal the main plot and the role of a wildcard character, so it is best saved for the last loop. One potential early strategy is to move another character into the Shrine and then pump it with :intrigue: via the Brain and your cards, to hide both the Brain and the Serial Killer while guaranteeing an early loop win. You should save Incidents for Loop 2, with Missing Person able to add 1 :intrigue: to the Shrine, and both the Suicide and Murder able to kill the Key Person as a last resort. You should try to get the doctor killed if you can, as his :goodwill: ability will either force you to block it every turn, or render the patient's Conspiracy Theorist ability useless.",
    "specialRules": [
      "N/A"
    ],
    "victory-conditions": "(1): The mysteryBoy (Key Person) is dead. <Serial Killer> <Murder> (2) [Loop End]: 2 :intrigue: on the Shrine. <Brain's Ability> < :intrigue:+ cards> <Missing Person>\n\n",
    "creator": "CipherCybr",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 164
      }
    ]
  },
  {
    "cast": {
      "soldier": "person",
      "blackCat": "fool",
      "shrineMaiden": "person",
      "ai": "poisoner",
      "boss": [
        "privateInvestigator",
        {
          "Turf": "School"
        }
      ],
      "journalist": "keyPerson",
      "officeWorker": "person",
      "youngGirl": "serialKiller",
      "henchman": "conspiracyTheorist",
      "transferStudent": [
        "friend",
        {
          "enters on day": 2
        }
      ]
    },
    "incidents": [
      {
        "day": 1,
        "incident": "closedCircle",
        "culprit": "youngGirl"
      },
      {
        "day": 2,
        "incident": "closedCircle",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "closedCircle",
        "culprit": "soldier"
      },
      {
        "day": 4,
        "incident": "closedCircle",
        "culprit": "ai"
      },
      {
        "day": 5,
        "incident": "closedCircle",
        "culprit": "blackCat"
      }
    ],
    "title": "Break through the blockade？",
    "mainPlot": [
      "dropStrychnine"
    ],
    "subPlots": [
      "smellGunpowder",
      "masterDetective"
    ],
    "story": "",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "Always use the first day's events（boss ability）and try to kill friend.\nSet :paranoia:+1】to boss and youngGirl,use CT‘s ability to youngGirl EVERY DAY,you will win（by 12 :paranoia:）.\nHide KP until the end.Poisoner only kill boss(undead) or AI itself\nSpecial rule is a trap for protagonists.\n*The protagonist's method of preventing victory*：Day 1 move boss or 2 characters into school and move heachman out,D2-4 use 3 :paranoia: -1】,D4-5 use youngGirl's teleport ability and kill any one have :paranoia:.",
    "specialRules": [
      "Mastermind removes :paranoia: -1】 from hand",
      "henchman starts always in the School"
    ],
    "victory-conditions": "1.Kill friend（SK and Poisoner EX2）\n2.12P on the remaining characters(Smell of gunpowder)\n3.Kill protagonists(Poisoner EX4)\n4.Kill KP（SK and Poisoner EX2）\n\n",
    "creator": "xinshouzhier",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 4,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 165
      }
    ]
  },
  {
    "cast": {
      "shrineMaiden": "vampire",
      "littleSister": "conspiracyTheorist",
      "blackCat": "serialKiller",
      "soldier": "showOff",
      "doctor": "coward",
      "ai": "coward",
      "copycat": "vampire",
      "classRep": "witch",
      "mysteryBoy": "nightmare",
      "godlyBeing": [
        "keyPerson",
        {
          "enters on loop": 65535
        }
      ]
    },
    "incidents": [
      {
        "day": 1,
        "incident": "nightMadness",
        "culprit": "School"
      }
    ],
    "title": "A Day Without God",
    "mainPlot": [
      "nobleBloodline"
    ],
    "subPlots": [
      "panicObsession",
      "peopleDontListen"
    ],
    "story": "Whether you have played Prime Evil or not,you can play this script.\nThis is a puzzle game with very unique structure.",
    "tragedySet": "primeEvil",
    "mastermindHints": "Shrine[ :intrigue:+1] City [ :intrigue:+2] Little Sister :paranoia:+1]",
    "specialRules": [
      "The play of the mastermind is determined by the script.\nAll characters are not person."
    ],
    "victory-conditions": "The mastermind always wins.\nThe protagonist needs to deduce the identity of all the characters.\n\n",
    "creator": "xinshouzhier",
    "daysPerLoop": 1,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 65535
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 166
      }
    ]
  },
  {
    "cast": {
      "henchman": "ninja",
      "illusion": "prophet",
      "alien": "person",
      "sacredTree": "magician",
      "richStudent": "immortalRole",
      "copycat": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "henchman"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "copycat"
      }
    ],
    "title": "Deserted Street",
    "mainPlot": [
      "maleConfrontation"
    ],
    "subPlots": [
      "showtimeDeath",
      "worshippersApocalypse"
    ],
    "story": "This script is used for a 2-player game only. And discussion is prohibited during each loop.",
    "tragedySet": "midnightZone",
    "mastermindHints": "Mix the winning conditions with 2 :intrigue: on the Shrine. Never let copycat suicide at 1 :paranoia:, using forbid :paranoia: and :paranoia: -1. Use movement to stop alien from killing RMD and Sacred Tree.",
    "specialRules": [
      "All cards in mastermind's hand have (1x∞)."
    ],
    "victory-conditions": "The mastermind will always win. The only thing the mastermind can do is try to slow down the protagonists seeing all the truth. And the protagonist's playing skill is expressed by winning with the least loop number.\n\n",
    "creator": "Lizabove",
    "daysPerLoop": 3,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 666
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 167
      }
    ]
  },
  {
    "cast": {
      "patient": "killer",
      "nurse": "person",
      "doctor": "friend",
      "godlyBeing": [
        "keyPerson",
        {
          "enters on loop": 2
        }
      ],
      "shrineMaiden": "person",
      "officeWorker": "friend",
      "popIdol": "brain",
      "richStudent": "conspiracyTheorist",
      "mysteryBoy": "factor"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "murder",
        "culprit": "shrineMaiden"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "popIdol"
      },
      {
        "day": 4,
        "incident": "hospitalIncident",
        "culprit": "patient"
      },
      {
        "day": 5,
        "incident": "suicide",
        "culprit": "officeWorker"
      },
      {
        "day": 6,
        "incident": "suicide",
        "culprit": "godlyBeing"
      }
    ],
    "title": "Madness over Madness",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "circleFriends",
      "threadsFate"
    ],
    "story": "The protagonists are determined to stop the killer in the city, only to be met by more unknown deaths in every corners and alleys. Now all the odds are turned against them, while risking the lives of their friends.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "(a) Kill Friend using incidents\n(b) Kill Key Person using incidents \n(c) Kill Protagonist using Hospital Incident / Killer ability",
    "specialRules": [
      ""
    ],
    "victory-conditions": "\n\n",
    "creator": "ChrisMaxheart",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      },
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 169
      }
    ]
  },
  {
    "cast": {
      "nurse": "person",
      "patient": "person",
      "alien": "person",
      "shrineMaiden": "lovedOne",
      "officeWorker": "person",
      "policeOfficer": "lover",
      "richStudent": "person",
      "boyStudent": "conspiracyTheorist",
      "classRep": "keyPerson"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "boyStudent"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "policeOfficer"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "patient"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "shrineMaiden"
      },
      {
        "day": 6,
        "incident": "suicide",
        "culprit": "classRep"
      }
    ],
    "title": "Chain of Hatred",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "loveAffair",
      "paranoiaVirus"
    ],
    "story": "A single incident caused a test tube break, infecting nearby bystanders, killing a special someone, causing the shrineMaiden to run amok, and it becomes neverending chain of hatred.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "(a) 3 :paranoia: and 1 :intrigue: in Loved One\n(b) 2 :intrigue: in Key Person\n(c) Key Person Suicide / Faraway Murder",
    "specialRules": [
      "N/A"
    ],
    "victory-conditions": "\n\n",
    "creator": "ChrisMaxheart",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      },
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 170
      }
    ]
  },
  {
    "cast": {
      "patient": "person",
      "alien": "timeTraveler",
      "shrineMaiden": "cultist",
      "blackCat": "person",
      "officeWorker": "person",
      "policeOfficer": "person",
      "journalist": "person",
      "henchman": "conspiracyTheorist",
      "youngGirl": "friend",
      "transferStudent": [
        "friend",
        {
          "enters on day": 2
        }
      ]
    },
    "incidents": [
      {
        "day": 2,
        "incident": "farawayMurder",
        "culprit": "henchman"
      },
      {
        "day": 4,
        "incident": "butterflyEffect",
        "culprit": "youngGirl"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "transferStudent"
      }
    ],
    "title": "death before alive",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "circleFriends",
      "unsettlingRumor"
    ],
    "story": "",
    "tragedySet": "basicTragedy",
    "mastermindHints": "The transferStudent had no enter day defined, so I set it randomly to two\n\nmake sure that incident \"Butterfly Effect\" happen and before end loop, there are 2 :intrigue: on the Shrine.\nIf you can, keep transferStudent alive all the loop.\nIf you can, do not let incident \"Hospital Incident\" really happen.\nUse incident \"Faraway Murder\" if they understand how to prevent incident \"Butterfly Effect\"",
    "specialRules": [
      "Mastermind removes \"Forbid :goodwill:\" from his hand. It cannot be used in any loop"
    ],
    "victory-conditions": "If you find the true way, you will actually win whatever mastermind do.\n\n",
    "creator": "Aster Mentha",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 173
      }
    ]
  },
  {
    "cast": {
      "alien": "person",
      "patient": "person",
      "nurse": "friend",
      "popIdol": "person",
      "journalist": "conspiracyTheorist",
      "policeOfficer": "cultist",
      "boyStudent": "brain",
      "girlStudent": "person",
      "classRep": "friend"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "popIdol"
      },
      {
        "day": 4,
        "incident": "murder",
        "culprit": "patient"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "classRep"
      },
      {
        "day": 6,
        "incident": "farawayMurder",
        "culprit": "policeOfficer"
      }
    ],
    "title": "Responsible Women",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "circleFriends",
      "paranoiaVirus"
    ],
    "story": "A short, simple script with a satisfying mystery.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Protagonists can figure out the Friends and Paranoia Virus more easily than the Brain, so you should aim to end the first two loops by the death of a Friend. On the third loop, you'll have to win via :intrigue: on the Shrine. You can keep the Brain and Conspiracy Theorist hidden, but the protagonists will be able to suss it out by using Paranoia Virus to create Serial Killers, so you'll have to try to avoid using either of them, or at least keep them together.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "2 :intrigue: on Shrine\nFriend dies (via Paranoia Virus, Murder, or Faraway Murder)\n\n",
    "creator": "Kitsunin",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 174
      }
    ]
  },
  {
    "cast": {
      "youngGirl": "friend",
      "officeWorker": "cultist",
      "doctor": "serialKiller",
      "shrineMaiden": "brain",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 3
        }
      ],
      "informer": "person",
      "boyStudent": "conspiracyTheorist",
      "classRep": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "butterflyEffect",
        "culprit": "youngGirl"
      },
      {
        "day": 2,
        "incident": "butterflyEffect",
        "culprit": "shrineMaiden"
      },
      {
        "day": 4,
        "incident": "foulEvil",
        "culprit": "godlyBeing"
      }
    ],
    "title": "The Ubiquitous, Yet Unseen Treasure",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "paranoiaVirus",
      "hiddenFreak"
    ],
    "story": "A Reporter has just arrived in the City to try and uncover the truth locked away in the Shrine for centuries. As she makes her way to the Shrine, she meets up with the young shrineMaiden who appears to know more than she lets on. However, as she starts trying to snoop around; she can't but help the feeling that she's being followed. Meanwhile in the School, a youngGirl has lost her plushie and two of her seniors have stayed till the break of dusk to try and help her. The classRepresentative is however becoming uneasy, feeling a set of human eyes peer through the windows, every now and again; she starts becoming paranoid. Curses, curses and curses. The Shrine's curse! The girl cackles, an infatuated grin forms on her face as :paranoia: takes ahold of her body. Bloodshed was all she could see. Eyes peer from the horizon, the empty sky; a God looks down upon the Shrine and smiles, for the true source of power shall remain hidden. But to Its sudden dismay, it appears that everything has gotten reverted..",
    "tragedySet": "basicTragedy",
    "mastermindHints": "If you want to effectively win on this Script, on the first day; you'll need to focus on two things.\n\n1. Making certain that the youngGirl is dead.\n2. Making certain that you begin making Cover Stories for 'The Sealed Item' from Loop 1.\n\nIf you cannot do this, then you're forced to play :intrigue: on the Shrine, making the Final Guess solvable near instantly. Try not to fall into a trap.\n\nIf the Protagonists ignore the youngGirl, then you have two choices: \n\nMove the boyStudent & classRep out, and move the doctor to the School; revealing your Serial Killer.\nOR\nTrigger Butterfly Effect, and transform the classRep into a Serial Killer by pushing hard for :paranoia:.\n\nIf the youngGirl is not ignored, you are effectively forced to play Forbid :goodwill: on her lest you want an early game over.\n-\nFrom there on out, your goal is to make sure to kill the Friend every single loop, which will become detrimentally harder during Loop 2, onwards. However, you still have the cover up stories of 'Change of Future' and 'Giant Time Bomb' at this stage; so if you cannot kill the youngGirl, push for :intrigue: by moving Cultist to the Shrine.\n\nYou should have won here, but now; the Protagonists should know that it cannot be Giant Time Bomb. \n\nNow, Protagonists will start focusing on trying to find the Time Traveler; so expect heavy :goodwill: pushes during Loop 3, also using the Serial Killer (If found). Thankfully, you have a new card to play with; The 'godlyBeing' who triggers Foul Evil. \n\nA word of warning, if you do trigger this incident successfully, the Protagonists will almost certainly deduce that it's The Sealed Item, and by this point will have known for certainty that 'A Hidden Freak' is in play, and depending on skill and playstyles; Paranoia Virus too.\n\nThe 4th Loop is all about trying to win without revealing who the brain is. Manage that, and you should have won. If not, it'll be hard. But you are the only one who can do it. I believe in you. Do not let them crush our wonderfully-crafted timeline, kyihihihi~..",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "At least 2 :intrigue: on the Shrine by Loop End.\n• Friend (youngGirl) is dead by Loop End.\n\n",
    "creator": "Chronicle [RealBernkastel]",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 176
      }
    ]
  },
  {
    "cast": {
      "officeWorker": "conspiracyTheorist",
      "boyStudent": "brain",
      "richStudent": "killer",
      "teacher": "friend",
      "nurse": "friend",
      "policeOfficer": "keyPerson",
      "patient": "person",
      "classRep": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "foulEvil",
        "culprit": "teacher"
      },
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "murder",
        "culprit": "patient"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "policeOfficer"
      },
      {
        "day": 5,
        "incident": "murder",
        "culprit": "classRep"
      }
    ],
    "title": "The Outsider's Wish",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "threadsFate",
      "circleFriends"
    ],
    "story": "A policeOfficer and his co-worker arrive at the City after receiving a call from the School teacher that there's been an incident which; while now, subsided, still requires looking into. The victim of the incident was brutally injured after falling from a flight of stairs; of which they claim was not accidental. While the patient is now being looked after in the Hospital, the policeOfficer's co-worker is not sure of this. A worrisome lad, he's always been. After all, they're just students; what's the worse that could brew? A Tragedy? Pfft~",
    "tragedySet": "basicTragedy",
    "mastermindHints": "This script is very flexible and can be played in a multitude of ways; all of which involve killing a SINGLE Friend and keeping the Key Person alive until necessary. Due to this, a general hint would be to try and hide the actual Serial Killer which might not be easy depending on the protagonists; and using the classRep as a decoy Serial Killer.\n\nOn Day 3, Triggering the Murder Incident is paramount if you were not able to kill the teacher prior to this day.\n\nThe Faraway Murder can be used in a plethora of ways; and it is up to you to figure out the best way at that current situation.\n\nTry to avoid the Suicide Incident. You do NOT want to trigger it. Protagonists might be able to use this to their advantage.",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Key Person (policeOfficer) dies. \n• At least ONE Friend (nurse/teacher) is dead by Loop End.\n\n",
    "creator": "Chronicle [RealBernkastel]",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 177
      }
    ]
  },
  {
    "cast": {
      "popIdol": "keyPerson",
      "patient": "lover",
      "classRep": "conspiracyTheorist",
      "shrineMaiden": "person",
      "nurse": "person",
      "girlStudent": "person",
      "richStudent": "lovedOne",
      "officeWorker": "killer",
      "journalist": "brain"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "girlStudent"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "shrineMaiden"
      }
    ],
    "title": "School Days",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "loveAffair",
      "paranoiaVirus"
    ],
    "story": "",
    "tragedySet": "basicTragedy",
    "mastermindHints": "It's untested",
    "specialRules": [
      ""
    ],
    "victory-conditions": "\n\n",
    "creator": "Test",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 178
      }
    ]
  },
  {
    "cast": {
      "classRep": "person",
      "shrineMaiden": "conspiracyTheorist",
      "richStudent": "person",
      "doctor": "person",
      "policeOfficer": "cultist",
      "journalist": "brain"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "policeOfficer"
      },
      {
        "day": 4,
        "incident": "foulEvil",
        "culprit": "doctor"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "shrineMaiden"
      }
    ],
    "title": "To Survive, We Must Fight!",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "threadsFate",
      "paranoiaVirus"
    ],
    "story": "This is a fun script that can be won by the Protagonists if embrace :paranoia:, exploiting Threads of Fate and Paranoia virus to turn characters with Person roles into Serial killers. If the Protagonists can successful kill the Mastermind’s Brain, Cultist and/or the culprit of Foul Evil, the Mastermind will not have enough resources to defeat the Protagonists via The Sealed Item.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Loop 1: Don’t put out any paranoid in round 1. Move as many characters as you can into the Shrine and get two :intrigue: on the Shrine via the Cultist on the last day (try to never activate the Brain or Cultist unless the Conspiracy Theorist is present to throw off the Final Guess). During the first loop, Protagonists may be thinking that there is a Time Traveler in the game because of the Butterfly Effect incident (which is just a cover up, the Butterfly Effect is unlikely to do anything significant and there is not a Time Traveler) and they may be will be focused on putting at least two :goodwill: on every character to avoid losing from Time Traveler. Most likely the players will know the main plot is The Sealed Item after the first loop but if you can prevent them from getting two :goodwill: on every character then it at least leaves the Time Traveler possibility open as the cause of their loss.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Having two :intrigue: on the Shrine at the end of the Loop is the only way for the Protagonists to lose. However the Mastermind has many ways of putting :intrigue: on the Shrine:\n\nOriginally uploaded by speter79 of boardgamegeek.com\n    Alan Tran\n\nStarting Scenario\nYour merry band of time travels fly through the crystalline-blue time streams. You see something disturbing in the strands of time. A Suicide… some Foul Evil brewing… and an innocuous action that causes Butterfly Effect all appear to lead up to a horrific tragedy. You detect the manipulation of the Mastermind at work and decide to investigate.\n    Alan Tran\n\nIf Loop One Fails: \nAll of a sudden you are thrown back into the time stream. You have failed but it happened so suddenly that you don’t know how it happened or what caused it. You hear the arrogant laughter of the Mastermind echoing through the time stream. You loop back to investigate.\n\n    Alan Tran\n\nIf Loop Two Fails: \nTentacle monster burse forth from the Shrine. They pour over the City, squeezing all the characters into jelly. The screams of the populace are forever etched into your psyche. The time stream becomes dark and think as the Mastermind attempts to lock in this reality. You manage to loop back for a final attempt at saving the City.\n    Alan Tran\n\nIf Loop Three Fails: \nA bright light, brighter than the sun, appears at the Shrine and Tentacle monster burse forth. To your horror, you see that there are people chanting around the Tentacle portal. Some there are people in this Tentacle Cult that are summoning the monsters. Due to the bright light you can’t make out the features of their faces but it must be characters that you have come into contact with in this Tentacle cult. A huge tentacle monster slithers toward you. The time stream becomes dark and think as the Mastermind attempts to lock in this reality. As the tentacle reaches for your leg you manage to loop back for a final attempt at saving the City.\n    Alan Tran\n\nIf the Protagonists Fail Loop Four: \nThe city is in ruins. Tentacle monsters everywhere! The mangled bodies of the populace cause your stomach to retch. The time stream is opaque and feels like a tub of molasses. You can’t escape this reality. Tentacle monsters are all around you, suckers extending for you face. Your only chance is to shatter the foundation of the time stream that the Mastermind has created. If you can perfectly deduce his plots, then you might be able to shatter this reality and get the threads of time flowing again. Make your final guess!\n    Alan Tran\n\nIf the Protagonists Prevent the Tragedy: \nThe evil cabal that sought to unleash an army of demonic tentacle monsters on the city has been stopped. The Mastermind used the journalist as the Brain behind his evil scheme. The policeOfficer harbored a dark hatred of the students at the school and joined the tentacle monster cabal as a Cultist to unleash horrors upon them. The shame of this dark desire put thoughts of Suicide into the policeOfficer’s head. \n\nThe doctor was an unwitting pawn of the cabal as they used him to spread Foul Evil through his legitimate medical practice by tainting his instruments of healing with demonic curses.\n\nThe three girls that foiled the plot, with inspiration from the Protagonists, were two students and the local shrineMaiden. The shrineMaiden led the group. She had a reputation for being a nutty Conspiracy Theorist before this incident. You wonder what led enabled this child to take down the tentacle cult. Was it the simple of a child that led to Butterfly Effect the Mastermind couldn’t predict or just her unbridled raw :paranoia: that ultimately took down the tentacle cult? It impossible to know for sure.\n\nAs your reenter the time stream you sense the presence of the Mastermind retreating upon your arrival. Gone is arrogance you have always sensed from the Mastermind entity. For an instant you detect the hint of a different emotion as he vanishes from you perceptions… fear.\n    Alan Tran\n\nIf the Leader Succeeds on the Final Guess: \nThe evil cabal that has succeeded in unleashing an army of demonic tentacle monsters on the city. However, you have unraveled the Mastermind’s plots. The Mastermind used the journalist as the Brain behind his evil scheme. The policeOfficer harbored a dark hatred of the students at the school and joined the tentacle monster cabal as a Cultist to unleash the horror upon them. The shame of this dark desire put thoughts of Suicide into the policeOfficer’s head. \n\nThe doctor was an unwitting pawn of the cabal as they used him to spread Foul Evil through his legitimate medical practice by tainting his instruments of healing with demonic curses.\n\nThe three girls that tried and failed to foil the plot were two students and the local shrineMaiden. The shrineMaiden led the group. She was viewed as bright Conspiracy Theorist before this incident, but her :paranoia: and the randomness of a Butterfly Effect ultimately were not enough to stop the tentacle cult. However, the where they failed, you have succeeded.\n\nWith a boom heard through the City, the static black threads of the Mastermind’s time stream break into chunks and are washed away. The horrible reality is annihilated and the healthy crystalline-blue time stream resumes flowing. You are free and back in the time stream. The Mastermind’s presence is nowhere to be seen. He has escaped. You wonder how many times you will be able to survive these close calls with death.\n    Alan Tran\n\nIf the Leader Fails on the Final Guess: \nThe Mastermind’s plots prove too difficult to untangle. The tentacle monsters envelope you. To your horror, you realize that they are not trying to squeeze you into jelly, only take you prisoner. You are taken to the school where to face the surviving members of the cult. The cult had many members from the City and was run by the journalist (the Brain). The policeOfficer harbored a dark hatred of the students at the school and joined the tentacle monster cabal as a Cultist to unleash horrors upon them. The shame of this dark desire put thoughts of Suicide into the policeOfficer’s head. \n\nOver the days of torture you piece together that the doctor was an unwitting pawn of the cabal as they used him to spread Foul Evil through his legitimate medical practice by tainting his instruments of healing with demonic curses.\n\nThe three girls that tried and failed to foil the plot were two students and the local shrineMaiden. The Shield Maiden led the group. She was viewed as a bright Conspiracy Theorist before this incident, but her :paranoia: and the randomness of a Butterfly Effect ultimately were not enough to stop the tentacle cult. \n\nOne by one you see the innocent charters tortured and killed by tentacle monsters for the entertainment of the cabal. The time streams are so dark you can barely perceive their existence. Did they ever exist at all or have you always existed in this world of pain and tentacles. There is no escape. The only question left is whether your mind or body will be first to break beyond repair. You know now that will be up to the Mastermind. He will is makes your reality.\n    Alan Tran",
    "creator": "speter79",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 6,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 2
      }
    ]
  },
  {
    "cast": {
      "girlStudent": "factor",
      "classRep": "conspiracyTheorist",
      "richStudent": "person",
      "shrineMaiden": "person",
      "doctor": "person",
      "patient": "timeTraveler",
      "popIdol": "person",
      "informer": "person",
      "officeWorker": "cultist"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "classRep"
      },
      {
        "day": 3,
        "incident": "foulEvil",
        "culprit": "shrineMaiden"
      },
      {
        "day": 4,
        "incident": "missingPerson",
        "culprit": "richStudent"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "popIdol"
      },
      {
        "day": 7,
        "incident": "hospitalIncident",
        "culprit": "doctor"
      }
    ],
    "title": "Twelve Monkeys",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "unsettlingRumor",
      "unknownFactor"
    ],
    "story": "A time traveller has travelled back in time to avoid a disastrous pandemic which originated in the city. He is immediately believed crazy and hospitalized. Unknown to him, however, the real trigger of the pandemic was not the :hospitalIncident:, but the event on a previous day when the :popIdol: was infected, becoming patient zero and transmitting the disease in the following weeks during her world tour. At the same time, other people mill around inciting strange events and muddling the investigation on what really happened.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "This script should be very tricky for the Mastermind, as it relies completely on subterfuge. The key is to make the Protagonist waste cards on preventing the :hospitalIncident:, while in reality you don't want to trigger it at all (since it will reveal the :patient: as the Time Traveller when he will not die). Spread :paranoia: liberally in order to trigger Incidents and use every opportunity to lay down :intrigue: and hit every loss condition possible at the end of the loop (basically try having 2 :intrigue: on all locations and all girls). Thanks to the informer, the subplots will be known at some time, so the idea is to keep the Main Plot hidden as much as possible. Also, the :officeWorker: having mandatory :goodwill: Refusel will whittle down the possible Plots, so try to keep them thinking that he could be the :factor: instead.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "1. Trigger \":butterflyEffect:\" (:changeOfFuture: victory condition)\n\nI like the idea of misleading the protagonists! Cool script!\n    Massola",
    "creator": "vetinari7878",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 4
      }
    ]
  },
  {
    "cast": {
      "richStudent": "augur",
      "crusader": "person",
      "boyStudent": "lover",
      "shrineMaiden": "person",
      "informer": "lover",
      "journalist": "potentate",
      "doctor": "person",
      "patient": "serialKiller",
      "nurse": "lover"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "proselytizing",
        "culprit": "informer"
      },
      {
        "day": 2,
        "incident": "ritualSacrifice",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "graveRobbing",
        "culprit": "nurse"
      }
    ],
    "title": "Supernatural Tragedy Story Script 1",
    "mainPlot": [
      "demonicPortal"
    ],
    "subPlots": [
      "fatalAttraction",
      "manifestation"
    ],
    "story": "This is an easy script that will help get players used to the Supernatural Tragedy ruleset.  This is the first script in a seven script story arc made for this ruleset.  I have only been able to do limited testing of this scenario and ruleset so any feedback is welcome! sean.petersen@gmail.com",
    "tragedySet": "supernatural",
    "mastermindHints": "Script: Undying Love\nTry to throw them off in the first loop by putting two :intrigue: on a character and making the players think that they lost to Metamorphosis when the actually lost to the Lovers.  If a Lover is killed, you can use the Potentate to move the :intrigue: from the character to the Shine to save the win.  You should centralize characters to mask the identity of the Potentate.",
    "specialRules": [
      "A character may be a culprit for more than one incident."
    ],
    "victory-conditions": "Put 2 :intrigue: on the Shrine via the Potentate (informer), Proselytizing and/or Grave Robbing\n\n",
    "creator": "Sean Petersen",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 4,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 41
      }
    ]
  },
  {
    "cast": {
      "informer": "person",
      "policeOfficer": "person",
      "richStudent": "person",
      "doctor": "supplicant",
      "shrineMaiden": "madGenius",
      "officeWorker": "contractKiller",
      "boyStudent": "keyPerson"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "abductions",
        "culprit": "officeWorker"
      },
      {
        "day": 3,
        "incident": "proselytizing",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "publicMeltdown",
        "culprit": "boyStudent"
      },
      {
        "day": 5,
        "incident": "suicide",
        "culprit": "doctor"
      }
    ],
    "title": "Supernatural Ruleset Story Script 02",
    "mainPlot": [
      "abomination"
    ],
    "subPlots": [
      "remoteBombing",
      "murderHire"
    ],
    "story": "This is the second script in a seven script story arc made for this ruleset. I have only been able to do limited testing of this scenario and ruleset so any feedback is welcome! sean.petersen@gmail.com",
    "tragedySet": "supernatural",
    "mastermindHints": "Gather as many characters as you can into one location and kill someone with the Contract Killer (officeWorker) to give the Mad Genius Body Parts.  Having lots of characters present will protect the identity of the Mad Genius and the Contract Killer.",
    "specialRules": [
      "A character may be a culprit for more than one incident."
    ],
    "victory-conditions": "Kill the policeOfficer via Suicide and have the Mad Genius collect his Body Parts, triggering Abomination.\n\n",
    "creator": "Sean Petersen",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 5,
        "numberOfLoops": 5
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 42
      }
    ]
  },
  {
    "cast": {
      "shrineMaiden": "keyPerson",
      "girlStudent": "serialKiller",
      "boyStudent": "person",
      "classRep": "person",
      "policeOfficer": "conspiracyTheorist",
      "popIdol": "cultist",
      "informer": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "shrineMaiden"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "popIdol"
      },
      {
        "day": 4,
        "incident": "murder",
        "culprit": "informer"
      }
    ],
    "title": "Schoolyard Bedlam",
    "mainPlot": [
      "placeProtect"
    ],
    "subPlots": [
      "shadowRipper"
    ],
    "story": "An anonymous tip has summoned the Police to the school, where it seems nothing is amiss. Yet because of his intimidating and inexplicable presence investigating the school, tensions are rising and a full-on panic is in danger of breaking out. All this has been arranged by the popIdol and her associate, members of a cult that need only the shrineMaiden to be eliminated before they can unleash their true supernatural horror. And while the anonymous tip was arranged on purpose, it wasn't entirely false: a vengeful girl has decided to take matters into her own hands and begin a killing spree. With so many forces lose in the school, it'll take a miracle for things not to fall apart completely...",
    "tragedySet": "firstSteps",
    "mastermindHints": "The incidents are designed to give you plenty of options. If you plan to win via a Power Play strategy, you can move the girlStudent to the Shrine on day one and immediately kill the shrineMaiden, revealing two roles but immediately ending the loop (this could be a good way to cover up the culprits of the later incidents). If you plan to coverup, you can use the policeOfficer's presence to increase :paranoia: and you can prevent the players from using the popIdol's ability because she's the cultist. This should help ensure that the incidents happen the way you want them to: get :intrigue: on the School and win at the end of the loop. Once the School has two :intrigue:, don't let the Informant murder the shrineMaiden if you can avoid it, unless you can get some :intrigue: on another location to obfuscate that victory condition (since if they see two :intrigue: on the school and none anywhere else, even if you win via murder they'll correctly assume you're on A Place To Protect)",
    "specialRules": [
      ""
    ],
    "victory-conditions": "shrineMaiden killed (girlStudent serial killer, Informant day 4 incident)\n\n",
    "creator": "Dav Flamerock",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 43
      }
    ]
  },
  {
    "cast": {
      "richStudent": "brain",
      "boyStudent": "person",
      "policeOfficer": "person",
      "officeWorker": "conspiracyTheorist",
      "nurse": "person",
      "shrineMaiden": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "officeWorker"
      },
      {
        "day": 2,
        "incident": "spreading",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "shrineMaiden"
      }
    ],
    "title": "A Battle of Wits",
    "mainPlot": [
      "lightAvenger"
    ],
    "subPlots": [
      "unsettlingRumor"
    ],
    "story": "This script is designed to introduce the protagonists to the \"Final Guess\" concept. It should be very difficult for them to simultaneously counter 2 :intrigue: onto the Hospital and the School when you have access to the Brain and the Unsettling Rumor abilities (though be careful with revealing the Brain!), but they can use the \"Final Guess\" to win if they can identify who the Brain is.",
    "tragedySet": "firstSteps",
    "mastermindHints": "This is a tricky one for the mastermind, because getting two :intrigue: on the School or Hospital isn't enough. If the protagonists can guess the plot and subplot correctly, they'll know that the Brain is either the Rich Man’s Daughter or the boyStudent, and if they can guess correctly at the end they'll win. Therefore, this is a game of subterfuge. Use the Conspiracy Theorist aggressively to land :paranoia: on everybody (it doesn't matter much because every subplot has a Conspirator) so that the Incidents go off repeatedly.  Increasing Unease can set up the Hospital Incident while Spreading can prevent the boyStudent from removing :paranoia: from the shrineMaiden. Keep the Daughter panicked as much as possible to conceal the culprits of the various incidents.",
    "specialRules": [
      "This scenario includes the \"Final Guess\" rule from the core game. If the protagonists don't successfully survive any of the three loops, they can guess the identity of all the roles to still win the game."
    ],
    "victory-conditions": "2 :intrigue: on the School (via the Rich Man’s Daughter ability, via Unsettling Rumor)\n\n",
    "creator": "Dav Flamerock",
    "daysPerLoop": 3,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 44
      }
    ]
  },
  {
    "cast": {
      "shrineMaiden": "person",
      "nurse": "friend",
      "richStudent": "person",
      "patient": "conspiracyTheorist",
      "alien": "friend",
      "popIdol": "person",
      "informer": "keyPerson",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 3
        }
      ],
      "mysteryBoy": "person"
    },
    "incidents": [
      {
        "day": 11,
        "incident": "increasingUnease",
        "culprit": "ai"
      },
      {
        "day": 12,
        "incident": "increasingUnease",
        "culprit": "ai"
      },
      {
        "day": 13,
        "incident": "increasingUnease",
        "culprit": "ai"
      },
      {
        "day": 14,
        "incident": "farawayMurder",
        "culprit": "ai"
      },
      {
        "day": 21,
        "incident": "increasingUnease",
        "culprit": "ai"
      },
      {
        "day": 22,
        "incident": "increasingUnease",
        "culprit": "ai"
      },
      {
        "day": 23,
        "incident": "missingPerson",
        "culprit": "patient"
      },
      {
        "day": 24,
        "incident": "hospitalIncident",
        "culprit": "patient"
      },
      {
        "day": 25,
        "incident": "increasingUnease",
        "culprit": "patient"
      },
      {
        "day": 26,
        "incident": "suicide",
        "culprit": "informer"
      }
    ],
    "title": "Vetinari Gambit",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "circleFriends",
      "paranoiaVirus"
    ],
    "story": "This is an unusual case. You’re not quite sure what happened. You think someone might have died, but you’re not quite sure how it went down.  Nerveless, you must be careful of who you trust, and tread carefully",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Since there are only eight days for incidents, I cant set it up as I would like. So for the first loop, increase unease until day four where a far away murder will happen.  Follow the same set for loop two until day 3, where the hospital patient will go missing, and day four, where a hospital incident will happen. Day five will be more unease, and if it hasn't ended, day  six will have the key person commit suicide.",
    "specialRules": [
      "Alternating loops: the days in a loop  alternate between four and six days, with odd loop numbers having four days and even loop numbers on six. Incidents in odd loops are on days prefixed with 1. For even with 2. e.g. Day 24 is the fouth day in the even loops"
    ],
    "victory-conditions": "Kill the Key person (Serial Killer, Hospital incident, Option 3)\n\n",
    "creator": "beatstopper",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      },
      {
        "difficulty": 0,
        "numberOfLoops": 5
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 46
      }
    ]
  },
  {
    "cast": {
      "popIdol": "witch",
      "patient": "lover",
      "boyStudent": "lovedOne",
      "godlyBeing": [
        "friend",
        {
          "enters on loop": 2
        }
      ],
      "shrineMaiden": "serialKiller",
      "nurse": "person",
      "journalist": "person",
      "officeWorker": "person",
      "richStudent": "person",
      "classRep": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "increasingUnease",
        "culprit": "journalist"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "shrineMaiden"
      },
      {
        "day": 6,
        "incident": "suicide",
        "culprit": "patient"
      }
    ],
    "title": "hal_7297108",
    "mainPlot": [
      "giantTimeBomb"
    ],
    "subPlots": [
      "loveAffair",
      "hiddenFreak"
    ],
    "story": "We just played through a custom script this afternoon written by our regular mastermind Max (our first custom script ever!), and it worked really well. The protagonists were one completely new player, one player who had only played an intro scenario, and me who has played about 12 times.\n\nThe protagonists won but it was close up until the final day of the final loop, so it might be a good script for those who are looking to play with a mixed group of new players and players who have already played the early scripts from the game.\n\nWe can't think of a decent name for the script though  Does anyone have any good ideas?",
    "tragedySet": "basicTragedy",
    "mastermindHints": "The witch might get discovered quite early - the popIdol's :goodwill: ability is a tempting one, so her :goodwill: refusel will probably be outed in the first loop. Given this, Max (our mastermind) decided to try and win through :intrigue: on the City in day one, and spread about some :intrigue: and :paranoia: elsewhere so it wasn't too obvious.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "\n\noriginally by hal_7297108 of boardgamegeek.com\n    Alan Tran",
    "creator": "hal_7297108's friend Max",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 5
      }
    ]
  },
  {
    "cast": {
      "doctor": "keyPerson",
      "nurse": "cultist",
      "patient": "person",
      "shrineMaiden": "serialKiller",
      "officeWorker": "person",
      "policeOfficer": "conspiracyTheorist",
      "boyStudent": "friend",
      "classRep": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "murder",
        "culprit": "patient"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "classRep"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "nurse"
      }
    ],
    "title": "The Red Fog",
    "mainPlot": [
      "placeProtect"
    ],
    "subPlots": [
      "shadowRipper"
    ],
    "story": "For this script, if played right, gives a handicap for the protagonists as the mastermind will have more options to win. When to reveal a characters role is the key. This was created by my 11 year old nephew who has a knack for mystery plots. Please do enjoy.",
    "tragedySet": "firstSteps",
    "mastermindHints": "Keep the nurse's role hidden as long as it takes as she is a key component in this script. Keep in mind that the Serial Killer can be used both by you and the Protagonists' advantage.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "* 2 :intrigue: in School\n* Kill the Key Person\n* Kill Protagonists during Hospital Incident\n* Kill the Friend\n\n",
    "creator": "ZenKristoffer",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 7,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 63
      }
    ]
  },
  {
    "cast": {
      "patient": "keyPerson",
      "doctor": "killer",
      "policeOfficer": "person",
      "nurse": "friend",
      "officeWorker": "conspiracyTheorist",
      "richStudent": "brain"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "missingPerson",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "hospitalIncident",
        "culprit": "doctor"
      },
      {
        "day": 4,
        "incident": "murder",
        "culprit": "officeWorker"
      },
      {
        "day": 5,
        "incident": "suicide",
        "culprit": "patient"
      }
    ],
    "title": "Goodbye, My Brother",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "story": "A young lady is losing her inheritance to her sick brother. Using her connections, she plots to kill him and take the endowment herself.",
    "tragedySet": "firstSteps",
    "mastermindHints": "A Power Plan plot that utilizes creative ways to achieve the goal. Once the players figure out the Key Person, take advantage of the doctor's Killer ability to optionally refuse :goodwill: to prevent the patient from leaving the Hospital. In the meantime, move in the officeWorker to increase the patient's :paranoia: for the Day 5 Incident. Due to the variety of the methods the Mastermind has, four or five loops is recommended to allow players to see the importance of the characters' :goodwill: and the :paranoia: -1 card.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "patient dies by: Killer ability / Hospital Incident / Murder Incident / Suicide Incident\n\ndoctor :Killer, mastermind can be used :goodwill: refusel, key person how to run =)))) ....... ==> patient : Friend, nurse : Key Person\n    Tô Quốc Thái\n\nMastermind tip: use the brain ability as soon as the protagonists know that murder plan is the plot. This applies after day 2 when the Rich Man’s Daughter missing person's into the hospital.\n    Justin Karp",
    "creator": "Res_kun",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 9,
        "numberOfLoops": 4
      },
      {
        "difficulty": 9,
        "numberOfLoops": 5
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 65
      }
    ]
  },
  {
    "cast": {
      "policeOfficer": "witch",
      "henchman": "friend",
      "boss": [
        "keyPerson",
        {
          "Turf": "City"
        }
      ],
      "popIdol": "lovedOne",
      "mysteryBoy": "lover",
      "godlyBeing": [
        "brain",
        {
          "enters on loop": 2
        }
      ],
      "alien": "timeTraveler"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "godlyBeing"
      },
      {
        "day": 3,
        "incident": "farawayMurder",
        "culprit": "alien"
      },
      {
        "day": 4,
        "incident": "butterflyEffect",
        "culprit": "alien"
      }
    ],
    "title": "Untitled",
    "mainPlot": [
      "giantTimeBomb"
    ],
    "subPlots": [
      "threadsFate"
    ],
    "story": "",
    "tragedySet": "basicTragedy",
    "mastermindHints": "",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Butterfly Effect\n\n I have added a random Turf since it was not specified same for goodly being arrival",
    "creator": "",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 66
      }
    ]
  },
  {
    "cast": {
      "girlStudent": "keyPerson",
      "classRep": "keyPerson",
      "richStudent": "keyPerson",
      "doctor": "brain",
      "patient": "killer",
      "informer": "conspiracyTheorist",
      "policeOfficer": "person",
      "officeWorker": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "spreading",
        "culprit": "officeWorker"
      },
      {
        "day": 5,
        "incident": "farawayMurder",
        "culprit": "doctor"
      },
      {
        "day": 6,
        "incident": "missingPerson",
        "culprit": "girlStudent"
      },
      {
        "day": 7,
        "incident": "hospitalIncident",
        "culprit": "patient"
      }
    ],
    "title": "Save The Students (untested)",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "unsettlingRumor",
      "threadsFate"
    ],
    "story": "Note: This script has not been tested yet and feedback would be greatly appreciated. Email me at yukutaaso@yahoo.com\n\nThe patient was an ex-student who was severely bullied by the 3 students who were members of a clique.\nWhen one of them pushed him down the stairs it broke some of his bones and he ended up in the hospital where his father works as a doctor.\nEnraged by the cruelty of the 3 students, the patient's heart grows cold and decides that they must pay for what they have done to him.\n(on day 7 is the patient's last, desperate attempt to exact revenge. His heart has grown so cold he would kill himself if it meant the death on the students)\nThe doctor, being a loving father, and mending the wounds of his victimized son decides to help in this evil plan.\nThe informer is a member of the police investigating the bullying incident that ended up with a child in the hospital.\nCan the Tragedy Loopers save the lives of these students or will the patient exact his revenge?",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Try to get the students out of the school and into the hospital.\nTry to trick the protagonists to befriend the doctor, allowing the patient to be brought to the students.\nUse the Missing Person incidents to get the students into the hospital.\nTry to keep the brain's role hidden.",
    "specialRules": [
      "N/A"
    ],
    "victory-conditions": "Death of a key person\nHospital Incident (Killing protagonists)\n4 :intrigue: on Killer (Killing protagonists)\n\nHaving multiple Key Persons is really going to throw the protagonists for a loop, since it's not actually legal by the game mechanics. You could switch out the Unsettling Rumour plot for Circle of Friends, change 2 of the students to Friends, and keep the same theme without breaking the rules.\n    TeabagNation",
    "creator": "Shadow_CPO",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "difficulty": 5,
        "numberOfLoops": 3
      },
      {
        "difficulty": 5,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 67
      }
    ]
  },
  {
    "cast": {
      "shrineMaiden": "keyPerson",
      "classRep": "cultist",
      "richStudent": "conspiracyTheorist",
      "girlStudent": "serialKiller",
      "nurse": "person",
      "informer": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "classRep"
      },
      {
        "day": 3,
        "incident": "missingPerson",
        "culprit": "shrineMaiden"
      },
      {
        "day": 4,
        "incident": "murder",
        "culprit": "girlStudent"
      }
    ],
    "title": "servants of Cthulhu",
    "mainPlot": [
      "placeProtect"
    ],
    "subPlots": [
      "shadowRipper"
    ],
    "story": "The three female students are servants of Cthulhu. The Rich Man’s Daughter and classRep cast incantations that cause the shrineMaiden to enter the school. There, the girlStudent may be incited to murder her as a sacrifice to fuel a ritual to summon The Great Old One into this world. If they perform the summoning without a sacrifice, they accidentally summon Azathoth instead.",
    "tragedySet": "firstSteps",
    "mastermindHints": "Try to kill the shrineMaiden (Key Person) using the Serial Killer or the Murder Incident on Day 4.\n\nUse the Increasing Unease to setup the Murder on Day 4.\n\nRemember that forbid movement & movement cards happen before other forbid & action cards.  Use this to setup the Cultist in the right location (school)",
    "specialRules": [
      ""
    ],
    "victory-conditions": "[Instant Loop Lost] - The shrineMaiden (Key Person) is killed\n[Loop End] - 2 :intrigue: on the School\n\n",
    "creator": "ProdigalPlaneswalker",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      },
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 68
      }
    ]
  },
  {
    "cast": {
      "boyStudent": "person",
      "girlStudent": "keyPerson",
      "richStudent": "friend",
      "shrineMaiden": "person",
      "policeOfficer": "conspiracyTheorist",
      "officeWorker": "person",
      "informer": "person",
      "doctor": "person",
      "patient": "friend"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "murder",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "murder",
        "culprit": "doctor"
      },
      {
        "day": 5,
        "incident": "suicide",
        "culprit": "girlStudent"
      },
      {
        "day": 8,
        "incident": "hospitalIncident",
        "culprit": "richStudent"
      }
    ],
    "title": "We have no choice but to die",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "circleFriends",
      "paranoiaVirus"
    ],
    "story": "Pushing the activation of the serial killers, paying attention to the incident on the last day, and making sure whether the protagonist have enough information for the final guess are factors that will decide the winner in this script. The mastermind can also force a win by placing the +2 :intrigue: on the hospital or the girlStudent, if :paranoia: isn't controlled by the boy/girlStudent and the doctor.\n\nDue to that, it should be a smooth sailing until the final guess.\n\nIf :paranoia: isn't countered, then the friendly Rich Man’s Daughter's \"we have no choice but to die\" system will activate, which ensures the hospital gets tiro-ed and the players meet their finale. However, if they try to get rid of her, then \"something is wrong with you when you throw your friend away\" system will activate, then it's back to \"no choice but to die\" \n\nThis just can't be right.\n\nWhile it can be easy for the protagonist to reach the answer if :paranoia: is controlled correctly, or using the shrineMaiden's ability, deciding where to start with the data collection is where it becomes interesting.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Template #1: +1 :paranoia: for boyStudent, +1 :paranoia: for doctor, +1 :intrigue: for girlStudent. This is where you use the boyStudent to kill the key person. If you can kill the key person or the friend, prioritize the key person.",
    "specialRules": [
      "Table talk on. If you want to set it to off, then give two more loops to the protagonists."
    ],
    "victory-conditions": "1. Killing the key person. (With serial killer, murder incident, suicide incident, Hospital incident)\n\n",
    "creator": "Sairanac",
    "daysPerLoop": 8,
    "difficultySets": [
      {
        "difficulty": 8,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 7
      }
    ]
  },
  {
    "cast": {
      "boyStudent": "killer",
      "girlStudent": "keyPerson",
      "classRep": "friend",
      "richStudent": "brain",
      "mysteryBoy": "serialKiller",
      "popIdol": "person",
      "shrineMaiden": "conspiracyTheorist"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "murder",
        "culprit": "boyStudent"
      },
      {
        "day": 4,
        "incident": "murder",
        "culprit": "shrineMaiden"
      }
    ],
    "title": "Battle Royale - Tragedy Looper Edition",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "story": "It's Battle Royale, but with Tragedy Looper characters and special rules! An example of making simple plots complex with the addition of extra rules. I recommend using this script with card covers/sheets that hide the back of cards due to the use of the four extra cards. Feel free to make any modifications and share your versions! If there are any questions, please comment.",
    "tragedySet": "firstSteps",
    "mastermindHints": "A Power Plan plot. The first person to target is the Key Person. Increasing Unease will make it easier to take it down the first few loops. Once the Players begin to protect the Key Person, killing the classRep will be an option for victory for you as well. This script requires heavy preparation, but gives the Mastermind many tools to achieve his goal. Be wary of the Serial Killer, as it may work against you.",
    "specialRules": [
      "Any characters with THREE :intrigue: dies instantly (Bomb Collar explodes). Do not wait until the end of the day.\nAny location with THREE :intrigue: kills everyone in the location and those who enter it (Forbidden Zone).\nEach Player deck and Mastermind deck receives ONE extra card (comes with the game, Extra Card A, B, C, and D).\nExtra cards are infinite usage of MINUS or PLUS one :intrigue: for both players and mastermind. It is the person's choice, and may be played on either character or location.\nPlayers' Forbid :intrigue: and Extra Card do not cancel each other and may be used together. However, the same cancel rule still applies when TWO Forbid :intrigue: are on the board.\nForbid :intrigue: may be used to cancel out a Mastermind's Extra Card.\nA Player's Extra Card may be used to cancel out a Mastermind's Extra Card."
    ],
    "victory-conditions": "[Key Person Dies]: Killer ability / Serial Killer ability / Forbidden Zone / Bomb Collar / Murder Incidents\n[Friend Dies]: Killer ability / Serial Killer ability / Forbidden Zone / Bomb Collar / Murder Incidents\n\n",
    "creator": "Res_kun",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 8,
        "numberOfLoops": 5
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 70
      }
    ]
  },
  {
    "cast": {
      "shrineMaiden": "keyPerson",
      "richStudent": "killer",
      "doctor": "brain",
      "patient": "lover",
      "henchman": "lovedOne",
      "policeOfficer": "friend",
      "classRep": "friend",
      "officeWorker": "conspiracyTheorist",
      "popIdol": "person",
      "boyStudent": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "officeWorker"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "doctor"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "popIdol"
      },
      {
        "day": 6,
        "incident": "murder",
        "culprit": "henchman"
      },
      {
        "day": 7,
        "incident": "murder",
        "culprit": "richStudent"
      }
    ],
    "title": "Best in Class",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "loveAffair",
      "circleFriends"
    ],
    "story": "A deranged and amoral student blames a classmate for her isolation from her peers and her crush's rejection. Unfortunately for her unsuspecting target, her father is both wealthy enough to make her twisted wish into reality, and sufficiently devastated by his failure to save his wife that he wants nothing more than to make those around him suffer as well. A diligent cop and his loyal daughter are doing their best to protect their beloved hometown, but they have much to contend with. An orderly unjustly fired from the hospital resents his new office job and seeks answers, but his assertions that something is wrong at the hospital are only creating panic. A violent young Yakuza member'rs behaviour is only becoming more erratic and unpredictable due to his concern for his younger brother, hospitalized and helpless. Will the protagonists be able to guide this town to a peaceful future, or will two twisted generations commit a crime that will tear their community asunder? They will be putting themselves at great risk to find out...",
    "tragedySet": "basicTragedy",
    "mastermindHints": "",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Kill the Key Person (using killer's ability, hospital incident, or murder incidents)\n\nKill a Friend (using hospital incident or murder incidents)\n\nKill the protagonists (using killer's ability, loved one's ability, or hospital incident)\n\nThis is my first attempt at creating a custom script. I have a group who will play test it this week or next. Any tips/critiques would be welcome - particularly, I'm concerned about balance. Reading through the creation guide, I know I picked several of the more difficult plots/subplots/culprits. My intention in creating this script was to make a fast-paced gameplay experience where there are several loss conditions the protagonists will need to account for, and lots of information to gradually be revealed throughout the loops. Let me know where I succeeded in this goal, and where I failed completely!\n    87",
    "creator": "C.J. Morton",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 72
      }
    ]
  },
  {
    "cast": {
      "patient": "keyPerson",
      "boyStudent": "lovedOne",
      "richStudent": "conspiracyTheorist",
      "girlStudent": "lover",
      "informer": "killer",
      "officeWorker": "brain",
      "popIdol": "person",
      "alien": "person",
      "classRep": "friend",
      "godlyBeing": [
        "friend",
        {
          "enters on loop": 3
        }
      ]
    },
    "incidents": [
      {
        "day": 2,
        "incident": "butterflyEffect",
        "culprit": "classRep"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "shrineMaiden"
      },
      {
        "day": 6,
        "incident": "suicide",
        "culprit": "godlyBeing"
      },
      {
        "day": 7,
        "incident": "hospitalIncident",
        "culprit": "richStudent"
      }
    ],
    "title": "Silly Things?",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "circleFriends",
      "loveAffair"
    ],
    "story": "This is my first attempt at a script.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "difficulty": 8,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 74
      }
    ]
  },
  {
    "cast": {
      "policeOfficer": "brain",
      "popIdol": "killer",
      "patient": "obstinate",
      "shrineMaiden": "twin",
      "alien": "person",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 3
        }
      ],
      "journalist": "person",
      "richStudent": "person",
      "henchman": "paranoiac"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "closedCircle",
        "culprit": "shrineMaiden"
      },
      {
        "day": 3,
        "incident": "bestialMurder",
        "culprit": "henchman"
      },
      {
        "day": 4,
        "incident": "terrorism",
        "culprit": "alien"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "patient"
      }
    ],
    "title": "Vroom Vroom",
    "mainPlot": [
      "tightropePlan"
    ],
    "subPlots": [
      "anAbsoluteWill",
      "trickyTwins"
    ],
    "story": "An exciting battle between the mastermind and the protagonists. Makes sense if you know me very well.",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "Just win kids.",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Tightrope Plan: Extra Gauge 1 or fewer\nHospital Incident: 2 :intrigue: or more on hospital\nTerrorism: 2 :intrigue: or more on city\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 3,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 78
      }
    ]
  },
  {
    "cast": {
      "informer": "person",
      "policeOfficer": "person",
      "mysteryBoy": "lover",
      "patient": "timeTraveler",
      "doctor": "serialKiller",
      "boyStudent": "friend",
      "girlStudent": "cultist",
      "shrineMaiden": "conspiracyTheorist",
      "alien": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "shrineMaiden"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "girlStudent"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "patient"
      }
    ],
    "title": "Confined",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "unsettlingRumor",
      "hiddenFreak"
    ],
    "story": "This is a fun script that uses misdirection to mislead the Protagonists about the main plot.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Loop 1: The goal of this loop is to misdirect the characters into thinking the main plot is The Sealed Item. Move as many characters as you can into the Shrine and place two :intrigue: on the Shine using the Cultist to neutralize Forbid :intrigue: if necessary. You can even put on an :intrigue: the Shrine using the Mastermind ability from An Unsettling Rumor to make it appear that there is a Brain in the Shrine, when in fact there is no Brain in the script. The Protagonists will lose due to lack of :goodwill: on the Time Traveler but they may think it is due to the :intrigue: on the Shrine and The Sealed Item. The danger for the Mastermind is having the doctor revealed as the Serial Killer. Try to keep him around lots of people so he doesn’t kill anyone.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Two :goodwill: or fewer on the patient (Time Traveler) at the end of the loop.\n\n",
    "creator": "speter79",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 5,
        "numberOfLoops": 5
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 8
      }
    ]
  },
  {
    "cast": {
      "classRep": "conspiracyTheorist",
      "richStudent": "keyPerson",
      "doctor": "brain",
      "henchman": "magician",
      "shrineMaiden": "immortalRole",
      "patient": "friend",
      "policeOfficer": "obstinate",
      "alien": "person",
      "mysteryBoy": "factor"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "classRep"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 5,
        "incident": "confession",
        "culprit": "henchman"
      },
      {
        "day": 6,
        "incident": "hospitalIncident",
        "culprit": "policeOfficer"
      }
    ],
    "title": "The Zone of Midnight",
    "mainPlot": [
      "secretRecord"
    ],
    "subPlots": [
      "showtimeDeath",
      "loveHateSpiral"
    ],
    "story": "An exciting dance under the moonlight.",
    "tragedySet": "midnightZone",
    "mastermindHints": "",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Killing Key Person: Rich Man’s Daughter\nHospital Incident: 2 :intrigue: on hospital\nShowtime of Death: 6 or fewer characters left\nFriend: patient dies\nSecret Record: Brain, Factor, or Magician revealed\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 80
      }
    ]
  },
  {
    "cast": {
      "forensicSpecialist": "magician",
      "alien": "immortalRole",
      "patient": "prophet",
      "boyStudent": "person",
      "boss": [
        "conspiracyTheorist",
        {
          "Turf": "School"
        }
      ],
      "richStudent": "friend",
      "officeWorker": "serialKiller",
      "mysteryBoy": "obstinate",
      "nurse": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "suicide",
        "culprit": "officeWorker"
      },
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 4,
        "incident": "serialMurder",
        "culprit": "forensicSpecialist"
      },
      {
        "day": 6,
        "incident": "hospitalIncident",
        "culprit": "mysteryBoy"
      }
    ],
    "title": "Leave us to Die",
    "mainPlot": [
      "fatedConnections"
    ],
    "subPlots": [
      "showtimeDeath",
      "worshippersApocalypse"
    ],
    "story": "untested",
    "tragedySet": "midnightZone",
    "mastermindHints": "",
    "specialRules": [
      "Mastermind removes Forbid :goodwill: from hand"
    ],
    "victory-conditions": "1) Have 6 or less characters alive\n2) Kill protagonists from the Hospital Incident\n3) Kill Key Persons created from Fated Connections\n\n",
    "creator": "Seiji-kun",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 82
      }
    ]
  },
  {
    "cast": {
      "alien": "keyPerson",
      "richStudent": "cultist",
      "policeOfficer": "ninja",
      "doctor": "magician",
      "forensicSpecialist": "immortalRole",
      "patient": "prophet",
      "mysteryBoy": "conspiracyTheorist",
      "officeWorker": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "serialMurder",
        "culprit": "forensicSpecialist"
      },
      {
        "day": 3,
        "incident": "serialMurder",
        "culprit": "forensicSpecialist"
      },
      {
        "day": 4,
        "incident": "serialMurder",
        "culprit": "forensicSpecialist"
      },
      {
        "day": 5,
        "incident": "serialMurder",
        "culprit": "richStudent"
      },
      {
        "day": 6,
        "incident": "suicide",
        "culprit": "alien"
      }
    ],
    "title": "Hide and Seek",
    "mainPlot": [
      "devilsHand"
    ],
    "subPlots": [
      "showtimeDeath",
      "worshippersApocalypse"
    ],
    "story": "",
    "tragedySet": "midnightZone",
    "mastermindHints": "",
    "specialRules": [
      "mastermind removes forbid :goodwill: from hand"
    ],
    "victory-conditions": "1) Have 6 or less characters alive\n2) Kill the Key Person (either from incidents, the Ninja's ability, or the Suicide on the last day)\n\n",
    "creator": "Seiji-kun",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 83
      }
    ]
  },
  {
    "cast": {
      "doctor": "person",
      "richStudent": "friend",
      "alien": "obstinate",
      "girlStudent": "person",
      "godlyBeing": [
        "serialKiller",
        {
          "enters on loop": 3
        }
      ],
      "forensicSpecialist": "serialKiller",
      "mysteryBoy": "ninja",
      "patient": "prophet"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "conspiracies",
        "culprit": "shrineMaiden"
      },
      {
        "day": 3,
        "incident": "serialMurder",
        "culprit": "girlStudent"
      },
      {
        "day": 4,
        "incident": "uproar",
        "culprit": "policeOfficer"
      },
      {
        "day": 5,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 6,
        "incident": [
          "fakeIncident",
          "confession"
        ],
        "culprit": "alien"
      }
    ],
    "title": "Summoning the Old Ones",
    "mainPlot": [
      "fatedConnections"
    ],
    "subPlots": [
      "diceOfGods",
      "worshippersApocalypse"
    ],
    "story": "untested",
    "tragedySet": "midnightZone",
    "mastermindHints": "",
    "specialRules": [
      "Mastermind removes forbid :goodwill: from hand"
    ],
    "victory-conditions": "1) Killing Key People created by Fated Connections/Dice of the Gods\n2) Killing the Friend\n\n",
    "creator": "Seiji-kun",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 84
      }
    ]
  },
  {
    "cast": {
      "informer": "keyPerson",
      "policeOfficer": "poisoner",
      "patient": "fool",
      "boyStudent": "friend",
      "richStudent": "paranoiac",
      "girlStudent": "serialKiller",
      "alien": "twin",
      "officeWorker": "person",
      "henchman": "person",
      "journalist": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "portent",
        "culprit": "henchman"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "serialMurder",
        "culprit": "alien"
      },
      {
        "day": 5,
        "incident": "serialMurder",
        "culprit": "alien"
      },
      {
        "day": 6,
        "incident": "suicide",
        "culprit": "patient"
      }
    ],
    "title": "Let's Try Again",
    "mainPlot": [
      "dropStrychnine"
    ],
    "subPlots": [
      "trickyTwins",
      "hiddenFreak"
    ],
    "story": "A new mystery circle script. I wanted to try to make one the protagonists could win this time.",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "Not sure, do your best. Try to kill the informer as soon as possible.",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Killing Key Person: informer\nKilling Friend: boyStudent\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 3,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 86
      }
    ]
  },
  {
    "cast": {
      "informer": "conspiracyTheorist",
      "alien": "serialKiller",
      "journalist": "friend",
      "henchman": "friend",
      "ai": "obstinate",
      "shrineMaiden": "factor",
      "richStudent": "person",
      "scientist": "person",
      "classRep": "person",
      "patient": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "serialMurder",
        "culprit": "ai"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "henchman"
      },
      {
        "day": 4,
        "incident": "serialMurder",
        "culprit": "ai"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "journalist"
      },
      {
        "day": 6,
        "incident": "serialMurder",
        "culprit": "ai"
      }
    ],
    "title": "Vroom Vroom 2",
    "mainPlot": [
      "fatedConnections"
    ],
    "subPlots": [
      "loveHateSpiral",
      "unsafeTrigger"
    ],
    "story": "",
    "tragedySet": "midnightZone",
    "mastermindHints": "",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Killing Key Person: shrineMaiden becomes one or character with extra card\nKilling Friend: (journalist/patient)\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 3,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 87
      }
    ]
  },
  {
    "cast": {
      "alien": "brain",
      "shrineMaiden": "person",
      "henchman": "lovedOne",
      "patient": "lover",
      "mysteryBoy": "conspiracyTheorist",
      "officeWorker": "person",
      "richStudent": "person",
      "boyStudent": "person",
      "girlStudent": "person",
      "godlyBeing": [
        "cultist",
        {
          "enters on loop": 2
        }
      ]
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "mysteryBoy"
      },
      {
        "day": 3,
        "incident": "hospitalIncident",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "murder",
        "culprit": "henchman"
      },
      {
        "day": 5,
        "incident": "butterflyEffect",
        "culprit": "godlyBeing"
      },
      {
        "day": 6,
        "incident": "foulEvil",
        "culprit": "boyStudent"
      }
    ],
    "title": "Vroom Vroom 3",
    "mainPlot": [
      "sealedItem"
    ],
    "subPlots": [
      "threadsFate",
      "loveAffair"
    ],
    "story": "",
    "tragedySet": "basicTragedy",
    "mastermindHints": "Idk.",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "2 :intrigue: on Shrine, 3 :paranoia: and 1 :intrigue: on loved one\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 6,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 88
      }
    ]
  },
  {
    "cast": {
      "alien": "brain",
      "doctor": "killer",
      "popIdol": "keyPerson",
      "richStudent": "factor",
      "officeWorker": "serialKiller",
      "patient": "friend",
      "policeOfficer": "person",
      "classRep": "person",
      "journalist": "person",
      "girlStudent": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "foulEvil",
        "culprit": "policeOfficer"
      },
      {
        "day": 3,
        "incident": "spreading",
        "culprit": "officeWorker"
      },
      {
        "day": 4,
        "incident": "murder",
        "culprit": "patient"
      },
      {
        "day": 6,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 7,
        "incident": "hospitalIncident",
        "culprit": "doctor"
      }
    ],
    "title": "The Hunt is On",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "unknownFactor",
      "hiddenFreak"
    ],
    "story": "It begins now. Beware, ye foolhardy protagonists, for the masterminds plan is coming to a dangerous and deadly fruition.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "There are only 3 (or 4) Loops here. Each Loop, you want to try and make sure you do NOT reveal which rolls you have. Here is a way to end the Loop each time:\nFirst Loop: Trigger the Hospital Incident on 1 :intrigue: to kill the Friend/Key Person/Factor who may be inside.\nSecond/Third Loop: Kill the Factor/Key Person OR kill the Protagonists by triggering the Hospital Incident on 2 or using the Killer on 4\n\nWith the alien as the Brain, you can’t actually get them into the Hospital, meaning it is pretty easy to shut down. However, moving them around through the City and School to trigger Factor powers is nice, and you can try and bluff an Unsettling Rumor by placing some early :intrigue: on the Shrine/School/City. Getting :intrigue: on any and all of these locations is key to mask the real goal.\n\nBasically: Hide the Serial Killer, Brain, and Friend, and confuse the Key Person/Factor. This shouldn’t be too hard.",
    "specialRules": [
      "None."
    ],
    "victory-conditions": "Kill the popIdol (Key Person)\nKill the Rich Man’s Daughter (Factor) with 2 :intrigue: on the City\nKill the patient (Friend)\nKill the Protagonists with the Hospital Incident\nKill the Protagonists with the Killer\n\n",
    "creator": "O'Malley",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "difficulty": 5,
        "numberOfLoops": 3
      },
      {
        "difficulty": 5,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 89
      }
    ]
  },
  {
    "cast": {
      "classRep": "person",
      "girlStudent": "conspiracyTheorist",
      "richStudent": "brain",
      "shrineMaiden": "person",
      "policeOfficer": "curmudgeon",
      "officeWorker": "person",
      "informer": "person",
      "doctor": "curmudgeon",
      "patient": "friend"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "girlStudent"
      },
      {
        "day": 3,
        "incident": "spreading",
        "culprit": "classRep"
      },
      {
        "day": 4,
        "incident": "missingPerson",
        "culprit": "policeOfficer"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "doctor"
      }
    ],
    "title": "Infiltration",
    "mainPlot": [
      "lightAvenger"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "story": "A script meant for beginners focusing on some of the less explained mechanics as teaching them.",
    "tragedySet": "firstSteps",
    "mastermindHints": "This is a Coverup Script.\n\nFor your Main Plot, you want to bluff ‘A Place to Protect.’ Have one of your characters always refuse :goodwill: (to act as the Cultist) and have the others always accept it. This will confuse your opponent. Hiding a Key Person is much harder, but possible. Outside of the Hospital Incident, there is no way to kill characters, so that might help.\n\nYou may want to let the characters trigger the doctor’s :goodwill: Ability and save the patient in the first few loops. If he dies and the Protagonists make it to the end of Day 5, they will immediately realise that he is the Friend meaning it must be a Hideous Script.\n\nYou can bluff either of the other two subplots by just using the Conspiracy Theorist and making sure two characters are never in the same region alone together. If you are really desperate, using the Brain’s ability to simulate an Unsettling Rumor can help.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "2 :intrigue: on the School at the end of the loop\nThe patient (Friend) is dead at the end of the loop\nThe Protagonists are killed in the Hospital Incident\n\nThanks! I hadn't noticed that. I changed the culprit to the policeOfficer, who is much harder to trigger. I also totally forgot about the fact that there isn't a Final Guess in First Steps, which is a major thing to consider.\n    O'Malley\n\nThe Mastermind can always win this script.\n    Emily Karp\n\nTo expand on Emily's comment:\n\nSince the doctor is a Curmudgeon and the girlStudent is a Conspiracy Theorist, the Protagonists effectively have no :paranoia: removal beyond their :paranoia: -1's, which have a maximum play rate of 1/day. So, you can get the girlStudent to :paranoia: Limit by Day 4 easily, and also have the Rich Man’s Daughter either trigger her incident on Day 2 or use her Mastermind ability to put :intrigue: on the school: the protagonists can't both play a :paranoia: -1 on her and move her out of the School on Day 1. So you can guarantee two :intrigue: on the School.\n    Augustin-Louis Cauchy",
    "creator": "O'Malley",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 2,
        "numberOfLoops": 3
      },
      {
        "difficulty": 2,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 90
      }
    ]
  },
  {
    "cast": {
      "nurse": "person",
      "patient": "person",
      "policeOfficer": "person",
      "popIdol": "keyPerson",
      "informer": "serialKiller",
      "classRep": "friend",
      "girlStudent": "conspiracyTheorist",
      "richStudent": "person",
      "shrineMaiden": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "butterflyEffect",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "classRep"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "popIdol"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "policeOfficer"
      }
    ],
    "title": "Hidden Knife among the people",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "hiddenFreak",
      "unsettlingRumor"
    ],
    "story": "",
    "tragedySet": "basicTragedy",
    "mastermindHints": "You may want to start off by moving the policeOfficer so that the serial killer can kill of the key character right a way. You will want the rich mans daughter to cause the butterfly effect in order to get some :intrigue: counters on the key person. Use suicide to kill off the key character. You can use faraway murder to kill the friend or the key person",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Place two :intrigue: counters on the key person using incidents or cards.\nKill popIdol or classRep using faraway murder or serial killer.\nGet popIdol to suicide.\n\nThis script is much harder than a 4/10. It is very difficult for the protagonists to win other than the final guess.\n    Emily Forster",
    "creator": "Hero7898",
    "daysPerLoop": 4,
    "difficultySets": [
      {
        "difficulty": 3,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 92
      }
    ]
  },
  {
    "cast": {
      "patient": "timeTraveler",
      "doctor": "cultist",
      "nurse": "friend",
      "boyStudent": "friend",
      "richStudent": "conspiracyTheorist",
      "girlStudent": "person",
      "classRep": "person",
      "shrineMaiden": "person",
      "popIdol": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "murder",
        "culprit": "shrineMaiden"
      },
      {
        "day": 2,
        "incident": "murder",
        "culprit": "patient"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "nurse"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "boyStudent"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "girlStudent"
      }
    ],
    "title": "Return to Sanity",
    "mainPlot": [
      "changeOfFuture"
    ],
    "subPlots": [
      "circleFriends",
      "threadsFate"
    ],
    "story": "Story: Dr. Isha was a normal doctor by day, but by night he was something more sinister. As the leader of a nefarious cult, he had finally completed their evil rituals and trapped a young patient of his in an inescapable time loop. // \n\nJimmy Kanja had had a weak constitution since he was young and could rarely attend school. His only friends were nurse Kangofu and his childhood bestie Danshi Gakusei. One day, after a routine check-up, he was told he wasn't allowed to leave. Strange men in robes performed a strange ritual on him and confined him to the hospital. A few days later, on the eve of his planned escape, he found his work undone, and the calendar reset. Four days later, it happened again. He was able to confirm: he was living the same four days, over and over again. // \n\nRepeating the same days, over and over again, confined to the same building, Jimmy's sanity slipped. Where once he saw friends, he later saw only enemies, until finally he couldn't even remember who they were. He would lash out if anyone showed him the slightest signs of kindness, often brutally. And the ritual's effects distorted causality, causing the surrounding town's inhabitants to have hideously altered memories of previous cycles. His friends were hit the hardest: their guilt over being unable to help Jimmy and their fear over his increasingly erratic behavior compounded from loop to loop, until they would try to kill themselves whenever Jimmy recognized them. Of course, this process was helped along by Dr. Isha's daughter, Ojou Isha. // \n\nThe protagonists manage to weaken the effects of the ritual and enter the time loop. However, to fully end it, they need Jimmy to willingly want it to end. That means they'll have to bring him back to sanity. But if he finds out his friends have killed themselves, or worse, that he's done the deed himself, he'll slip back farther than ever... // \n\nAlso, there's a murdering shrineMaiden, and a Hospital Incidenting girlStudent. They don't really fit into the plot.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "The secret rule is that you must play :intrigue: +1 or :intrigue: +2 on the Hospitalk during your first day's plays.\n\nYour goal, obviously, is to set up the \"Friend revealed -> :goodwill: placement -> 2 :paranoia:\" loop on as many friends as possible, so as to make their suicides a running thing. Unless you get some unwitting help from the Protagonists, you probably won't be able to have the friends commit suicide on Loop 2; you can get around this with the Time Traveller loss condition, or by having the shrineMaiden murder a friend (if she received :goodwill: on an earlier loop). The Protagonists' goal is to either use the nurse, girlStudent, and popIdol abilities to reduce the Friends below :paranoia: limit, or to have the shrineMaiden sniff out the Time Traveller. The shrineMaiden can do this by checking the patient with her 5 :goodwill: ability (or all other candidates, by process of elimination); by having the Hospital Incident reveal the Timend Traveller; or possibly by having just the shrineMaiden and patient in the Hospital after Day 1, forcing the shrineMaiden to attempt to murder the Immortal patient; be on guard for all three. You can try feinting Sealed Item by occasionally trying to get :intrigue: on the Shrine in the early parts of the game, so that if they uncover that the doctor is a Cultist, they'll guess the wrong Plot; however, if you're forced to use the Time Traveller loss condition, they'll know right away which Plot they have. Otherwise, victory will mainly come from good play, by keeping the shrineMaiden from finding out too much information and by keeping at least one friend dying every loop.",
    "specialRules": [
      "The mastermind has a secret rule that dictates part of their play"
    ],
    "victory-conditions": "Friend dies (loop end);\nTime Traveller has insufficient :goodwill: (day end, last day)\n\n",
    "creator": "Augustin-Louis Cauchy",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 6,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 94
      }
    ]
  },
  {
    "cast": {
      "doctor": "conspiracyTheorist",
      "patient": "friend",
      "officeWorker": "person",
      "henchman": "friend",
      "godlyBeing": [
        "person",
        {
          "enters on loop": 2
        }
      ],
      "shrineMaiden": "brain",
      "richStudent": "keyPerson",
      "girlStudent": "killer",
      "mysteryBoy": "cultist"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "suicide",
        "culprit": "richStudent"
      },
      {
        "day": 3,
        "incident": "increasingUnease",
        "culprit": "patient"
      },
      {
        "day": 4,
        "incident": "hospitalIncident",
        "culprit": "doctor"
      },
      {
        "day": 5,
        "incident": "murder",
        "culprit": "girlStudent"
      }
    ],
    "title": "Who stole the cookie? Why so serious?",
    "mainPlot": [
      "murderPlan"
    ],
    "subPlots": [
      "circleFriends",
      "paranoiaVirus"
    ],
    "story": "The last cookie in the cookie jar is gone, who might have it.",
    "tragedySet": "basicTragedy",
    "mastermindHints": "I have added a random loop entry for goodly beeing",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Kill one of friends via serial killer virus\nHospital Incident to kill patient \ngirlStudent murders Rich man daughter \nSuicide\n\nThis script is extremely difficult for the protagonists. They essentially cannot win. Even if the protagonists have perfect information, they cannot guarantee a win. \n    Emily Forster",
    "creator": "Cookie Monster",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 4
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 95
      }
    ]
  },
  {
    "cast": {
      "richStudent": "brain",
      "policeOfficer": "fool",
      "classRep": "friend",
      "girlStudent": "conspiracyTheorist",
      "shrineMaiden": "friend",
      "alien": "privateInvestigator",
      "mysteryBoy": "obstinate",
      "patient": "person",
      "henchman": "person",
      "officeWorker": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "terrorism",
        "culprit": "patient"
      },
      {
        "day": 2,
        "incident": "terrorism",
        "culprit": "girlStudent"
      },
      {
        "day": 3,
        "incident": "fakedSuicide",
        "culprit": "policeOfficer"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "shrineMaiden"
      },
      {
        "day": 5,
        "incident": "hospitalIncident",
        "culprit": "mysteryBoy"
      }
    ],
    "title": "The Fall of the Vroom",
    "mainPlot": [
      "blackSchool"
    ],
    "subPlots": [
      "danceFools",
      "masterDetective"
    ],
    "story": "",
    "tragedySet": "mysteryCircle",
    "mastermindHints": "",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Friend dies: classRep or shrineMaiden\n :intrigue: on School\nHospital Incident Level 2\nTerrorism Level 2\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 0,
        "numberOfLoops": 5
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 96
      }
    ]
  },
  {
    "cast": {
      "boyStudent": "person",
      "girlStudent": "brain",
      "mysteryBoy": "serialKiller",
      "shrineMaiden": "friend",
      "policeOfficer": "person",
      "richStudent": "conspiracyTheorist",
      "officeWorker": "curmudgeon"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "increasingUnease",
        "culprit": "boyStudent"
      },
      {
        "day": 2,
        "incident": "missingPerson",
        "culprit": "shrineMaiden"
      },
      {
        "day": 3,
        "incident": "murder",
        "culprit": "mysteryBoy"
      },
      {
        "day": 4,
        "incident": "farawayMurder",
        "culprit": "girlStudent"
      }
    ],
    "title": "Hopeless",
    "mainPlot": [
      "lightAvenger"
    ],
    "subPlots": [
      "hideousScript"
    ],
    "story": "School sucks. The girlStudent thought. Might as well go away with a bang. How should she do this? She plans and plans... And finally decided no one is going to survive in the school... But, the school is weirder than she first thought. Can she successfully destroy the school?",
    "tragedySet": "firstSteps",
    "mastermindHints": "mysteryBoy is a double edge sword. One thing he can kill shrineMaiden. Another thing is that mysteryBoy can murder the Brain, girlStudent.\nYou can hide the brain within the students. \nYou can also use Murder and Faraway Murder to kill the shrineMaiden.\nYou only can win at the end of the loop, so be careful of those :goodwill: abilities that can reveal your plots.",
    "specialRules": [
      ""
    ],
    "victory-conditions": "shrineMaiden is dead.\n-There is 2 :intrigue: in the school. (Brain's starting location)\n\n",
    "creator": "The Gem Pirate",
    "daysPerLoop": 5,
    "difficultySets": [
      {
        "difficulty": 2,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 97
      }
    ]
  },
  {
    "cast": {
      "doctor": "keyPerson",
      "informer": "brain",
      "popIdol": "conspiracyTheorist",
      "officeWorker": "magician",
      "richStudent": "immortalRole",
      "shrineMaiden": "prophet",
      "alien": "person",
      "policeOfficer": "person",
      "patient": "person",
      "forensicSpecialist": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "popIdol"
      },
      {
        "day": 3,
        "incident": "suicide",
        "culprit": "doctor"
      },
      {
        "day": 4,
        "incident": "serialMurder",
        "culprit": "richStudent"
      },
      {
        "day": 5,
        "incident": "uproar",
        "culprit": "forensicSpecialist"
      },
      {
        "day": 6,
        "incident": "hospitalIncident",
        "culprit": "policeOfficer"
      },
      {
        "day": 7,
        "incident": "confession",
        "culprit": "officeWorker"
      }
    ],
    "title": "Overvroomed?",
    "mainPlot": [
      "secretRecord"
    ],
    "subPlots": [
      "worshippersApocalypse",
      "showtimeDeath"
    ],
    "story": "",
    "tragedySet": "midnightZone",
    "mastermindHints": "",
    "specialRules": [
      "None"
    ],
    "victory-conditions": "Kill Key Person: doctor, 6 or fewer characters left alive, Brain/Magician revealed (informer/officeWorker)\n\n",
    "creator": "Justin Karp",
    "daysPerLoop": 7,
    "difficultySets": [
      {
        "difficulty": 3,
        "numberOfLoops": 3
      }
    ],
    "set": [
      {
        "name": "www.tragedylooperscripts.com (not reviewed)",
        "number": 98
      }
    ]
  }
],
 ...[
  {
    "title": "My First Tragedy Looper Scenario",
    "creator": "Sawerux",
    "difficultySets": [
      {
        "numberOfLoops": 3,
        "difficulty": 5
      },
      {
        "numberOfLoops": 4,
        "difficulty": 4
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "threadsFate",
      "unsettlingRumor"
    ],
    "daysPerLoop": 4,
    "cast": {
      "alien": "keyPerson",
      "boyStudent": "conspiracyTheorist",
      "mysteryBoy": "lovedOne",
      "journalist": "person",
      "richStudent": "person",
      "shrineMaiden": "person"
    },
    "incidents": [
      {
        "day": 1,
        "incident": "farawayMurder",
        "culprit": "richStudent"
      },
      {
        "day": 2,
        "incident": "increasingUnease",
        "culprit": "alien"
      },
      {
        "day": 3,
        "incident": "butterflyEffect",
        "culprit": "shrineMaiden"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "mysteryBoy"
      }
    ],
    "description": "Idk, more strange things in this scenario but i love idea of some things here.",
    "specialRules": [
      "Time Travaler Ability works at the end of the loop."
    ],
    "victory-conditions": "2 INTRIGUE ON KEY CHARACTER AT THE END OF THE LOOP; Alien.\nKILLING PROTAGONISTS; Loved One Ability.",
    "story": "No story for this time :3",
    "mastermindHints": "This Scenario made more for conceal, so be careful to Kill the Key Person or Shrine Maiden (Try to Stack intrigue of both of them.)\nAlso, try to Stack Intrigue on the Shrine and Don't play more Anti Goodwill cards without need.\nBe careful about Mystery Boy suicide, if you wanna win by their his ability. (Because they suicide faster then you win in 4 day.)\nDon't forget about Murder in First Day, try to guess between move in the city and paranoia stack on she to don't kill anyone.",
    "submitter": "Sawerux",
    "submitDate": "2026-25-01"
  }
],
 ...[
  {
    "title": "Techno-virus",
    "creator": "null :D",
    "difficultySets": [
      {
        "numberOfLoops": 4,
        "difficulty": 3
      }
    ],
    "tragedySet": "basicTragedy",
    "mainPlot": [
      "signWithMe"
    ],
    "subPlots": [
      "paranoiaVirus",
      "loveAffair"
    ],
    "daysPerLoop": 4,
    "cast": {
      "popIdol": "keyPerson",
      "ai": "conspiracyTheorist",
      "metaworldDenizen": "lover",
      "transferStudent": [
        "lovedOne",
        {
          "enters on day": 2
        }
      ],
      "classRep": "person",
      "officeWorker": "person",
      "richStudent": "person"
    },
    "incidents": [
      {
        "day": 2,
        "incident": "farawayMurder",
        "culprit": "classRep"
      },
      {
        "day": 3,
        "incident": "murder",
        "culprit": "blackCat"
      },
      {
        "day": 4,
        "incident": "suicide",
        "culprit": "popIdol"
      }
    ],
    "description": "A strange game..",
    "specialRules": [
      ""
    ],
    "victory-conditions": "Being in a loop with no tragedies.",
    "story": "",
    "mastermindHints": "",
    "submitter": "LoganEndreman",
    "submitDate": "2026-28-01"
  }
],
 ...[]
] as const;
    ;


export const tags = [

 ...[
  {
    "id": "immortal",
    "name": "Immortal",
    "description": "This character cannot die.",
    "showInGmScreen": true
  }
],
 ...[
  {
    "id": "afterdeath",
    "name": "after death",
    "showInGmScreen": true
  }
],
 ...[
  {
    "id": "supernaturalTag",
    "name": "Supernatural"
  }
]
] as const;
    export const tagsLookup = tags.reduce((p, c) => {
        if (typeof c === 'object' && c !== null && 'id' in c) {
            p[c.id] = c;
        }
        return p;
    }, {} as Record<typeof tags[number]['id'], ReadonlyRecursive<Tag>>);
    
    export function isTagId(id: unknown): id is keyof typeof tagsLookup {
        return typeof id === 'string' && id in tagsLookup;
    }
    
    